import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useIsCraneHire, useIsConcretePumping } from '../hooks/useCurrentIndustry';
import { useChat } from '../context/ChatContext';
import { useTier } from '../context/TierContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import QuickAddCustomerDialog from '../components/QuickAddCustomerDialog';
import { SupportAssetPanel } from '../components/SupportAssetPanel';
import CancelJobDialog from '../components/CancelJobDialog';
// DryHireInvoicePreview retired — replaced by schedule/DryHireInvoicingPanel.
import HireLifecyclePanel from '../components/HireLifecyclePanel';
import HaulageStopsEditor from '../components/HaulageStopsEditor';
import { RouteProgressList } from '../components/RouteProgressList';
import PinpointMapModal from '../components/PinpointMapModal';
import CompliancePackModal from '../components/CompliancePackModal';
import HireInspectionsReviewPanel from '../components/HireInspectionsReviewPanel';
import { RigTrailers } from '../components/RigTrailers';
import { ComboMembers } from '../components/ComboMembers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { AutoResizeTextarea } from '../components/ui/auto-resize-textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SortableTableHead } from '../components/ui/sortable-table-head';
import { useSortableTable } from '../hooks/useSortableTable';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { ListViewHeader } from '../components/ui/list-view-header';
import { GooglePlacesAutocomplete } from '../components/ui/google-places-autocomplete';
import useLocale from '../hooks/useLocale';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  List,
  LayoutGrid,
  CalendarDays,
  Folder,
  AlertTriangle,
  Pencil,
  Search,
  MapPin,
  Building2,
  Truck,
  Users,
  Clock,
  MoreHorizontal,
  Copy,
  RefreshCw,
  Kanban,
  Wrench,
  FileText,
  Star,
  Sparkles,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  MessageSquare,
  User,
  Eye,
  EyeOff,
  FileDown,
  Send,
  Check,
  X,
  ExternalLink,
  ClipboardCheck,
  ClipboardList,
  AlertCircle,
  RotateCcw,
  Ban,
  Loader2,
  ArrowRight,
  CalendarPlus,
  Camera,
  Package,
  Phone,
  Mail,
  MailCheck,
  MailX,
  Upload,
  File as FileIcon,
  Bell,
  Layers,
  Filter,
  Moon,
  Link2,
  LinkIcon,
  Navigation,
  ShieldCheck,
  Shield,
  Receipt,
  Repeat,
} from 'lucide-react';
import SendJobConfirmationModal from '../components/SendJobConfirmationModal';
import RecurrenceConfig from '../components/RecurrenceConfig';
import { RecurringIndicator, ViewPlanDialog, EditFutureDialog, CancelFutureDialog, ChangePatternDialog } from '../components/RecurringJobManager';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { DatePicker } from '../components/ui/date-picker';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { format, addDays, subDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, parse, parseISO } from 'date-fns';
import { usePresence, isVersionConflict } from '../hooks/usePresence';
import { PresenceIndicator, VersionConflictAlert } from '../components/PresenceIndicator';
import { JobFilesTab } from '../components/JobFilesTab';
import { JobActivityLog } from '../components/JobActivityLog';
import NotifyOperatorDialog from '../components/NotifyOperatorDialog';
import MobilisationDialog from './MobilisationDialog';
import TransportPromptDialog from '../components/TransportPromptDialog';
import SetupGroupPanel from './SetupGroupPanel';
import SetupGroupGanttRibbon from '../components/SetupGroupGanttRibbon';
import DryHireView from '../components/schedule/DryHireView';
import TowerCraneView from '../components/schedule/TowerCraneView';
import DryHireAllocationModal from '../components/schedule/DryHireAllocationModal';
import DryHireAssetPicker from '../components/schedule/DryHireAssetPicker';
import DryHireInvoicingPanel from '../components/schedule/DryHireInvoicingPanel';
import { getStatesForCountry, getCountryByCode } from '../utils/countryStates';
import { formatTime, formatDateTime } from '../utils/dateFormatting';
import { hasDepotTimes, deriveDepotTravel } from '../lib/docketTimes';
import { getApiErrorMessage } from '../utils/apiError';
import { notifyAllocationConflicts } from '../utils/allocationConflicts';
import { clearMinHire } from '../utils/docketItems';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { TimeField } from '../components/ui/time-field';
import ScheduleFolderView from '../components/schedule/ScheduleFolderView';
import QuickReserveDialog from '../components/QuickReserveDialog';
import { CalendarClock } from 'lucide-react';
import { WorkerAckDot } from '../components/workerAck/WorkerAckDot';
import { JobAckPanel } from '../components/workerAck/JobAckPanel';
import NotifyWorkersDialog from '../components/NotifyWorkersDialog';
import AssetRequirementsEditor from '../components/AssetRequirementsEditor';
import LeaveOverrideDialog from '../components/LeaveOverrideDialog';
import { DayCrewReadOnly } from '../components/DayCrewReadOnly';
import { getLeaveBlock, withLeaveOverride } from '../lib/leaveOverride';

// Safely turn an API error (incl. FastAPI 422 detail arrays) into a toast-safe string.
const parseApiError = (error, fallback = 'Operation failed') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg || d?.message || 'Invalid input').join(', ');
  if (typeof detail === 'object') return detail.msg || detail.message || fallback;
  return fallback;
};


const Schedule = () => {
  const { api, user } = useAuth();
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const notifyOperatorEnabled = isFeatureEnabled('notify_operator');
  const workerAckEnabled = isFeatureEnabled('worker_job_confirmation');
  const compliancePacksEnabled = isFeatureEnabled('compliance_packs');
  const multiAssetEnabled = isFeatureEnabled('multi_asset_jobs');
  const projectsEnabled = isFeatureEnabled('projects');
  const isCraneHire = useIsCraneHire();
  const isConcretePumping = useIsConcretePumping();
  const { openJobChat, canUseChat } = useChat();
  const { isAiAvailable } = useTier();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Initial tab/view honours `?view=` so external links (e.g. Dashboard's
  // "Dry-Hire Operations" card → /schedule?view=dryhire) land on the right tab.
  const initialView = ['list', 'week', 'board', 'dryhire', 'towercrane', 'folders'].includes(searchParams.get('view'))
    ? searchParams.get('view')
    : 'list';
  const [view, setView] = useState(initialView);
  // Week-view row density (persisted per device). Drives CSS variables on the
  // grid container so row heights change without touching the fragile grid math.
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('schedule_density') || 'comfortable'; } catch { return 'comfortable'; }
  });
  useEffect(() => {
    try { localStorage.setItem('schedule_density', density); } catch (e) { /* ignore */ }
  }, [density]);
  const densityHeights = ({
    comfortable: { row: '80px', span: '72px', cell: '40px', cellEmpty: '80px' },
    cozy:        { row: '60px', span: '54px', cell: '32px', cellEmpty: '60px' },
    compact:     { row: '44px', span: '40px', cell: '24px', cellEmpty: '44px' },
  })[density] || { row: '80px', span: '72px', cell: '40px', cellEmpty: '80px' };

  // Bumps when a dry-hire job is created/updated/transitioned so the
  // Dry-Hire view re-fetches itself without a full schedule refresh.
  const [dryHireRefreshKey, setDryHireRefreshKey] = useState(0);
  // Standalone centered modal for the slim "Allocate Asset" flow on dry-hire.
  const [dryHireAllocModal, setDryHireAllocModal] = useState({ open: false, job: null, allocation: null });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [allocations, setAllocations] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [operators, setOperators] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [depots, setDepots] = useState([]);
  const [sites, setSites] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedMultiDayJobs, setExpandedMultiDayJobs] = useState({}); // Track expanded state for multi-day jobs
  const [tenantSettings, setTenantSettings] = useState({ 
    date_format: 'DD/MM/YYYY', 
    time_format: '24h',
    timezone: 'Australia/Sydney',
    currency: 'AUD',
    currency_symbol: '$',
    tax_name: 'GST',
    tax_rate: 10,
    industry: 'crane_hire',
    locale: 'en-AU',
    country: 'AU'
  });
  const t = useLocale(tenantSettings.locale);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assetTypeFilter, setAssetTypeFilter] = useState([]);
  const [quickDateFilter, setQuickDateFilter] = useState('today'); // Default to Today
  const [dateFilterEnabled, setDateFilterEnabled] = useState(false);
  const [dateFrom, setDateFrom] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  // List-view row grouping (v1: none | customer | asset_type | site). Persisted per browser.
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem('schedule_list_group_by') || 'none');
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Job Dialog
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [scheduleReserveOpen, setScheduleReserveOpen] = useState(false);

  // Auto-open Create Job dialog when navigated with state.openJobDialog
  useEffect(() => {
    if (location.state?.openJobDialog) {
      setEditingJob(null);
      setJobDialogOpen(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  // Mandatory-forms completion status per job for schedule card indicators { job_id: {total, completed, all_complete} }
  const [mandatoryFormsStatus, setMandatoryFormsStatus] = useState({});
  const [jobDrawerTab, setJobDrawerTab] = useState('details');
  const [editingJob, setEditingJob] = useState(null);
  const [emailJob, setEmailJob] = useState(null);
  const [jobEditMode, setJobEditMode] = useState('overall'); // 'overall' or 'day'
  const [editingJobDate, setEditingJobDate] = useState(null); // Date being edited for day-specific edit
  const [editModePromptOpen, setEditModePromptOpen] = useState(false); // Prompt for multi-day jobs
  const [suggestedEditDate, setSuggestedEditDate] = useState(null); // Date to highlight in prompt when opened from child row
  const [dayWorkDescription, setDayWorkDescription] = useState(''); // Day-specific work description
  const [dayStartTime, setDayStartTime] = useState(''); // Per-day start time override (HH:MM)
  const [dayEndTime, setDayEndTime] = useState(''); // Per-day end time override (HH:MM)
  const [reschedulePrompt, setReschedulePrompt] = useState({ open: false, jobId: null, deltaDays: 0, newStart: null, newEnd: null });
  const [dayWorkerSelections, setDayWorkerSelections] = useState([]); // [{worker_type_id, operator_id}] for day-specific worker editing
  const [jobDayDetails, setJobDayDetails] = useState({}); // Cache of day details keyed by job_id
  const [versionConflict, setVersionConflict] = useState(null); // For optimistic locking
  const [staleDataBanner, setStaleDataBanner] = useState(null); // For freshness polling notifications
  const [mobilisationPrompt, setMobilisationPrompt] = useState({ open: false, jobId: null, jobData: null });
  const [transportPrompt, setTransportPrompt] = useState({
    open: false, jobId: null, jobData: null, equipmentType: null, transportType: null
  });
  // Compliance Pack generator — opens for a job from the row dropdown
  const [compliancePackJob, setCompliancePackJob] = useState(null);
  const lastFetchTimestampRef = useRef(new Date().toISOString());
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [activeProjects, setActiveProjects] = useState([]);
  const [projectPopoverOpen, setProjectPopoverOpen] = useState(false);
  const [jobForm, setJobForm] = useState({
    parent_job_id: '',
    customer_id: '',
    contact_id: '',
    site_id: '',
    site_contact_ids: [], // Site contacts for this job
    // Structured one-off address fields
    one_off_street: '',
    one_off_suburb: '',
    one_off_state: '',
    one_off_postcode: '',
    use_one_off_address: false, // Toggle between site and one-off address
    requires_multi_stops: false,
    latitude: null,
    longitude: null,
    equipment_type: '',
    create_equipment_id: '',
    attachment_type_ids: [],
    worker_requirements: [], // [{worker_type_id, quantity}]
    start_date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '07:00',
    end_time: '',
    depot_time: '',
    depot_id: '',
    end_date: '',
    est_duration: '',
    description: '',
    status: 'not_allocated',
    notes: '',
    tags: [],
    is_night_shift: false,
    is_tbc: false,
    po_number: '',
    starting_location: '',
    // Dry-Hire (Asset-Only) fields — wet is the default, matching the
    // backend model. When hire_type === 'dry' the operator-allocation UI
    // is suppressed and the hire-period fields appear.
    hire_type: 'wet',
    hire_period_unit: '',
    pickup_from_yard: false,
    return_to_yard: false,
    hire_notes: '',
    expected_off_hire_date: '',
    is_tower_crane: false,
    tower_crane_billing_period: '',
    // Multi-asset (gated by multi_asset_jobs flag)
    asset_requirements: [{ equipment_type: '', quantity: 1 }],
    // Project link (gated by projects flag)
    project_id: '',
  });

  const [tagInput, setTagInput] = useState('');
  
  // Pending files for new job creation (uploaded after job is created)
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingFilesVisible, setPendingFilesVisible] = useState(false);
  // Recurring job state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceConfig, setRecurrenceConfig] = useState(null);
  // Recurring series management dialogs
  const [viewPlanOpen, setViewPlanOpen] = useState(false);
  const [editFutureOpen, setEditFutureOpen] = useState(false);
  const [cancelFutureOpen, setCancelFutureOpen] = useState(false);
  const [changePatternOpen, setChangePatternOpen] = useState(false);
  const pendingFileInputRef = useRef(null);
  
  // Customer contacts state
  const [customerContacts, setCustomerContacts] = useState([]);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    position: ''
  });

  // Site contacts state
  const [siteContacts, setSiteContacts] = useState([]);
  const [addSiteContactOpen, setAddSiteContactOpen] = useState(false);
  const [savingSiteContact, setSavingSiteContact] = useState(false);
  const [newSiteContact, setNewSiteContact] = useState({ name: '', job_title: '', phone: '', email: '' });

  // Track if selected customer requires PO
  const [selectedCustomerRequiresPO, setSelectedCustomerRequiresPO] = useState(false);
  
  // Customer combobox state
  const [customerComboboxOpen, setCustomerComboboxOpen] = useState(false);
  // Contact combobox state
  const [contactComboboxOpen, setContactComboboxOpen] = useState(false);
  // Site combobox state
  const [siteComboboxOpen, setSiteComboboxOpen] = useState(false);
  // Site Contact combobox state
  const [siteContactComboboxOpen, setSiteContactComboboxOpen] = useState(false);
  // Asset Type combobox state
  const [assetTypeComboboxOpen, setAssetTypeComboboxOpen] = useState(false);
  // Asset (equipment) combobox state for create-time allocation
  const [createAssetComboboxOpen, setCreateAssetComboboxOpen] = useState(false);

  // Presence tracking for job editing
  const { othersViewing: jobOthersViewing, removePresence: removeJobPresence } = usePresence(
    'job',
    editingJob?.id,
    { enabled: !!editingJob?.id }
  );

  // States for dropdown based on tenant country
  const statesList = getStatesForCountry(tenantSettings.country);
  const countryMeta = getCountryByCode(tenantSettings.country);

  // Allocation Dialog
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [dayEdit, setDayEdit] = useState(null); // { jobId, date, allocationId } — day-row edit of a multi-day job
  useEffect(() => { if (!allocationDialogOpen) setDayEdit(null); }, [allocationDialogOpen]);
  const [unavailableWorkers, setUnavailableWorkers] = useState({}); // { worker_id: [entries] }
  const [selectedJobForAllocation, setSelectedJobForAllocation] = useState(null);
  const [confirmJobId, setConfirmJobId] = useState(null);
  const [editingAllocation, setEditingAllocation] = useState(null);
  const [allocationVersionConflict, setAllocationVersionConflict] = useState(null);
  const [overrideCheck, setOverrideCheck] = useState(false);
  // EW2-286 — compliance block/override dialog
  const [complianceBlock, setComplianceBlock] = useState({ open: false });
  const [complianceReason, setComplianceReason] = useState('');
  // Leave override dialog (preflight blocks or fallback catch)
  const [leaveOverride, setLeaveOverride] = useState({ open: false, blocks: [], callback: null });
  const [allocationForm, setAllocationForm] = useState({
    equipment_id: '',
    operator_id: '',
    start_date: '',
    end_date: '',
    attachment_ids: [],
    worker_allocations: [], // [{worker_type_id, operator_id}]
    // Combo allocation fields
    is_combo_allocation: false,
    combo_equipment_ids: [], // [{component_type_id, equipment_id}]
    // Mobilisation Phase 2.1 — Prime Mover pairing
    prime_mover_equipment_id: '',
    // Multi-trailer rigs (B-Double / Road Train) — trailers 2..N
    additional_trailer_equipment_ids: [],
    // Multi-asset job: which requirement slot this allocation is for
    asset_requirement_id: '',
    asset_requirement_index: null,
  });
  
  // Presence tracking for allocation editing
  const { othersViewing: allocationOthersViewing, removePresence: removeAllocationPresence } = usePresence(
    'allocation',
    editingAllocation?.id,
    { enabled: !!editingAllocation?.id }
  );
  
  // Worker allocations state
  const [workerAllocations, setWorkerAllocations] = useState([]);
  
  // Allocation conflict warning state
  const [conflictWarnings, setConflictWarnings] = useState([]);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [pendingAllocationData, setPendingAllocationData] = useState(null);
  
  // EW2-311: Designated operator warning state
  const [designatedOpWarning, setDesignatedOpWarning] = useState({ open: false, operator: null, reason: null, equipmentId: null });
  
  // EW2-311: Check designated operator when equipment is selected
  const handleEquipmentSelectionForAlloc = async (equipmentId) => {
    setAllocationForm(prev => ({ ...prev, equipment_id: equipmentId }));
    
    if (!equipmentId) return;
    
    // Only auto-assign for wet-hire
    const job = selectedJobForAllocation;
    if (job?.hire_type === 'dry' || job?.hire_type === 'labour') return;
    
    // Determine the date for this allocation
    const allocDate = allocationForm.start_date || selectedDateForAllocation || editingAllocation?.date;
    if (!allocDate) return;
    
    try {
      const { data } = await api.get(`/equipment/${equipmentId}/designated-operator-availability?date=${allocDate}`);
      if (!data.designated_operator) return; // No designated operator
      
      if (data.available) {
        // Auto-assign with toast
        setAllocationForm(prev => ({ ...prev, operator_id: data.designated_operator.id }));
        // Show non-blocking toast for pending or part-day leave warnings
        if (data.warning === 'leave_pending') {
          toast.info(`Designated operator ${data.designated_operator.name} has requested leave`);
        } else if (data.warning === 'part_day_leave') {
          toast.info(`Designated operator ${data.designated_operator.name} has part-day leave`);
        } else {
          toast.success(`Auto-assigned designated operator: ${data.designated_operator.name}`);
        }
      } else {
        // Show warning dialog
        setDesignatedOpWarning({
          open: true,
          operator: data.designated_operator,
          reason: data.reason,
          equipmentId,
        });
      }
    } catch (e) {
      // Non-fatal — just skip auto-assignment
    }
  };

  const [conflictContext, setConflictContext] = useState(null); // 'single' or 'multi'
  
  // Multi-day allocation management drawer
  const [multiDayDrawerOpen, setMultiDayDrawerOpen] = useState(false);
  const [multiDayJob, setMultiDayJob] = useState(null);
  const [multiDayAllocations, setMultiDayAllocations] = useState([]);
  const [multiDayStats, setMultiDayStats] = useState(null);
  const [loadingMultiDay, setLoadingMultiDay] = useState(false);
  const [editingMultiDayAllocation, setEditingMultiDayAllocation] = useState(null);
  const [deleteBlockConfirm, setDeleteBlockConfirm] = useState(null); // allocation ID to confirm delete
  const [multiDayAllocationForm, setMultiDayAllocationForm] = useState({
    start_date: '',
    end_date: '',
    operator_id: '',
    equipment_id: '',
    status: 'draft',
    attachment_ids: [],
    worker_allocations: [],
    is_combo_allocation: false,
    combo_equipment_ids: []
  });
  
  // Extend Allocations Modal (when job end date is extended)
  const [extendAllocationsModal, setExtendAllocationsModal] = useState({
    open: false,
    jobId: null,
    jobDescription: '',
    oldEndDate: null,
    newEndDate: null,
    allocations: [],
    pendingJobData: null
  });

  // Cascade-mobilisation-dates modal (when a setup-parent job's date changes
  // and we need to ask whether to shift the linked mob/demob legs too).
  const [cascadeMobModal, setCascadeMobModal] = useState({
    open: false,
    jobId: null,
    jobDescription: '',
    setupGroupId: null,
    deltaDays: 0,
    legCount: 0,
    pendingJobData: null,
  });

  // Extend Job Modal (quick extend from list view)
  const [extendJobModal, setExtendJobModal] = useState({
    open: false,
    job: null,
    dateFrom: '',
    dateTo: '',
    allocations: [],
    extendAllocations: true,
    loading: false
  });

  // Delete Job Confirmation State
  const [deleteJobDialogOpen, setDeleteJobDialogOpen] = useState(false);
  const [weekPreSelectedEquipmentId, setWeekPreSelectedEquipmentId] = useState(null);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [isDeletingJob, setIsDeletingJob] = useState(false);

  // Cancel Job State
  const [cancelJobDialogOpen, setCancelJobDialogOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState(null);
  const [isCancellingJob, setIsCancellingJob] = useState(false);
  
  // Smart Operator Suggestions
  const [operatorSuggestions, setOperatorSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false); // Collapsed by default

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState([]);

  // Quick Add Customer Dialog
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);

  // Quick Add Site Dialog
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [siteDialogStopIdx, setSiteDialogStopIdx] = useState(null);
  const [parentJobSearchDate, setParentJobSearchDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [pinpointMapOpen, setPinpointMapOpen] = useState(false); // Track which haulage stop triggered Add Site
  const [siteForm, setSiteForm] = useState({
    name: '',
    street_address: '',
    suburb: '',
    state: '',
    postcode: '',
    notes: ''
  });

  // Docket Dialog
  const [docketDialogOpen, setDocketDialogOpen] = useState(false);

  // Notify Operator Dialog
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [notifyJobData, setNotifyJobData] = useState(null);
  const [notifyAllocationData, setNotifyAllocationData] = useState(null);

  // Duplicate Job PO Dialog
  const [duplicatePoDialogOpen, setDuplicatePoDialogOpen] = useState(false);
  const [duplicateJobData, setDuplicateJobData] = useState(null);
  const [duplicatePoNumber, setDuplicatePoNumber] = useState('');
  const [duplicatePoUseExisting, setDuplicatePoUseExisting] = useState(true);
  const [duplicateJobDate, setDuplicateJobDate] = useState(null);
  const [duplicateJobEndDate, setDuplicateJobEndDate] = useState(null);
  const [selectedJobForDocket, setSelectedJobForDocket] = useState(null);
  const [docketDateForJob, setDocketDateForJob] = useState(null); // The date for the docket (for multi-day jobs)
  const [docketItems, setDocketItems] = useState([]);
  const [docketNotes, setDocketNotes] = useState('');
  const [docketWorkCompleted, setDocketWorkCompleted] = useState('');
  const [docketPaperUrl, setDocketPaperUrl] = useState('');
  const [docketPaperName, setDocketPaperName] = useState('');
  const [docketUploading, setDocketUploading] = useState(false);
  const [priceLists, setPriceLists] = useState([]);
  const [customerPriceList, setCustomerPriceList] = useState(null);
  const [selectedPriceListId, setSelectedPriceListId] = useState('');
  const [equipmentTypesWithIds, setEquipmentTypesWithIds] = useState([]);
  
  // Material Loads (for earthmoving industry)
  const [docketMaterialLoads, setDocketMaterialLoads] = useState([]);
  // Concrete pumping: cubic metres pumped (required for concrete_pumping tenants)
  const [docketConcretePumped, setDocketConcretePumped] = useState('');
  const [showMaterialLoadDialog, setShowMaterialLoadDialog] = useState(false);
  const [editingMaterialLoad, setEditingMaterialLoad] = useState(null);
  const [yards, setYards] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);

  const [viewDocketDrawerOpen, setViewDocketDrawerOpen] = useState(false);
  const [viewingDocket, setViewingDocket] = useState(null);
  const [photoLightbox, setPhotoLightbox] = useState({ open: false, index: 0 });
  const [photoBlobUrls, setPhotoBlobUrls] = useState([]);
  const [jobDocketsList, setJobDocketsList] = useState([]);
  const [docketsListDrawerOpen, setDocketsListDrawerOpen] = useState(false);
  const [selectedJobForDocketsList, setSelectedJobForDocketsList] = useState(null);
  const [jobDocketsCache, setJobDocketsCache] = useState({}); // Cache dockets per job_id
  
  // Docket editing state for Invoice tab
  const [docketEditItems, setDocketEditItems] = useState([]);
  const [docketEditNotes, setDocketEditNotes] = useState('');
  const [docketSaving, setDocketSaving] = useState(false);
  const [docketRecalculating, setDocketRecalculating] = useState(false);
  const [docketUsedPriceList, setDocketUsedPriceList] = useState(null);

  // Computed: depot mode for the create docket sheet (excludes haulage/multi-stop jobs)
  const depotTimesEffective = !!(isFeatureEnabled('docket_depot_times') && tenantSettings.docket_depot_times_enabled);
  const sheetDepotMode = depotTimesEffective && !(selectedJobForDocket?.job_type === 'haulage' || selectedJobForDocket?.requires_multi_stops);

  const fetchData = async () => {
    try {
      // Use optimized V2 endpoint - single call replaces 14 API calls
      const [initRes, docketsRes, attachmentsRes] = await Promise.all([
        api.get('/v2/schedule/init'),
        api.get('/dockets'),
        api.get('/attachments'),
      ]);
      
      const data = initRes.data;
      
      // New structured response: { ui_config, filters, resources, schedule }
      const { ui_config, filters, resources, schedule } = data;
      
      // Set schedule data
      setAllocations(schedule?.allocations || []);
      setJobs(schedule?.jobs || []);
      setWorkerAllocations(schedule?.worker_allocations || []);
      // Bump the Dry-Hire view's refresh key so it re-syncs whenever the
      // schedule data refreshes (e.g. after job create/edit/allocate).
      setDryHireRefreshKey(k => k + 1);
      
      // Set resource data
      setEquipment(resources?.equipment || []);
      setOperators(resources?.operators || []);
      setCustomers(resources?.customers || []);
      setSites(resources?.sites || []);
      
      // Set filter/lookup data
      setEquipmentTypes(filters?.equipment_types || []);
      setWorkerTypes(filters?.worker_types || []);
      setAttachments(attachmentsRes.data || []); // Trailer Load P3 — chips rely on instances
      setAttachmentTypes(filters?.attachment_types || []);
      setEquipmentTypesWithIds(filters?.equipment_types || []);
      
      // Build dockets cache by job_id for quick lookup
      const docketsData = docketsRes.data || [];
      const docketsByJob = {};
      docketsData.forEach(d => {
        if (!docketsByJob[d.job_id]) {
          docketsByJob[d.job_id] = [];
        }
        docketsByJob[d.job_id].push(d);
      });
      setJobDocketsCache(docketsByJob);
      
      if (ui_config) {
        setTenantSettings({
          date_format: ui_config.date_format || 'DD/MM/YYYY',
          time_format: ui_config.time_format || '24h',
          timezone: ui_config.timezone || 'Australia/Sydney',
          currency: ui_config.currency || 'AUD',
          currency_symbol: ui_config.currency_symbol || '$',
          tax_name: ui_config.tax_name || 'GST',
          tax_rate: ui_config.tax_rate ?? 10,
          industry: ui_config.industry || 'crane_hire',
          depot_time_enabled: ui_config.field_rules?.depot_time?.enabled ?? ui_config.depot_time_enabled ?? false,
          default_depot_time: ui_config.default_depot_time || '06:30',
          depots_enabled: ui_config.depots_enabled ?? false,
          worker_job_email_enabled: ui_config.worker_job_email_enabled ?? false,
          locale: ui_config.locale || 'en-AU',
          field_rules: ui_config.field_rules || {},
          country: ui_config.country || 'AU',
          xero_connected: ui_config.xero_connected || false,
          myob_connected: ui_config.myob_connected || false,
          auto_confirm_jobs: ui_config.auto_confirm_jobs ?? true,
          separate_operator_line: ui_config.separate_operator_line ?? false,
          docket_depot_times_enabled: ui_config.docket_depot_times_enabled ?? false,
          cancellation_reasons: ui_config.cancellation_reasons || [],
        });
        if (ui_config.depots_enabled) {
          api.get('/depots?active_only=true').then(r => setDepots(r.data || [])).catch(() => {});
        }
      }
      
      // Fetch day details for multi-day jobs
      const multiDayJobs = (schedule?.jobs || []).filter(j => j.end_date && j.end_date !== j.start_date);
      if (multiDayJobs.length > 0) {
        try {
          const dayDetailsRes = await api.get('/job-day-details');
          const detailsByJob = {};
          dayDetailsRes.data.forEach(d => {
            if (!detailsByJob[d.job_id]) {
              detailsByJob[d.job_id] = {};
            }
            detailsByJob[d.job_id][d.date] = d;
          });
          setJobDayDetails(detailsByJob);
        } catch (err) {
          console.error('Failed to fetch job day details:', err);
        }
      }
    } catch (error) {
      console.error('Failed to fetch schedule data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      lastFetchTimestampRef.current = new Date().toISOString();
      setStaleDataBanner(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  // Fetch active projects for the project selector (lazy, when flag is on)
  const fetchActiveProjects = useCallback(async () => {
    if (!projectsEnabled) return;
    try {
      const { data } = await api.get('/projects', { params: { status: 'active', page_size: 200 } });
      setActiveProjects(data.items || []);
    } catch { setActiveProjects([]); }
  }, [api, projectsEnabled]);

  // Auto-open job from URL param (e.g., ?editJob=<jobId>&date=2026-04-08)
  useEffect(() => {
    const editJobId = searchParams.get('editJob');
    const dateParam = searchParams.get('date');
    
    // Handle date param from Planning Board navigation
    if (dateParam && !editJobId) {
      setCurrentDate(parse(dateParam, 'yyyy-MM-dd', new Date()));
      setQuickDateFilter('all');
      setDateFilterEnabled(true);
      setDateFrom(dateParam);
      setDateTo(dateParam);
      searchParams.delete('date');
      setSearchParams(searchParams, { replace: true });
    }
    
    if (editJobId && jobs.length > 0) {
      const job = jobs.find(j => j.id === editJobId);
      if (job) {
        setEditingJob(job);
        setEditingJobDate(dateParam || job.start_date);
        // Clear the param so it doesn't re-trigger
        searchParams.delete('editJob');
        searchParams.delete('date');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [jobs, searchParams]);

  // Re-fetch data when returning from Settings if tenant config was changed
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const updated = sessionStorage.getItem('tenant_settings_updated');
        if (updated) {
          sessionStorage.removeItem('tenant_settings_updated');
          fetchData();
        }
      }
    };
    const handleFocus = () => {
      const updated = sessionStorage.getItem('tenant_settings_updated');
      if (updated) {
        sessionStorage.removeItem('tenant_settings_updated');
        fetchData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Poll for schedule freshness every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/v2/schedule/freshness?since=${encodeURIComponent(lastFetchTimestampRef.current)}`);
        if (data.has_changes) {
          const parts = [];
          if (data.jobs_created > 0) parts.push(`${data.jobs_created} new job${data.jobs_created > 1 ? 's' : ''}`);
          if (data.jobs_modified > 0) parts.push(`${data.jobs_modified} job${data.jobs_modified > 1 ? 's' : ''} modified`);
          if (data.allocations_modified > 0) parts.push(`${data.allocations_modified} allocation${data.allocations_modified > 1 ? 's' : ''} changed`);
          setStaleDataBanner(parts.join(', ') || 'Schedule updated by another user');
        }
      } catch {
        // Silent fail for polling
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [api]);

  const fetchTags = async () => {
    try {
      const response = await api.get('/jobs/tags/all');
      // Ensure tags are strings (handle both string and object formats)
      const tags = (response.data.tags || []).map(tag => 
        typeof tag === 'string' ? tag : (tag?.name || tag?.id || String(tag))
      ).filter(Boolean);
      setAvailableTags(tags);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData();
      toast.success('Data refreshed');
    } finally {
      setRefreshing(false);
    }
  };

  // Format date according to tenant settings
  // IMPORTANT: Date-only strings ("2026-04-03") must be parsed as LOCAL dates,
  // not UTC. Appending T00:00:00 forces local timezone interpretation.
  const toLocalDate = (dateString) => {
    if (!dateString) return null;
    if (typeof dateString !== 'string') return dateString; // already a Date
    return dateString.includes('T') ? new Date(dateString) : new Date(dateString + 'T00:00:00');
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return '-';
    const date = toLocalDate(dateString);
    const dateFormat = tenantSettings.date_format || 'DD/MM/YYYY';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[date.getMonth()];
    
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD-MMM-YYYY':
        return `${day}-${monthShort}-${year}`;
      default:
        return `${day}/${month}/${year}`;
    }
  };

  // Long date format for headers (e.g. "Thursday, 2 April 2026" or "Thursday, April 2, 2026")
  const formatDateLong = (date) => {
    if (!date) return '';
    const d = typeof date === 'string' ? toLocalDate(date) : date;
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayName = dayNames[d.getDay()];
    const day = d.getDate();
    const monthName = monthNames[d.getMonth()];
    const year = d.getFullYear();
    const dateFormat = tenantSettings.date_format || 'DD/MM/YYYY';

    if (dateFormat === 'MM/DD/YYYY') {
      return `${dayName}, ${monthName} ${day}, ${year}`;
    }
    return `${dayName}, ${day} ${monthName} ${year}`;
  };

  // Short date format for week headers (e.g. "Apr 2" or "2 Apr")
  const formatDateShort = (date) => {
    if (!date) return '';
    const d = typeof date === 'string' ? toLocalDate(date) : date;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = d.getDate();
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    const dateFormat = tenantSettings.date_format || 'DD/MM/YYYY';

    if (dateFormat === 'MM/DD/YYYY') {
      return `${month} ${day}`;
    }
    return `${day} ${month}`;
  };

  const formatDateShortWithYear = (date) => {
    if (!date) return '';
    const d = typeof date === 'string' ? toLocalDate(date) : date;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = d.getDate();
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    const dateFormat = tenantSettings.date_format || 'DD/MM/YYYY';

    if (dateFormat === 'MM/DD/YYYY') {
      return `${month} ${day}, ${year}`;
    }
    return `${day} ${month} ${year}`;
  };

  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const end = endOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  const navigateWeek = (direction) => {
    setCurrentDate(prev => addDays(prev, direction * 7));
  };

  const navigateDay = (direction) => {
    setCurrentDate(prev => addDays(prev, direction));
  };

  // ================== MEMOIZED LOOKUP MAPS ==================
  // These replace repeated .find() calls with O(1) Map lookups for performance
  const jobsMap = useMemo(() => new Map(jobs.map(j => [j.id, j])), [jobs]);
  const equipmentMap = useMemo(() => new Map(equipment.map(e => [e.id, e])), [equipment]);
  const operatorsMap = useMemo(() => new Map(operators.map(o => [o.id, o])), [operators]);
  const customersMap = useMemo(() => new Map(customers.map(c => [c.id, c])), [customers]);
  const sitesMap = useMemo(() => new Map(sites.map(s => [s.id, s])), [sites]);
  const allocationsMap = useMemo(() => new Map(allocations.map(a => [a.job_id, a])), [allocations]);
  const attachmentTypesMap = useMemo(() => new Map(attachmentTypes.map(t => [t.id, t])), [attachmentTypes]);

  // Week view: Build resource-based rows (asset rows) for vertical alignment
  const weekAssetRows = useMemo(() => {
    if (view !== 'week') return [];
    
    const weekStart = format(weekDays[0], 'yyyy-MM-dd');
    const weekEnd = format(weekDays[6], 'yyyy-MM-dd');
    const weekJobs = jobs.filter(job => {
      // Dry-hire jobs live on the dedicated Dry Hire tab.
      if (job.hire_type === 'dry') return false;
      const start = job.start_date;
      const end = job.end_date || job.start_date;
      const inWeek = start <= weekEnd && end >= weekStart;
      const matchesFilter = assetTypeFilter.length === 0 || assetTypeFilter.includes(job.equipment_type);
      return inWeek && matchesFilter;
    });

    const assetMap = new Map();
    const unallocated = { label: 'Unallocated', sortKey: 'zzz', equipmentType: null, equipmentId: null, assetId: null, operatorName: null, dayJobs: new Map(), spans: [] };
    const labourOnly = { label: 'Labour Only', sortKey: 'zzy_labour', equipmentType: 'No Asset', equipmentId: null, assetId: null, operatorName: null, dayJobs: new Map(), spans: [] };
    
    for (const job of weekJobs) {
      // "No Asset" jobs go directly to the Labour Only row
      if (job.equipment_type === 'No Asset') {
        for (const day of weekDays) {
          const dayStr = format(day, 'yyyy-MM-dd');
          const start = job.start_date;
          const end = job.end_date || job.start_date;
          if (dayStr >= start && dayStr <= end && !(job.excluded_dates && job.excluded_dates.includes(dayStr))) {
            if (!labourOnly.dayJobs.has(dayStr)) labourOnly.dayJobs.set(dayStr, []);
            labourOnly.dayJobs.get(dayStr).push(job);
          }
        }
        continue;
      }
      
      // For each day the job spans in the week, find the date-specific allocation
      // Use a Set to track which asset row this job belongs to (find allocation for any day)
      let jobAlloc = null;
      for (const day of weekDays) {
        const dayStr = format(day, 'yyyy-MM-dd');
        const start = job.start_date;
        const end = job.end_date || job.start_date;
        if (dayStr >= start && dayStr <= end && !(job.excluded_dates && job.excluded_dates.includes(dayStr))) {
          const dayAlloc = allocations.find(a => a.job_id === job.id && a.start_date <= dayStr && (a.end_date || a.start_date) >= dayStr);
          if (dayAlloc) { jobAlloc = dayAlloc; break; }
        }
      }
      
      let row;
      if (jobAlloc) {
        const eqId = jobAlloc.equipment_id || (
          jobAlloc.is_combo_allocation && jobAlloc.combo_equipment_ids?.length
            ? jobAlloc.combo_equipment_ids[0]
            : ''
        );
        const eq = eqId ? equipmentMap.get(eqId) : null;
        const assetKey = eqId || `combo-${jobAlloc.id}`;
        if (!assetMap.has(assetKey)) {
          const eqType = eq?.equipment_type || job.equipment_type || 'Unknown';
          const eqAssetId = eq?.asset_id || '';
          assetMap.set(assetKey, {
            label: eqAssetId ? `${eqType} (${eqAssetId})` : eqType,
            sortKey: `${eqType} ${eqAssetId}`.toLowerCase(),
            equipmentType: eqType,
            equipmentId: eqId,
            assetId: eqAssetId,
            operatorName: null,
            dayJobs: new Map(),
            spans: [],
          });
        }
        row = assetMap.get(assetKey);
        if (jobAlloc.operator_id) {
          const op = operatorsMap.get(jobAlloc.operator_id);
          if (op) row.operatorName = op.name;
        }
      } else {
        row = unallocated;
      }
      
      for (const day of weekDays) {
        const dayStr = format(day, 'yyyy-MM-dd');
        const start = job.start_date;
        const end = job.end_date || job.start_date;
        if (dayStr >= start && dayStr <= end && !(job.excluded_dates && job.excluded_dates.includes(dayStr))) {
          if (!row.dayJobs.has(dayStr)) row.dayJobs.set(dayStr, []);
          row.dayJobs.get(dayStr).push(job);
        }
      }
    }

    // --- Compute spanning bars for multi-day jobs ---
    const allRows = [...assetMap.values(), labourOnly, unallocated];
    for (const row of allRows) {
      const seenJobIds = new Set();
      for (const [, dayJobsList] of row.dayJobs) {
        for (const job of dayJobsList) {
          if (seenJobIds.has(job.id)) continue;
          seenJobIds.add(job.id);

          // Only multi-day jobs that appear on 2+ days in this week
          const visibleDayIndices = [];
          for (let i = 0; i < 7; i++) {
            const dayStr = format(weekDays[i], 'yyyy-MM-dd');
            if (row.dayJobs.get(dayStr)?.some(j => j.id === job.id)) {
              visibleDayIndices.push(i);
            }
          }
          if (visibleDayIndices.length < 2) continue;

          // Check contiguity: all indices must be consecutive
          let contiguous = true;
          for (let k = 1; k < visibleDayIndices.length; k++) {
            if (visibleDayIndices[k] !== visibleDayIndices[k - 1] + 1) {
              contiguous = false;
              break;
            }
          }
          if (!contiguous) continue;

          const startCol = visibleDayIndices[0];
          const endCol = visibleDayIndices[visibleDayIndices.length - 1];

          row.spans.push({ job, startCol, endCol });
        }
      }

      // Remove spanned jobs from dayJobs so they don't render as individual cards
      const spannedJobIds = new Set(row.spans.map(s => s.job.id));
      if (spannedJobIds.size > 0) {
        for (const [dayStr, dayJobsList] of row.dayJobs) {
          const filtered = dayJobsList.filter(j => !spannedJobIds.has(j.id));
          row.dayJobs.set(dayStr, filtered);
        }
      }
    }

    const sorted = [...assetMap.values()].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    if (labourOnly.dayJobs.size > 0 || labourOnly.spans.length > 0) {
      sorted.push(labourOnly);
    }
    if (unallocated.dayJobs.size > 0 || unallocated.spans.length > 0) {
      sorted.push(unallocated);
    }
    return sorted;
  }, [view, jobs, weekDays, allocations, equipmentMap, operatorsMap, assetTypeFilter]);

  // Helper functions - now use memoized Maps for O(1) lookups
  const getJobInfo = useCallback((jobId) => jobsMap.get(jobId), [jobsMap]);
  const getEquipmentInfo = useCallback((eqId) => equipmentMap.get(eqId), [equipmentMap]);
  const getOperatorInfo = useCallback((opId) => operatorsMap.get(opId), [operatorsMap]);
  const getCustomerInfo = useCallback((custId) => customersMap.get(custId), [customersMap]);
  const getSiteInfo = useCallback((siteId) => sitesMap.get(siteId), [sitesMap]);
  const getCustomerSites = useCallback((customerId) => sites.filter(s => s.customer_id === customerId), [sites]);
  const getJobAllocation = useCallback((jobId) => allocationsMap.get(jobId), [allocationsMap]);
  const getAttachmentTypeName = useCallback((typeId) => attachmentTypesMap.get(typeId)?.name || '', [attachmentTypesMap]);
  
  // Check if operator is allocated to another job during a date range
  const getOperatorConflicts = useCallback((operatorId, startDate, endDate, excludeJobId = null) => {
    if (!operatorId || !startDate) return [];
    const effectiveEndDate = endDate || startDate;
    
    return allocations.filter(alloc => {
      if (alloc.operator_id !== operatorId) return false;
      if (excludeJobId && alloc.job_id === excludeJobId) return false;
      
      const allocStart = alloc.start_date;
      const allocEnd = alloc.end_date || alloc.start_date;
      
      // Check for date overlap
      return allocStart <= effectiveEndDate && allocEnd >= startDate;
    }).map(alloc => {
      const job = jobsMap.get(alloc.job_id);
      const customer = job ? customersMap.get(job.customer_id) : null;
      return {
        job_id: alloc.job_id,
        start_date: alloc.start_date,
        customer_name: customer?.company_name || 'Unknown',
        job_description: job?.description || ''
      };
    });
  }, [allocations, jobsMap, customersMap]);
  
  // Format site address from structured fields or legacy address
  const formatSiteAddress = useCallback((site) => {
    if (!site) return '';
    // Try structured address first
    if (site.street_address || site.suburb) {
      const parts = [site.street_address, site.suburb, site.state, site.postcode].filter(Boolean);
      return parts.join(', ');
    }
    // Fall back to legacy address field
    return site.address || '';
  }, []);
  
  // Get job location - either from site or one-off address
  const getJobLocation = useCallback((job) => {
    // Haulage or Multi-Stop jobs show the route summary instead of a single location
    if ((job.job_type === 'haulage' || job.requires_multi_stops) && (job.route_summary || (job.stops && job.stops.length))) {
      const summary = job.route_summary
        || (job.stops || []).map(s => s.suburb || s.address || 'TBD').join(' → ');
      return {
        name: summary || 'Route',
        address: summary,
        suburb: (job.stops && job.stops[0]?.suburb) || '',
        isOneOff: false,
        isHaulage: job.job_type === 'haulage',
        isMultiStop: true,
        stopCount: (job.stops || []).length
      };
    }
    if (job.site_id) {
      const site = getSiteInfo(job.site_id);
      return {
        name: site?.name || formatSiteAddress(site) || 'Unknown Site',
        address: formatSiteAddress(site),
        suburb: site?.suburb || '',
        isOneOff: false
      };
    } else if (job.one_off_street || job.one_off_suburb) {
      // Build formatted address from structured fields
      const addressParts = [job.one_off_street, job.one_off_suburb, job.one_off_state, job.one_off_postcode].filter(Boolean);
      return {
        name: 'One-off Location',
        address: addressParts.join(', '),
        suburb: job.one_off_suburb || '',
        isOneOff: true
      };
    }
    return { name: 'No Location', address: '', suburb: '', isOneOff: false };
  }, [getSiteInfo, formatSiteAddress]);
  
  // Check if an equipment type is a combo type
  const getEquipmentTypeInfo = (equipmentTypeName) => {
    if (!equipmentTypeName || !equipmentTypesWithIds?.length) return null;
    return equipmentTypesWithIds.find(et => et.name === equipmentTypeName);
  };
  
  const isComboEquipmentType = (equipmentTypeName) => {
    const eqType = getEquipmentTypeInfo(equipmentTypeName);
    return eqType?.is_combo || false;
  };

  // NEW combo model: the type is a combo AND there are combo *assets* of that
  // type (assets carrying combo_members). In that case the allocation selects
  // the combo asset directly (equipment_id) rather than picking one machine per
  // component — so the legacy per-component combo picker must NOT be shown.
  const usesComboAssets = (equipmentTypeName) => {
    if (!isComboEquipmentType(equipmentTypeName)) return false;
    return (equipment || []).some(
      e => e.equipment_type === equipmentTypeName && (e.combo_members || []).length > 0
    );
  };

  // Display job number — handles linked sub-jobs: "57-1" format
  const formatJobNumber = (job) => {
    if (!job) return '-';
    if (job.parent_job_number && job.sub_job_index) {
      return `#${job.parent_job_number}-${job.sub_job_index}`;
    }
    return job.job_number ? `#${job.job_number}` : '-';
  };

  
  // Get combo component types for a combo equipment type
  const getComboComponents = (equipmentTypeName) => {
    const eqType = getEquipmentTypeInfo(equipmentTypeName);
    if (!eqType?.is_combo || !eqType?.combo_component_ids?.length) return [];
    
    // Map component IDs to their equipment type objects, filtering out any that don't exist
    return eqType.combo_component_ids
      .map(id => equipmentTypesWithIds.find(et => et.id === id))
      .filter(comp => comp && comp.id && comp.name); // Ensure component has required fields
  };
  
  // Get attachment types filtered by equipment type
  const getFilteredAttachmentTypes = (equipmentType) => {
    if (!equipmentType) return attachmentTypes;
    
    // Find the equipment type ID from the name
    const eqType = equipmentTypesWithIds.find(et => et.name === equipmentType);
    if (!eqType) return attachmentTypes;
    
    // Return attachment types that either:
    // 1. Have no equipment_type_ids (universal attachments)
    // 2. Include this equipment type's ID
    return attachmentTypes.filter(at => 
      !at.equipment_type_ids || 
      at.equipment_type_ids.length === 0 || 
      at.equipment_type_ids.includes(eqType.id)
    );
  };
  
  // Get allocated attachment names as a formatted string for display
  const getAllocatedAttachmentsDisplay = (jobId) => {
    const allocation = getJobAllocation(jobId);
    if (!allocation?.attachment_ids?.length) return '';
    const attachmentNames = allocation.attachment_ids
      .map(id => attachments.find(a => a.id === id)?.name)
      .filter(Boolean);
    return attachmentNames.length ? `(${attachmentNames.join(', ')})` : '';
  };

  // Memoized available resources
  const availableEquipment = useMemo(() => equipment.filter(e => e.status === 'available'), [equipment]);
  const availableOperators = useMemo(() => operators.filter(o => o.is_available), [operators]);

  // Get allocations for a specific date (for calendar view) - memoized
  const getAllocationsForDate = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return allocations.filter(alloc => {
      const startDate = alloc.start_date;
      const endDate = alloc.end_date || alloc.start_date;
      return dateStr >= startDate && dateStr <= endDate;
    });
  }, [allocations]);

  // Get combo equipment display for an allocation - memoized
  const getComboEquipmentDisplay = useCallback((allocation) => {
    if (!allocation?.is_combo_allocation || !allocation?.combo_equipment_ids?.length) {
      return null;
    }
    return allocation.combo_equipment_ids.map(comp => {
      const eq = equipmentMap.get(comp.equipment_id);
      return eq ? eq.asset_id : comp.component_type_name;
    }).join(' + ');
  }, [equipmentMap]);

  // Get all jobs for a specific date (including unallocated) - memoized
  const getJobsForDate = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return jobs.filter(job => {
      // Dry-hire jobs live on the dedicated Dry Hire tab — exclude from daily grids.
      if (job.hire_type === 'dry') return false;
      const startDate = job.start_date;
      const endDate = job.end_date || job.start_date;
      if (dateStr < startDate || dateStr > endDate) return false;
      if (job.excluded_dates && job.excluded_dates.includes(dateStr)) return false;
      return true;
    });
  }, [jobs]);

  // Get date range based on quick filter - memoized
  const getQuickFilterDateRange = useCallback(() => {
    const today = new Date();
    switch (quickDateFilter) {
      case 'today':
        return { from: format(today, 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') };
      case 'yesterday':
        const yesterday = subDays(today, 1);
        return { from: format(yesterday, 'yyyy-MM-dd'), to: format(yesterday, 'yyyy-MM-dd') };
      case 'tomorrow':
        const tomorrow = addDays(today, 1);
        return { from: format(tomorrow, 'yyyy-MM-dd'), to: format(tomorrow, 'yyyy-MM-dd') };
      case 'last_week':
        const lastWeekStart = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        return { from: format(lastWeekStart, 'yyyy-MM-dd'), to: format(lastWeekEnd, 'yyyy-MM-dd') };
      case 'this_week':
        const thisWeekStart = startOfWeek(today, { weekStartsOn: 1 });
        const thisWeekEnd = endOfWeek(today, { weekStartsOn: 1 });
        return { from: format(thisWeekStart, 'yyyy-MM-dd'), to: format(thisWeekEnd, 'yyyy-MM-dd') };
      case 'next_week':
        const nextWeekStart = startOfWeek(addDays(today, 7), { weekStartsOn: 1 });
        const nextWeekEnd = endOfWeek(addDays(today, 7), { weekStartsOn: 1 });
        return { from: format(nextWeekStart, 'yyyy-MM-dd'), to: format(nextWeekEnd, 'yyyy-MM-dd') };
      case 'all':
        return { from: null, to: null };
      default:
        return { from: format(today, 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') };
    }
  }, [quickDateFilter]);

  // Filter jobs for list view - MEMOIZED for performance
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      // Dry-hire jobs live on the dedicated Dry Hire tab — keep them out
      // of the List / Week / Board grids.
      if (job.hire_type === 'dry') return false;
      const customer = customersMap.get(job.customer_id);
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (job.description || '').toLowerCase().includes(searchLower) ||
        (customer?.company_name || '').toLowerCase().includes(searchLower) ||
        (job.job_number?.toString() || '').includes(searchTerm) ||
        (job.po_number || '').toLowerCase().includes(searchLower);
      const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
      const matchesAssetType = assetTypeFilter.length === 0 || assetTypeFilter.includes(job.equipment_type);
      
      // Get job date range
      const jobStartDate = job.start_date;
      const jobEndDate = job.end_date || job.start_date;
      
      // Date filtering: custom date range overrides quick filter when enabled
      let matchesDate = true;
      if (dateFilterEnabled) {
        // Custom date range is active — use it exclusively
        matchesDate = jobStartDate <= dateTo && jobEndDate >= dateFrom;
        // For single-day filter, check excluded_dates
        if (matchesDate && dateFrom === dateTo && job.excluded_dates?.includes(dateFrom)) {
          matchesDate = false;
        }
      } else if (quickDateFilter !== 'all') {
        // Quick date filter (only when custom range is not enabled)
        const { from, to } = getQuickFilterDateRange();
        if (from && to) {
          matchesDate = jobStartDate <= to && jobEndDate >= from;
          // For single-day filter, check excluded_dates
          if (matchesDate && from === to && job.excluded_dates?.includes(from)) {
            matchesDate = false;
          }
        }
      }
      
      return matchesSearch && matchesStatus && matchesAssetType && matchesDate;
    });
  }, [jobs, customersMap, searchTerm, statusFilter, assetTypeFilter, quickDateFilter, dateFilterEnabled, dateFrom, dateTo, getQuickFilterDateRange]);

  // Schedule list view sorting
  const scheduleColumnAccessors = useMemo(() => ({
    job_number: (j) => j.job_number ? parseInt(j.job_number, 10) || j.job_number : null,
    date: 'start_date',
    customer: (j) => customersMap.get(j.customer_id)?.company_name?.toLowerCase() || '',
    type: (j) => (j.equipment_type || '')?.toLowerCase(),
    site: (j) => {
      const s = sitesMap.get(j.site_id);
      return (s?.name || '')?.toLowerCase();
    },
    description: (j) => (j.description || '')?.toLowerCase(),
    status: 'status',
  }), [customersMap, sitesMap]);

  const { sortedData: sortedJobs, requestSort: requestScheduleSort, getSortDirection: getScheduleSortDirection } = useSortableTable(filteredJobs, scheduleColumnAccessors);

  // Asset types present in currently visible jobs (excluding asset type filter itself)
  const visibleAssetTypes = useMemo(() => {
    const baseFiltered = jobs.filter(job => {
      const customer = customersMap.get(job.customer_id);
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (job.description || '').toLowerCase().includes(searchLower) ||
        (customer?.company_name || '').toLowerCase().includes(searchLower) ||
        (job.job_number?.toString() || '').includes(searchTerm) ||
        (job.po_number || '').toLowerCase().includes(searchLower);
      const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
      const jobStartDate = job.start_date;
      const jobEndDate = job.end_date || job.start_date;
      let matchesDate = true;
      if (dateFilterEnabled) {
        matchesDate = jobStartDate <= dateTo && jobEndDate >= dateFrom;
        if (matchesDate && dateFrom === dateTo && job.excluded_dates?.includes(dateFrom)) {
          matchesDate = false;
        }
      } else if (quickDateFilter !== 'all') {
        const { from, to } = getQuickFilterDateRange();
        if (from && to) {
          matchesDate = jobStartDate <= to && jobEndDate >= from;
          if (matchesDate && from === to && job.excluded_dates?.includes(from)) {
            matchesDate = false;
          }
        }
      }
      return matchesSearch && matchesStatus && matchesDate;
    });
    return [...new Set(baseFiltered.map(j => j.equipment_type).filter(Boolean))].sort();
  }, [jobs, customersMap, searchTerm, statusFilter, quickDateFilter, dateFilterEnabled, dateFrom, dateTo, getQuickFilterDateRange]);

  // ── List-view grouping ──────────────────────────────────────────────
  // Buckets the already-filtered jobs by the selected dimension. Returns null
  // when grouping is off so the list renders as a flat array.
  const groupJobList = useMemo(() => {
    if (groupBy === 'none') return null;
    const getInfo = (job) => {
      if (groupBy === 'customer') {
        const c = customersMap.get(job.customer_id);
        return { key: job.customer_id || '__none__', label: c?.company_name || 'Unknown Customer' };
      }
      if (groupBy === 'asset_type') {
        const type = job.equipment_type || 'No Asset';
        return { key: type, label: type };
      }
      if (groupBy === 'asset') {
        const alloc = getJobAllocation(job.id);
        const eq = alloc?.equipment_id ? getEquipmentInfo(alloc.equipment_id) : null;
        if (!eq) return { key: '__unallocated_asset__', label: 'Unallocated' };
        const name = eq.equipment_type || eq.name || 'Asset';
        const label = eq.asset_id ? `${name} / ${eq.asset_id}` : name;
        return { key: `asset:${alloc.equipment_id}`, label };
      }
      // site / address
      const loc = getJobLocation(job);
      if (loc.isMultiStop || loc.isHaulage) return { key: '__route__', label: 'Route / Multi-stop' };
      if (job.site_id) {
        const label = loc.name && loc.name !== 'No Location' ? loc.name : 'Unknown Site';
        return { key: `site:${job.site_id}`, label };
      }
      if (loc.isOneOff) {
        const label = loc.address || 'One-off Location';
        return { key: `oneoff:${label}`, label };
      }
      return { key: '__noloc__', label: 'No Location' };
    };
    const map = new Map();
    for (const job of filteredJobs) {
      const { key, label } = getInfo(job);
      if (!map.has(key)) map.set(key, { key, label, jobs: [] });
      map.get(key).jobs.push(job);
    }
    const groups = [...map.values()];
    // For asset grouping, attach a utilisation figure: distinct days the asset
    // is committed within the current view's date range.
    if (groupBy === 'asset') {
      const range = (dateFilterEnabled && dateFrom && dateTo)
        ? { from: dateFrom, to: dateTo }
        : getQuickFilterDateRange();
      const daysInRange = (job) => {
        const startStr = job.start_date;
        if (!startStr) return [];
        const endStr = job.end_date || startStr;
        const out = [];
        let d = new Date(startStr + 'T00:00:00');
        const endD = new Date(endStr + 'T00:00:00');
        let guard = 0;
        while (d <= endD && guard < 400) {
          const s = format(d, 'yyyy-MM-dd');
          if (!range.from || (s >= range.from && s <= range.to)) out.push(s);
          d = addDays(d, 1);
          guard += 1;
        }
        return out;
      };
      for (const g of groups) {
        const set = new Set();
        for (const j of g.jobs) for (const s of daysInRange(j)) set.add(s);
        g.bookedDays = set.size;
      }
    }
    return groups.sort((a, b) => a.label.localeCompare(b.label));
  }, [groupBy, filteredJobs, customersMap, getJobLocation, getJobAllocation, getEquipmentInfo, dateFilterEnabled, dateFrom, dateTo, getQuickFilterDateRange]);

  // Flattened render list: group-header sentinels interleaved with jobs.
  const displayItems = useMemo(() => {
    if (!groupJobList) return sortedJobs;
    const items = [];
    for (const g of groupJobList) {
      items.push({ __group: true, key: g.key, label: g.label, count: g.jobs.length, bookedDays: g.bookedDays });
      if (!collapsedGroups[g.key]) items.push(...g.jobs);
    }
    return items;
  }, [groupJobList, sortedJobs, collapsedGroups]);

  const handleGroupByChange = (value) => {
    setGroupBy(value);
    localStorage.setItem('schedule_list_group_by', value);
  };

  // Job CRUD handlers
  const resetJobForm = () => {
    setJobForm({
      parent_job_id: '',
      customer_id: '',
      contact_id: '',
      site_id: '',
      site_contact_ids: [],
      job_quantity: 1,
      one_off_street: '',
      one_off_suburb: '',
      one_off_state: '',
      one_off_postcode: '',
      use_one_off_address: false,
      requires_multi_stops: false,
      latitude: null,
      longitude: null,
      equipment_type: '',
      create_equipment_id: '',
      attachment_type_ids: [],
      worker_requirements: [],
      start_date: format(new Date(), 'yyyy-MM-dd'),
      start_time: '07:00',
      end_time: '',
      depot_time: tenantSettings.depot_time_enabled ? (tenantSettings.default_depot_time || '06:30') : '',
      depot_id: '',
      end_date: '',
      est_duration: '',
      description: '',
      status: 'not_allocated',
      notes: '',
      tags: [],
      is_night_shift: false,
      is_tbc: false,
      po_number: '',
      starting_location: '',
      hire_type: 'wet',
      hire_period_unit: '',
      pickup_from_yard: false,
      return_to_yard: false,
      hire_notes: '',
      expected_off_hire_date: '',
      // Haulage fields
      job_type: tenantSettings?.industry === 'haulage' ? 'haulage' : 'standard',
      stops: [],
      // Multi-asset
      asset_requirements: [{ equipment_type: '', quantity: 1 }],
      // Project link
      project_id: '',
    });
    setEditingJob(null);
    setSelectedCustomerRequiresPO(false);
    setCustomerContacts([]);
    setSiteContacts([]);
    setPendingFiles([]);
    setPendingFilesVisible(false);
    setIsRecurring(false);
    setRecurrenceConfig(null);
  };

  // Fetch site contacts when a site is selected
  const fetchSiteContacts = async (siteId) => {
    if (!siteId) {
      setSiteContacts([]);
      return;
    }
    try {
      const response = await api.get(`/sites/${siteId}/contacts`);
      setSiteContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch site contacts:', error);
      setSiteContacts([]);
    }
  };

  const handleCreateSiteContact = async () => {
    if (!jobForm.site_id) return;
    if (!newSiteContact.name.trim()) {
      toast.error('Contact name is required');
      return;
    }
    setSavingSiteContact(true);
    try {
      const res = await api.post(`/sites/${jobForm.site_id}/contacts`, {
        site_id: jobForm.site_id,
        name: newSiteContact.name.trim(),
        job_title: newSiteContact.job_title.trim() || null,
        phone: newSiteContact.phone.trim() || null,
        email: newSiteContact.email.trim() || null,
      });
      const created = res.data;
      const listRes = await api.get(`/sites/${jobForm.site_id}/contacts`);
      setSiteContacts(listRes.data);
      if (created?.id) {
        setJobForm(prev => ({ ...prev, site_contact_ids: [...(prev.site_contact_ids || []), created.id] }));
      }
      setNewSiteContact({ name: '', job_title: '', phone: '', email: '' });
      setAddSiteContactOpen(false);
      toast.success('Site contact added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add site contact');
    } finally {
      setSavingSiteContact(false);
    }
  };

  // Fetch job day details for a job
  const fetchJobDayDetails = async (jobId) => {
    try {
      const response = await api.get(`/job-day-details/job/${jobId}`);
      const details = {};
      response.data.forEach(d => {
        details[d.date] = d;
      });
      setJobDayDetails(prev => ({ ...prev, [jobId]: details }));
      return details;
    } catch (error) {
      console.error('Failed to fetch job day details:', error);
      return {};
    }
  };

  // Apply the chosen strategy to per-day overrides after a multi-day job's dates change
  const applyRescheduleStrategy = async (strategy) => {
    const { jobId, deltaDays, newStart, newEnd } = reschedulePrompt;
    try {
      const res = await api.post('/job-day-details/reschedule', {
        job_id: jobId,
        strategy,
        delta_days: deltaDays,
        new_start_date: newStart,
        new_end_date: newEnd,
      });
      await fetchJobDayDetails(jobId);
      fetchData();
      const { removed = 0 } = res.data || {};
      toast.success(
        strategy === 'reset' ? 'Custom day times reset to default'
          : strategy === 'shift' ? `Custom day times moved with the job${removed ? ` (${removed} outside new dates removed)` : ''}`
          : `Custom day times kept on their dates${removed ? ` (${removed} outside new dates removed)` : ''}`
      );
    } catch (error) {
      console.error('Failed to reschedule overrides:', error);
      toast.error('Failed to update custom day times');
    } finally {
      setReschedulePrompt({ open: false, jobId: null, deltaDays: 0, newStart: null, newEnd: null });
    }
  };

  // Save day-specific overrides (work description + per-day start/end times)
  const saveDayWorkDescription = async (jobId, date, workDescription) => {
    try {
      await api.post('/job-day-details', {
        job_id: jobId,
        date: date,
        work_description: workDescription,
        start_time: dayStartTime || null,
        end_time: dayEndTime || null
      });
      // Refresh cache
      await fetchJobDayDetails(jobId);
      toast.success('Day details saved');
    } catch (error) {
      console.error('Failed to save day details:', error);
      toast.error('Failed to save day details');
    }
  };

  // Handle opening job dialog with edit mode prompt for multi-day jobs
  const handleOpenJobDialog = (job = null, date = null, forceMode = null, assetContext = null) => {
    setVersionConflict(null); // Clear any previous conflict
    setJobDrawerTab('details'); // Reset to details tab
    fetchTags(); // Fetch available tags for autocomplete
    fetchActiveProjects(); // Fetch active projects for the project selector
    
    if (job) {
      const isMultiDay = isMultiDayJob(job);
      
      // If multi-day job and no forceMode, show prompt
      // Set suggested date if opened from a child row.
      // Dry-hire jobs always edit at the "overall" level (day-by-day edits
      // make no sense for asset-only hires that span the full period), so
      // skip the mode-selector for them.
      if (isMultiDay && !forceMode && job.hire_type !== 'dry') {
        setEditingJob(job);
        setSuggestedEditDate(date); // Will be null if from parent, or specific date if from child
        setEditModePromptOpen(true);
        return;
      }
      
      // Set edit mode based on forceMode or if date is provided (child row click)
      const editMode = forceMode || (date ? 'day' : 'overall');
      setJobEditMode(editMode);
      setEditingJobDate(date || null);
      setSuggestedEditDate(null); // Clear suggested date
      
      // Fetch day details and load worker allocations for multi-day jobs
      if (isMultiDay) {
        fetchJobDayDetails(job.id).then(details => {
          if (editMode === 'day' && date && details[date]) {
            setDayWorkDescription(details[date].work_description || '');
            setDayStartTime(details[date].start_time || '');
            setDayEndTime(details[date].end_time || '');
          } else {
            setDayWorkDescription('');
            setDayStartTime('');
            setDayEndTime('');
          }
        });
        
        // Load day worker allocations for day-specific editing
        // Pre-populate with ALL job worker requirements, filling in allocations where they exist
        if (editMode === 'day' && date) {
          const dayWorkerAllocs = workerAllocations.filter(wa => 
            wa.job_id === job.id && 
            wa.start_date <= date && 
            (wa.end_date || wa.start_date) >= date
          );
          
          // Build selections based on job requirements
          const selections = [];
          
          if (job.worker_requirements && job.worker_requirements.length > 0) {
            // For each requirement, create rows and fill in allocations
            job.worker_requirements.forEach(req => {
              const quantity = req.quantity || 1;
              const allocsForType = dayWorkerAllocs.filter(wa => wa.worker_type_id === req.worker_type_id);
              
              // Create rows for each required worker of this type
              for (let i = 0; i < quantity; i++) {
                const existingAlloc = allocsForType[i]; // Match allocation to slot
                selections.push({
                  id: existingAlloc?.id || null,
                  worker_type_id: req.worker_type_id,
                  operator_id: existingAlloc?.operator_id || ''
                });
              }
            });
            
            // Also add any extra allocations that don't match requirements (additional workers)
            dayWorkerAllocs.forEach(wa => {
              const isAccountedFor = selections.some(s => s.id === wa.id);
              if (!isAccountedFor) {
                selections.push({
                  id: wa.id,
                  worker_type_id: wa.worker_type_id,
                  operator_id: wa.operator_id
                });
              }
            });
          } else {
            // No requirements, just show existing allocations
            dayWorkerAllocs.forEach(wa => {
              selections.push({
                id: wa.id,
                worker_type_id: wa.worker_type_id,
                operator_id: wa.operator_id
              });
            });
          }
          
          setDayWorkerSelections(selections);
        } else {
          setDayWorkerSelections([]);
        }
      } else {
        setDayWorkDescription('');
        setDayWorkerSelections([]);
      }
      
      setEditingJob(job);
      // Check if customer requires PO
      const customer = customers.find(c => c.id === job.customer_id);
      setSelectedCustomerRequiresPO(customer?.requires_po || false);
      // Fetch contacts for this customer
      if (job.customer_id) {
        fetchCustomerContacts(job.customer_id);
      }
      // Fetch site contacts if a site is selected
      if (job.site_id) {
        fetchSiteContacts(job.site_id);
      } else {
        setSiteContacts([]);
      }
      const hasOneOffAddress = (job.one_off_street || job.one_off_suburb) && !job.site_id;
      setJobForm({
        customer_id: job.customer_id,
        contact_id: job.contact_id || '',
        site_id: job.site_id || '',
        site_contact_ids: job.site_contact_ids || (job.site_contact_id ? [job.site_contact_id] : []),
        one_off_street: job.one_off_street || '',
        one_off_suburb: job.one_off_suburb || '',
        one_off_state: job.one_off_state || '',
        one_off_postcode: job.one_off_postcode || '',
        use_one_off_address: hasOneOffAddress,
        equipment_type: job.equipment_type || '',
        attachment_type_ids: job.attachment_type_ids || [],
        worker_requirements: job.worker_requirements || [],
        start_date: job.start_date,
        start_time: job.start_time || '07:00',
        end_time: job.end_time || '',
        depot_time: job.depot_time || '',
        depot_id: job.depot_id || '',
        end_date: job.end_date || '',
        est_duration: job.est_duration || '',
        description: job.description,
        status: job.status,
        notes: job.notes || '',
        // Ensure tags are strings (handle object format from legacy data)
        tags: (job.tags || []).map(tag => typeof tag === 'string' ? tag : (tag?.name || tag?.id || String(tag))),
        is_night_shift: !!job.is_night_shift,
        is_tbc: !!job.is_tbc,
        parent_job_id: job.parent_job_id || '',
        requires_multi_stops: !!job.requires_multi_stops,
        oversize: !!job.oversize,
        oversize_meters: job.oversize_meters || '',
        latitude: job.latitude || null,
        longitude: job.longitude || null,
        po_number: job.po_number || '',
        starting_location: job.starting_location || '',
        // Dry-Hire fields (default to wet for legacy docs without the field)
        hire_type: job.hire_type || 'wet',
        hire_period_unit: job.hire_period_unit || '',
        pickup_from_yard: !!job.pickup_from_yard,
        return_to_yard: !!job.return_to_yard,
        hire_notes: job.hire_notes || '',
        expected_off_hire_date: job.expected_off_hire_date || '',
        is_tower_crane: !!job.is_tower_crane,
        tower_crane_billing_period: job.tower_crane_billing_period || '',
        // Haulage fields
        job_type: job.job_type || 'standard',
        stops: job.stops || [],
        signature_timing: job.signature_timing ?? null,
        waypoint_signature: job.waypoint_signature ?? null,
        // Multi-asset
        asset_requirements: job.asset_requirements?.length > 0
          ? job.asset_requirements
          : [{ equipment_type: job.equipment_type || '', quantity: 1 }],
        // Project link
        project_id: job.project_id || '',
      });
    } else {
      resetJobForm();
      setJobEditMode('overall');
      setEditingJobDate(null);
      setDayWorkDescription('');
      // Build pre-fill overrides from date + asset context
      const prefill = {};
      if (date) {
        prefill.start_date = format(date, 'yyyy-MM-dd');
      }
      if (assetContext?.equipmentType) {
        prefill.equipment_type = assetContext.equipmentType;
      }
      if (assetContext?.equipmentId) {
        prefill.create_equipment_id = assetContext.equipmentId;
      }
      if (Object.keys(prefill).length > 0) {
        setJobForm(prev => ({ ...prev, ...prefill }));
      }
      // Store the pre-selected equipment ID for auto-allocation after job creation
      setWeekPreSelectedEquipmentId(assetContext?.equipmentId || null);
    }
    setJobDialogOpen(true);
  };

  // Handle edit mode selection from prompt
  const handleEditModeSelection = (mode, specificDate = null) => {
    setEditModePromptOpen(false);
    if (editingJob) {
      handleOpenJobDialog(editingJob, specificDate, mode);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !jobForm.tags.includes(tagInput.trim())) {
      setJobForm(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
      // Refresh tags list in the background to include the new tag for future autocomplete
      fetchTags();
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setJobForm(prev => ({ ...prev, tags: prev.tags.filter(tag => tag !== tagToRemove) }));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRefreshAndReload = async () => {
    setVersionConflict(null);
    await fetchData();
    // Reload the job being edited with fresh data
    if (editingJob) {
      const freshJob = jobs.find(j => j.id === editingJob.id);
      if (freshJob) {
        handleOpenJobDialog(freshJob);
      } else {
        setJobDialogOpen(false);
        resetJobForm();
        toast.info('Job may have been deleted');
      }
    }
  };

  const handleSubmitJob = async (e) => {
    e.preventDefault();
    setVersionConflict(null);
    
    // Validate required fields
    if (!jobForm.customer_id) {
      toast.error('Customer is required');
      return;
    }
    const isLabourJob = jobForm.hire_type === 'labour' || jobForm.equipment_type === 'No Asset';
    // Multi-asset validation
    if (multiAssetEnabled && !isLabourJob) {
      const validReqs = jobForm.asset_requirements.filter(r => r.equipment_type);
      if (validReqs.length === 0) {
        toast.error('At least one asset type is required');
        return;
      }
    } else if (!isLabourJob && !jobForm.equipment_type) {
      toast.error('Asset Type is required');
      return;
    }
    if (!jobForm.start_date) {
      toast.error('Start Date is required');
      return;
    }
    if (!jobForm.start_time) {
      toast.error('Start Time is required');
      return;
    }
    if (!jobForm.description?.trim()) {
      toast.error('Description is required');
      return;
    }
    // Location validation: haulage & multi-stop use stops, others use site or one-off address
    if (jobForm.job_type === 'haulage') {
      if (!jobForm.stops || jobForm.stops.filter(s => !s.deleted).length < 2) {
        toast.error('Haulage jobs require at least 2 stops (pickup + dropoff)');
        return;
      }
    } else if (jobForm.requires_multi_stops) {
      if (!jobForm.stops || jobForm.stops.filter(s => !s.deleted).length < 1) {
        toast.error('Multi-stop jobs require at least 1 route stop');
        return;
      }
    } else {
      if (!jobForm.use_one_off_address && !jobForm.site_id) {
        toast.error('Location is required - select a site or use a one-off address');
        return;
      }
      if (jobForm.use_one_off_address && !jobForm.one_off_street?.trim()) {
        toast.error('Street address is required for one-off locations');
        return;
      }
    }

    // Starting Location — required when the rule is enabled + mandatory
    const _slRule = tenantSettings.field_rules?.starting_location;
    if (_slRule?.enabled && _slRule?.required && !jobForm.starting_location?.trim()) {
      toast.error('Starting Location is required');
      return;
    }

    // Estimated Duration — required when the tenant rule is enabled
    if (tenantSettings.field_rules?.require_est_duration?.enabled && !jobForm.est_duration) {
      toast.error('Estimated duration is required');
      return;
    }
    
    try {
      const submitData = { ...jobForm };

      // Multi-asset: prepare asset_requirements for submission
      if (multiAssetEnabled && jobForm.hire_type !== 'labour') {
        const validReqs = (jobForm.asset_requirements || []).filter(r => r.equipment_type);
        if (validReqs.length > 0) {
          submitData.asset_requirements = validReqs;
          submitData.equipment_type = validReqs[0].equipment_type;
        } else {
          submitData.asset_requirements = null;
        }
      } else {
        delete submitData.asset_requirements;
      }

      if (!submitData.end_date) submitData.end_date = null;
      if (!submitData.est_duration && submitData.est_duration !== 0) submitData.est_duration = null;
      if (!submitData.end_time) submitData.end_time = null;
      if (!submitData.starting_location?.trim()) submitData.starting_location = null;
      
      // Extract create_equipment_id before sending (not a job field)
      const createEquipmentId = submitData.create_equipment_id;
      delete submitData.create_equipment_id;

      // Bulk-create quantity (create-only; not a backend field). 1–20.
      const jobQuantity = !editingJob ? Math.min(Math.max(parseInt(submitData.job_quantity, 10) || 1, 1), 20) : 1;
      delete submitData.job_quantity;
      
      // Handle site vs one-off address
      if (submitData.use_one_off_address) {
        submitData.site_id = null;
        submitData.site_contact_ids = [];
      } else {
        // Clear one-off fields when using site
        submitData.one_off_street = null;
        submitData.one_off_suburb = null;
        submitData.one_off_state = null;
        submitData.one_off_postcode = null;
      }
      delete submitData.use_one_off_address; // Don't send to backend
      
      // Handle haulage / multi-stop stops — keep stops, generate route_summary
      if (submitData.job_type === 'haulage' || submitData.requires_multi_stops) {
        // Location comes from the route stops, not the job-level site / one-off address
        submitData.site_id = null;
        submitData.site_contact_ids = [];
        submitData.one_off_street = null;
        submitData.one_off_suburb = null;
        submitData.one_off_state = null;
        submitData.one_off_postcode = null;
        const locations = (submitData.stops || []).filter(s => !s.deleted).map(s => s.suburb || s.address || 'TBD');
        submitData.route_summary = locations.join(' → ');
      } else {
        delete submitData.stops;
        delete submitData.route_summary;
      }
      
      // For new jobs, always set status to not_allocated
      if (!editingJob) {
        submitData.status = 'not_allocated';
      }

      // Dry-Hire normalisation — backend rejects `hire_period_unit` on
      // wet-hire jobs; also clear the flags that only apply to dry-hire.
      if (submitData.hire_type !== 'dry') {
        submitData.hire_period_unit = null;
        submitData.pickup_from_yard = false;
        submitData.return_to_yard = false;
        submitData.hire_notes = null;
        submitData.expected_off_hire_date = null;
        submitData.is_tower_crane = false;
        submitData.tower_crane_billing_period = null;
      } else {
        if (!submitData.hire_period_unit) {
          submitData.hire_period_unit = 'day';
        }
        // Dry-hire is open-ended by default — empty end_date stays null.
        if (submitData.end_date === '') submitData.end_date = null;
        if (submitData.expected_off_hire_date === '') submitData.expected_off_hire_date = null;
        // Tower crane: default billing period if enabled but not set
        if (submitData.is_tower_crane && !submitData.tower_crane_billing_period) {
          submitData.tower_crane_billing_period = 'weekly';
        }
        if (!submitData.is_tower_crane) {
          submitData.tower_crane_billing_period = null;
        }
      }
      
      if (editingJob) {
        // Check if end_date is being extended and there are existing allocations
        const oldEndDate = editingJob.end_date;
        const newEndDate = submitData.end_date;
        const isExtendingEndDate = newEndDate && (!oldEndDate || newEndDate > oldEndDate);
        
        if (isExtendingEndDate) {
          // Check for existing allocations that might need extending
          try {
            const allocResponse = await api.get(`/jobs/${editingJob.id}/allocations`);
            const existingAllocations = allocResponse.data.allocations || [];
            
            // Find allocations that end before the new job end date
            const allocationsToExtend = existingAllocations.filter(alloc => {
              const allocEndDate = alloc.end_date || alloc.start_date;
              return allocEndDate < newEndDate;
            });
            
            if (allocationsToExtend.length > 0) {
              // Show modal to ask user if they want to extend allocations
              setExtendAllocationsModal({
                open: true,
                jobId: editingJob.id,
                jobDescription: submitData.description || editingJob.description,
                oldEndDate: oldEndDate || editingJob.start_date,
                newEndDate: newEndDate,
                allocations: allocationsToExtend,
                pendingJobData: { ...submitData, version: editingJob.version, excluded_dates: editingJob.excluded_dates || [] }
              });
              return; // Don't proceed with job update yet
            }
          } catch (err) {
            // If we can't fetch allocations, proceed with normal update
            console.warn('Could not fetch allocations for extension check:', err);
          }
        }
        
        // Setup-parent jobs: if the start_date is changing, ask whether to
        // cascade the same shift to all linked mobilisation/demobilisation legs.
        const startDateChanged =
          editingJob.is_setup_parent === true &&
          editingJob.setup_group_id &&
          submitData.start_date &&
          submitData.start_date !== editingJob.start_date;
        if (startDateChanged) {
          try {
            const oldD = new Date(editingJob.start_date + 'T00:00:00');
            const newD = new Date(submitData.start_date + 'T00:00:00');
            const deltaDays = Math.round((newD - oldD) / 86400000);
            if (deltaDays !== 0) {
              const groupRes = await api.get(`/setup-groups/${editingJob.setup_group_id}`);
              const sg = groupRes.data || {};
              const legCount = (sg.mobilisation_jobs || []).length + (sg.demobilisation_jobs || []).length;
              if (legCount > 0) {
                setCascadeMobModal({
                  open: true,
                  jobId: editingJob.id,
                  jobDescription: submitData.description || editingJob.description,
                  setupGroupId: editingJob.setup_group_id,
                  deltaDays,
                  legCount,
                  pendingJobData: { ...submitData, version: editingJob.version, excluded_dates: editingJob.excluded_dates || [] },
                });
                return; // wait for user's answer; submitForm will be re-invoked from the modal handlers
              }
            }
          } catch (err) {
            // If we can't fetch the setup group, fall through to the normal save path
            console.warn('Could not fetch setup group for cascade prompt:', err);
          }
        }

        // Include version for optimistic locking
        submitData.version = editingJob.version;
        // Preserve excluded_dates (managed via dedicated endpoints, not the form)
        submitData.excluded_dates = editingJob.excluded_dates || [];
        const updateResponse = await api.put(`/jobs/${editingJob.id}`, submitData);
        
        // Check if backend cleaned up orphaned worker allocations
        const cleanup = updateResponse.data?._cleanup;
        if (cleanup?.orphaned_workers_removed > 0) {
          toast.info(`${cleanup.orphaned_workers_removed} worker allocation(s) removed (no longer required)`);
        }
        
        toast.success('Job updated');
        notifyAllocationConflicts(updateResponse.data);

        // Per-day override reconciliation when a multi-day job's dates change (Option A)
        try {
          const oldStart = editingJob.start_date;
          const newStart = submitData.start_date;
          const newEnd = submitData.end_date || newStart;
          const datesChanged = newStart !== oldStart || (submitData.end_date || '') !== (editingJob.end_date || '');
          if (datesChanged) {
            const details = await fetchJobDayDetails(editingJob.id);
            const hasOverrides = details && Object.values(details).some(d => d && (d.start_time || d.end_time));
            if (hasOverrides) {
              const deltaDays = Math.round((new Date(newStart + 'T00:00:00') - new Date(oldStart + 'T00:00:00')) / 86400000);
              setReschedulePrompt({ open: true, jobId: editingJob.id, deltaDays, newStart, newEnd });
            }
          }
        } catch (e) {
          console.warn('Override reconcile check failed:', e);
        }
      } else if (!editingJob && isRecurring && recurrenceConfig) {
        // ── Recurring job creation ──────────────────────────
        const res = await api.post('/jobs/recurring', {
          job: submitData,
          recurrence: recurrenceConfig,
        });
        const count = res.data?.total_created || 0;
        toast.success(`${count} recurring job${count !== 1 ? 's' : ''} created`);
        resetJobForm();
        await fetchData();
      } else if (jobQuantity > 1) {
        // Bulk create — N identical jobs. Assets are allocated afterward on the
        // schedule (the same asset can't be shared), and we skip the mobilisation/
        // transport prompts (they are per-single-job).
        const createdIds = [];
        let failedCount = 0;
        for (let i = 0; i < jobQuantity; i++) {
          try {
            const res = await api.post('/jobs', submitData);
            if (res.data?.id) createdIds.push(res.data.id);
          } catch (err) {
            failedCount++;
          }
        }
        if (failedCount === 0) {
          toast.success(`${createdIds.length} jobs created`);
        } else {
          toast.warning(`${createdIds.length}/${jobQuantity} jobs created — ${failedCount} failed`);
        }
        // Upload any attached files to each created job
        if (pendingFiles.length > 0 && createdIds.length > 0) {
          let uploadCount = 0;
          for (const jid of createdIds) {
            for (const pf of pendingFiles) {
              const formData = new FormData();
              formData.append('file', pf.file);
              formData.append('visible_to_operators', pf.visibleToOperators);
              try {
                await api.post(`/jobs/${jid}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                uploadCount++;
              } catch (err) {
                toast.error(`Failed to upload ${pf.file.name}`);
              }
            }
          }
          if (uploadCount > 0) toast.success(`${uploadCount} file${uploadCount > 1 ? 's' : ''} attached across ${createdIds.length} jobs`);
        }
      } else {
        const response = await api.post('/jobs', submitData);
        const newJobId = response.data?.id;
        toast.success('Job created');
        
        // Auto-allocate asset if selected during creation
        if (newJobId && createEquipmentId) {
          try {
            // skip_validation when pre-selected from Week View (user explicitly chose this asset+date)
            const skipValidation = weekPreSelectedEquipmentId ? '?skip_validation=true' : '';
            await api.post(`/allocations${skipValidation}`, {
              job_id: newJobId,
              equipment_id: createEquipmentId,
              operator_id: '',
              start_date: submitData.start_date,
              end_date: submitData.end_date || submitData.start_date,
              status: 'draft'
            });
            // Backend recalculates job.status -> 'partially_allocated' inside POST /allocations,
            // so no separate PUT /jobs/{id} status update is needed.
            toast.success('Asset allocated to job');
          } catch (allocErr) {
            console.error('Failed to create allocation:', allocErr);
            toast.error('Job created but asset allocation failed');
          }
        }
        
        // Upload pending files if any
        if (newJobId && pendingFiles.length > 0) {
          let uploadSuccess = 0;
          for (const pf of pendingFiles) {
            const formData = new FormData();
            formData.append('file', pf.file);
            formData.append('visible_to_operators', pf.visibleToOperators);
            try {
              await api.post(`/jobs/${newJobId}/files`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              uploadSuccess++;
            } catch (err) {
              toast.error(`Failed to upload ${pf.file.name}`);
            }
          }
          if (uploadSuccess > 0) {
            toast.success(`${uploadSuccess} file${uploadSuccess > 1 ? 's' : ''} attached`);
          }
        }

        // Check if equipment type requires setup profile (mobilisation)
        const eqType = equipmentTypesWithIds.find(t => t.name === submitData.equipment_type);
        if (newJobId && eqType?.requires_setup && (eqType?.setup_profile_ids?.length > 0)) {
          setMobilisationPrompt({ open: true, jobId: newJobId, jobData: { ...submitData, job_number: response.data?.job_number } });
        } else if (newJobId && eqType?.requires_transport && eqType?.transport_equipment_type_id) {
          // Mobilisation Phase 2 — prompt for transport leg
          const transportType = equipmentTypesWithIds.find(t => t.id === eqType.transport_equipment_type_id);
          if (transportType) {
            setTransportPrompt({
              open: true,
              jobId: newJobId,
              jobData: { ...submitData, job_number: response.data?.job_number },
              equipmentType: eqType,
              transportType,
            });
          }
        }
      }
      setJobDialogOpen(false);
      resetJobForm();
      
      setWeekPreSelectedEquipmentId(null);
      removeJobPresence();
      fetchData();
      // Trigger a refresh of the Dry-Hire view as a dry-hire job may have been
      // created/edited (no-op for the active List/Week/Board view).
      setDryHireRefreshKey(k => k + 1);
    } catch (error) {
      // Check for version conflict (optimistic locking)
      if (isVersionConflict(error)) {
        const conflictData = error.response.data;
        setVersionConflict(conflictData);
        toast.error('This job was modified by another user');
      } else {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string') {
          toast.error(detail);
        } else if (Array.isArray(detail)) {
          toast.error(detail.map(d => d.msg || JSON.stringify(d)).join('; '));
        } else if (detail && typeof detail === 'object') {
          toast.error(detail.message || detail.error || 'Operation failed');
        } else {
          toast.error('Operation failed');
        }
      }
    }
  };

  const handleDeleteJob = async (id) => {
    const job = jobs.find(j => j.id === id);
    setJobToDelete(job);
    setDeleteJobDialogOpen(true);
  };

  // ---- Job Cancellation ----
  const getCustomerEmail = (job) => {
    if (!job) return '';
    const cust = customers.find(c => c.id === job.customer_id);
    return cust?.email || '';
  };

  const handleOpenCancelJob = (job) => {
    setJobToCancel(job);
    setCancelJobDialogOpen(true);
  };

  const handleConfirmCancelJob = async ({ reason, notes, send_email }) => {
    if (!jobToCancel) return;
    setIsCancellingJob(true);
    try {
      const { data } = await api.post(`/jobs/${jobToCancel.id}/cancel`, { reason, notes, send_email });
      if (send_email && !data.cancellation_email_sent) {
        toast.warning('Job cancelled, but the email could not be sent', { description: data.email_error || 'Check email configuration' });
      } else if (send_email) {
        toast.success('Job cancelled and customer notified by email');
      } else {
        toast.success('Job cancelled');
      }
      setCancelJobDialogOpen(false);
      setJobToCancel(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel job');
    } finally {
      setIsCancellingJob(false);
    }
  };

  const handleUncancelJob = async (job) => {
    try {
      await api.post(`/jobs/${job.id}/uncancel`);
      toast.success('Job un-cancelled — returned to Unallocated');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to un-cancel job');
    }
  };

  const handleSendCancellationEmail = async (job) => {
    try {
      const { data } = await api.post(`/jobs/${job.id}/send-cancellation-email`);
      toast.success(data.message || 'Cancellation email sent');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send cancellation email');
    }
  };

  // Remove a single day from a multi-day job (uses excluded_dates — no splitting)
  const handleRemoveDayFromJob = async (job, forDate) => {
    const dateStr = typeof forDate === 'string' ? forDate : format(forDate, 'yyyy-MM-dd');
    const allDates = getJobDates(job);
    
    // If only 1 day left, fall back to full job delete
    if (allDates.length <= 1) {
      handleDeleteJob(job.id);
      return;
    }

    try {
      await api.delete(`/jobs/${job.id}/dates/${dateStr}`);
      toast.success(`Removed ${formatDateDisplay(dateStr)} from job #${job.job_number}`);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.error === 'version_conflict') {
        toast.error('Job was modified elsewhere. Refreshing...');
        fetchData();
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to remove day');
      }
    }
  };

  // Re-add a previously excluded date back to a job
  const handleReaddDate = async (jobId, date) => {
    try {
      await api.post(`/jobs/${jobId}/dates/${date}`);
      toast.success(`Re-added ${formatDateDisplay(date)}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-add date');
    }
  };

  // Confirm or revert a specific day within an allocation
  const handleUpdateDayStatus = async (jobId, dateStr, newStatus) => {
    // Find the allocation covering this date
    const alloc = allocations.find(a => 
      a.job_id === jobId && 
      a.start_date <= dateStr && 
      (a.end_date || a.start_date) >= dateStr
    );
    if (!alloc) {
      toast.error('No allocation found for this date');
      return;
    }
    try {
      await api.put(`/allocations/${alloc.id}/days/${dateStr}/status`, { status: newStatus });
      toast.success(`${formatDateDisplay(dateStr)} ${newStatus === 'confirmed' ? 'confirmed' : 'reverted to draft'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to update day status`);
    }
  };

  // Build a job update payload from existing job data
  const buildJobUpdatePayload = (job) => ({
    customer_id: job.customer_id,
    contact_id: job.contact_id || null,
    site_id: job.site_id || '',
    site_contact_ids: job.site_contact_ids || (job.site_contact_id ? [job.site_contact_id] : []),
    one_off_street: job.one_off_street || '',
    one_off_suburb: job.one_off_suburb || '',
    one_off_state: job.one_off_state || '',
    one_off_postcode: job.one_off_postcode || '',
    equipment_type: job.equipment_type,
    attachment_type_ids: job.attachment_type_ids || [],
    worker_requirements: job.worker_requirements || [],
    start_date: job.start_date,
    start_time: job.start_time || '07:00',
    end_time: job.end_time || null,
    depot_time: job.depot_time || null,
    end_date: job.end_date,
    est_duration: job.est_duration != null ? job.est_duration : null,
    description: job.description || '',
    status: job.status,
    notes: job.notes || '',
    tags: job.tags || [],
    po_number: job.po_number || '',
    excluded_dates: job.excluded_dates || [],
    version: job.version,
  });

  const handleConfirmDeleteJob = async () => {
    if (!jobToDelete) return;
    setIsDeletingJob(true);
    try {
      await api.delete(`/jobs/${jobToDelete.id}`);
      toast.success('Job deleted');
      fetchData();
      setDeleteJobDialogOpen(false);
      setJobToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeletingJob(false);
    }
  };

  // Handle extending allocations when job end date is extended
  const handleExtendAllocationsConfirm = async (extendAllocations) => {
    const { jobId, pendingJobData, allocations, newEndDate } = extendAllocationsModal;
    
    try {
      // First, update the job
      await api.put(`/jobs/${jobId}`, pendingJobData);
      
      // If user chose to extend allocations, update them
      if (extendAllocations && allocations.length > 0) {
        const updatePromises = allocations.map(alloc => 
          api.put(`/allocations/${alloc.id}`, {
            ...alloc,
            end_date: newEndDate
          })
        );
        
        await Promise.all(updatePromises);
        toast.success(`Job updated and ${allocations.length} allocation${allocations.length > 1 ? 's' : ''} extended`);
      } else {
        toast.success('Job updated');
      }
      
      // Close modal and reset
      setExtendAllocationsModal({
        open: false,
        jobId: null,
        jobDescription: '',
        oldEndDate: null,
        newEndDate: null,
        allocations: [],
        pendingJobData: null
      });
      setJobDialogOpen(false);
      resetJobForm();
      removeJobPresence();
      fetchData();
    } catch (error) {
      if (isVersionConflict(error)) {
        const conflictData = error.response.data;
        setVersionConflict(conflictData);
        toast.error('This job was modified by another user');
      } else {
        toast.error(parseApiError(error, 'Operation failed'));
      }
      // Close the extend modal but keep job dialog open
      setExtendAllocationsModal({
        open: false,
        jobId: null,
        jobDescription: '',
        oldEndDate: null,
        newEndDate: null,
        allocations: [],
        pendingJobData: null
      });
    }
  };

  const handleExtendAllocationsCancel = () => {
    setExtendAllocationsModal({
      open: false,
      jobId: null,
      jobDescription: '',
      oldEndDate: null,
      newEndDate: null,
      allocations: [],
      pendingJobData: null
    });
  };

  // Cascade-mobilisation-dates: complete the parent PUT, then optionally shift legs.
  const finishCascadeMobModal = async (alsoShiftLegs) => {
    const { jobId, deltaDays, setupGroupId, pendingJobData } = cascadeMobModal;
    if (!jobId || !pendingJobData) {
      setCascadeMobModal((m) => ({ ...m, open: false }));
      return;
    }
    try {
      const updateResponse = await api.put(`/jobs/${jobId}`, pendingJobData);
      const cleanup = updateResponse.data?._cleanup;
      if (cleanup?.orphaned_workers_removed > 0) {
        toast.info(`${cleanup.orphaned_workers_removed} worker allocation(s) removed (no longer required)`);
      }

      if (alsoShiftLegs && deltaDays !== 0 && setupGroupId) {
        try {
          const shiftRes = await api.post(`/setup-groups/${setupGroupId}/shift-dates`, {
            delta_days: deltaDays,
            exclude_job_id: jobId,
          });
          const shifted = shiftRes.data?.shifted || 0;
          const skipped = shiftRes.data?.skipped || [];
          if (skipped.length > 0) {
            toast.success(`Job updated. Shifted ${shifted} linked job(s); skipped ${skipped.length} (already in flight).`);
          } else {
            toast.success(`Job updated. Shifted ${shifted} linked mobilisation job(s) by ${deltaDays > 0 ? '+' : ''}${deltaDays} day(s).`);
          }
        } catch (shiftErr) {
          toast.error('Job updated, but cascading dates to linked jobs failed: ' + (shiftErr.response?.data?.detail || 'unknown error'));
        }
      } else {
        toast.success('Job updated');
      }

      setCascadeMobModal({ open: false, jobId: null, jobDescription: '', setupGroupId: null, deltaDays: 0, legCount: 0, pendingJobData: null });
      setJobDialogOpen(false);
      resetJobForm();
      removeJobPresence();
      fetchData();
    } catch (error) {
      if (isVersionConflict(error)) {
        setVersionConflict(error.response.data);
        toast.error('This job was modified by another user');
      } else {
        toast.error(error.response?.data?.detail || 'Job update failed');
      }
      setCascadeMobModal({ open: false, jobId: null, jobDescription: '', setupGroupId: null, deltaDays: 0, legCount: 0, pendingJobData: null });
    }
  };

  const handleCascadeMobConfirm = () => finishCascadeMobModal(true);
  const handleCascadeMobOnlyParent = () => finishCascadeMobModal(false);
  const handleCascadeMobCancel = () => {
    setCascadeMobModal({ open: false, jobId: null, jobDescription: '', setupGroupId: null, deltaDays: 0, legCount: 0, pendingJobData: null });
    // Keep job dialog open so the user can adjust or cancel.
  };

  // Confirm job - makes it visible in mobile app
  const handleConfirmJob = async (job) => {
    try {
      await api.post(`/jobs/${job.id}/confirm`);
      toast.success('Job confirmed - Now visible to operator');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm job');
    }
  };

  // Unconfirm job - returns confirmed job + its allocations to draft
  const handleUnconfirmJob = async (job) => {
    try {
      const res = await api.post(`/jobs/${job.id}/unconfirm`);
      const newStatus = res.data?.job_status || 'allocated';
      toast.success(`Job unconfirmed — allocations returned to draft (status: ${newStatus})`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unconfirm job');
    }
  };

  // Notify Operator handler
  const handleNotifyOperator = (job) => {
    const alloc = allocations.find(a => a.job_id === job.id);
    const operatorInfo = alloc ? getOperatorInfo(alloc.operator_id) : null;
    const equipmentInfo = alloc ? (
      getEquipmentInfo(alloc.equipment_id) || (
        alloc.is_combo_allocation && alloc.combo_equipment_ids?.length
          ? getEquipmentInfo(alloc.combo_equipment_ids[0])
          : null
      )
    ) : null;

    setNotifyJobData({
      ...job,
      customer_name: customers.find(c => c.id === job.customer_id)?.name || '',
    });
    setNotifyAllocationData(alloc ? {
      ...alloc,
      operator_name: operatorInfo?.name || 'Unknown',
      equipment_name: equipmentInfo ? `${equipmentInfo.equipment_type || equipmentInfo.name || 'Equipment'} / ${equipmentInfo.asset_id || ''}` : ''
    } : null);
    setNotifyDialogOpen(true);
  };

  // Send to Worker — flips every draft allocation on the job to `sent`, so the
  // worker sees the Confirm Receipt prompt in the mobile app. Gated by the
  // worker_job_confirmation feature flag. Runs without a notification template
  // (use Notify Operator if you also want to push a custom template).
  const handleSendToWorker = async (job) => {
    if (job.is_tbc) {
      toast.error('Cannot send a TBC job to workers. Remove the TBC flag first.');
      return;
    }
    try {
      const res = await api.post(`/jobs/${job.id}/send-to-workers`);
      const sent = res?.data?.summary?.sent ?? 0;
      const skipped = res?.data?.summary?.skipped ?? 0;
      const newStatus = res?.data?.job_status;
      const statusBits = newStatus && newStatus !== job.status
        ? ` · job now ${newStatus === 'sent' ? 'Sent' : newStatus}`
        : '';
      if (sent > 0) {
        toast.success(
          `Sent to ${sent} worker${sent === 1 ? '' : 's'}${skipped ? ` · ${skipped} skipped` : ''}${statusBits}`
        );
      } else if (skipped > 0) {
        toast.warning(
          `${skipped} allocation${skipped === 1 ? '' : 's'} skipped — no assigned worker.${statusBits}`
        );
      } else if (newStatus === 'sent') {
        toast.info(`Already sent — every worker already has this job${statusBits}.`);
      } else {
        toast.info('No draft allocations to send. Allocate a worker first.');
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send to workers');
    }
  };

  // Duplicate job handler
  const handleDuplicateJob = async (job) => {
    const customer = customers.find(c => c.id === job.customer_id);
    const needsPo = customer?.requires_po;
    
    // Always show the dialog so user can pick a date (and PO if needed)
    setDuplicateJobData(job);
    setDuplicatePoNumber(job.po_number || '');
    setDuplicatePoUseExisting(true);
    setDuplicateJobDate(job.start_date ? new Date(job.start_date + 'T00:00:00') : new Date());
    setDuplicateJobEndDate(job.end_date ? new Date(job.end_date + 'T00:00:00') : null);
    setDuplicatePoDialogOpen(true);
  };

  const executeDuplicateJob = async (job, poNumber, startDate, endDate) => {
    try {
      const formattedDate = startDate instanceof Date
        ? format(startDate, 'yyyy-MM-dd')
        : startDate || job.start_date;
      
      const formattedEndDate = endDate instanceof Date
        ? format(endDate, 'yyyy-MM-dd')
        : null;
      
      const duplicateData = {
        customer_id: job.customer_id,
        site_id: job.site_id || '',
        equipment_type: job.equipment_type,
        start_date: formattedDate,
        start_time: job.start_time || '07:00',
        depot_time: job.depot_time || undefined,
        end_date: formattedEndDate,
        description: job.description || 'Duplicated job',
        status: 'not_allocated',
        notes: job.notes || '',
        po_number: poNumber || undefined
      };
      await api.post('/jobs', duplicateData);
      toast.success('Job duplicated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate job');
    }
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicateJobData) return;
    const customer = customers.find(c => c.id === duplicateJobData.customer_id);
    const needsPo = customer?.requires_po;
    const po = needsPo
      ? (duplicatePoUseExisting ? duplicateJobData.po_number : duplicatePoNumber)
      : (duplicateJobData.po_number || '');
    setDuplicatePoDialogOpen(false);
    await executeDuplicateJob(duplicateJobData, po, duplicateJobDate, duplicateJobEndDate);
    setDuplicateJobData(null);
  };

  // Open Extend Job modal
  const handleOpenExtendJob = async (job) => {
    // Calculate date from (current last day + 1)
    const lastDay = job.end_date || job.start_date;
    const lastDayDate = new Date(lastDay + 'T00:00:00');
    lastDayDate.setDate(lastDayDate.getDate() + 1);
    const dateFrom = format(lastDayDate, 'yyyy-MM-dd');
    
    // Fetch existing allocations
    let allocations = [];
    try {
      const response = await api.get(`/jobs/${job.id}/allocations`);
      allocations = response.data.allocations || [];
    } catch (err) {
      console.warn('Could not fetch allocations:', err);
    }
    
    setExtendJobModal({
      open: true,
      job: job,
      dateFrom: dateFrom,
      dateTo: '',
      allocations: allocations,
      extendAllocations: allocations.length > 0,
      loading: false
    });
  };

  // Handle Extend Job submit
  const handleExtendJobSubmit = async () => {
    const { job, dateTo, allocations, extendAllocations } = extendJobModal;
    
    if (!dateTo) {
      toast.error('Please select an end date');
      return;
    }
    
    setExtendJobModal(prev => ({ ...prev, loading: true }));
    
    try {
      // Update the job's end date - include all existing job fields to preserve data
      const jobUpdateData = {
        customer_id: job.customer_id,
        equipment_type: job.equipment_type,
        start_date: job.start_date,
        end_date: dateTo,
        description: job.description || '',
        status: job.status,
        version: job.version
      };
      
      // Include optional fields if they have values (preserve existing data)
      if (job.site_id) jobUpdateData.site_id = job.site_id;
      if (job.start_time) jobUpdateData.start_time = job.start_time;
      if (job.depot_time) jobUpdateData.depot_time = job.depot_time;
      if (job.depot_id !== undefined) jobUpdateData.depot_id = job.depot_id || null;
      if (job.end_time) jobUpdateData.end_time = job.end_time;
      if (job.notes) jobUpdateData.notes = job.notes;
      if (job.po_number) jobUpdateData.po_number = job.po_number;
      if (job.job_type) jobUpdateData.job_type = job.job_type;
      if (job.worker_requirements) jobUpdateData.worker_requirements = job.worker_requirements;
      if (job.one_off_street) jobUpdateData.one_off_street = job.one_off_street;
      if (job.one_off_suburb) jobUpdateData.one_off_suburb = job.one_off_suburb;
      if (job.one_off_state) jobUpdateData.one_off_state = job.one_off_state;
      if (job.one_off_postcode) jobUpdateData.one_off_postcode = job.one_off_postcode;
      if (job.tags) jobUpdateData.tags = job.tags;
      
      await api.put(`/jobs/${job.id}`, jobUpdateData);
      
      // If user wants to extend allocations, update them
      if (extendAllocations && allocations.length > 0) {
        const updatePromises = allocations.map(alloc => {
          const allocUpdateData = {
            job_id: alloc.job_id,
            operator_id: alloc.operator_id,
            start_date: alloc.start_date,
            end_date: dateTo,
            status: alloc.status || 'confirmed'
          };
          if (alloc.equipment_id) allocUpdateData.equipment_id = alloc.equipment_id;
          return api.put(`/allocations/${alloc.id}`, allocUpdateData);
        });
        await Promise.all(updatePromises);
        toast.success(`Job extended and ${allocations.length} allocation${allocations.length > 1 ? 's' : ''} updated`);
      } else {
        toast.success('Job extended');
      }
      
      // Close modal and refresh
      setExtendJobModal({
        open: false,
        job: null,
        dateFrom: '',
        dateTo: '',
        allocations: [],
        extendAllocations: true,
        loading: false
      });
      fetchData();
    } catch (error) {
      // Handle error - detail could be string or object array
      let errorMessage = 'Failed to extend job';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Validation errors array
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
      setExtendJobModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Check if a job is multi-day
  const isMultiDayJob = (job) => {
    return job.end_date && job.end_date !== job.start_date;
  };

  // Fetch dockets for a specific job
  const fetchJobDockets = async (jobId) => {
    try {
      const res = await api.get(`/dockets/job/${jobId}`);
      const dockets = res.data || [];
      // Cache the result
      setJobDocketsCache(prev => ({ ...prev, [jobId]: dockets }));
      return dockets;
    } catch (error) {
      console.error('Failed to fetch job dockets:', error);
      return [];
    }
  };

  // Check if a job has any dockets submitted
  const jobHasDockets = async (jobId) => {
    // Check cache first
    if (jobDocketsCache[jobId] !== undefined) {
      return jobDocketsCache[jobId].length > 0;
    }
    const dockets = await fetchJobDockets(jobId);
    return dockets.length > 0;
  };

  // Handle View Docket(s) click
  const handleViewDockets = async (job) => {
    const dockets = await fetchJobDockets(job.id);
    
    if (dockets.length === 0) {
      toast.error('No dockets found for this job');
      return;
    }
    
    if (isMultiDayJob(job) && dockets.length > 1) {
      // Multi-day job with multiple dockets: show list of dockets
      setSelectedJobForDocketsList(job);
      setJobDocketsList(dockets);
      setDocketsListDrawerOpen(true);
    } else {
      // Single-day job OR multi-day with only one docket: open docket directly
      handleOpenViewDocket(dockets[0]);
    }
  };

  // Open a specific docket from the list
  const handleOpenDocketFromList = (docket) => {
    handleOpenViewDocket(docket);
  };

  // Docket action handlers for the drawer
  const handleSubmitDocketForApproval = async (docketId) => {
    try {
      await api.put(`/dockets/${docketId}`, { status: 'pending_approval' });
      toast.success('Docket submitted for approval');
      setViewDocketDrawerOpen(false);
      // Refresh dockets cache
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit docket');
    }
  };

  const handleApproveDocketFromDrawer = async (docketId) => {
    try {
      await api.put(`/dockets/${docketId}`, { 
        status: 'approved',
        approved_at: new Date().toISOString()
      });
      toast.success('Docket approved');
      setViewDocketDrawerOpen(false);
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve docket');
    }
  };

  const handleRejectDocketFromDrawer = async (docketId) => {
    try {
      await api.put(`/dockets/${docketId}`, { status: 'draft' });
      toast.success('Docket returned to draft');
      setViewDocketDrawerOpen(false);
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject docket');
    }
  };

  const handleResetDocketFromDrawer = async (docketId) => {
    try {
      await api.post(`/dockets/${docketId}/reset`);
      toast.success('Docket reset to draft');
      setViewDocketDrawerOpen(false);
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset docket');
    }
  };

  const handleGenerateDocketPDF = async (docket) => {
    try {
      const response = await api.get(`/dockets/${docket.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `docket_${docket.docket_number || docket.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleExportDocketToXero = async (docket) => {
    if (docket.xero_invoice_id || docket.status === 'exported') {
      toast.error('This docket has already been exported to Xero');
      return;
    }
    try {
      const response = await api.post('/xero/push-invoice', { docket_id: docket.id });
      toast.success(`Invoice ${response.data.xero_invoice_number} created in Xero`);
      setViewDocketDrawerOpen(false);
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export to Xero');
    }
  };

  const handleExportDocketToMyob = async (docket) => {
    if (docket.myob_invoice_id || docket.status === 'exported') {
      toast.error('This docket has already been exported to MYOB');
      return;
    }
    try {
      const response = await api.post('/myob/push-invoice', { docket_id: docket.id });
      toast.success(`Invoice created in MYOB`);
      setViewDocketDrawerOpen(false);
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export to MYOB');
    }
  };

  const handleExportDocketToQbo = async (docket) => {
    if (docket.qbo_invoice_id || docket.status === 'exported') {
      toast.error('This docket has already been exported to QuickBooks');
      return;
    }
    try {
      await api.post('/quickbooks/push-invoice', { docket_id: docket.id });
      toast.success('Invoice created in QuickBooks');
      setViewDocketDrawerOpen(false);
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export to QuickBooks');
    }
  };


  // Docket editing handlers for Invoice tab
  const handleOpenViewDocket = (docket) => {
    setViewingDocket(docket);
    setDocketEditItems(docket.items?.map(item => ({ ...item })) || []);
    setDocketEditNotes(docket.notes || '');
    setDocketUsedPriceList(null);
    setViewDocketDrawerOpen(true);

    // Load docket photos as authenticated blob URLs
    setPhotoBlobUrls([]);
    if (docket.photos?.length > 0) {
      docket.photos.forEach((_, idx) => {
        api.get(`/dockets/${docket.id}/photos/${idx}`, { responseType: 'blob' })
          .then(res => {
            const blobUrl = URL.createObjectURL(res.data);
            setPhotoBlobUrls(prev => {
              const next = [...prev];
              next[idx] = blobUrl;
              return next;
            });
          })
          .catch(() => {
            setPhotoBlobUrls(prev => {
              const next = [...prev];
              next[idx] = null;
              return next;
            });
          });
      });
    }
  };

  const handleDocketEditItemChange = (itemId, field, value) => {
    setDocketEditItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      const base = (field === 'qty' || field === 'uom') ? clearMinHire(item) : item;
      return { ...base, [field]: value };
    }));
  };

  const handleDocketAddLineItem = () => {
    const newItem = {
      id: `new-${Date.now()}`,
      item_type: 'equipment',
      type_name: '',
      display_name: '',
      qty: 0,
      uom: 'HOUR',
      unit_price: 0,
      description: '',
      account_item_id: ''
    };
    setDocketEditItems(prev => [...prev, newItem]);
  };

  const handleDocketRemoveLineItem = (itemId) => {
    setDocketEditItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleDocketSaveEdit = async () => {
    if (!viewingDocket) return;
    setDocketSaving(true);
    try {
      await api.put(`/dockets/${viewingDocket.id}`, {
        items: docketEditItems,
        notes: docketEditNotes
      });
      toast.success('Docket updated');
      setViewDocketDrawerOpen(false);
      if (viewingDocket?.job_id) {
        await fetchJobDockets(viewingDocket.job_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update docket');
    } finally {
      setDocketSaving(false);
    }
  };

  const handleDocketRecalculatePrices = async () => {
    if (!viewingDocket) return;
    setDocketRecalculating(true);
    try {
      const response = await api.post(`/dockets/${viewingDocket.id}/recalculate-prices`);
      const recalculatedItems = response.data.items || [];
      const priceListName = response.data.price_list_name;
      setDocketEditItems(recalculatedItems);
      setDocketUsedPriceList(priceListName);
      toast.success(`Prices recalculated using "${priceListName}"`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate prices');
    } finally {
      setDocketRecalculating(false);
    }
  };

  // Calculate docket totals
  const calculateDocketSubtotal = (items) => {
    if (!items) return 0;
    return items.reduce((sum, item) => sum + (parseFloat(item.billed_qty ?? item.qty ?? item.quantity ?? 0) * parseFloat(item.unit_price || item.rate || 0)), 0);
  };

  const calculateDocketTax = (subtotal) => {
    if (tenantSettings.tax_name === 'None' || !tenantSettings.tax_rate) return 0;
    return subtotal * (tenantSettings.tax_rate / 100);
  };

  // Docket handlers
  const handleOpenDocketDialog = async (job) => {
    // For multi-day jobs, use the current view date; for single-day jobs, use the job's start_date
    const isMultiDay = job.end_date && job.end_date !== job.start_date;
    const docketDate = isMultiDay ? format(currentDate, 'yyyy-MM-dd') : job.start_date;
    
    // Check if docket already exists for this job on this date
    try {
      const checkRes = await api.get(`/dockets/check/${job.id}/${docketDate}`);
      if (checkRes.data.exists) {
        toast.error(`A docket already exists for this job on ${formatDateDisplay(docketDate)}`);
        return;
      }
    } catch (error) {
      // Endpoint might not exist yet, continue
    }

    setSelectedJobForDocket(job);
    setDocketDateForJob(docketDate); // Store the date for the docket
    setDocketNotes('');
    setDocketWorkCompleted('');
    setDocketPaperUrl('');
    setDocketPaperName('');
    setDocketMaterialLoads([]);
    setDocketConcretePumped('');

    // Fetch fresh tenant data to get current industry setting
    let currentIndustry = tenantSettings.industry;
    try {
      const tenantRes = await api.get(`/tenants/${user?.client_id}`);
      if (tenantRes.data?.industry) {
        currentIndustry = tenantRes.data.industry;
        setTenantSettings(prev => ({
          ...prev,
          industry: tenantRes.data.industry
        }));
      }
    } catch (error) {
      console.error('Failed to fetch tenant industry:', error);
    }

    // Fetch yards and material types for material loads
    try {
      const [yardsRes, matTypesRes] = await Promise.all([
        api.get('/material-yards'),
        api.get('/material-types')
      ]);
      setYards(yardsRes.data || []);
      setMaterialTypes(matTypesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch yards/material types:', error);
    }

    // Build docket items (time-based, no pricing)
    buildDocketItems(job);
    setDocketDialogOpen(true);
  };

  // Open docket dialog for a specific date (for multi-day jobs)
  const handleOpenDocketDialogForDate = async (job, dateStr) => {
    // Check if docket already exists for this job on this date
    try {
      const checkRes = await api.get(`/dockets/check/${job.id}/${dateStr}`);
      if (checkRes.data.exists) {
        toast.error(`A docket already exists for this job on ${formatDateDisplay(dateStr)}`);
        return;
      }
    } catch (error) {
      // Endpoint might not exist yet, continue
    }

    setSelectedJobForDocket(job);
    setDocketDateForJob(dateStr);
    setDocketNotes('');
    setDocketWorkCompleted('');
    setDocketPaperUrl('');
    setDocketPaperName('');
    setDocketConcretePumped('');

    // Fetch yards and material types if TnD industry
    try {
      const [yardsRes, materialTypesRes] = await Promise.all([
        api.get('/tnd/yards'),
        api.get('/tnd/material-types')
      ]);
      setYards(yardsRes.data || []);
      setMaterialTypes(materialTypesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch yards/material types:', error);
    }

    // Build docket items (time-based, no pricing)
    buildDocketItems(job);
    setDocketDialogOpen(true);
  };

  // Function to build docket items without pricing (time-based like mobile)
  const buildDocketItems = (job) => {
    const items = [];
    const isNoAsset = job.equipment_type === 'No Asset';

    // Equipment type item — skip entirely for labour-only (No Asset) jobs
    if (!isNoAsset) {
      items.push({
        id: `eq-${Date.now()}`,
        item_type: 'equipment',
        type_name: job.equipment_type,
        qty: 9,
        uom: 'HOUR',
        start_time: '08:00',
        finish_time: '17:00',
        lunch_minutes: 0,
        travel_minutes: 0
      });

      // Separate Primary Operator Line — when enabled, add a worker line for the
      // primary operator so their hours can differ from the equipment hours.
      if (tenantSettings.separate_operator_line) {
        const jobAlloc = allocations.find(a => a.job_id === job.id);
        const primaryOpId = jobAlloc?.operator_id;
        if (primaryOpId) {
          // Look up the operator from the operators list (workers collection)
          const opWorker = operators.find(o => o.id === primaryOpId);
          // Workers store their types in worker_type_ids (array).
          // Also check worker_allocations as a fallback.
          const jobWAs = workerAllocations.filter(wa => wa.job_id === job.id);
          const opWa = jobWAs.find(wa => wa.operator_id === primaryOpId);
          const opWorkerTypeId = opWa?.worker_type_id
            || (opWorker?.worker_type_ids && opWorker.worker_type_ids[0])
            || opWorker?.worker_type_id;

          if (opWorkerTypeId) {
            const wt = workerTypes.find(t => t.id === opWorkerTypeId);
            items.push({
              id: `primary-op-${Date.now()}`,
              item_type: 'worker',
              type_name: wt?.name || 'Operator',
              display_name: opWorker?.name ? `${wt?.name || 'Operator'} - ${opWorker.name}` : (wt?.name || 'Operator'),
              worker_name: opWorker?.name || '',
              worker_type_id: opWorkerTypeId,
              is_primary_operator: true,
              qty: 9,
              uom: 'HOUR',
              start_time: '08:00',
              finish_time: '17:00',
              lunch_minutes: 0,
              travel_minutes: 0
            });
          }
        }
      }
    }

    // Attachment type items with default times
    if (job.attachment_type_ids && job.attachment_type_ids.length > 0) {
      job.attachment_type_ids.forEach(attTypeId => {
        const attType = attachmentTypes.find(t => t.id === attTypeId);
        if (attType) {
          items.push({
            id: `att-${attTypeId}-${Date.now()}`,
            item_type: 'attachment',
            type_name: attType.name,
            qty: 9,
            uom: 'HOUR',
            start_time: '08:00',
            finish_time: '17:00',
            lunch_minutes: 0,
            travel_minutes: 0
          });
        }
      });
    }

    // Worker rows — one per assigned worker_allocation for this job.
    // Critical for No Asset (labour-only) jobs so the admin can capture dogman
    // hours, but also useful for asset jobs with additional workers.
    const jobWAs = workerAllocations.filter(wa => wa.job_id === job.id);
    // When separate_operator_line is on, the primary operator was already added
    // above — skip their worker allocation to avoid a duplicate line.
    const primaryOpIdForSkip = (!isNoAsset && tenantSettings.separate_operator_line)
      ? allocations.find(a => a.job_id === job.id)?.operator_id
      : null;
    let primaryOpSkipped = false;
    jobWAs.forEach((wa, wIdx) => {
      if (primaryOpIdForSkip && wa.operator_id === primaryOpIdForSkip && !primaryOpSkipped) {
        primaryOpSkipped = true;
        return;
      }
      const wt = workerTypes.find(t => t.id === wa.worker_type_id);
      const op = operators.find(o => o.id === wa.operator_id);
      items.push({
        id: `wrk-${wa.id || wIdx}-${Date.now()}`,
        item_type: 'worker',
        type_name: wt?.name || 'Worker',
        display_name: op?.name ? `${wt?.name || 'Worker'} - ${op.name}` : (wt?.name || 'Worker'),
        worker_type_id: wa.worker_type_id,
        qty: 9,
        uom: 'HOUR',
        start_time: '08:00',
        finish_time: '17:00',
        lunch_minutes: 0,
        travel_minutes: 0
      });
    });

    setDocketItems(items);
  };

  // Calculate hours from time inputs
  const calculateDocketHours = (startTime, finishTime, lunchMinutes = 0, travelMinutes = 0) => {
    try {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [finishHour, finishMin] = finishTime.split(':').map(Number);
      
      let startMinutes = startHour * 60 + startMin;
      let finishMinutes = finishHour * 60 + finishMin;
      
      // Handle overnight shifts
      if (finishMinutes < startMinutes) {
        finishMinutes += 24 * 60;
      }
      
      // Calculate: (Finish - Start) - Lunch + Travel
      const totalMinutes = finishMinutes - startMinutes - lunchMinutes + travelMinutes;
      return Math.round((totalMinutes / 60) * 100) / 100;
    } catch (error) {
      return 0;
    }
  };

  // Handle docket paper upload
  const handleDocketPaperUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setDocketUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/dockets/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocketPaperUrl(response.data.url);
      setDocketPaperName(response.data.filename);
      toast.success('Paper docket uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setDocketUploading(false);
    }
  };

  // Handle delete paper docket
  const handleDeleteDocketPaper = async () => {
    if (!docketPaperUrl) return;
    
    try {
      // Extract filename from URL
      let filename = '';
      if (docketPaperUrl.includes('dockets/')) {
        const parts = docketPaperUrl.split('dockets/');
        if (parts.length > 1) {
          filename = 'dockets/' + parts[parts.length - 1].split('?')[0];
        }
      }
      
      if (filename) {
        const encodedFilename = filename.split('/').map(p => encodeURIComponent(p)).join('/');
        await api.delete(`/dockets/files/${encodedFilename}`);
      }
      
      setDocketPaperUrl('');
      setDocketPaperName('');
      toast.success('Paper docket removed');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete paper docket');
    }
  };

  // Material Loads handlers (earthmoving industry only)
  const handleAddMaterialLoad = () => {
    setEditingMaterialLoad({
      id: `load-${Date.now()}`,
      yard_id: '',
      yard_name: '',
      material_type_id: '',
      material_type_name: '',
      load_type: 'pickup', // pickup, disposal, buy_back
      quantity: 1,
      unit: 'tonne',
      tip_docket_number: '',
      tip_docket_photo_url: '',
      notes: ''
    });
    setShowMaterialLoadDialog(true);
  };

  const handleEditMaterialLoad = (load) => {
    setEditingMaterialLoad({ ...load });
    setShowMaterialLoadDialog(true);
  };

  const handleDeleteMaterialLoad = (loadId) => {
    setDocketMaterialLoads(prev => prev.filter(l => l.id !== loadId));
    toast.success('Material load removed');
  };

  const handleSaveMaterialLoad = () => {
    if (!editingMaterialLoad.yard_id) {
      toast.error('Please select a yard');
      return;
    }
    if (!editingMaterialLoad.material_type_id) {
      toast.error('Please select a material type');
      return;
    }
    if (!editingMaterialLoad.quantity || editingMaterialLoad.quantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    const existingIndex = docketMaterialLoads.findIndex(l => l.id === editingMaterialLoad.id);
    if (existingIndex >= 0) {
      // Update existing
      setDocketMaterialLoads(prev => prev.map(l => 
        l.id === editingMaterialLoad.id ? editingMaterialLoad : l
      ));
    } else {
      // Add new
      setDocketMaterialLoads(prev => [...prev, editingMaterialLoad]);
    }

    setShowMaterialLoadDialog(false);
    setEditingMaterialLoad(null);
    toast.success('Material load saved');
  };

  const handleMaterialLoadYardChange = (yardId) => {
    const yard = yards.find(y => y.id === yardId);
    setEditingMaterialLoad(prev => ({
      ...prev,
      yard_id: yardId,
      yard_name: yard?.name || '',
      material_type_id: '', // Reset material type when yard changes
      material_type_name: ''
    }));
  };

  const handleMaterialLoadTypeChange = (typeId) => {
    const matType = materialTypes.find(t => t.id === typeId);
    setEditingMaterialLoad(prev => ({
      ...prev,
      material_type_id: typeId,
      material_type_name: matType?.name || ''
    }));
  };

  const handleTipDocketPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/dockets/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditingMaterialLoad(prev => ({
        ...prev,
        tip_docket_photo_url: response.data.url
      }));
      toast.success('Tip docket photo uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const handleDocketItemChange = (itemId, field, value) => {
    setDocketItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      
      const updatedItem = { ...item, [field]: value };
      
      // In depot mode, derive travel when depot-related fields change
      const depotFields = ['depart_depot_time', 'arrive_depot_time', 'start_time', 'finish_time'];
      if (sheetDepotMode && depotFields.includes(field) && updatedItem.item_type !== 'attachment') {
        const derived = deriveDepotTravel(updatedItem);
        if (derived) {
          updatedItem.travel_out_minutes = derived.travel_out_minutes;
          updatedItem.travel_return_minutes = derived.travel_return_minutes;
          updatedItem.travel_minutes = derived.travel_minutes;
        } else {
          updatedItem.travel_out_minutes = null;
          updatedItem.travel_return_minutes = null;
        }
      }
      
      // Recalculate hours when time fields change
      if ((field === 'start_time' || field === 'finish_time' || field === 'lunch_minutes' || field === 'travel_minutes' || depotFields.includes(field)) && updatedItem.uom === 'HOUR') {
        if (updatedItem.start_time && updatedItem.finish_time) {
          updatedItem.qty = calculateDocketHours(
            updatedItem.start_time,
            updatedItem.finish_time,
            updatedItem.lunch_minutes || 0,
            updatedItem.travel_minutes || 0
          );
          updatedItem.travel_excluded_hours = 0;
        }
      }
      
      return updatedItem;
    }));
  };

  const handleSubmitDocket = async (e) => {
    e.preventDefault();
    if (!selectedJobForDocket) return;

    // Validate at least one item has hours
    const filledItems = docketItems.filter(item => item.qty > 0);
    if (filledItems.length === 0) {
      toast.error('Please enter hours for at least one item');
      return;
    }

    // Concrete pumping: cubic metres pumped is required
    if (isConcretePumping) {
      const m3 = parseFloat(docketConcretePumped);
      if (!docketConcretePumped || isNaN(m3) || m3 <= 0) {
        toast.error('Please enter the cubic metres of concrete pumped');
        return;
      }
    }

    try {
      const docketData = {
        job_id: selectedJobForDocket.id,
        job_date: docketDateForJob || selectedJobForDocket.start_date, // Use the stored docket date
        customer_id: selectedJobForDocket.customer_id,
        site_id: selectedJobForDocket.site_id,
        equipment_type: selectedJobForDocket.equipment_type,
        notes: docketNotes,
        work_completed: docketWorkCompleted,
        paper_docket_url: docketPaperUrl || null,
        paper_docket_name: docketPaperName || null,
        items: docketItems.map(item => ({
          item_type: item.item_type,
          type_name: item.type_name,
          qty: parseFloat(item.qty) || 0,
          uom: item.uom,
          description: '',
          start_time: item.start_time || null,
          finish_time: item.finish_time || null,
          lunch_minutes: item.lunch_minutes || 0,
          travel_minutes: item.travel_minutes || 0,
          ...(sheetDepotMode && item.item_type !== 'attachment' ? {
            depart_depot_time: item.depart_depot_time || null,
            arrive_depot_time: item.arrive_depot_time || null,
          } : {}),
          worker_type_id: item.worker_type_id || null,
          is_primary_operator: item.is_primary_operator || false,
        })),
        material_loads: docketMaterialLoads.map(load => ({
          yard_id: load.yard_id,
          yard_name: load.yard_name,
          material_type_id: load.material_type_id,
          material_type_name: load.material_type_name,
          load_type: load.load_type,
          quantity: parseFloat(load.quantity) || 0,
          unit: load.unit,
          tip_docket_number: load.tip_docket_number || '',
          tip_docket_photo_url: load.tip_docket_photo_url || '',
          notes: load.notes || ''
        })),
        concrete_pumped_m3: isConcretePumping ? parseFloat(docketConcretePumped) : null
      };

      await api.post('/dockets', docketData);
      toast.success('Docket created successfully');
      setDocketDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create docket'));
    }
  };

  // Allocation handlers
  const fetchWorkerUnavailability = async (startDate, endDate) => {
    try {
      const params = new URLSearchParams({ start_date: startDate });
      if (endDate) params.set('end_date', endDate);
      const res = await api.get(`/worker-availability/bulk-check?${params}`);
      setUnavailableWorkers(res.data?.unavailable || {});
    } catch {
      setUnavailableWorkers({});
    }
  };

  // Multi-day job whose crew are owned by allocation blocks (tower crane jobs excluded)
  const isCrewBlockJob = (job) => !!(job && job.end_date && job.end_date > job.start_date && !job.is_tower_crane);
  // Day views show crew read-only only when the block owns them (labour-only jobs keep their editors)
  const isBlockCrewReadOnly = (job) => isCrewBlockJob(job) && job.equipment_type !== 'No Asset';

  // Crew shown read-only for one day: rows linked to this block, plus unlinked rows (pre-repair)
  const getDayCrewForBlock = (jobId, date, allocationId) => workerAllocations.filter(wa =>
    wa.job_id === jobId && wa.is_active !== false &&
    wa.start_date <= date && (wa.end_date || wa.start_date) >= date &&
    (!wa.allocation_id || wa.allocation_id === allocationId)
  );

  const handleOpenAllocationDialog = async (job) => {
    setDayEdit(null);
    // Check if this is a multi-day job
    const isMultiDay = job.end_date && job.end_date !== job.start_date;
    
    if (isMultiDay) {
      // Open the multi-day allocation drawer instead
      handleOpenMultiDayDrawer(job);
      return;
    }
    
    // Single-day job - use existing dialog
    setSelectedJobForAllocation(job);
    setOverrideCheck(false);
    setShowSuggestions(false);
    fetchWorkerUnavailability(job.start_date, job.end_date || job.start_date);
    const existingAllocation = getJobAllocation(job.id);
    
    // Check if this is a combo equipment type
    // Legacy per-component combo picker only applies when the type has NO combo
    // assets. When combo assets exist (new model) allocate the combo asset directly.
    const isCombo = isComboEquipmentType(job.equipment_type) && !usesComboAssets(job.equipment_type);
    const comboComponents = isCombo ? getComboComponents(job.equipment_type) : [];
    
    // Get existing worker allocations for this job
    const jobWorkerAllocations = workerAllocations.filter(wa => wa.job_id === job.id);
    const workerAllocationMap = jobWorkerAllocations.map(wa => ({
      worker_type_id: wa.worker_type_id,
      operator_id: wa.operator_id,
      id: wa.id // Keep ID for deletion
    }));
    
    // Helper to create combo equipment slots safely
    const createComboSlots = (components) => {
      return components.map(comp => ({
        component_type_id: comp?.id || '',
        component_type_name: comp?.name || 'Unknown',
        equipment_id: ''
      })).filter(slot => slot.component_type_id); // Only include valid slots
    };
    
    if (existingAllocation) {
      // Initialize combo equipment IDs from existing allocation or create empty slots
      let comboEquipIds = existingAllocation.combo_equipment_ids || [];
      
      // If we have existing combo equipment IDs but they're stored as strings (from backend),
      // we need to convert them back to objects with component type info
      if (isCombo && comboEquipIds.length > 0 && typeof comboEquipIds[0] === 'string') {
        // Map the stored equipment IDs to the combo component structure
        comboEquipIds = comboComponents.map((comp, index) => ({
          component_type_id: comp?.id || '',
          component_type_name: comp?.name || 'Unknown',
          equipment_id: comboEquipIds[index] || '' // Use stored equipment ID at same index
        }));
      } else if (isCombo && comboEquipIds.length === 0 && comboComponents.length > 0) {
        // Create empty slots for each combo component
        comboEquipIds = createComboSlots(comboComponents);
      }
      
      setAllocationForm({
        equipment_id: existingAllocation.equipment_id || '',
        operator_id: existingAllocation.operator_id || '',
        start_date: existingAllocation.start_date || job.start_date,
        end_date: existingAllocation.end_date || '',
        attachment_ids: existingAllocation.attachment_ids || [],
        worker_allocations: workerAllocationMap,
        is_combo_allocation: existingAllocation.is_combo_allocation || isCombo,
        combo_equipment_ids: comboEquipIds,
        prime_mover_equipment_id: existingAllocation.prime_mover_equipment_id || '',
        additional_trailer_equipment_ids: existingAllocation.additional_trailer_equipment_ids || [],
        asset_requirement_id: existingAllocation.asset_requirement_id || '',
        asset_requirement_index: existingAllocation.asset_requirement_index ?? null,
      });
    } else {
      // Pre-populate worker allocations based on job requirements, merging existing assignments
      const initialWorkerAllocations = (job.worker_requirements || []).map(wr => {
        const existing = workerAllocationMap.find(wa => wa.worker_type_id === wr.worker_type_id);
        return {
          worker_type_id: wr.worker_type_id,
          operator_id: existing?.operator_id || '',
          id: existing?.id,
          quantity: wr.quantity
        };
      });
      
      // Create empty combo equipment slots for combo types
      const initialComboEquipIds = createComboSlots(comboComponents);

      // Multi-asset: auto-select first unallocated slot
      let initAssetReqId = '';
      let initAssetReqIndex = null;
      if (job.asset_requirements?.length > 0) {
        for (const req of job.asset_requirements) {
          for (let i = 0; i < (req.quantity || 1); i++) {
            const hasAlloc = allocations.some(
              a => a.job_id === job.id && a.asset_requirement_id === req.id && (a.asset_requirement_index ?? 0) === i
            );
            if (!hasAlloc) {
              initAssetReqId = req.id;
              initAssetReqIndex = i;
              break;
            }
          }
          if (initAssetReqId) break;
        }
        // If all allocated, select the first slot
        if (!initAssetReqId) {
          initAssetReqId = job.asset_requirements[0].id;
          initAssetReqIndex = 0;
        }
      }
      
      setAllocationForm({
        equipment_id: '',
        operator_id: '',
        start_date: job.start_date || '',
        end_date: job.end_date || '',
        attachment_ids: [],
        worker_allocations: initialWorkerAllocations,
        is_combo_allocation: isCombo,
        combo_equipment_ids: initialComboEquipIds,
        prime_mover_equipment_id: '',
        additional_trailer_equipment_ids: [],
        asset_requirement_id: initAssetReqId,
        asset_requirement_index: initAssetReqIndex,
      });
    }
    setAllocationDialogOpen(true);
    
    // Fetch smart operator suggestions
    fetchOperatorSuggestions(job);
  };

  // Edit allocation for a specific day (used by Week View "Edit Allocation" link)
  // Always opens the single-day Allocate Resources dialog, even for multi-day jobs
  const handleEditDayAllocation = async (job, forDate) => {
    const dateStr = forDate ? format(forDate, 'yyyy-MM-dd') : job.start_date;

    setSelectedJobForAllocation(job);
    setOverrideCheck(false);
    setShowSuggestions(false);

    // Find allocation covering this specific date
    const existingAllocation = allocations.find(a =>
      a.job_id === job.id && a.start_date <= dateStr && (a.end_date || a.start_date) >= dateStr
    );
    setDayEdit(isBlockCrewReadOnly(job)
      ? { jobId: job.id, date: dateStr, allocationId: existingAllocation?.id || null }
      : null);

    // Worker allocations for this specific date
    const dayWorkerAllocs = workerAllocations.filter(wa =>
      wa.job_id === job.id && wa.start_date <= dateStr && (wa.end_date || wa.start_date) >= dateStr
    );
    const workerAllocationMap = dayWorkerAllocs.map(wa => ({
      worker_type_id: wa.worker_type_id,
      operator_id: wa.operator_id,
      id: wa.id
    }));

    const isCombo = isComboEquipmentType(job.equipment_type) && !usesComboAssets(job.equipment_type);
    const comboComponents = isCombo ? getComboComponents(job.equipment_type) : [];
    const createComboSlots = (components) => {
      return components.map(comp => ({
        component_type_id: comp?.id || '',
        component_type_name: comp?.name || 'Unknown',
        equipment_id: ''
      })).filter(slot => slot.component_type_id);
    };

    if (existingAllocation) {
      let comboEquipIds = existingAllocation.combo_equipment_ids || [];
      if (isCombo && comboEquipIds.length > 0 && typeof comboEquipIds[0] === 'string') {
        comboEquipIds = comboComponents.map((comp, index) => ({
          component_type_id: comp?.id || '',
          component_type_name: comp?.name || 'Unknown',
          equipment_id: comboEquipIds[index] || ''
        }));
      } else if (isCombo && comboEquipIds.length === 0 && comboComponents.length > 0) {
        comboEquipIds = createComboSlots(comboComponents);
      }
      setAllocationForm({
        equipment_id: existingAllocation.equipment_id || '',
        operator_id: existingAllocation.operator_id || '',
        start_date: dateStr,
        end_date: dateStr,
        attachment_ids: existingAllocation.attachment_ids || [],
        worker_allocations: workerAllocationMap,
        is_combo_allocation: existingAllocation.is_combo_allocation || isCombo,
        combo_equipment_ids: comboEquipIds
      });
    } else {
      const initialWorkerAllocations = (job.worker_requirements || []).map(wr => {
        const existing = workerAllocationMap.find(wa => wa.worker_type_id === wr.worker_type_id);
        return {
          worker_type_id: wr.worker_type_id,
          operator_id: existing?.operator_id || '',
          id: existing?.id,
          quantity: wr.quantity
        };
      });
      const initialComboEquipIds = createComboSlots(comboComponents);
      setAllocationForm({
        equipment_id: '',
        operator_id: '',
        start_date: dateStr,
        end_date: dateStr,
        attachment_ids: [],
        worker_allocations: initialWorkerAllocations,
        is_combo_allocation: isCombo,
        combo_equipment_ids: initialComboEquipIds
      });
    }
    setAllocationDialogOpen(true);
    fetchOperatorSuggestions(job);
  };

  // Helper to get job's worker allocations
  const getJobWorkerAllocations = (jobId) => {
    return workerAllocations.filter(wa => wa.job_id === jobId);
  };

  const fetchOperatorSuggestions = async (job) => {
    // Only fetch if AI is available
    if (!isAiAvailable()) {
      setOperatorSuggestions([]);
      return;
    }
    
    setSuggestionsLoading(true);
    try {
      const res = await api.get('/smart-suggestions/operators', {
        params: {
          job_id: job.id,
          customer_id: job.customer_id,
          site_id: job.site_id,
          equipment_type: job.equipment_type,
          job_date: job.start_date
        }
      });
      setOperatorSuggestions(res.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch operator suggestions', error);
      setOperatorSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const selectSuggestedOperator = (operatorId) => {
    setAllocationForm(prev => ({ ...prev, operator_id: operatorId }));
  };

  // Check for allocation conflicts before saving
  const checkAllocationConflicts = async (jobId, operatorId, equipmentId, startDate, endDate, workerAllocations = []) => {
    try {
      const response = await api.post('/allocations/check-conflicts', {
        job_id: jobId,
        operator_id: operatorId,
        equipment_id: equipmentId || '',
        start_date: startDate,
        end_date: endDate || startDate,
        worker_allocations: workerAllocations.filter(wa => wa.operator_id).map(wa => ({
          worker_type_id: wa.worker_type_id,
          operator_id: wa.operator_id
        }))
      });
      return response.data;
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return { has_conflicts: false, warnings: [] };
    }
  };

  // Build preflight items from allocation data and run leave-preflight check
  // UTC-safe date arithmetic helper (YYYY-MM-DD strings) — shared by
  // preflight item builders and the save functions' remainder logic.
  const addDaysStr = (dateStr, days) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + days));
    return dt.toISOString().split('T')[0];
  };

  // Compute crew remainder items for days outside the edited range.
  // Used by both the preflight builder and executeAllocationSave so they stay in sync.
  const computeCrewRemainders = (overlappingWAs, editStart, editEnd, jobStart, jobEnd) => {
    const result = [];
    for (const wa of overlappingWAs) {
      if (!wa.operator_id) continue;
      const effectiveEnd = wa.end_date || jobEnd;
      if (effectiveEnd > editEnd) {
        const nextDay = addDaysStr(editEnd, 1);
        const clampedEnd = effectiveEnd > jobEnd ? jobEnd : effectiveEnd;
        if (nextDay <= clampedEnd) {
          result.push({ operator_id: wa.operator_id, worker_type_id: wa.worker_type_id, start_date: nextDay, end_date: clampedEnd });
        }
      }
      if (wa.start_date < editStart) {
        const prevDay = addDaysStr(editStart, -1);
        const clampedStart = wa.start_date < jobStart ? jobStart : wa.start_date;
        if (prevDay >= clampedStart) {
          result.push({ operator_id: wa.operator_id, worker_type_id: wa.worker_type_id, start_date: clampedStart, end_date: prevDay });
        }
      }
    }
    return result;
  };

  // Build preflight items for a single-day save
  const buildSingleDayPreflightItems = async (data) => {
    const { submitData, existingAllocation, worker_allocations, selectedJob } = data;
    const items = [];
    const startDate = submitData.start_date;
    const endDate = submitData.end_date || startDate;
    // Day-row edit of a multi-day job: only a changed operator needs a leave check, for the day
    if (data.dayEdit) {
      const operatorChanged = !existingAllocation || submitData.operator_id !== existingAllocation.operator_id;
      return submitData.operator_id && operatorChanged ? [{ operator_id: submitData.operator_id, start_date: startDate, end_date: endDate }] : [];
    }
    const jobStart = selectedJob.start_date;
    const jobEnd = selectedJob.end_date || selectedJob.start_date;
    // Main allocation operator
    if (submitData.operator_id) {
      items.push({ operator_id: submitData.operator_id, start_date: startDate, end_date: endDate });
    }
    // Operator remainder allocations (existing dates outside edited range)
    if (existingAllocation) {
      const origStart = existingAllocation.start_date;
      const origEnd = existingAllocation.end_date || existingAllocation.start_date;
      if (existingAllocation.operator_id) {
        if (origEnd > endDate) {
          const nextDay = addDaysStr(endDate, 1);
          const clampedEnd = origEnd > jobEnd ? jobEnd : origEnd;
          if (nextDay <= clampedEnd) items.push({ operator_id: existingAllocation.operator_id, start_date: nextDay, end_date: clampedEnd });
        }
        if (origStart < startDate) {
          const prevDay = addDaysStr(startDate, -1);
          const clampedStart = origStart < jobStart ? jobStart : origStart;
          if (prevDay >= clampedStart) items.push({ operator_id: existingAllocation.operator_id, start_date: clampedStart, end_date: prevDay });
        }
      }
    }
    // New crew rows for the edited range
    for (const wa of (worker_allocations || [])) {
      if (wa.operator_id) {
        items.push({ operator_id: wa.operator_id, start_date: startDate, end_date: endDate });
      }
    }
    // Crew remainder items (existing crew days outside the edited range)
    if (selectedJob?.id) {
      try {
        const waRes = await api.get(`/worker-allocations/job/${selectedJob.id}`);
        const liveWAs = waRes.data || [];
        const overlappingWAs = liveWAs.filter(wa => {
          const effectiveEnd = wa.end_date || jobEnd;
          return wa.start_date <= endDate && effectiveEnd >= startDate;
        });
        const crewRemainders = computeCrewRemainders(overlappingWAs, startDate, endDate, jobStart, jobEnd);
        for (const rem of crewRemainders) {
          items.push({ operator_id: rem.operator_id, start_date: rem.start_date, end_date: rem.end_date });
        }
      } catch { /* crew remainder preflight non-fatal */ }
    }
    return items;
  };

  // Build preflight items for a multi-day save
  const buildMultiDayPreflightItems = (data) => {
    const { allocationData, worker_allocations, existingAllocation } = data;
    const items = [];
    const startDate = allocationData.start_date;
    const endDate = allocationData.end_date || startDate;
    if (allocationData.operator_id) {
      items.push({ operator_id: allocationData.operator_id, start_date: startDate, end_date: endDate });
    }
    const existingWorkers = existingAllocation?.worker_allocations || [];
    const oldStart = existingAllocation?.start_date;
    const oldEnd = existingAllocation?.end_date || oldStart;
    for (const wa of (worker_allocations || [])) {
      if (!wa.operator_id) continue;
      const ew = wa.id && existingWorkers.find(e => e.id === wa.id && e.worker_type_id === wa.worker_type_id);
      if (!ew || ew.operator_id !== wa.operator_id) {
        items.push({ operator_id: wa.operator_id, start_date: startDate, end_date: endDate });
        continue;
      }
      // Unchanged crew: only those matching the old block range follow it, for the days it gains
      if (ew.start_date !== oldStart || (ew.end_date || ew.start_date) !== oldEnd) continue;
      if (startDate < oldStart) {
        const e = addDaysStr(oldStart, -1);
        items.push({ operator_id: wa.operator_id, start_date: startDate, end_date: e < endDate ? e : endDate });
      }
      if (endDate > oldEnd) {
        const s = addDaysStr(oldEnd, 1);
        items.push({ operator_id: wa.operator_id, start_date: s > startDate ? s : startDate, end_date: endDate });
      }
    }
    return items;
  };

  // Run the leave-preflight API call with pre-built items
  const runLeavePreflight = async (items) => {
    if (!items || items.length === 0) return null;
    try {
      const { data: result } = await api.post('/allocations/leave-preflight', { items });
      return result?.blocks?.length > 0 ? result.blocks : null;
    } catch {
      return null; // Preflight failed — proceed and let the save catch it
    }
  };

  // Wrapper: preflight → dialog → save
  const saveWithLeavePreflight = async (data, skipValidation, complianceOverrideReason, saveFn) => {
    try {
      const items = (saveFn === executeMultiDayAllocationSave)
        ? buildMultiDayPreflightItems(data)
        : await buildSingleDayPreflightItems(data);
      const blocks = await runLeavePreflight(items);
      if (blocks) {
        setLeaveOverride({
          open: true,
          blocks,
          callback: (reason) => saveFn(data, skipValidation, complianceOverrideReason, reason),
        });
        return;
      }
      await saveFn(data, skipValidation, complianceOverrideReason, null);
    } catch (err) {
      console.error('[saveWithLeavePreflight] unexpected error', err);
      toast.error('Failed to save allocation');
    }
  };

  // Proceed with allocation after conflict confirmation
  const proceedWithAllocation = async () => {
    setShowConflictDialog(false);
    
    if (conflictContext === 'single' && pendingAllocationData) {
      await saveWithLeavePreflight(pendingAllocationData, true, null, executeAllocationSave);
    } else if (conflictContext === 'multi' && pendingAllocationData) {
      await saveWithLeavePreflight(pendingAllocationData, true, null, executeMultiDayAllocationSave);
    }
    
    setPendingAllocationData(null);
    setConflictContext(null);
    setConflictWarnings([]);
  };

  // Execute single-day allocation save
  const executeAllocationSave = async (data, skipValidation = false, complianceOverrideReason = null, leaveOverrideReason = null) => {
    try {
      const { submitData, existingAllocation, worker_allocations, selectedJob } = data;
      const _params = [];
      if (skipValidation) _params.push('skip_validation=true');
      if (complianceOverrideReason) _params.push(`compliance_override_reason=${encodeURIComponent(complianceOverrideReason)}`);
      if (leaveOverrideReason) _params.push(`leave_override_reason=${encodeURIComponent(leaveOverrideReason)}`);
      const queryParam = _params.length ? `?${_params.join('&')}` : '';
      const _compParams = [];
      if (complianceOverrideReason) _compParams.push(`compliance_override_reason=${encodeURIComponent(complianceOverrideReason)}`);
      if (leaveOverrideReason) _compParams.push(`leave_override_reason=${encodeURIComponent(leaveOverrideReason)}`);
      const complianceQS = _compParams.length ? `?${_compParams.join('&')}` : '';

      if (data.dayEdit) {
        // Multi-day job, one day: isolate the day from its block, then update it. Crew stay on their blocks.
        if (existingAllocation) {
          let targetId = existingAllocation.id;
          if (existingAllocation.start_date !== (existingAllocation.end_date || existingAllocation.start_date)) {
            const { data: iso } = await api.post(`/allocations/${existingAllocation.id}/isolate-day`, { date: data.dayEdit.date });
            targetId = iso.allocation_id;
          }
          await api.put(`/allocations/${targetId}${queryParam}`, submitData);
          toast.success('Allocation updated');
        } else {
          await api.post(`/allocations${queryParam}`, submitData);
          toast.success('Allocation created - Ready to confirm');
        }
      } else if (existingAllocation) {
        const origStart = existingAllocation.start_date;
        const origEnd = existingAllocation.end_date || existingAllocation.start_date;
        const newStart = submitData.start_date;
        const newEnd = submitData.end_date || submitData.start_date;

        // Check if saving would shrink the existing allocation's date range
        const needsSplit = origEnd > newEnd || origStart < newStart;

        if (needsSplit) {
          // UTC-safe date arithmetic helper
          const addDaysUtil = (dateStr, days) => {
            const [y, m, d] = dateStr.split('-').map(Number);
            const dt = new Date(Date.UTC(y, m - 1, d + days));
            return dt.toISOString().split('T')[0];
          };

          // Clamp remainder dates to the job's actual date range
          const jobStart = selectedJob.start_date;
          const jobEnd = selectedJob.end_date || selectedJob.start_date;

          // Create remainder allocation(s) to preserve days outside the edited range
          // Remainder AFTER edited range (e.g., original 13-17, editing 13-15 → create 16-17 remainder)
          if (origEnd > newEnd) {
            const nextDay = addDaysUtil(newEnd, 1);
            const clampedEnd = origEnd > jobEnd ? jobEnd : origEnd;
            if (nextDay <= clampedEnd) {
              const remainderData = {
                job_id: selectedJob.id,
                equipment_id: existingAllocation.equipment_id || '',
                operator_id: existingAllocation.operator_id || '',
                start_date: nextDay,
                end_date: clampedEnd,
                attachment_ids: existingAllocation.attachment_ids || [],
                notes: existingAllocation.notes || '',
                is_combo_allocation: existingAllocation.is_combo_allocation || false,
                combo_equipment_ids: existingAllocation.combo_equipment_ids || [],
              };
              await api.post(withLeaveOverride(`/allocations?skip_validation=true`, leaveOverrideReason), remainderData);
            }
          }
          // Remainder BEFORE edited range (e.g., original 13-17, editing 15-17 → create 13-14 remainder)
          if (origStart < newStart) {
            const prevDay = addDaysUtil(newStart, -1);
            const clampedStart = origStart < jobStart ? jobStart : origStart;
            if (prevDay >= clampedStart) {
              const remainderData = {
                job_id: selectedJob.id,
                equipment_id: existingAllocation.equipment_id || '',
                operator_id: existingAllocation.operator_id || '',
                start_date: clampedStart,
                end_date: prevDay,
                attachment_ids: existingAllocation.attachment_ids || [],
                notes: existingAllocation.notes || '',
                is_combo_allocation: existingAllocation.is_combo_allocation || false,
                combo_equipment_ids: existingAllocation.combo_equipment_ids || [],
              };
              await api.post(withLeaveOverride(`/allocations?skip_validation=true`, leaveOverrideReason), remainderData);
            }
          }
        }

        // Update the existing allocation with the new data/dates
        await api.put(`/allocations/${existingAllocation.id}${queryParam}`, submitData);
        toast.success('Allocation updated');
      } else {
        await api.post(`/allocations${queryParam}`, submitData);
        toast.success('Allocation created - Ready to confirm');
      }

      // Handle worker allocations — split any that extend beyond the edited range
      if (!data.dayEdit) {
      const editStart = submitData.start_date;
      const editEnd = submitData.end_date || submitData.start_date;
      const jobEndDate = selectedJob.end_date || selectedJob.start_date;

      console.warn('[ALLOC-SPLIT] editStart:', editStart, 'editEnd:', editEnd, 'jobEndDate:', jobEndDate, 'jobId:', selectedJob.id);

      // Fetch FRESH worker allocations from API (React state may be stale)
      let liveWorkerAllocs = [];
      try {
        const waRes = await api.get(`/worker-allocations/job/${selectedJob.id}`);
        liveWorkerAllocs = waRes.data || [];
      } catch (e) {
        // Fallback to state if API fails
        liveWorkerAllocs = workerAllocations.filter(wa => wa.job_id === selectedJob.id);
      }

      // Find WAs that overlap the edited range
      const overlappingWAs = liveWorkerAllocs.filter(wa => {
        const waEffectiveEnd = wa.end_date || jobEndDate;
        return wa.start_date <= editEnd && waEffectiveEnd >= editStart;
      });

      // Create remainder worker allocations using the shared helper
      const jobStartDate = selectedJob.start_date;
      const crewRemainders = computeCrewRemainders(overlappingWAs, editStart, editEnd, jobStartDate, jobEndDate);
      for (const rem of crewRemainders) {
        await api.post(withLeaveOverride('/worker-allocations', leaveOverrideReason), {
          job_id: selectedJob.id,
          worker_type_id: rem.worker_type_id,
          operator_id: rem.operator_id,
          start_date: rem.start_date,
          end_date: rem.end_date,
        });
      }

      // Now delete worker allocations overlapping the edited range
      await api.delete(`/worker-allocations/job/${selectedJob.id}?start_date=${editStart}&end_date=${editEnd}`);
      
      const workerAllocsToCreate = worker_allocations
        .filter(wa => wa.operator_id)
        .map(wa => ({
          job_id: selectedJob.id,
          worker_type_id: wa.worker_type_id,
          operator_id: wa.operator_id,
          start_date: editStart,
          end_date: editEnd
        }));
      
      if (workerAllocsToCreate.length > 0) {
        const bulkUrl = withLeaveOverride(`/worker-allocations/bulk${complianceQS}`, leaveOverrideReason);
        await api.post(bulkUrl, workerAllocsToCreate);
      }
      }

      // Multi-asset: keep dialog open and advance to next unallocated slot
      const assetReqs = selectedJob?.asset_requirements || [];
      if (assetReqs.length > 0 && submitData?.asset_requirement_id) {
        await fetchData();
        // Find the next unallocated slot
        const freshAllocations = await api.get('/allocations').then(r => r.data);
        let nextReqId = '';
        let nextReqIdx = null;
        for (const req of assetReqs) {
          for (let i = 0; i < (req.quantity || 1); i++) {
            const hasAlloc = freshAllocations.some(
              a => a.job_id === selectedJob.id && a.asset_requirement_id === req.id && (a.asset_requirement_index ?? 0) === i
            );
            if (!hasAlloc) {
              nextReqId = req.id;
              nextReqIdx = i;
              break;
            }
          }
          if (nextReqId) break;
        }

        if (nextReqId) {
          // Auto-advance to next slot
          setAllocationForm(prev => ({
            ...prev,
            equipment_id: '',
            operator_id: '',
            asset_requirement_id: nextReqId,
            asset_requirement_index: nextReqIdx,
          }));
          toast.success('Allocation saved — select next asset slot');
        } else {
          // All slots allocated — close dialog
          toast.success('All asset slots allocated');
          setAllocationDialogOpen(false);
        }
      } else {
        setAllocationDialogOpen(false);
        fetchData();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'compliance_block') {
        setComplianceReason('');
        setComplianceBlock({
          open: true,
          message: detail.message,
          missing: detail.missing_mandatory || [],
          canOverride: !!detail.can_override,
          pendingData: data,
          skipValidation,
          leaveOverrideReason,
        });
        return;
      }
      // Leave block fallback — never re-run the save; just toast and refresh
      const lb = getLeaveBlock(error);
      if (lb) {
        toast.error(`${lb.message}. Part of this save may not have completed — the job has been refreshed; please check it.`);
        fetchData();
        return;
      }
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to save allocation'));
    }
  };

  // EW2-286 — admin confirms an override with a mandatory reason
  const confirmComplianceOverride = async () => {
    if (!complianceReason.trim()) {
      toast.error('Please enter a reason to override');
      return;
    }
    const reason = complianceReason.trim();
    const { pendingData, skipValidation, labourOnly, leaveOverrideReason } = complianceBlock;
    setComplianceBlock({ open: false });
    setComplianceReason('');
    if (labourOnly) {
      // Labour-only (No Asset) job: re-run just the worker-allocation bulk with the override.
      try {
        const { selectedJob, worker_allocations } = pendingData;
        const editStart = selectedJob.start_date;
        const editEnd = selectedJob.end_date || selectedJob.start_date;
        const toCreate = (worker_allocations || []).filter(wa => wa.operator_id).map(wa => ({
          job_id: selectedJob.id, worker_type_id: wa.worker_type_id, operator_id: wa.operator_id,
          start_date: editStart, end_date: editEnd,
        }));
        // If we already have a leave override, skip the preflight
        if (!leaveOverrideReason) {
          const preflightItems = toCreate.map(wa => ({ operator_id: wa.operator_id, start_date: wa.start_date, end_date: wa.end_date }));
          if (preflightItems.length > 0) {
            try {
              const { data: pf } = await api.post('/allocations/leave-preflight', { items: preflightItems });
              if (pf?.blocks?.length > 0) {
                setLeaveOverride({
                  open: true,
                  blocks: pf.blocks,
                  callback: async (leaveReason) => {
                    try {
                      await api.delete(`/worker-allocations/job/${selectedJob.id}?start_date=${editStart}&end_date=${editEnd}`);
                      if (toCreate.length > 0) {
                        const qs = `compliance_override_reason=${encodeURIComponent(reason)}&leave_override_reason=${encodeURIComponent(leaveReason)}`;
                        await api.post(`/worker-allocations/bulk?${qs}`, toCreate);
                      }
                      toast.success('Worker allocation saved (compliance overridden)');
                      setAllocationDialogOpen(false);
                      fetchData();
                    } catch (err) {
                      toast.error(getApiErrorMessage(err, 'Failed to save allocation'));
                    }
                  },
                });
                return;
              }
            } catch { /* preflight failed — proceed */ }
          }
        }
        await api.delete(`/worker-allocations/job/${selectedJob.id}?start_date=${editStart}&end_date=${editEnd}`);
        if (toCreate.length > 0) {
          const qs_parts = [`compliance_override_reason=${encodeURIComponent(reason)}`];
          if (leaveOverrideReason) qs_parts.push(`leave_override_reason=${encodeURIComponent(leaveOverrideReason)}`);
          await api.post(`/worker-allocations/bulk?${qs_parts.join('&')}`, toCreate);
        }
        toast.success('Worker allocation saved (compliance overridden)');
        setAllocationDialogOpen(false);
        fetchData();
      } catch (error) {
        const detail = error.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to save allocation'));
      }
      return;
    }
    await executeAllocationSave(pendingData, skipValidation, reason, leaveOverrideReason);
  };

  // EW2-286 — user acknowledges the preferred-compliance warning and proceeds
  const confirmComplianceWarning = () => {
    setComplianceBlock({ open: false });
    // Re-run the save path with the warning explicitly skipped (avoid stale state).
    handleSubmitAllocation(null, true);
  };

  const handleSubmitAllocation = async (e, skipPreferredWarning = false) => {
    if (e) e.preventDefault();
    try {
      // Validate required fields
      if (!allocationForm.start_date) {
        toast.error('Start Date is required');
        return;
      }

      // EW2-286 — preferred compliance warning ("Proceed?"). Non-blocking; only
      // shown when the operator meets mandatory but is missing PREFERRED certs.
      if (allocationForm.operator_id && !skipPreferredWarning) {
        try {
          const vr = await api.post('/compliance/validate-allocation', {
            operator_id: allocationForm.operator_id,
            job_id: selectedJobForAllocation.id,
          });
          const data = vr.data || {};
          if (data.meets_mandatory && (data.missing_preferred || []).length > 0) {
            const names = data.missing_preferred.map(m => `${m.name}${m.source ? ` (${m.source})` : ''}`).join(', ');
            const opName = (operators.find(o => o.id === allocationForm.operator_id)?.name) || 'This operator';
            const custName = (customers.find(c => c.id === selectedJobForAllocation?.customer_id)?.company_name) || 'this customer';
            setComplianceReason('');
            setComplianceBlock({
              open: true,
              mode: 'warning',
              message: `${opName} lacks preferred compliance for ${custName}: ${names}. Proceed?`,
              missing: data.missing_preferred,
              canOverride: false,
            });
            return; // wait for the user to Proceed (confirmComplianceWarning) or Cancel
          }
        } catch (_) { /* validation is advisory; never block save on its failure */ }
      }

      // Find existing allocation covering the form's date range
      // For multi-asset: scope to the selected asset requirement slot
      const formDateStr = allocationForm.start_date;
      const existingAllocation = dayEdit ? (allocations.find(a => a.id === dayEdit.allocationId) || null) : (allocations.find(a => {
        if (a.job_id !== selectedJobForAllocation.id) return false;
        if (!(a.start_date <= formDateStr && (a.end_date || a.start_date) >= formDateStr)) return false;
        // Multi-asset: match by slot
        if (allocationForm.asset_requirement_id) {
          return a.asset_requirement_id === allocationForm.asset_requirement_id &&
                 (a.asset_requirement_index ?? 0) === (allocationForm.asset_requirement_index ?? 0);
        }
        return true;
      }) || (allocationForm.asset_requirement_id ? null : getJobAllocation(selectedJobForAllocation.id)));
      const { worker_allocations, ...mainAllocationData } = allocationForm;

      // Labour-only (No Asset) jobs: skip the main Allocation entirely and
      // only create worker_allocations. The job has no asset/operator to book.
      if (selectedJobForAllocation?.equipment_type === 'No Asset') {
        const editStart = allocationForm.start_date;
        const editEnd = allocationForm.end_date || allocationForm.start_date;
        const workerAllocsToCreate = (worker_allocations || [])
          .filter(wa => wa.operator_id)
          .map(wa => ({
            job_id: selectedJobForAllocation.id,
            worker_type_id: wa.worker_type_id,
            operator_id: wa.operator_id,
            start_date: editStart,
            end_date: editEnd,
          }));
        // Preflight check for labour-only before delete
        const preflightItems = workerAllocsToCreate.map(wa => ({ operator_id: wa.operator_id, start_date: wa.start_date, end_date: wa.end_date }));
        if (preflightItems.length > 0) {
          try {
            const { data: pf } = await api.post('/allocations/leave-preflight', { items: preflightItems });
            if (pf?.blocks?.length > 0) {
              setLeaveOverride({
                open: true,
                blocks: pf.blocks,
                callback: async (reason) => {
                  try {
                    await api.delete(`/worker-allocations/job/${selectedJobForAllocation.id}?start_date=${editStart}&end_date=${editEnd}`);
                    if (workerAllocsToCreate.length > 0) {
                      await api.post(withLeaveOverride('/worker-allocations/bulk', reason), workerAllocsToCreate);
                    }
                    toast.success('Worker allocation saved');
                    setAllocationDialogOpen(false);
                    fetchData();
                  } catch (err) {
                    const cbDetail = err.response?.data?.detail;
                    if (cbDetail && typeof cbDetail === 'object' && cbDetail.error === 'compliance_block') {
                      setComplianceReason('');
                      setComplianceBlock({
                        open: true,
                        message: cbDetail.message,
                        missing: cbDetail.missing_mandatory || [],
                        canOverride: !!cbDetail.can_override,
                        pendingData: { selectedJob: selectedJobForAllocation, worker_allocations },
                        labourOnly: true,
                        leaveOverrideReason: reason,
                      });
                    } else {
                      toast.error(getApiErrorMessage(err, 'Failed to save allocation'));
                    }
                  }
                },
              });
              return;
            }
          } catch { /* preflight failed — proceed */ }
        }
        // Clean slate for the edited range, then insert fresh worker allocations
        await api.delete(`/worker-allocations/job/${selectedJobForAllocation.id}?start_date=${editStart}&end_date=${editEnd}`);
        if (workerAllocsToCreate.length > 0) {
          await api.post('/worker-allocations/bulk', workerAllocsToCreate);
          toast.success('Worker allocation saved');
        } else {
          toast.success('Worker allocations cleared');
        }
        setAllocationDialogOpen(false);
        fetchData();
        return;
      }
      
      // For combo allocations, validate component equipment selection
      if (allocationForm.is_combo_allocation) {
        const comboItems = allocationForm.combo_equipment_ids || [];

        // Check all components are filled
        const requiredMissing = comboItems.filter(c => !c?.equipment_id);
        if (requiredMissing.length > 0) {
          toast.error(`Please select equipment for: ${requiredMissing.map(c => c.component_type_name).join(', ')}`);
          return;
        }
      }
      
      // Convert combo_equipment_ids from objects to just equipment ID strings for backend
      const comboEquipmentIdStrings = allocationForm.is_combo_allocation
        ? (allocationForm.combo_equipment_ids || []).map(c => c?.equipment_id).filter(Boolean)
        : [];
      
      // Warn if job requires attachments but none selected
      const requiredAttTypeIds = selectedJobForAllocation?.attachment_type_ids || [];
      if (requiredAttTypeIds.length > 0 && (!allocationForm.attachment_ids || allocationForm.attachment_ids.length === 0)) {
        const attNames = requiredAttTypeIds.map(id => getAttachmentTypeName(id)).join(', ');
        toast.warning(`Missing required attachment(s): ${attNames}. You can still proceed.`, { duration: 5000 });
      }
      
      const submitData = {
        job_id: selectedJobForAllocation.id,
        operator_id: allocationForm.operator_id,
        start_date: allocationForm.start_date,
        end_date: allocationForm.end_date || allocationForm.start_date,
        attachment_ids: allocationForm.attachment_ids || [],
        notes: allocationForm.notes || '',
        is_combo_allocation: allocationForm.is_combo_allocation,
        equipment_id: allocationForm.is_combo_allocation 
          ? (comboEquipmentIdStrings[0] || '')
          : allocationForm.equipment_id,
        combo_equipment_ids: comboEquipmentIdStrings,
        prime_mover_equipment_id: allocationForm.prime_mover_equipment_id || null,
        additional_trailer_equipment_ids: (allocationForm.additional_trailer_equipment_ids || []).filter(Boolean),
        // Multi-asset slot link
        ...(allocationForm.asset_requirement_id ? {
          asset_requirement_id: allocationForm.asset_requirement_id,
          asset_requirement_index: allocationForm.asset_requirement_index ?? null,
        } : {}),
      };

      // If override is already checked, skip conflict check
      if (overrideCheck) {
        await saveWithLeavePreflight({
          submitData,
          existingAllocation,
          worker_allocations,
          dayEdit,
          selectedJob: selectedJobForAllocation
        }, true, null, executeAllocationSave);
        return;
      }

      // Check for conflicts first
      const conflictResult = await checkAllocationConflicts(
        selectedJobForAllocation.id,
        allocationForm.operator_id,
        allocationForm.is_combo_allocation ? comboEquipmentIdStrings[0] : allocationForm.equipment_id,
        allocationForm.start_date,
        allocationForm.end_date,
        worker_allocations
      );

      if (conflictResult.has_conflicts) {
        // Show conflict warning dialog
        setConflictWarnings(conflictResult.warnings);
        setPendingAllocationData({
          submitData,
          existingAllocation,
          worker_allocations,
          dayEdit,
          selectedJob: selectedJobForAllocation
        });
        setConflictContext('single');
        setShowConflictDialog(true);
      } else {
        // No conflicts, proceed directly (with preflight)
        await saveWithLeavePreflight({
          submitData,
          existingAllocation,
          worker_allocations,
          dayEdit,
          selectedJob: selectedJobForAllocation
        }, false, null, executeAllocationSave);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'compliance_block') {
        setComplianceReason('');
        setComplianceBlock({
          open: true,
          message: detail.message,
          missing: detail.missing_mandatory || [],
          canOverride: !!detail.can_override,
          pendingData: { submitData: null, worker_allocations, selectedJob: selectedJobForAllocation, existingAllocation: null },
          skipValidation: false,
          labourOnly: true,
        });
        return;
      }
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to save allocation'));
    }
  };

  const handleDeleteAllocation = async (allocId) => {
    try {
      await api.delete(`/allocations/${allocId}`);
      // Also delete worker allocations
      if (selectedJobForAllocation) {
        await api.delete(`/worker-allocations/job/${selectedJobForAllocation.id}`);
      }
      toast.success('Allocation removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove allocation');
    }
  };

  // ========== MULTI-DAY ALLOCATION MANAGEMENT ==========
  
  // Helper to get allocation status for operators/equipment in a date range
  const getResourceAllocationStatus = useCallback((resourceType, resourceId, startDate, endDate, excludeJobId = null) => {
    if (!resourceId || !startDate) return null;
    
    const effectiveEndDate = endDate || startDate;
    
    // Check allocations for conflicts
    const conflictingAllocations = allocations.filter(alloc => {
      // Skip allocations for the current job being edited
      if (excludeJobId && alloc.job_id === excludeJobId) return false;
      
      // Check resource match
      const resourceMatch = resourceType === 'operator' 
        ? alloc.operator_id === resourceId
        : alloc.equipment_id === resourceId;
      
      if (!resourceMatch) return false;
      
      // Check date overlap
      const allocStart = alloc.start_date;
      const allocEnd = alloc.end_date || alloc.start_date;
      
      return allocStart <= effectiveEndDate && allocEnd >= startDate;
    });
    
    if (conflictingAllocations.length === 0) return null;
    
    // Get job info for display
    const jobs = conflictingAllocations.map(alloc => {
      const job = getJobInfo(alloc.job_id);
      const customer = job ? getCustomerInfo(job.customer_id) : null;
      return {
        job_id: alloc.job_id,
        customer_name: customer?.company_name || 'Unknown',
        start_date: alloc.start_date,
        end_date: alloc.end_date
      };
    });
    
    return {
      status: 'allocated',
      conflicts: jobs
    };
  }, [allocations, getJobInfo, getCustomerInfo]);

  // Amber note above a block when unlinked crew overlap its dates (before the repair script runs)
  const renderUnlinkedCrewNote = (alloc) => {
    if (!isCrewBlockJob(multiDayJob) || alloc.is_support_asset) return null;
    const aEnd = alloc.end_date || alloc.start_date;
    const loose = workerAllocations.filter(wa =>
      wa.job_id === alloc.job_id && !wa.allocation_id && wa.is_active !== false &&
      wa.start_date <= aEnd && (wa.end_date || wa.start_date) >= alloc.start_date
    );
    if (loose.length === 0) return null;
    const names = [...new Set(loose.map(wa => operators.find(o => o.id === wa.operator_id)?.name || 'Unknown'))].join(', ');
    return (
      <div className="flex items-start gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-xs text-amber-700 dark:text-amber-300" data-testid={`unlinked-crew-note-${alloc.id}`}>
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>Some crew on these dates aren't linked to this block yet: {names}. They still show on the Schedule. Ask an admin to run the crew repair before changing crew here.</span>
      </div>
    );
  };

  const handleOpenMultiDayDrawer = async (job) => {
    // Close any other open drawers
    setJobDialogOpen(false);
    setAllocationDialogOpen(false);
    
    setMultiDayJob(job);
    setMultiDayDrawerOpen(true);
    setEditingMultiDayAllocation(null);
    await fetchMultiDayAllocations(job.id);
  };

  const fetchMultiDayAllocations = async (jobId) => {
    setLoadingMultiDay(true);
    try {
      const response = await api.get(`/jobs/${jobId}/allocations`);
      setMultiDayAllocations(response.data.allocations || []);
      setMultiDayStats(response.data.stats || null);
    } catch (error) {
      toast.error('Failed to fetch allocations');
      setMultiDayAllocations([]);
      setMultiDayStats(null);
    } finally {
      setLoadingMultiDay(false);
    }
  };

  const handleAddMultiDayAllocation = () => {
    if (!multiDayJob) return;
    
    // Find next unallocated date or use job start
    const nextDate = multiDayStats?.next_unallocated_date || multiDayJob.start_date;
    
    // Check if this is a combo equipment type
    const isCombo = isComboEquipmentType(multiDayJob.equipment_type) && !usesComboAssets(multiDayJob.equipment_type);
    const comboComponents = isCombo ? getComboComponents(multiDayJob.equipment_type) : [];
    
    // Pre-populate worker allocations based on job requirements, merging existing assignments.
    // Only linked crew seed the defaults — unlinked rows get linked to the new block by the backend.
    const jobWorkerAllocs = workerAllocations.filter(wa => wa.job_id === multiDayJob.id && wa.allocation_id);
    const initialWorkerAllocations = (multiDayJob.worker_requirements || []).map(wr => {
      const existing = jobWorkerAllocs.find(wa => wa.worker_type_id === wr.worker_type_id);
      return {
        worker_type_id: wr.worker_type_id,
        operator_id: existing?.operator_id || '',
        quantity: wr.quantity
      };
    });
    
    // Create empty combo equipment slots
    const initialComboEquipIds = comboComponents.map(comp => ({
      component_type_id: comp?.id || '',
      component_type_name: comp?.name || 'Unknown',
      equipment_id: ''
    })).filter(slot => slot.component_type_id);
    
    setMultiDayAllocationForm({
      start_date: nextDate,
      end_date: '',
      operator_id: '',
      equipment_id: '',
      status: tenantSettings.auto_confirm_jobs !== false ? 'confirmed' : 'draft',
      attachment_ids: [],
      worker_allocations: initialWorkerAllocations,
      is_combo_allocation: isCombo,
      combo_equipment_ids: initialComboEquipIds
    });
    setEditingMultiDayAllocation('new');
  };

  const handleEditMultiDayAllocation = (allocation) => {
    const isCombo = (allocation.is_combo_allocation || isComboEquipmentType(multiDayJob?.equipment_type)) && !usesComboAssets(multiDayJob?.equipment_type);
    const comboComponents = isCombo ? getComboComponents(multiDayJob?.equipment_type) : [];
    
    // Convert combo equipment IDs if needed
    let comboEquipIds = allocation.combo_equipment_ids || [];
    if (isCombo && comboEquipIds.length > 0 && typeof comboEquipIds[0] === 'string') {
      comboEquipIds = comboComponents.map((comp, index) => ({
        component_type_id: comp?.id || '',
        component_type_name: comp?.name || 'Unknown',
        equipment_id: comboEquipIds[index] || ''
      }));
    } else if (isCombo && comboEquipIds.length === 0 && comboComponents.length > 0) {
      comboEquipIds = comboComponents.map(comp => ({
        component_type_id: comp?.id || '',
        component_type_name: comp?.name || 'Unknown',
        equipment_id: ''
      })).filter(slot => slot.component_type_id);
    }
    
    setMultiDayAllocationForm({
      start_date: allocation.start_date || '',
      end_date: allocation.end_date || '',
      operator_id: allocation.operator_id || '',
      equipment_id: allocation.equipment_id || '',
      status: allocation.status || 'confirmed',
      attachment_ids: allocation.attachment_ids || [],
      worker_allocations: (allocation.worker_allocations || []).map(wa => ({
        worker_type_id: wa.worker_type_id,
        operator_id: wa.operator_id,
        id: wa.id
      })),
      is_combo_allocation: isCombo,
      combo_equipment_ids: comboEquipIds
    });
    setEditingMultiDayAllocation(allocation);
  };

  // Execute multi-day allocation save (after conflict confirmation or when no conflicts)
  const executeMultiDayAllocationSave = async (data, skipValidation = false, complianceOverrideReason = null, leaveOverrideReason = null) => {
    const { allocationData, worker_allocations, isNew, job, existingAllocation } = data;
    
    try {
      const _params = [];
      if (skipValidation) _params.push('skip_validation=true');
      if (complianceOverrideReason) _params.push(`compliance_override_reason=${encodeURIComponent(complianceOverrideReason)}`);
      if (leaveOverrideReason) _params.push(`leave_override_reason=${encodeURIComponent(leaveOverrideReason)}`);
      const queryParam = _params.length ? `?${_params.join('&')}` : '';
      
      if (isNew) {
        const response = await api.post(`/allocations${queryParam}`, allocationData);
        const newAllocationId = response.data.id;
        
        for (const wa of worker_allocations) {
          if (wa.operator_id) {
            await api.post(withLeaveOverride('/worker-allocations', leaveOverrideReason), {
              job_id: job.id,
              worker_type_id: wa.worker_type_id,
              operator_id: wa.operator_id,
              start_date: allocationData.start_date,
              end_date: allocationData.end_date,
              allocation_id: newAllocationId,
              status: allocationData.status
            });
          }
        }
        
        toast.success(allocationData.status === 'draft' 
          ? 'Draft allocation saved' 
          : 'Allocation confirmed and visible to operator'
        );
      } else {
        await api.put(`/allocations/${existingAllocation.id}${queryParam}`, allocationData);

        // Diff the block's crew by id — the backend already moved them with any block date change
        const existingWorkers = existingAllocation.worker_allocations || [];
        const keep = new Map();
        for (const wa of worker_allocations) {
          const ew = wa.id && wa.operator_id && existingWorkers.find(e => e.id === wa.id && e.worker_type_id === wa.worker_type_id);
          if (ew) keep.set(ew.id, wa);
        }
        for (const ew of existingWorkers) {
          if (ew.id && !keep.has(ew.id)) await api.delete(`/worker-allocations/${ew.id}`);
        }
        for (const wa of worker_allocations) {
          if (!wa.operator_id) continue;
          const ew = keep.get(wa.id) === wa ? existingWorkers.find(e => e.id === wa.id) : null;
          if (ew) {
            if (ew.operator_id !== wa.operator_id) {
              await api.patch(withLeaveOverride(`/worker-allocations/${wa.id}`, leaveOverrideReason), { operator_id: wa.operator_id });
            }
            continue;
          }
          await api.post(withLeaveOverride('/worker-allocations', leaveOverrideReason), {
            job_id: job.id,
            worker_type_id: wa.worker_type_id,
            operator_id: wa.operator_id,
            start_date: allocationData.start_date,
            end_date: allocationData.end_date,
            allocation_id: existingAllocation.id,
            status: allocationData.status
          });
        }
        
        toast.success('Allocation updated');
      }
      
      setEditingMultiDayAllocation(null);
      await fetchMultiDayAllocations(job.id);
      fetchData();
    } catch (error) {
      // Leave block fallback — never re-run the save; just toast and refresh
      const lb = getLeaveBlock(error);
      if (lb) {
        toast.error(`${lb.message}. Part of this save may not have completed — the job has been refreshed; please check it.`);
        fetchData();
        await fetchMultiDayAllocations(job.id);
        return;
      }
      toast.error(getApiErrorMessage(error, 'Failed to save allocation'));
    }
  };

  const handleSaveMultiDayAllocation = async () => {
    if (!multiDayJob) return;
    
    const isNew = editingMultiDayAllocation === 'new';
    const { worker_allocations, ...mainData } = multiDayAllocationForm;
    
    // For combo allocations, validate all components are filled
    if (mainData.is_combo_allocation) {
      const comboItems = mainData.combo_equipment_ids || [];

      const requiredMissing = comboItems.filter(c => !c?.equipment_id);
      if (requiredMissing.length > 0) {
        toast.error(`Please select equipment for: ${requiredMissing.map(c => c.component_type_name).join(', ')}`);
        return;
      }
    }
    
    // Prepare allocation data
    const comboEquipStrings = mainData.is_combo_allocation
      ? mainData.combo_equipment_ids.map(c => c.equipment_id).filter(Boolean)
      : [];
    const allocationData = {
      job_id: multiDayJob.id,
      ...mainData,
      end_date: mainData.end_date || mainData.start_date,
      // For combo allocations, set equipment_id to the first component
      // (mirrors single-day save behaviour so backend status calc works)
      equipment_id: mainData.is_combo_allocation
        ? (comboEquipStrings[0] || '')
        : mainData.equipment_id,
      combo_equipment_ids: comboEquipStrings
    };
    
    const saveData = {
      allocationData,
      worker_allocations,
      isNew,
      job: multiDayJob,
      existingAllocation: isNew ? null : editingMultiDayAllocation
    };
    
    // Preflight → dialog → save
    await saveWithLeavePreflight(saveData, true, null, executeMultiDayAllocationSave);
  };

  const handleConfirmAllocation = async (allocationId) => {
    try {
      await api.post(`/allocations/${allocationId}/confirm`);
      toast.success('Allocation confirmed - operator can now see this job');
      await fetchMultiDayAllocations(multiDayJob.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm allocation');
    }
  };

  const handleRevertToDraft = async (allocationId) => {
    try {
      await api.post(`/allocations/${allocationId}/revert-to-draft`);
      toast.success('Allocation reverted to draft - operator can no longer see this');
      await fetchMultiDayAllocations(multiDayJob.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert allocation');
    }
  };

  const handleCopyAllocation = async (allocationId, leaveOverrideReason = null) => {
    if (!multiDayJob || !multiDayStats?.next_unallocated_date) {
      toast.error('No unallocated dates available');
      return;
    }
    
    try {
      const url = withLeaveOverride(`/allocations/${allocationId}/copy`, leaveOverrideReason);
      await api.post(url, {
        start_date: multiDayStats.next_unallocated_date,
        status: 'draft'
      });
      toast.success('Allocation copied to next available period');
      await fetchMultiDayAllocations(multiDayJob.id);
      fetchData();
    } catch (error) {
      const lb = getLeaveBlock(error);
      if (lb && !leaveOverrideReason) {
        setLeaveOverride({
          open: true,
          blocks: [lb],
          callback: (reason) => handleCopyAllocation(allocationId, reason),
        });
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to copy allocation');
    }
  };

  const handleDeleteMultiDayAllocation = async (allocationId) => {
    setDeleteBlockConfirm(allocationId);
  };

  const confirmDeleteMultiDayAllocation = async () => {
    const allocationId = deleteBlockConfirm;
    setDeleteBlockConfirm(null);
    if (!allocationId) return;

    try {
      await api.delete(`/allocations/${allocationId}`);
      toast.success('Allocation deleted');
      await fetchMultiDayAllocations(multiDayJob.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete allocation');
    }
  };

  // ========== END MULTI-DAY ALLOCATION ==========

  // Customer selection handler
  const handleCustomerSelect = (value) => {
    if (value === '__add_new__') {
      setCustomerDialogOpen(true);
    } else {
      // Check if customer requires PO
      const customer = customers.find(c => c.id === value);
      setSelectedCustomerRequiresPO(customer?.requires_po || false);
      // If the new customer differs from the project's customer, clear the project link
      setJobForm(prev => {
        const clearProject = prev.project_id && value !== prev._project_customer_id;
        return { ...prev, customer_id: value, contact_id: '', site_id: '', ...(clearProject ? { project_id: '', _project_customer_id: '' } : {}) };
      });
      // Fetch contacts for the selected customer
      fetchCustomerContacts(value);
    }
  };

  // Fetch contacts for a customer
  const fetchCustomerContacts = async (customerId) => {
    try {
      const response = await api.get(`/customers/${customerId}/contacts`);
      setCustomerContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      setCustomerContacts([]);
    }
  };

  // Contact selection handler
  const handleContactSelect = (value) => {
    if (value === '__add_new__') {
      setContactForm({ name: '', phone: '', email: '', position: '' });
      setContactDialogOpen(true);
    } else {
      setJobForm(prev => ({ ...prev, contact_id: value }));
    }
  };

  // Create new contact
  const handleCreateContact = async (e) => {
    e.preventDefault();
    try {
      const contactData = {
        ...contactForm,
        customer_id: jobForm.customer_id
      };
      const response = await api.post(`/customers/${jobForm.customer_id}/contacts`, contactData);
      const newContact = response.data;
      toast.success('Contact created');
      setContactDialogOpen(false);
      // Refresh contacts and auto-select the new one
      await fetchCustomerContacts(jobForm.customer_id);
      // Use functional updater so we don't clobber any field the user changed
      // while the network calls were in-flight (e.g. hire_type switched to 'dry').
      setJobForm(prev => ({ ...prev, contact_id: newContact.id }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contact');
    }
  };

  // Site selection handler
  const handleSiteSelect = async (value) => {
    if (value === '__add_new__') {
      setSiteForm({ name: '', address: '', notes: '' });
      setSiteDialogOpen(true);
    } else {
      setJobForm(prev => ({ ...prev, site_id: value, site_contact_ids: [] }));
      // Fetch site contacts for the selected site
      await fetchSiteContacts(value);
    }
  };


  // Project selection handler — auto-fills customer, contacts, site
  const handleProjectSelect = async (projectId) => {
    if (!projectId) {
      // "Clear project" — keep customer and site, just remove the link
      setJobForm(prev => ({ ...prev, project_id: '', _project_customer_id: '' }));
      setProjectPopoverOpen(false);
      return;
    }
    const project = activeProjects.find(p => p.id === projectId);
    if (!project) return;

    // Set project_id + stash project's customer for mismatch detection
    setJobForm(prev => ({
      ...prev,
      project_id: project.id,
      _project_customer_id: project.customer_id,
      customer_id: project.customer_id,
      contact_id: '',
      site_id: project.site_id || '',
      use_one_off_address: false,
    }));

    // Run the same side-effects as handleCustomerSelect
    const customer = customers.find(c => c.id === project.customer_id);
    setSelectedCustomerRequiresPO(customer?.requires_po || false);
    await fetchCustomerContacts(project.customer_id);

    // If the project has a site, run handleSiteSelect logic
    if (project.site_id) {
      await fetchSiteContacts(project.site_id);
    } else {
      setSiteContacts([]);
    }

    setProjectPopoverOpen(false);
  };

  // Quick-add customer: refresh list + auto-select
  const handleCustomerCreated = async (newCustomer) => {
    // Append the new customer to the existing list instead of re-fetching
    setCustomers(prev => [...prev, { id: newCustomer.id, company_name: newCustomer.company_name, requires_po: newCustomer.requires_po }]);
    // Functional updater — avoid clobbering hire_type (or any other field) the
    // user may have changed while the customers list was being re-fetched.
    setJobForm(prev => ({ ...prev, customer_id: newCustomer.id, contact_id: '', site_id: '' }));
    setSelectedCustomerRequiresPO(newCustomer?.requires_po || false);
    setCustomerContacts([]); // New customer has no contacts yet
  };

  // Create new site
  const handleCreateSite = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/sites', { ...siteForm, customer_id: jobForm.customer_id });
      const newSite = response.data;
      toast.success('Site created');
      setSiteDialogOpen(false);
      setSiteForm({ name: '', street_address: '', suburb: '', state: '', postcode: '', notes: '' });
      const sitesRes = await api.get('/sites');
      setSites(sitesRes.data);

      if (siteDialogStopIdx !== null) {
        // Came from haulage stop — populate that stop's address from the new site
        setJobForm(prev => {
          const stops = [...(prev.stops || [])];
          if (stops[siteDialogStopIdx]) {
            stops[siteDialogStopIdx] = {
              ...stops[siteDialogStopIdx],
              site_id: newSite.id,
              address: newSite.street_address || '',
              suburb: newSite.suburb || '',
              state: newSite.state || '',
              postcode: newSite.postcode || '',
            };
          }
          return { ...prev, stops };
        });
        setSiteDialogStopIdx(null);
      } else {
        setJobForm(prev => ({ ...prev, site_id: newSite.id }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create site');
    }
  };

  const getStatusBadge = (status) => {
    // Status colors use CSS variables that change based on theme
    const baseClass = "border";
    switch (status) {
      case 'not_allocated': 
        return <Badge variant="secondary" className={baseClass} style={{ backgroundColor: 'var(--status-not-allocated-bg)', color: 'var(--status-not-allocated-text)', borderColor: 'var(--status-not-allocated-border)' }}>Not Allocated</Badge>;
      case 'partially_allocated': 
        return <Badge variant="secondary" className={baseClass} style={{ backgroundColor: 'var(--status-partially-allocated-bg)', color: 'var(--status-partially-allocated-text)', borderColor: 'var(--status-partially-allocated-border)' }}>Partial</Badge>;
      case 'allocated': 
        return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-allocated-bg)', color: 'var(--status-allocated-text)', borderColor: 'var(--status-allocated-border)' }}>Allocated</Badge>;
      case 'sent':
        return <Badge className={baseClass} style={{ backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#f59e0b' }}>Sent</Badge>;
      case 'confirmed': 
        return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-confirmed-bg)', color: 'var(--status-confirmed-text)', borderColor: 'var(--status-confirmed-border)' }}>Confirmed</Badge>;
      case 'docketed': 
        return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-docketed-bg)', color: 'var(--status-docketed-text)', borderColor: 'var(--status-docketed-border)' }}>Docketed</Badge>;
      case 'invoiced': 
        return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-invoiced-bg)', color: 'var(--status-invoiced-text)', borderColor: 'var(--status-invoiced-border)' }}>Invoiced</Badge>;
      case 'mixed':
        return <Badge className={baseClass} style={{ backgroundColor: '#f59e0b20', color: '#d97706', borderColor: '#f59e0b' }}>Mixed</Badge>;
      case 'tbc':
        return <Badge className={baseClass} style={{ backgroundColor: '#71717a20', color: '#a1a1aa', borderColor: '#71717a' }}>TBC</Badge>;
      case 'cancelled':
        return <Badge className={baseClass} style={{ backgroundColor: '#3f3f4630', color: '#a1a1aa', borderColor: '#52525b' }} data-testid="status-badge-cancelled">Cancelled</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Get status color for calendar/card views
  // Light mode colors use Tailwind status colors, dark mode uses existing colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'not_allocated': return { 
        bg: 'bg-status-not-allocated-bg dark:bg-red-500/20', 
        border: 'border-status-not-allocated-border dark:border-red-500', 
        text: 'text-status-not-allocated-text dark:text-red-400', 
        hover: 'hover:bg-red-200 dark:hover:bg-red-500/30' 
      };
      case 'partially_allocated': return { 
        bg: 'bg-status-partially-allocated-bg dark:bg-orange-500/20', 
        border: 'border-status-partially-allocated-border dark:border-orange-500', 
        text: 'text-status-partially-allocated-text dark:text-orange-400', 
        hover: 'hover:bg-orange-200 dark:hover:bg-orange-500/30' 
      };
      case 'allocated': return { 
        bg: 'bg-status-allocated-bg dark:bg-amber-500/20', 
        border: 'border-status-allocated-border dark:border-amber-500', 
        text: 'text-status-allocated-text dark:text-amber-400', 
        hover: 'hover:bg-amber-200 dark:hover:bg-amber-500/30' 
      };
      case 'confirmed': return { 
        bg: 'bg-status-confirmed-bg dark:bg-blue-500/20', 
        border: 'border-status-confirmed-border dark:border-blue-500', 
        text: 'text-status-confirmed-text dark:text-blue-400', 
        hover: 'hover:bg-blue-200 dark:hover:bg-blue-500/30' 
      };
      case 'docketed': return { 
        bg: 'bg-status-docketed-bg dark:bg-purple-500/20', 
        border: 'border-status-docketed-border dark:border-purple-500', 
        text: 'text-status-docketed-text dark:text-purple-400', 
        hover: 'hover:bg-purple-200 dark:hover:bg-purple-500/30' 
      };
      case 'invoiced': return { 
        bg: 'bg-status-invoiced-bg dark:bg-green-500/20', 
        border: 'border-status-invoiced-border dark:border-green-500', 
        text: 'text-status-invoiced-text dark:text-green-400', 
        hover: 'hover:bg-emerald-200 dark:hover:bg-green-500/30' 
      };
      case 'mixed': return { 
        bg: 'bg-amber-500/10 dark:bg-amber-500/20', 
        border: 'border-amber-500 dark:border-amber-500', 
        text: 'text-amber-600 dark:text-amber-400', 
        hover: 'hover:bg-amber-200 dark:hover:bg-amber-500/30' 
      };
      case 'tbc': return { 
        bg: 'bg-zinc-500/10 dark:bg-zinc-500/20', 
        border: 'border-zinc-400 dark:border-zinc-500', 
        text: 'text-zinc-500 dark:text-zinc-400', 
        hover: 'hover:bg-zinc-200 dark:hover:bg-zinc-500/30' 
      };
      case 'cancelled': return { 
        bg: 'bg-zinc-400/20 dark:bg-zinc-800/60', 
        border: 'border-zinc-400 dark:border-zinc-600', 
        text: 'text-zinc-500 dark:text-zinc-500 line-through', 
        hover: 'hover:bg-zinc-300/30 dark:hover:bg-zinc-800' 
      };
      default: return { bg: 'bg-gray-500/20', border: 'border-gray-500', text: 'text-gray-400', hover: 'hover:bg-gray-500/30' };
    }
  };

  // Get effective status for a job on a specific date
  // For multi-day jobs, check if a docket exists for that date
  const getEffectiveStatus = (job, forDate = null) => {
    if (!job) return 'not_allocated';

    // Cancelled overrides everything — job stays on board, greyed out
    if (job.status === 'cancelled') return 'cancelled';

    // TBC overrides everything — always show as TBC
    if (job.is_tbc || job.status === 'tbc') return 'tbc';
    
    // Docketed/invoiced are workflow-locked — never downgrade to allocation-derived status
    // For single-day jobs, always respect the locked status
    // For multi-day jobs, check per-day dockets first, but fall back to job.status if locked
    const isWorkflowLocked = job.status === 'docketed' || job.status === 'invoiced';
    
    const dateToCheck = forDate || format(currentDate, 'yyyy-MM-dd');
    const isMultiDay = job.end_date && job.end_date !== job.start_date;
    
    // For multi-day jobs, check if a docket exists for this specific date
    if (isMultiDay) {
      const jobDockets = jobDocketsCache[job.id] || [];
      const docketForDate = jobDockets.find(d => d.job_date === dateToCheck);
      
      if (docketForDate) {
        if (docketForDate.status === 'approved' || docketForDate.status === 'exported') {
          if (job.status === 'invoiced') return 'invoiced';
        }
        return 'docketed';
      }
      
      // No docket for this date — if job is workflow-locked, respect that
      if (isWorkflowLocked) return job.status;
      
      // No docket for this date — derive day-specific allocation status
      const dayAlloc = allocations.find(a => 
        a.job_id === job.id && 
        a.start_date <= dateToCheck && 
        (a.end_date || a.start_date) >= dateToCheck
      );
      const dayWorkerAllocs = workerAllocations.filter(wa => 
        wa.job_id === job.id && 
        wa.start_date <= dateToCheck && 
        (wa.end_date || wa.start_date) >= dateToCheck
      );
      
      const hasMainAlloc = dayAlloc && (dayAlloc.equipment_id || (dayAlloc.is_combo_allocation && dayAlloc.combo_equipment_ids?.length));
      const hasOperator = dayAlloc && dayAlloc.operator_id;
      const hasWorkers = dayWorkerAllocs.length > 0;
      
      if (!hasMainAlloc && !hasWorkers) {
        return 'not_allocated';
      }
      if (hasMainAlloc && hasOperator) {
        // Resolve per-day status from allocation's day_statuses
        const dayStatus = (dayAlloc.day_statuses || {})[dateToCheck] || dayAlloc.status || 'draft';
        if (dayStatus === 'confirmed') return 'confirmed';
        if (dayStatus === 'sent') return 'sent';
        return 'allocated';
      }
      return 'partially_allocated';
    }
    
    // Single-day job — check docket cache as well (covers cases where job.status
    // hasn't been updated yet, e.g. mobile submission race condition)
    const singleDayDockets = jobDocketsCache[job.id] || [];
    const singleDayDocket = singleDayDockets.find(d => d.job_date === dateToCheck);
    if (singleDayDocket) {
      if (singleDayDocket.status === 'approved' || singleDayDocket.status === 'exported') {
        if (job.status === 'invoiced') return 'invoiced';
      }
      return 'docketed';
    }

    // Respect workflow-locked statuses
    if (isWorkflowLocked) return job.status;
    
    // Check allocation day status
    const alloc = allocations.find(a => a.job_id === job.id);
    if (alloc) {
      const hasEquip = alloc.equipment_id || (alloc.is_combo_allocation && alloc.combo_equipment_ids?.length);
      const dayStatus = (alloc.day_statuses || {})[job.start_date] || alloc.status || 'draft';
      if (dayStatus === 'confirmed') return 'confirmed';
      if (dayStatus === 'sent' && hasEquip && alloc.operator_id) return 'sent';
      if (hasEquip && alloc.operator_id) return 'allocated';
      return 'partially_allocated';
    }
    
    return job.status;
  };

  // Get all dates for a multi-day job
  const getJobDates = (job) => {
    const excluded = new Set(job.excluded_dates || []);
    if (!job.end_date || job.end_date === job.start_date) {
      return excluded.has(job.start_date) ? [] : [job.start_date];
    }
    
    const dates = [];
    let current = parseISO(job.start_date);
    const end = parseISO(job.end_date);
    
    while (current <= end) {
      const ds = format(current, 'yyyy-MM-dd');
      if (!excluded.has(ds)) {
        dates.push(ds);
      }
      current = addDays(current, 1);
    }
    
    return dates;
  };

  // Get aggregate status for multi-day job (returns 'mixed' if statuses differ)
  const getAggregateStatus = (job) => {
    if (!isMultiDayJob(job)) {
      return job.status;
    }
    
    const dates = getJobDates(job);
    const statuses = dates.map(date => getEffectiveStatus(job, date));
    const uniqueStatuses = [...new Set(statuses)];
    
    if (uniqueStatuses.length === 1) {
      return uniqueStatuses[0];
    }
    
    return 'mixed';
  };

  // Toggle expanded state for a multi-day job
  const toggleJobExpanded = (jobId) => {
    setExpandedMultiDayJobs(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  // Initialize expanded state for multi-day jobs (default to collapsed)
  useEffect(() => {
    const multiDayJobIds = jobs.filter(isMultiDayJob).map(j => j.id);
    const newExpandedState = {};
    multiDayJobIds.forEach(id => {
      if (expandedMultiDayJobs[id] === undefined) {
        newExpandedState[id] = false; // Default to collapsed
      }
    });
    if (Object.keys(newExpandedState).length > 0) {
      setExpandedMultiDayJobs(prev => ({ ...prev, ...newExpandedState }));
    }
  }, [jobs]);

  // Fetch mandatory-forms completion status in bulk for all visible jobs (single call, not per-card)
  useEffect(() => {
    const jobIds = jobs.map(j => j.id).filter(Boolean);
    if (jobIds.length === 0) {
      setMandatoryFormsStatus({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post('/jobs/mandatory-forms-status/bulk', { job_ids: jobIds });
        if (!cancelled) setMandatoryFormsStatus(data || {});
      } catch (e) {
        // Non-blocking: no indicator on failure
      }
    })();
    return () => { cancelled = true; };
  }, [jobs.map(j => j.id).join(',')]);

  // Renders the required-forms clipboard indicator for a job (amber=incomplete, green=all complete, nothing if no mandatory forms)
  const MandatoryFormsBadge = ({ jobId, size = 'sm' }) => {
    const st = mandatoryFormsStatus[jobId];
    if (!st || !st.total) return null;
    const done = st.all_complete;
    const iconCls = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';
    return (
      <span
        className={`inline-flex items-center gap-0.5 rounded px-1 py-0 shrink-0 ${done ? 'bg-green-500/15 text-green-500' : 'bg-amber-500/15 text-amber-500'}`}
        title={done ? 'All required forms complete' : `Required forms: ${st.completed} of ${st.total} complete`}
        data-testid={`job-forms-indicator-${jobId}`}
        onClick={(e) => e.stopPropagation()}
      >
        {done ? <ClipboardCheck className={iconCls} /> : <ClipboardList className={iconCls} />}
        {!done && <span className="text-[9px] font-semibold leading-none">{st.completed}/{st.total}</span>}
      </span>
    );
  };

  // Calendar Job Card (shows any job, with allocation info if available)
  const JobCard = ({ job, forDate }) => {
    if (!job) return null;
    
    // Use the specific date if provided (for week/calendar views), otherwise use currentDate
    const dateForStatus = forDate ? format(forDate, 'yyyy-MM-dd') : null;
    
    // Date-specific allocation lookup for multi-day jobs
    const allocation = (() => {
      if (forDate && isMultiDayJob(job)) {
        const dateStr = format(forDate, 'yyyy-MM-dd');
        return allocations.find(a => a.job_id === job.id && a.start_date <= dateStr && (a.end_date || a.start_date) >= dateStr);
      }
      return getJobAllocation(job.id);
    })();
    const eq = allocation ? getEquipmentInfo(allocation.equipment_id) : null;
    const op = allocation ? getOperatorInfo(allocation.operator_id) : null;
    // Prime Mover (Phase 2.1)
    const jobEqTypeCfg = equipmentTypesWithIds.find(t => t.name === job.equipment_type);
    const isPrimeMoverJob = !!jobEqTypeCfg?.requires_prime_mover;
    const primeMoverTypeNameCard = isPrimeMoverJob
      ? (equipmentTypesWithIds.find(t => t.id === jobEqTypeCfg.prime_mover_asset_type_id)?.name || 'Prime Mover')
      : null;
    const primeMoverEq = (allocation?.prime_mover_equipment_id)
      ? getEquipmentInfo(allocation.prime_mover_equipment_id)
      : null;
    const customer = getCustomerInfo(job.customer_id);
    const attachmentsDisplay = getAllocatedAttachmentsDisplay(job.id);
    const effectiveStatus = getEffectiveStatus(job, dateForStatus);
    const statusColor = getStatusColor(effectiveStatus);
    const isAllocated = !!allocation;

    // Worker allocations for this date
    const dayWorkerAllocs = (() => {
      if (forDate && isMultiDayJob(job)) {
        const dateStr = format(forDate, 'yyyy-MM-dd');
        return workerAllocations.filter(wa => wa.job_id === job.id && wa.start_date <= dateStr && (wa.end_date || wa.start_date) >= dateStr);
      }
      return workerAllocations.filter(wa => wa.job_id === job.id);
    })();

    // Multi-day: compute "Day X of Y"
    const multiDayLabel = (() => {
      if (!isMultiDayJob(job) || !forDate) return null;
      const allDays = getJobDates(job);
      const dayStr = format(forDate, 'yyyy-MM-dd');
      const idx = allDays.indexOf(dayStr);
      if (idx === -1) return null;
      return `Day ${idx + 1} of ${allDays.length}`;
    })();

    return (
      <div 
        className={`p-2 rounded-sm text-xs cursor-pointer border-l-4 ${statusColor.bg} ${statusColor.border} ${statusColor.hover} relative group`}
        onClick={(e) => {
          e.stopPropagation();
          handleOpenJobDialog(job);
        }}
      >
        {/* Delete button - appears on hover */}
        <button
          data-testid={`week-delete-job-${job.id}`}
          className="absolute top-1 right-1 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity bg-red-500/80 hover:bg-red-600 text-white z-10"
          onClick={(e) => {
            e.stopPropagation();
            if (forDate && isMultiDayJob(job)) {
              handleRemoveDayFromJob(job, forDate);
            } else {
              handleDeleteJob(job.id);
            }
          }}
          title={forDate && isMultiDayJob(job) ? 'Remove this day' : 'Delete job'}
        >
          <Trash2 className="h-3 w-3" />
        </button>
        {tenantSettings.depot_time_enabled && job.depot_time && job.depot_time !== job.start_time && (
          <div className="text-[#c9a227] text-[10px] mb-0.5">Depot {formatTime(job.depot_time, tenantSettings.time_format)}</div>
        )}
        {job.start_time && (
          <div className={`font-bold mb-1 ${statusColor.text}`}>
            {tenantSettings.depot_time_enabled && job.depot_time && job.depot_time !== job.start_time ? 'Start ' : ''}{formatTime(job.start_time, tenantSettings.time_format)}
            {job.end_time && (<span className="font-normal text-muted-foreground"> – {formatTime(job.end_time, tenantSettings.time_format)}</span>)}
            {multiDayLabel && <span className="ml-1 font-normal text-muted-foreground text-[10px]">({multiDayLabel})</span>}
          </div>
        )}
        {!job.start_time && multiDayLabel && (
          <div className="text-muted-foreground text-[10px] mb-0.5">{multiDayLabel}</div>
        )}
        {/* Night Shift / TBC / Recurring indicators */}
        {(job.is_night_shift || job.is_tbc || job.recurrence_group_id) && (
          <div className="flex items-center gap-1 mb-0.5">
            {job.is_night_shift && <Moon className="h-3 w-3 text-indigo-400" data-testid={`night-shift-icon-${job.id}`} />}
            {job.is_tbc && <span className="text-[9px] font-semibold text-zinc-400 bg-zinc-500/20 px-1 rounded" data-testid={`tbc-badge-${job.id}`}>TBC</span>}
            {job.recurrence_group_id && <span className="text-[9px] font-semibold text-amber-400 bg-amber-500/15 px-1 rounded" data-testid={`series-badge-${job.id}`}>Series</span>}
          </div>
        )}
        <div className="font-semibold truncate flex items-center gap-1" title={customer?.company_name || 'Unknown'}>
          <span className="truncate">{customer?.company_name || 'Unknown'}</span>
          <MandatoryFormsBadge jobId={job.id} />
        </div>
        {isAllocated ? (
          <>
            <div className="text-muted-foreground truncate">{eq?.equipment_type || 'No equipment'} {eq?.asset_id ? `(${eq.asset_id})` : ''}</div>
            <RigTrailers trailerIds={allocation?.additional_trailer_equipment_ids} getEquipmentInfo={getEquipmentInfo} variant="week" jobId={job.id} />
            <ComboMembers equipmentId={allocation?.equipment_id} getEquipmentInfo={getEquipmentInfo} allocations={allocations} allocation={allocation} variant="week" jobId={job.id} />
            {isPrimeMoverJob && (
              <div className="text-blue-400 truncate" data-testid={`week-prime-mover-${job.id}`}>
                {primeMoverEq
                  ? `${primeMoverEq.equipment_type || primeMoverTypeNameCard} (${primeMoverEq.asset_id})`
                  : `No ${primeMoverTypeNameCard?.toLowerCase() || 'prime mover'}`}
              </div>
            )}
            <div className="text-muted-foreground truncate">{op?.name || 'Unassigned'}</div>
            {attachmentsDisplay && (
              <div className="text-muted-foreground truncate">{attachmentsDisplay}</div>
            )}
            {/* Trailer Load (P3) — cargo summary chip on trailer legs */}
            {(() => {
              const loadEq = allocation?.cargo_equipment_ids || [];
              const loadAtt = allocation?.cargo_attachment_ids || [];
              const total = loadEq.length + loadAtt.length;
              if (total === 0) return null;
              const parts = [];
              loadEq.slice(0, 3).forEach(eid => {
                const e = equipment.find(x => x.id === eid);
                if (e) parts.push(e.asset_id || e.name);
              });
              loadAtt.slice(0, 3).forEach(aid => {
                const a = attachments.find(x => x.id === aid);
                if (a) parts.push(a.name || a.asset_id);
              });
              const overflow = total - parts.length;
              const title = [...loadEq.map(id => {
                const e = equipment.find(x => x.id === id);
                return e ? (e.asset_id || e.name) : id;
              }), ...loadAtt.map(id => {
                const a = attachments.find(x => x.id === id);
                return a ? (a.name || a.asset_id) : id;
              })].join(' · ');
              return (
                <div
                  className="text-amber-300/90 truncate"
                  title={title}
                  data-testid={`week-load-${job.id}`}
                >
                  Load: {parts.join(' · ')}{overflow > 0 ? ` · +${overflow}` : ''}
                </div>
              );
            })()}
            <div 
              className="text-amber-500 truncate cursor-pointer hover:underline"
              data-testid={`week-edit-allocation-${job.id}`}
              onClick={(e) => {
                e.stopPropagation();
                forDate ? handleEditDayAllocation(job, forDate) : handleOpenAllocationDialog(job);
              }}
            >
              Edit Allocation
            </div>
          </>
        ) : (
          <>
            {isPrimeMoverJob && (
              <div className="text-blue-400/70 truncate italic" data-testid={`week-prime-mover-${job.id}`}>
                + {primeMoverTypeNameCard || 'Prime Mover'}
              </div>
            )}
            <div 
              className="text-amber-500 truncate cursor-pointer hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                forDate ? handleEditDayAllocation(job, forDate) : handleOpenAllocationDialog(job);
              }}
            >
              + Allocate
            </div>
          </>
        )}
        {/* Worker allocations – cap to avoid DOM explosion from orphaned data */}
        {dayWorkerAllocs.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {dayWorkerAllocs.slice(0, 10).map((wa, idx) => {
              const workerOp = getOperatorInfo(wa.operator_id);
              const wt = workerTypes.find(w => w.id === wa.worker_type_id);
              const workerColor = wt?.color || '#8B5CF6';
              return (
                <span 
                  key={wa.id || `wa-${idx}`} 
                  className="text-[10px] text-white px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: workerColor }}
                >
                  {workerOp?.name?.split(' ')[0] || wt?.name || 'Worker'}
                </span>
              );
            })}
            {dayWorkerAllocs.length > 10 && (
              <span className="text-[10px] text-amber-400 px-1.5 py-0.5 rounded bg-amber-500/10">
                +{dayWorkerAllocs.length - 10} more
              </span>
            )}
          </div>
        )}
        {/* Unfilled worker requirement pills */}
        {job.worker_requirements?.length > 0 && (() => {
          const unfilled = job.worker_requirements.flatMap((req) => {
            const wt = workerTypes.find(w => w.id === req.worker_type_id);
            const assignedCount = dayWorkerAllocs.filter(wa => wa.worker_type_id === req.worker_type_id).length;
            const unfilledCount = Math.max(0, (req.quantity || 1) - assignedCount);
            return Array.from({ length: unfilledCount }, (_, i) => ({ wt, key: `${req.worker_type_id}-${i}` }));
          });
          if (unfilled.length === 0) return null;
          return (
            <div className="flex flex-wrap gap-1 mt-1">
              {unfilled.map(({ wt, key }) => (
                <span 
                  key={key}
                  className="text-[10px] px-1.5 py-0.5 rounded border-2 border-dashed"
                  style={{ 
                    borderColor: wt?.color ? `${wt.color}60` : '#8B5CF660',
                    color: wt?.color || '#8B5CF6'
                  }}
                >
                  {wt?.name || 'Worker'}
                </span>
              ))}
            </div>
          );
        })()}
      </div>
    );
  };

  // Legacy AllocationCard (kept for compatibility)
  const AllocationCard = ({ alloc, forDate }) => {
    const job = getJobInfo(alloc.job_id);
    const eq = getEquipmentInfo(alloc.equipment_id);
    const op = getOperatorInfo(alloc.operator_id);
    const customer = job ? getCustomerInfo(job.customer_id) : null;
    const attachmentsDisplay = getAllocatedAttachmentsDisplay(alloc.job_id);
    
    // Use the specific date if provided
    const dateForStatus = forDate ? format(forDate, 'yyyy-MM-dd') : null;
    const effectiveStatus = job ? getEffectiveStatus(job, dateForStatus) : 'allocated';
    const statusColor = getStatusColor(effectiveStatus);

    return (
      <div 
        className={`allocation-block ${statusColor.bg} border-l-4 ${statusColor.border} p-2 rounded-sm text-xs cursor-pointer ${statusColor.hover}`}
        onClick={() => job && handleOpenJobDialog(job)}
      >
        {tenantSettings.depot_time_enabled && job?.depot_time && job.depot_time !== job.start_time && (
          <div className="text-[#c9a227] text-[10px] mb-0.5">Depot {formatTime(job.depot_time, tenantSettings.time_format)}</div>
        )}
        {job?.start_time && (
          <div className={`${statusColor.text} font-bold mb-1`}>{tenantSettings.depot_time_enabled && job.depot_time && job.depot_time !== job.start_time ? 'Start ' : ''}{formatTime(job.start_time, tenantSettings.time_format)}{job.end_time && (<span className="font-normal text-muted-foreground"> – {formatTime(job.end_time, tenantSettings.time_format)}</span>)}</div>
        )}
        {/* Night Shift / TBC / Recurring indicators */}
        {job && (job.is_night_shift || job.is_tbc || job.recurrence_group_id) && (
          <div className="flex items-center gap-1 mb-0.5">
            {job.is_night_shift && <Moon className="h-3 w-3 text-indigo-400" />}
            {job.is_tbc && <span className="text-[9px] font-semibold text-zinc-400 bg-zinc-500/20 px-1 rounded">TBC</span>}
            {job.recurrence_group_id && <span className="text-[9px] font-semibold text-amber-400 bg-amber-500/15 px-1 rounded">Series</span>}
          </div>
        )}
        <div className="font-semibold truncate">{customer?.company_name || 'Unknown'}</div>
        <div className="text-muted-foreground truncate">{eq?.equipment_type || 'No equipment'} {eq?.asset_id ? `(${eq.asset_id})` : ''}</div>
        <div className="text-muted-foreground truncate">{op?.name || 'Unassigned'}</div>
        {attachmentsDisplay && (
          <div className="text-muted-foreground truncate">{attachmentsDisplay}</div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="schedule-page">
      {/* Sticky header section */}
      <div className="shrink-0 space-y-4 pb-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Jobs & Schedule</h1>
          <p className="text-muted-foreground">Manage jobs and allocate resources</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleRefresh} 
            disabled={refreshing}
            data-testid="refresh-btn"
            title="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate(`/planning-board?date=${format(currentDate, 'yyyy-MM-dd')}`)}
            data-testid="planning-board-btn"
            title="Open Planning Board"
          >
            <Kanban className="h-4 w-4 mr-2" />
            Planning Board
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate(`/utilisation?year=${currentDate.getFullYear()}&month=${currentDate.getMonth() + 1}`)}
            data-testid="utilisation-btn"
            title="Open Utilisation View"
          >
            <Truck className="h-4 w-4 mr-2" />
            Utilisation
          </Button>
          {isFeatureEnabled('quick_reserve') && (
            <Button variant="outline" onClick={() => setScheduleReserveOpen(true)} data-testid="schedule-quick-reserve-btn">
              <CalendarClock className="h-4 w-4 mr-2" />
              Quick Reserve
            </Button>
          )}
          <Button className="btn-volt" onClick={() => view === 'towercrane' ? navigate('/schedule/tower-crane/new') : handleOpenJobDialog()} data-testid="new-job-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Job
          </Button>
        </div>
      </div>
      {isFeatureEnabled('quick_reserve') && (
        <QuickReserveDialog
          open={scheduleReserveOpen}
          onOpenChange={setScheduleReserveOpen}
          api={api}
          presetDate={format(currentDate, 'yyyy-MM-dd')}
          onCreated={() => { toast.success('Reservation added'); }}
        />
      )}

      {/* View Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="list" data-testid="view-list-btn">
              <List className="h-4 w-4 mr-1" />
              List
            </TabsTrigger>
            <TabsTrigger value="week" data-testid="view-week-btn">
              <LayoutGrid className="h-4 w-4 mr-1" />
              Week
            </TabsTrigger>
            <TabsTrigger value="board" data-testid="view-board-btn">
              <Kanban className="h-4 w-4 mr-1" />
              Status
            </TabsTrigger>
            <TabsTrigger value="dryhire" data-testid="view-dryhire-btn">
              <CalendarDays className="h-4 w-4 mr-1" />
              Dry Hire
            </TabsTrigger>
            {isCraneHire && (
              <TabsTrigger value="towercrane" data-testid="view-towercrane-btn">
                <Package className="h-4 w-4 mr-1" />
                Tower Cranes
              </TabsTrigger>
            )}
            {isFeatureEnabled('schedule_folders') && (
              <TabsTrigger value="folders" data-testid="view-folders-btn">
                <Folder className="h-4 w-4 mr-1" />
                Folders
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        {/* Stale data notification banner */}
        {staleDataBanner && (
          <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-2.5 text-sm" data-testid="stale-data-banner">
            <RefreshCw className="h-4 w-4 text-amber-500 shrink-0" />
            <span className="flex-1 text-amber-700 dark:text-amber-400">
              Schedule updated by another user: {staleDataBanner}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-amber-500/40 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10"
              onClick={() => { fetchData(); }}
              data-testid="stale-data-refresh-btn"
            >
              <RefreshCw className="h-3 w-3 mr-1" /> Refresh Now
            </Button>
            <button
              className="text-amber-500/60 hover:text-amber-500 p-0.5"
              onClick={() => setStaleDataBanner(null)}
              data-testid="stale-data-dismiss-btn"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {(view === 'week' || view === 'board' || view === 'list' || view === 'folders') && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => {
              if (view === 'week') {
                navigateWeek(-1);
              } else if (view === 'list') {
                // In list view, navigate by day and set custom date filter to show that day
                const newDate = addDays(currentDate, -1);
                setCurrentDate(newDate);
                setQuickDateFilter('all'); // Disable quick filter
                setDateFilterEnabled(true); // Enable custom date range
                const dateStr = format(newDate, 'yyyy-MM-dd');
                setDateFrom(dateStr);
                setDateTo(dateStr);
              } else {
                navigateDay(-1);
              }
            }}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="font-heading font-semibold min-w-[200px] text-center">
              {view === 'week' 
                ? `${formatDateShort(weekDays[0])} - ${formatDateShortWithYear(weekDays[6])}`
                : formatDateLong(currentDate)
              }
            </div>
            <Button variant="outline" size="sm" onClick={() => {
              if (view === 'week') {
                navigateWeek(1);
              } else if (view === 'list') {
                // In list view, navigate by day and set custom date filter to show that day
                const newDate = addDays(currentDate, 1);
                setCurrentDate(newDate);
                setQuickDateFilter('all'); // Disable quick filter
                setDateFilterEnabled(true); // Enable custom date range
                const dateStr = format(newDate, 'yyyy-MM-dd');
                setDateFrom(dateStr);
                setDateTo(dateStr);
              } else {
                navigateDay(1);
              }
            }}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => {
              setCurrentDate(new Date());
              if (view === 'list') {
                // In list view, set quick filter to today
                setQuickDateFilter('today');
                setDateFilterEnabled(false);
              }
            }}>
              Today
            </Button>
          </div>
        )}
      </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto min-h-0">

      {/* LIST VIEW */}
      {view === 'list' && (
        <Card className="card-industrial">
          <CardHeader className="pb-3">
            <ListViewHeader
              count={filteredJobs.length}
              countLabel="Jobs"
              showRefresh={false}
              
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search jobs..."
              searchTestId="job-search"
              
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              statusTestId="job-status-filter"
              statusOptions={[
                { value: 'all', label: 'All Status' },
                { value: 'not_allocated', label: 'Not Allocated' },
                { value: 'allocated', label: 'Allocated' },
                { value: 'sent', label: 'Sent to Worker' },
                { value: 'confirmed', label: 'Confirmed' },
                { value: 'docketed', label: 'Docketed' },
                { value: 'invoiced', label: 'Invoiced' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
              
              showSecondaryFilter={true}
              secondaryFilter={assetTypeFilter}
              onSecondaryFilterChange={setAssetTypeFilter}
              secondaryPlaceholder="All Asset Types"
              secondaryTestId="asset-type-filter"
              secondaryFilterOptions={[
                { value: 'all', label: 'All Asset Types' },
                ...visibleAssetTypes.map(et => ({ value: et, label: et }))
              ]}
              
              dateFilter={quickDateFilter}
              onDateFilterChange={(val) => {
                setQuickDateFilter(val);
                setAssetTypeFilter([]);
                setDateFilterEnabled(false);
                // Update the header date to match the quick filter
                const today = new Date();
                switch (val) {
                  case 'yesterday': setCurrentDate(subDays(today, 1)); break;
                  case 'today': setCurrentDate(today); break;
                  case 'tomorrow': setCurrentDate(addDays(today, 1)); break;
                  case 'this_week': setCurrentDate(today); break;
                  case 'last_week': setCurrentDate(subDays(today, 7)); break;
                  case 'next_week': setCurrentDate(addDays(today, 7)); break;
                  default: break;
                }
              }}
              dateFilterOptions={[
                { value: 'yesterday', label: 'Yesterday' },
                { value: 'today', label: 'Today' },
                { value: 'tomorrow', label: 'Tomorrow' },
                { value: 'last_week', label: 'Last Week' },
                { value: 'this_week', label: 'This Week' },
                { value: 'next_week', label: 'Next Week' },
              ]}
              
              showCustomDateRange={true}
              customDateEnabled={dateFilterEnabled}
              onCustomDateToggle={(enabled) => {
                setDateFilterEnabled(enabled);
                if (enabled) setQuickDateFilter('all');
              }}
              dateFrom={dateFrom}
              onDateFromChange={setDateFrom}
              dateTo={dateTo}
              onDateToChange={setDateTo}
              formatDateDisplay={formatDateDisplay}
            >
              {/* Active filter indicators */}
              {(statusFilter !== 'all' || assetTypeFilter.length > 0) && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">Active filters:</span>
                  {statusFilter !== 'all' && (
                    <Badge variant="secondary" className="text-xs gap-1">
                      Status: {statusFilter.replace('_', ' ')}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => setStatusFilter('all')} />
                    </Badge>
                  )}
                  {assetTypeFilter.map(type => (
                    <Badge key={type} variant="secondary" className="text-xs gap-1">
                      {type}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => setAssetTypeFilter(assetTypeFilter.filter(t => t !== type))} />
                    </Badge>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => { setStatusFilter('all'); setAssetTypeFilter([]); }}
                    data-testid="clear-all-filters"
                  >
                    Clear all
                  </Button>
                </div>
              )}
            </ListViewHeader>
          </CardHeader>
          <CardContent>
            {filteredJobs.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No jobs found</p>
                <Button className="mt-4 btn-volt" onClick={() => handleOpenJobDialog()}>
                  CREATE JOB
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="flex items-center gap-2 mb-3" data-testid="group-by-bar">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Group by</span>
                  <Select value={groupBy} onValueChange={handleGroupByChange}>
                    <SelectTrigger className="w-[180px] h-8" data-testid="group-by-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="asset">Asset</SelectItem>
                      <SelectItem value="asset_type">Asset Type</SelectItem>
                      <SelectItem value="site">Site / Address</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="ml-auto flex items-center gap-1 rounded-md border border-border p-0.5" data-testid="list-density-toggle">
                    <span className="px-1.5 text-xs text-muted-foreground hidden sm:inline">Density</span>
                    {[
                      { key: 'comfortable', label: 'Comfortable' },
                      { key: 'cozy', label: 'Cozy' },
                      { key: 'compact', label: 'Compact' },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setDensity(opt.key)}
                        data-testid={`list-density-${opt.key}`}
                        className={`px-2 py-1 text-xs rounded transition-colors ${
                          density === opt.key
                            ? 'bg-[#ffd108] text-black font-semibold'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className={density === 'compact' ? 'sch-list-compact' : density === 'cozy' ? 'sch-list-cozy' : ''}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead sortKey="job_number" requestSort={requestScheduleSort} getSortDirection={getScheduleSortDirection} className="label-overline">Job #</SortableTableHead>
                      <SortableTableHead sortKey="date" requestSort={requestScheduleSort} getSortDirection={getScheduleSortDirection} className="label-overline">Date</SortableTableHead>
                      <SortableTableHead sortKey="customer" requestSort={requestScheduleSort} getSortDirection={getScheduleSortDirection} className="label-overline">Customer</SortableTableHead>
                      <SortableTableHead sortKey="type" requestSort={requestScheduleSort} getSortDirection={getScheduleSortDirection} className="label-overline">Type</SortableTableHead>
                      <SortableTableHead sortKey="site" requestSort={requestScheduleSort} getSortDirection={getScheduleSortDirection} className="label-overline">Site</SortableTableHead>
                      <SortableTableHead sortKey="description" requestSort={requestScheduleSort} getSortDirection={getScheduleSortDirection} className="label-overline">Description</SortableTableHead>
                      <TableHead className="label-overline">Allocation</TableHead>
                      <SortableTableHead sortKey="status" requestSort={requestScheduleSort} getSortDirection={getScheduleSortDirection} className="label-overline">Status</SortableTableHead>
                      <TableHead className="label-overline text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayItems.map((item) => {
                      if (item.__group) {
                        const collapsed = !!collapsedGroups[item.key];
                        return (
                          <TableRow key={`group-${item.key}`} className="bg-muted/50 hover:bg-muted/50 border-t-2 border-[#ffd108]/30" data-testid={`job-group-${item.key}`}>
                            <TableCell colSpan={9} className="py-2">
                              <button
                                type="button"
                                className="flex items-center gap-2 font-semibold text-sm"
                                onClick={() => setCollapsedGroups(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                                data-testid={`job-group-toggle-${item.key}`}
                              >
                                {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                <span>{item.label || 'Ungrouped'}</span>
                                <Badge variant="secondary" className="text-xs">{item.count} {item.count === 1 ? 'job' : 'jobs'}</Badge>
                                {groupBy === 'asset' && item.bookedDays > 0 && (
                                  <Badge variant="outline" className="text-xs border-[#ffd108]/40 text-[#ffd108]" data-testid={`job-group-util-${item.key}`}>
                                    {item.bookedDays} {item.bookedDays === 1 ? 'day' : 'days'} booked
                                  </Badge>
                                )}
                              </button>
                            </TableCell>
                          </TableRow>
                        );
                      }
                      const job = item;
                      const customer = getCustomerInfo(job.customer_id);
                      const location = getJobLocation(job);
                      const allocation = getJobAllocation(job.id);
                      const allocEquipment = allocation ? getEquipmentInfo(allocation.equipment_id) : null;
                      const allocOperator = allocation ? getOperatorInfo(allocation.operator_id) : null;
                      const isMultiDay = isMultiDayJob(job);
                      const isExpanded = expandedMultiDayJobs[job.id] === true; // Default to collapsed
                      
                      // Get the current view's date range to filter child rows
                      const viewDateRange = dateFilterEnabled
                        ? { from: dateFrom, to: dateTo }
                        : getQuickFilterDateRange();
                      const allJobDates = isMultiDay ? getJobDates(job) : [job.start_date];
                      
                      // Filter job dates to only show those within the current view's date range
                      const jobDates = allJobDates.filter(dateStr => {
                        if (!viewDateRange.from || !viewDateRange.to) return true;
                        return dateStr >= viewDateRange.from && dateStr <= viewDateRange.to;
                      });
                      
                      // Determine if we're in a single-day view (Today / Yesterday / Tomorrow
                      // quick filters, OR a custom date range where from === to — e.g. the
                      // user navigated day-by-day using the < > arrows).
                      const isSingleDayView = (
                        quickDateFilter === 'today' ||
                        quickDateFilter === 'yesterday' ||
                        quickDateFilter === 'tomorrow' ||
                        (dateFilterEnabled && dateFrom && dateTo && dateFrom === dateTo)
                      );
                      
                      // For multi-day jobs in multi-day views, render parent row + child rows
                      // For single-day views, show multi-day jobs as single rows with that day's status
                      if (isMultiDay && !isSingleDayView) {
                        const aggregateStatus = getAggregateStatus(job);
                        return (
                          <React.Fragment key={job.id}>
                            {/* Parent Row */}
                            <TableRow 
                              data-testid={`job-row-${job.id}`}
                              className="cursor-pointer hover:bg-muted/50 bg-muted/30"
                            >
                              <TableCell className="font-mono text-sm font-bold text-[#ffd108]">
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleJobExpanded(job.id); }}
                                    className="p-0.5 hover:bg-muted rounded"
                                  >
                                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  </button>
                                  {formatJobNumber(job)}
                                  {job.parent_job_id && <Link2 className="h-3 w-3 text-[#ffd108]" title={`Linked to parent #${job.parent_job_number || '?'}`} />}
                                  {job.hire_type === 'dry' && (
                                    <Badge
                                      className="text-[9px] px-1 py-0 h-4 bg-amber-500 text-white border-amber-500 hover:bg-amber-500"
                                      title="Dry-hire — asset only"
                                    >
                                      DRY
                                    </Badge>
                                  )}
                                  {job.is_night_shift && <Moon className="h-3.5 w-3.5 text-indigo-400" title="Night Shift" />}
                                  {job.is_tbc && <span className="text-[9px] font-semibold text-zinc-400 bg-zinc-500/20 px-1 rounded">TBC</span>}
                                  {job.recurrence_group_id && <span className="text-[9px] font-semibold text-amber-400 bg-amber-500/15 px-1 rounded" title="Recurring series">Series</span>}
                                </div>
                              </TableCell>
                              <TableCell className="font-mono text-sm">
                                <div>{formatDateDisplay(allJobDates[0] || job.start_date)} - {formatDateDisplay(allJobDates[allJobDates.length - 1] || job.end_date)}</div>
                                {tenantSettings.depot_time_enabled && job.depot_time && job.depot_time !== job.start_time && (
                                  <div className="text-[#c9a227] text-xs">Depot {formatTime(job.depot_time, tenantSettings.time_format)}</div>
                                )}
                                {job.start_time && (
                                  <div className="text-[#ffd108]">{tenantSettings.depot_time_enabled && job.depot_time && job.depot_time !== job.start_time ? 'Start ' : ''}{formatTime(job.start_time, tenantSettings.time_format)}</div>
                                )}
                                <div className="text-muted-foreground text-xs sch-date-meta">{jobDates.length} days</div>
                                {job.tags?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1 sch-date-meta">
                                    {job.tags.map((tag, tIdx) => {
                                      const tagName = typeof tag === 'string' ? tag : (tag?.name || String(tag));
                                      return (
                                        <span key={tIdx} className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">{tagName}</span>
                                      );
                                    })}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="font-medium" onClick={() => handleOpenJobDialog(job)}>
                                {customer?.company_name || 'Unknown'}
                                {job.po_number && (
                                  <div className="text-xs text-muted-foreground font-normal" data-testid={`job-po-${job.id}`}>PO: {job.po_number}</div>
                                )}
                              </TableCell>
                              <TableCell className="text-sm" onClick={() => handleOpenJobDialog(job)}>
                                {job.equipment_type || '-'}
                              </TableCell>
                              <TableCell onClick={() => handleOpenJobDialog(job)}>
                                {location.name !== 'No Location' ? (
                                  <span className="flex items-center gap-1 text-muted-foreground text-sm" data-testid={`job-location-${job.id}`}>
                                    {location.isMultiStop ? <Navigation className="h-3 w-3 text-[#ffd108] shrink-0" /> : <MapPin className="h-3 w-3" />}
                                    {location.isMultiStop ? (
                                      <span className="flex items-center gap-1">
                                        <span className="truncate max-w-[180px]" title={location.address}>{location.name}</span>
                                        {location.stopCount > 0 && (
                                          <Badge variant="outline" className="text-[10px] px-1 py-0 border-[#ffd108]/40 text-[#ffd108]">
                                            {location.stopCount} {location.stopCount === 1 ? 'stop' : 'stops'}
                                          </Badge>
                                        )}
                                      </span>
                                    ) : location.isOneOff ? (
                                      <span className="truncate max-w-[180px]" title={location.address}>
                                        {location.address || 'One-off Location'}
                                      </span>
                                    ) : (
                                      location.name
                                    )}
                                  </span>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="max-w-[150px] truncate" onClick={() => handleOpenJobDialog(job)}>
                                {job.description}
                              </TableCell>
                              <TableCell>
                                <span className="text-muted-foreground text-xs italic">See daily breakdown below</span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {getStatusBadge(aggregateStatus)}
                                  {job.status === 'cancelled' && (
                                    job.cancellation_email_sent ? (
                                      <span className="inline-flex items-center gap-1 text-xs text-green-500" title={`Cancellation email sent${job.cancellation_reason ? ` · ${job.cancellation_reason}` : ''}`} data-testid={`cancel-email-sent-${job.id}`}>
                                        <MailCheck className="h-3.5 w-3.5" /> Email sent
                                      </span>
                                    ) : (
                                      <button
                                        className="inline-flex items-center gap-1 text-xs text-amber-500 hover:underline"
                                        title="Cancellation email not sent — click to send"
                                        onClick={(e) => { e.stopPropagation(); handleSendCancellationEmail(job); }}
                                        data-testid={`cancel-email-send-${job.id}`}
                                      >
                                        <MailX className="h-3.5 w-3.5" /> Email not sent
                                      </button>
                                    )
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenJobDialog(job); }}>
                                      <Pencil className="h-4 w-4 mr-2" />
                                      Edit Job
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="group" onClick={(e) => { e.stopPropagation(); handleOpenAllocationDialog(job); }}>
                                      <Truck className="h-4 w-4 mr-2 text-[#ffd108] group-focus:text-black" />
                                      {allocation ? 'Edit Allocation' : 'Allocate Job'}
                                    </DropdownMenuItem>
                                    {(tenantSettings.worker_job_email_enabled || workerAckEnabled || notifyOperatorEnabled) && (
                                      <DropdownMenuItem
                                        className="group"
                                        onClick={(e) => { e.stopPropagation(); setEmailJob(job); }}
                                        data-testid={`notify-workers-${job.id}`}
                                        disabled={job.is_tbc}
                                        title={job.is_tbc ? 'Cannot notify workers on TBC jobs' : undefined}
                                      >
                                        <Bell className="h-4 w-4 mr-2 text-[#ffd108] group-focus:text-black" />
                                        Notify Workers
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewJobDockets(job); }}>
                                      <Eye className="h-4 w-4 mr-2 text-purple-500" />
                                      View Dockets
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setConfirmJobId(job.id); }} data-testid={`send-confirmation-${job.id}`}>
                                      <Mail className="h-4 w-4 mr-2 text-green-500" />
                                      Send Customer Confirmation
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicateJob(job); }}>
                                      <Copy className="h-4 w-4 mr-2" />
                                      Duplicate Job
                                    </DropdownMenuItem>
                                    {compliancePacksEnabled && (
                                      <DropdownMenuItem
                                        className="group"
                                        onClick={(e) => { e.stopPropagation(); setCompliancePackJob(job); }}
                                        data-testid={`compliance-pack-${job.id}`}
                                      >
                                        <ShieldCheck className="h-4 w-4 mr-2 text-[#ffd108] group-focus:text-black" />
                                        Compliance Pack
                                      </DropdownMenuItem>
                                    )}
                                    {job.excluded_dates?.length > 0 && (
                                      <>
                                        <DropdownMenuSeparator />
                                        {job.excluded_dates.map(d => (
                                          <DropdownMenuItem
                                            key={d}
                                            onClick={(e) => { e.stopPropagation(); handleReaddDate(job.id, d); }}
                                            data-testid={`readd-date-menu-${d}`}
                                          >
                                            <Plus className="h-4 w-4 mr-2 text-green-500" />
                                            Re-add {formatDateDisplay(d)}
                                          </DropdownMenuItem>
                                        ))}
                                      </>
                                    )}
                                    <DropdownMenuSeparator />
                                    {job.status === 'cancelled' ? (
                                      <DropdownMenuItem
                                        onClick={(e) => { e.stopPropagation(); handleUncancelJob(job); }}
                                        data-testid={`uncancel-job-${job.id}`}
                                      >
                                        <RotateCcw className="h-4 w-4 mr-2 text-blue-500" />
                                        Un-cancel Job
                                      </DropdownMenuItem>
                                    ) : (
                                      !['docketed', 'invoiced'].includes(job.status) && (
                                        <DropdownMenuItem
                                          onClick={(e) => { e.stopPropagation(); handleOpenCancelJob(job); }}
                                          data-testid={`cancel-job-${job.id}`}
                                        >
                                          <Ban className="h-4 w-4 mr-2 text-zinc-500" />
                                          Cancel Job
                                        </DropdownMenuItem>
                                      )
                                    )}
                                    <DropdownMenuItem
                                      onClick={(e) => { e.stopPropagation(); handleDeleteJob(job.id); }}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete Job
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                            
                            {/* Child Rows - Individual Days */}
                            {isExpanded && jobDates.map((dateStr) => {
                              const dayStatus = getEffectiveStatus(job, dateStr);
                              const dayDocket = (jobDocketsCache[job.id] || []).find(d => d.job_date === dateStr);
                              
                              // Calculate the actual day number from the job start
                              const actualDayNumber = allJobDates.indexOf(dateStr) + 1;
                              
                              // Get allocation for this specific date
                              const dayAllocation = allocations.find(a => 
                                a.job_id === job.id && 
                                a.start_date <= dateStr && 
                                (a.end_date || a.start_date) >= dateStr
                              );
                              const dayEquipment = dayAllocation ? (
                                getEquipmentInfo(dayAllocation.equipment_id) || (
                                  dayAllocation.is_combo_allocation && dayAllocation.combo_equipment_ids?.length
                                    ? getEquipmentInfo(dayAllocation.combo_equipment_ids[0])
                                    : null
                                )
                              ) : null;
                              const dayOperator = dayAllocation ? getOperatorInfo(dayAllocation.operator_id) : null;
                              // --- Prime Mover (Phase 2.1) ---
                              const dayJobEqType = equipmentTypesWithIds.find(t => t.name === job.equipment_type);
                              const dayIsPrimeMover = !!dayJobEqType?.requires_prime_mover;
                              const dayPrimeMoverName = dayIsPrimeMover
                                ? (equipmentTypesWithIds.find(t => t.id === dayJobEqType.prime_mover_asset_type_id)?.name || 'Prime Mover')
                                : null;
                              const dayPrimeMover = (dayAllocation?.prime_mover_equipment_id)
                                ? getEquipmentInfo(dayAllocation.prime_mover_equipment_id)
                                : null;
                              
                              // Get worker allocations for this specific date
                              const dayWorkerAllocs = workerAllocations.filter(wa => 
                                wa.job_id === job.id && 
                                wa.start_date <= dateStr && 
                                (wa.end_date || wa.start_date) >= dateStr
                              );
                              
                              // Get day work description for this date
                              const dayDetails = jobDayDetails[job.id]?.[dateStr];
                              const dayWorkDesc = dayDetails?.work_description;
                              
                              return (
                                <TableRow 
                                  key={`${job.id}-${dateStr}`}
                                  data-testid={`job-row-${job.id}-day-${actualDayNumber}`}
                                  className="cursor-pointer hover:bg-muted/50 bg-background"
                                  onClick={() => handleOpenJobDialog(job, dateStr)}
                                >
                                  <TableCell className="font-mono text-sm text-muted-foreground pl-10">
                                    Day {actualDayNumber}
                                  </TableCell>
                                  <TableCell className="font-mono text-sm">
                                    {formatDateDisplay(dateStr)}
                                    {(dayDetails?.start_time || dayDetails?.end_time) && (
                                      <span
                                        className="mt-0.5 flex items-center gap-1 text-[11px] text-amber-600"
                                        title="Custom time for this day (overrides the job default)"
                                        data-testid={`day-time-override-${job.id}-${dateStr}`}
                                      >
                                        <Clock className="h-3 w-3" />
                                        {dayDetails?.start_time || job.start_time || '—'}
                                        {(dayDetails?.end_time || job.end_time) ? `–${dayDetails?.end_time || job.end_time}` : ''}
                                      </span>
                                    )}
                                  </TableCell>
                                  {/* Spans Customer, Type, Site columns */}
                                  <TableCell className="text-muted-foreground text-sm" colSpan={3}>
                                    {dayDocket ? (
                                      <span className="flex items-center gap-2">
                                        <FileText className="h-3 w-3 text-purple-500" />
                                        Docket #{dayDocket.docket_number}
                                      </span>
                                    ) : (
                                      <span className="text-xs">No docket</span>
                                    )}
                                  </TableCell>
                                  {/* Day Work Description - in Description column */}
                                  <TableCell className="max-w-[180px]">
                                    {dayWorkDesc ? (
                                      <p className="text-xs text-amber-600 truncate" title={dayWorkDesc}>
                                        {dayWorkDesc}
                                      </p>
                                    ) : (
                                      <span className="text-muted-foreground/40 text-xs italic">-</span>
                                    )}
                                  </TableCell>
                                  {/* Allocation for this day */}
                                  <TableCell>
                                    {dayAllocation ? (
                                      <div className="text-xs space-y-1">
                                        {dayEquipment && (
                                          <div className="flex items-center gap-1">
                                            <Truck className="h-3 w-3 text-[#ffd108]" />
                                            <span className="truncate max-w-[120px]">{dayEquipment.asset_id || dayEquipment.name}</span>
                                          </div>
                                        )}
                                        <RigTrailers trailerIds={dayAllocation?.additional_trailer_equipment_ids} getEquipmentInfo={getEquipmentInfo} variant="list" jobId={`${job.id}-${dateStr}`} />
                                        <ComboMembers equipmentId={dayAllocation?.equipment_id} getEquipmentInfo={getEquipmentInfo} allocations={allocations} allocation={dayAllocation} variant="list" jobId={`${job.id}-${dateStr}`} />
                                        {dayIsPrimeMover && (
                                          <div className="flex items-center gap-1" data-testid={`prime-mover-cell-day-${job.id}-${dateStr}`}>
                                            {dayPrimeMover ? (
                                              <>
                                                <Truck className="h-3 w-3 text-blue-500" />
                                                <span className="truncate max-w-[120px]">{dayPrimeMover.asset_id || dayPrimeMover.name}</span>
                                              </>
                                            ) : (
                                              <>
                                                <div className="w-4 h-4 rounded-full border-2 border-dashed border-blue-500/40 flex items-center justify-center">
                                                  <Truck className="h-2.5 w-2.5 text-blue-500/40" />
                                                </div>
                                                <span className="text-muted-foreground/50 italic text-[10px]">No {dayPrimeMoverName?.toLowerCase() || 'prime mover'}</span>
                                              </>
                                            )}
                                          </div>
                                        )}
                                        {dayOperator && (
                                          <div className="flex items-center gap-1">
                                            <Users className="h-3 w-3 text-[#ffd108]" />
                                            <span>{dayOperator.name}</span>
                                          </div>
                                        )}
                                        {/* Trailer Load summary — assets/attachments riding on this trailer */}
                                        {(() => {
                                          const loadEq = dayAllocation?.cargo_equipment_ids || [];
                                          const loadAtt = dayAllocation?.cargo_attachment_ids || [];
                                          const total = loadEq.length + loadAtt.length;
                                          if (total === 0) return null;
                                          const parts = [];
                                          loadEq.slice(0, 2).forEach(eid => {
                                            const e = equipment.find(x => x.id === eid);
                                            if (e) parts.push(e.asset_id || e.name);
                                          });
                                          loadAtt.slice(0, 2).forEach(aid => {
                                            const a = attachments.find(x => x.id === aid);
                                            if (a) parts.push(a.name || a.asset_id);
                                          });
                                          const overflow = total - parts.length;
                                          const title = [
                                            ...loadEq.map(id => {
                                              const e = equipment.find(x => x.id === id);
                                              return e ? (e.asset_id || e.name) : id;
                                            }),
                                            ...loadAtt.map(id => {
                                              const a = attachments.find(x => x.id === id);
                                              return a ? (a.name || a.asset_id) : id;
                                            }),
                                          ].join(' · ');
                                          return (
                                            <div
                                              className="flex items-start gap-1 text-amber-300/90"
                                              title={title}
                                              data-testid={`list-load-day-${job.id}-${dateStr}`}
                                            >
                                              <Truck className="h-3 w-3 mt-[1px] shrink-0" />
                                              <span className="truncate max-w-[140px]">
                                                Load: {parts.join(' · ')}{overflow > 0 ? ` · +${overflow}` : ''}
                                              </span>
                                            </div>
                                          );
                                        })()}
                                        {/* Workers for this day + empty pills for unfilled requirements */}
                                        {(dayWorkerAllocs.length > 0 || (job.worker_requirements && job.worker_requirements.length > 0)) && (
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            {dayWorkerAllocs.slice(0, 10).map((wa, idx) => {
                                              const op = getOperatorInfo(wa.operator_id);
                                              const wt = workerTypes.find(w => w.id === wa.worker_type_id);
                                              const workerColor = wt?.color || '#8B5CF6';
                                              return (
                                                <span 
                                                  key={`assigned-${idx}`} 
                                                  className="text-[10px] text-white px-1.5 py-0.5 rounded"
                                                  style={{ backgroundColor: workerColor }}
                                                >
                                                  {op?.name?.split(' ')[0] || 'Unknown'}
                                                </span>
                                              );
                                            })}
                                            {dayWorkerAllocs.length > 10 && (
                                              <span className="text-[10px] text-amber-400 px-1.5 py-0.5 rounded bg-amber-500/10">
                                                +{dayWorkerAllocs.length - 10}
                                              </span>
                                            )}
                                            {/* Empty pills for unfilled requirements */}
                                            {job.worker_requirements && job.worker_requirements.map((req, rIdx) => {
                                              const wt = workerTypes.find(w => w.id === req.worker_type_id);
                                              const assignedCount = dayWorkerAllocs.filter(wa => wa.worker_type_id === req.worker_type_id).length;
                                              const unfilledCount = (req.quantity || 1) - assignedCount;
                                              return Array.from({ length: Math.max(0, unfilledCount) }).map((_, i) => (
                                                <span 
                                                  key={`unfilled-${rIdx}-${i}`} 
                                                  className="text-[10px] px-1.5 py-0.5 rounded border-2 border-dashed"
                                                  style={{ 
                                                    borderColor: wt?.color ? `${wt.color}60` : '#8B5CF660',
                                                    color: wt?.color ? `${wt.color}` : '#8B5CF6'
                                                  }}
                                                >
                                                  {wt?.name || 'Worker'}
                                                </span>
                                              ));
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      /* No main allocation - always show empty pills */
                                      <div className="text-xs space-y-1">
                                        {job.equipment_type !== 'No Asset' && (
                                          <>
                                            <div className="flex items-center gap-1">
                                              <div className="w-4 h-4 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center">
                                                <Truck className="h-2.5 w-2.5 text-muted-foreground/40" />
                                              </div>
                                              <span className="text-muted-foreground/50 italic text-[10px]">No asset</span>
                                            </div>
                                            {dayIsPrimeMover && (
                                              <div className="flex items-center gap-1" data-testid={`prime-mover-cell-day-${job.id}-${dateStr}`}>
                                                <div className="w-4 h-4 rounded-full border-2 border-dashed border-blue-500/40 flex items-center justify-center">
                                                  <Truck className="h-2.5 w-2.5 text-blue-500/40" />
                                                </div>
                                                <span className="text-muted-foreground/50 italic text-[10px]">No {dayPrimeMoverName?.toLowerCase() || 'prime mover'}</span>
                                              </div>
                                            )}
                                            <div className="flex items-center gap-1">
                                              <div className="w-4 h-4 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center">
                                                <Users className="h-2.5 w-2.5 text-muted-foreground/40" />
                                              </div>
                                              <span className="text-muted-foreground/50 italic text-[10px]">Unassigned</span>
                                            </div>
                                          </>
                                        )}
                                        {(dayWorkerAllocs.length > 0 || (job.worker_requirements && job.worker_requirements.length > 0)) && (
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            {dayWorkerAllocs.slice(0, 10).map((wa, idx) => {
                                              const op = getOperatorInfo(wa.operator_id);
                                              const wt = workerTypes.find(w => w.id === wa.worker_type_id);
                                              const workerColor = wt?.color || '#8B5CF6';
                                              return (
                                                <span 
                                                  key={`assigned-${idx}`} 
                                                  className="text-[10px] text-white px-1.5 py-0.5 rounded"
                                                  style={{ backgroundColor: workerColor }}
                                                >
                                                  {op?.name?.split(' ')[0] || 'Unknown'}
                                                </span>
                                              );
                                            })}
                                            {dayWorkerAllocs.length > 10 && (
                                              <span className="text-[10px] text-amber-400 px-1.5 py-0.5 rounded bg-amber-500/10">
                                                +{dayWorkerAllocs.length - 10}
                                              </span>
                                            )}
                                            {job.worker_requirements && job.worker_requirements.map((req, rIdx) => {
                                              const wt = workerTypes.find(w => w.id === req.worker_type_id);
                                              const assignedCount = dayWorkerAllocs.filter(wa => wa.worker_type_id === req.worker_type_id).length;
                                              const unfilledCount = (req.quantity || 1) - assignedCount;
                                              return Array.from({ length: Math.max(0, unfilledCount) }).map((_, i) => (
                                                <span 
                                                  key={`unfilled-${rIdx}-${i}`} 
                                                  className="text-[10px] px-1.5 py-0.5 rounded border-2 border-dashed"
                                                  style={{ 
                                                    borderColor: wt?.color ? `${wt.color}60` : '#8B5CF660',
                                                    color: wt?.color ? `${wt.color}` : '#8B5CF6'
                                                  }}
                                                >
                                                  {wt?.name || 'Worker'}
                                                </span>
                                              ));
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell>{getStatusBadge(dayStatus)}</TableCell>
                                  <TableCell className="text-right">
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                          <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        {/* Confirm / Revert day status */}
                                        {dayAllocation && dayStatus !== 'docketed' && dayStatus !== 'invoiced' && (
                                          <>
                                            {dayStatus !== 'confirmed' ? (
                                              <DropdownMenuItem onClick={(e) => { 
                                                e.stopPropagation(); 
                                                handleUpdateDayStatus(job.id, dateStr, 'confirmed');
                                              }}
                                              data-testid={`confirm-day-${dateStr}`}
                                              >
                                                <Check className="h-4 w-4 mr-2 text-green-500" />
                                                Confirm {formatDateDisplay(dateStr)}
                                              </DropdownMenuItem>
                                            ) : (
                                              <DropdownMenuItem onClick={(e) => { 
                                                e.stopPropagation(); 
                                                handleUpdateDayStatus(job.id, dateStr, 'draft');
                                              }}
                                              data-testid={`revert-day-${dateStr}`}
                                              >
                                                <X className="h-4 w-4 mr-2 text-amber-500" />
                                                Revert to Draft
                                              </DropdownMenuItem>
                                            )}
                                            <DropdownMenuSeparator />
                                          </>
                                        )}
                                        {/* Create Docket - only for today or past dates, and not already docketed/invoiced */}
                                        {!dayDocket && dayStatus !== 'invoiced' && dayStatus !== 'docketed' && dateStr <= format(new Date(), 'yyyy-MM-dd') && (
                                          <DropdownMenuItem onClick={(e) => { 
                                            e.stopPropagation(); 
                                            setDocketDateForJob(dateStr);
                                            handleOpenDocketDialogForDate(job, dateStr);
                                          }}>
                                            <FileText className="h-4 w-4 mr-2 text-purple-500" />
                                            Create Docket for {formatDateDisplay(dateStr)}
                                          </DropdownMenuItem>
                                        )}
                                        {dayDocket && (
                                          <DropdownMenuItem onClick={(e) => { 
                                            e.stopPropagation(); 
                                            handleOpenViewDocket(dayDocket);
                                          }}>
                                            <Eye className="h-4 w-4 mr-2 text-purple-500" />
                                            View Docket
                                          </DropdownMenuItem>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          onClick={(e) => { e.stopPropagation(); handleRemoveDayFromJob(job, dateStr); }}
                                          className="text-destructive focus:text-destructive"
                                          data-testid={`remove-day-${dateStr}`}
                                        >
                                          <Trash2 className="h-4 w-4 mr-2" />
                                          Remove {formatDateDisplay(dateStr)}
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </React.Fragment>
                        );
                      }

                      // Single-day job - render normally
                      // For multi-day jobs in single-day views, pass the view date
                      const clickDate = (isMultiDay && isSingleDayView) ? format(viewDateRange.from, 'yyyy-MM-dd') : null;
                      
                      // For multi-day jobs in single-day views, get allocation for this specific date
                      const viewDateStr = isSingleDayView ? format(viewDateRange.from, 'yyyy-MM-dd') : null;
                      const dayAllocation = (isMultiDay && isSingleDayView) 
                        ? allocations.find(a => a.job_id === job.id && a.start_date <= viewDateStr && (a.end_date || a.start_date) >= viewDateStr)
                        : allocation;
                      const dayAllocEquipment = dayAllocation ? (
                        getEquipmentInfo(dayAllocation.equipment_id) || (
                          dayAllocation.is_combo_allocation && dayAllocation.combo_equipment_ids?.length
                            ? getEquipmentInfo(dayAllocation.combo_equipment_ids[0])
                            : null
                        )
                      ) : null;
                      const dayAllocOperator = dayAllocation ? getOperatorInfo(dayAllocation.operator_id) : null;
                      // --- Mobilisation Phase 2.1: Prime Mover (paired asset) ---
                      const jobEqType = equipmentTypesWithIds.find(t => t.name === job.equipment_type);
                      const isPrimeMoverJob = !!jobEqType?.requires_prime_mover;
                      const primeMoverTypeName = isPrimeMoverJob
                        ? (equipmentTypesWithIds.find(t => t.id === jobEqType.prime_mover_asset_type_id)?.name || 'Prime Mover')
                        : null;
                      const dayAllocPrimeMover = (dayAllocation?.prime_mover_equipment_id)
                        ? getEquipmentInfo(dayAllocation.prime_mover_equipment_id)
                        : null;
                      
                      // Get worker allocations for this specific date (for multi-day jobs in single-day views)
                      const dayWorkerAllocs = (isMultiDay && isSingleDayView)
                        ? workerAllocations.filter(wa => wa.job_id === job.id && wa.start_date <= viewDateStr && (wa.end_date || wa.start_date) >= viewDateStr)
                        : workerAllocations.filter(wa => wa.job_id === job.id);

                      // Per-day custom start/end time override for multi-day jobs shown on a single day
                      const dayTimeOverride = (isMultiDay && isSingleDayView) ? jobDayDetails[job.id]?.[viewDateStr] : null;
                      const effStartTime = dayTimeOverride?.start_time || job.start_time;
                      const effEndTime = dayTimeOverride?.end_time || job.end_time;
                      const hasDayTimeOverride = !!(dayTimeOverride?.start_time || dayTimeOverride?.end_time);
                      
                      return (
                        <React.Fragment key={job.id}>
                        <TableRow 
                          data-testid={`job-row-${job.id}`}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleOpenJobDialog(job, clickDate)}
                        >
                          <TableCell className="font-mono text-sm font-bold text-[#ffd108]">
                            <div className="flex items-center gap-1">
                              <span>{formatJobNumber(job)}</span>
                              {job.parent_job_id && <Link2 className="h-3 w-3 text-[#ffd108]" title={`Linked to parent #${job.parent_job_number || '?'}`} />}
                              {job.hire_type === 'dry' && (
                                <Badge
                                  className="text-[9px] px-1 py-0 h-4 bg-amber-500 text-white border-amber-500 hover:bg-amber-500"
                                  data-testid={`list-dry-pill-${job.id}`}
                                  title="Dry-hire — asset only"
                                >
                                  DRY
                                </Badge>
                              )}
                              {job.is_night_shift && <Moon className="h-3.5 w-3.5 text-indigo-400" data-testid={`list-night-shift-${job.id}`} title="Night Shift" />}
                              {job.is_tbc && <span className="text-[9px] font-semibold text-zinc-400 bg-zinc-500/20 px-1 rounded" data-testid={`list-tbc-badge-${job.id}`}>TBC</span>}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {/* For multi-day jobs in single-day views, show the VIEW date, not job start date */}
                            <div>{isMultiDay && isSingleDayView ? formatDateDisplay(format(viewDateRange.from, 'yyyy-MM-dd')) : formatDateDisplay(job.start_date)}</div>
                            {tenantSettings.depot_time_enabled && job.depot_time && job.depot_time !== effStartTime && (
                              <div className="text-[#c9a227] text-xs">Depot {formatTime(job.depot_time, tenantSettings.time_format)}</div>
                            )}
                            {effStartTime && (
                              <div className="text-[#ffd108] flex items-center gap-1">{tenantSettings.depot_time_enabled && job.depot_time && job.depot_time !== effStartTime ? 'Start ' : ''}{formatTime(effStartTime, tenantSettings.time_format)}{effEndTime && (<span className="text-[#c9a227]"> – {formatTime(effEndTime, tenantSettings.time_format)}</span>)}{hasDayTimeOverride && (<Clock className="h-3 w-3 text-amber-600" title="Custom time for this day (overrides the job default)" data-testid={`list-day-time-override-${job.id}`} />)}</div>
                            )}
                            {isMultiDay && isSingleDayView && (() => {
                              const allDays = getJobDates(job);
                              const viewDate = format(viewDateRange.from, 'yyyy-MM-dd');
                              const dayIndex = allDays.indexOf(viewDate);
                              if (dayIndex !== -1) {
                                return (
                                  <div className="text-muted-foreground text-xs sch-date-meta">
                                    Day {dayIndex + 1} of {allDays.length}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            {job.tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1 sch-date-meta">
                                {job.tags.map((tag, tIdx) => {
                                  const tagName = typeof tag === 'string' ? tag : (tag?.name || String(tag));
                                  return (
                                    <span key={tIdx} className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">{tagName}</span>
                                  );
                                })}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {customer?.company_name || 'Unknown'}
                            {job.po_number && (
                              <div className="text-xs text-muted-foreground font-normal" data-testid={`job-po-${job.id}`}>PO: {job.po_number}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {job.equipment_type || '-'}
                            {job.asset_requirements?.length > 0 && (() => {
                              const totalSlots = job.asset_requirements.reduce((s, r) => s + (r.quantity || 1), 0);
                              const typeCount = job.asset_requirements.length;
                              const filledSlots = allocations.filter(a => a.job_id === job.id && a.asset_requirement_id).length;
                              return (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 border-[#ffd108]/40 text-[#ffd108]" data-testid={`multi-asset-badge-${job.id}`}>
                                    {totalSlots} asset{totalSlots !== 1 ? 's' : ''}{typeCount > 1 ? ` · ${typeCount} types` : ''}
                                  </Badge>
                                  {filledSlots > 0 && filledSlots < totalSlots && (
                                    <span className="text-[10px] text-amber-400">{filledSlots}/{totalSlots}</span>
                                  )}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            {location.name !== 'No Location' ? (
                              <span className="flex items-center gap-1 text-muted-foreground text-sm">
                                {location.isMultiStop ? <Navigation className="h-3 w-3 text-[#ffd108] shrink-0" /> : <MapPin className="h-3 w-3" />}
                                {location.isMultiStop ? (
                                  <span className="flex items-center gap-1">
                                    <span className="truncate max-w-[180px]" title={location.address}>{location.name}</span>
                                    {location.stopCount > 0 && (
                                      <Badge variant="outline" className="text-[10px] px-1 py-0 border-[#ffd108]/40 text-[#ffd108]">
                                        {location.stopCount} {location.stopCount === 1 ? 'stop' : 'stops'}
                                      </Badge>
                                    )}
                                  </span>
                                ) : location.isOneOff ? (
                                  <span className="flex items-center gap-1">
                                    <span className="text-[#ffd108] text-xs">📍</span>
                                    <span className="truncate max-w-[180px]" title={location.address}>
                                      {location.address || 'One-off Location'}
                                    </span>
                                  </span>
                                ) : (
                                  location.name
                                )}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate">
                            <div className="flex items-center gap-1">
                              {job.setup_group_id && (
                                <Layers className="h-3 w-3 text-blue-500 shrink-0" title="Part of setup group" />
                              )}
                              <span className="truncate">{job.description}</span>
                              <MandatoryFormsBadge jobId={job.id} />
                            </div>
                          </TableCell>
                          <TableCell 
                            className="cursor-pointer hover:bg-muted"
                            onClick={(e) => { e.stopPropagation(); handleOpenAllocationDialog(job); }}
                          >
                            {/* Multi-asset job: show all allocated slots */}
                            {job.asset_requirements?.length > 0 && (() => {
                              const jobAllocs = (isMultiDay && isSingleDayView)
                                ? allocations.filter(a => a.job_id === job.id && a.asset_requirement_id && a.start_date <= viewDateStr && (a.end_date || a.start_date) >= viewDateStr)
                                : allocations.filter(a => a.job_id === job.id && a.asset_requirement_id);
                              const totalSlots = job.asset_requirements.reduce((s, r) => s + (r.quantity || 1), 0);
                              if (jobAllocs.length === 0) return null; // fall through to default unallocated view below
                              const showAllocs = jobAllocs.slice(0, 2);
                              const extraCount = jobAllocs.length - 2;
                              const unfilledCount = totalSlots - jobAllocs.length;
                              return (
                                <div className="text-xs space-y-1.5" data-testid={`multi-alloc-cell-${job.id}`}>
                                  {showAllocs.map((alloc, idx) => {
                                    const eq = getEquipmentInfo(alloc.equipment_id);
                                    const op = getOperatorInfo(alloc.operator_id);
                                    return (
                                      <div key={alloc.id || idx} className="flex items-center gap-1.5">
                                        <div className="flex flex-col min-w-0">
                                          <div className="flex items-center gap-1 truncate">
                                            <Truck className="h-3 w-3 text-[#ffd108] shrink-0" />
                                            <span className="truncate">{eq ? `${eq.equipment_type || eq.name || 'Asset'} / ${eq.asset_id}` : 'Unassigned asset'}</span>
                                          </div>
                                          <div className="flex items-center gap-1 truncate ml-4">
                                            {op ? (
                                              <span className="text-muted-foreground truncate">{op.name}</span>
                                            ) : (
                                              <span className="text-muted-foreground/50 italic">No operator</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {extraCount > 0 && (
                                    <div className="flex items-center gap-1">
                                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-[#ffd108]/40 text-[#ffd108]" data-testid={`multi-alloc-more-${job.id}`}>
                                        +{extraCount} more
                                      </Badge>
                                    </div>
                                  )}
                                  {unfilledCount > 0 && (
                                    <div className="flex items-center gap-1">
                                      <div className="w-4 h-4 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center">
                                        <Truck className="h-2.5 w-2.5 text-muted-foreground/40" />
                                      </div>
                                      <span className="text-muted-foreground/50 italic">{unfilledCount} slot{unfilledCount > 1 ? 's' : ''} unfilled</span>
                                    </div>
                                  )}
                                  {/* Worker allocations */}
                                  {(dayWorkerAllocs.length > 0 || (job.worker_requirements && job.worker_requirements.length > 0)) && (
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                      {dayWorkerAllocs.slice(0, 10).map((wa, idx) => {
                                        const op = getOperatorInfo(wa.operator_id);
                                        const wt = workerTypes.find(w => w.id === wa.worker_type_id);
                                        const workerColor = wt?.color || '#8B5CF6';
                                        return (
                                          <span key={`wa-${idx}`} className="text-[10px] text-white px-1.5 py-0.5 rounded" style={{ backgroundColor: workerColor }}>
                                            {op?.name?.split(' ')[0] || 'Unknown'}
                                          </span>
                                        );
                                      })}
                                      {dayWorkerAllocs.length > 10 && (
                                        <span className="text-[10px] text-amber-400 px-1.5 py-0.5 rounded bg-amber-500/10">+{dayWorkerAllocs.length - 10}</span>
                                      )}
                                      {job.worker_requirements && job.worker_requirements.map((req, rIdx) => {
                                        const wt = workerTypes.find(w => w.id === req.worker_type_id);
                                        const assignedCount = dayWorkerAllocs.filter(wa => wa.worker_type_id === req.worker_type_id).length;
                                        const unfilled = (req.quantity || 1) - assignedCount;
                                        return Array.from({ length: Math.max(0, unfilled) }).map((_, i) => (
                                          <span key={`uf-${rIdx}-${i}`} className="text-[10px] px-1.5 py-0.5 rounded border-2 border-dashed"
                                            style={{ borderColor: wt?.color ? `${wt.color}60` : '#8B5CF660', color: wt?.color || '#8B5CF6' }}>
                                            {wt?.name || 'Worker'}
                                          </span>
                                        ));
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {/* Single-asset job (or multi-asset with 0 allocations — fall through) */}
                            {!(job.asset_requirements?.length > 0 && allocations.some(a => a.job_id === job.id && a.asset_requirement_id)) && dayAllocation && (
                              <div className="text-xs space-y-1">
                                {job.equipment_type !== 'No Asset' && (
                                  <>
                                    <div className="flex items-center gap-1">
                                      {dayAllocEquipment ? (
                                        <>
                                          <Truck className="h-3 w-3 text-[#ffd108]" />
                                          {dayAllocation?.is_combo_allocation && dayAllocation?.combo_equipment_ids?.length > 1 ? (
                                            <span className="truncate">
                                              {dayAllocation.combo_equipment_ids.map(eid => {
                                                const eq = getEquipmentInfo(eid);
                                                return eq ? `${eq.asset_id}` : '';
                                              }).filter(Boolean).join(' + ') || `${dayAllocEquipment.equipment_type || 'Equipment'} / ${dayAllocEquipment.asset_id}`}
                                            </span>
                                          ) : (
                                            `${dayAllocEquipment.equipment_type || dayAllocEquipment.name || 'Equipment'} / ${dayAllocEquipment.asset_id}`
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <div className="w-5 h-5 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center">
                                            <Truck className="h-3 w-3 text-muted-foreground/40" />
                                          </div>
                                          <span className="text-muted-foreground/50 italic">No asset</span>
                                        </>
                                      )}
                                    </div>
                                    <RigTrailers trailerIds={dayAllocation?.additional_trailer_equipment_ids} getEquipmentInfo={getEquipmentInfo} variant="list" jobId={job.id} />
                                    <ComboMembers equipmentId={dayAllocation?.equipment_id} getEquipmentInfo={getEquipmentInfo} allocations={allocations} allocation={dayAllocation} variant="list" jobId={job.id} />
                                    {isPrimeMoverJob && (
                                      <div className="flex items-center gap-1" data-testid={`prime-mover-cell-${job.id}`}>
                                        {dayAllocPrimeMover ? (
                                          <>
                                            <Truck className="h-3 w-3 text-blue-500" />
                                            {`${dayAllocPrimeMover.equipment_type || dayAllocPrimeMover.name || primeMoverTypeName} / ${dayAllocPrimeMover.asset_id}`}
                                          </>
                                        ) : (
                                          <>
                                            <div className="w-5 h-5 rounded-full border-2 border-dashed border-blue-500/40 flex items-center justify-center">
                                              <Truck className="h-3 w-3 text-blue-500/40" />
                                            </div>
                                            <span className="text-muted-foreground/50 italic">No {primeMoverTypeName?.toLowerCase() || 'prime mover'}</span>
                                          </>
                                        )}
                                      </div>
                                    )}
                                    <div className="flex items-center gap-1">
                                      {dayAllocOperator ? (
                                        <>
                                          <Users className="h-3 w-3 text-[#ffd108]" />
                                          {dayAllocOperator.name}
                                        </>
                                      ) : job.hire_type === 'dry' ? (
                                        <span className="text-amber-500 italic text-xs">Dry-hire — no operator</span>
                                      ) : (
                                        <>
                                          <div className="w-5 h-5 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center">
                                            <Users className="h-3 w-3 text-muted-foreground/40" />
                                          </div>
                                          <span className="text-muted-foreground/50 italic">Unassigned</span>
                                        </>
                                      )}
                                    </div>
                                  </>
                                )}
                                {/* Trailer Load summary — assets/attachments riding on this trailer */}
                                {(() => {
                                  const loadEq = dayAllocation?.cargo_equipment_ids || [];
                                  const loadAtt = dayAllocation?.cargo_attachment_ids || [];
                                  const total = loadEq.length + loadAtt.length;
                                  if (total === 0) return null;
                                  const parts = [];
                                  loadEq.slice(0, 2).forEach(eid => {
                                    const e = equipment.find(x => x.id === eid);
                                    if (e) parts.push(e.asset_id || e.name);
                                  });
                                  loadAtt.slice(0, 2).forEach(aid => {
                                    const a = attachments.find(x => x.id === aid);
                                    if (a) parts.push(a.name || a.asset_id);
                                  });
                                  const overflow = total - parts.length;
                                  const title = [
                                    ...loadEq.map(id => {
                                      const e = equipment.find(x => x.id === id);
                                      return e ? (e.asset_id || e.name) : id;
                                    }),
                                    ...loadAtt.map(id => {
                                      const a = attachments.find(x => x.id === id);
                                      return a ? (a.name || a.asset_id) : id;
                                    }),
                                  ].join(' · ');
                                  return (
                                    <div
                                      className="flex items-start gap-1 text-amber-300/90"
                                      title={title}
                                      data-testid={`list-load-${job.id}`}
                                    >
                                      <Truck className="h-3 w-3 mt-[1px] shrink-0" />
                                      <span className="truncate max-w-[180px]">
                                        Load: {parts.join(' · ')}{overflow > 0 ? ` · +${overflow}` : ''}
                                      </span>
                                    </div>
                                  );
                                })()}
                                {getAllocatedAttachmentsDisplay(job.id) && (
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    <Wrench className="h-3 w-3" />
                                    {getAllocatedAttachmentsDisplay(job.id)}
                                  </div>
                                )}
                                {/* Support assets — non-billable logistics assets */}
                                {(() => {
                                  const supportAllocs = allocations.filter(a => a.job_id === job.id && a.is_support_asset);
                                  if (supportAllocs.length === 0) return null;
                                  return supportAllocs.map(sa => {
                                    const eq = getEquipmentInfo(sa.equipment_id);
                                    return (
                                      <div key={`sa-${sa.id}`} className="flex items-center gap-1 text-cyan-500/80" data-testid={`support-asset-chip-${sa.id}`}>
                                        <LinkIcon className="h-3 w-3 shrink-0" />
                                        <span className="truncate text-[10px]">{eq ? `${eq.asset_id}` : 'Support'}</span>
                                      </div>
                                    );
                                  });
                                })()}
                                {/* Worker allocations for this day + empty pills for unfilled requirements */}
                                {(dayWorkerAllocs.length > 0 || (job.worker_requirements && job.worker_requirements.length > 0)) && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {/* Show assigned workers */}
                                    {dayWorkerAllocs.slice(0, 10).map((wa, idx) => {
                                      const op = getOperatorInfo(wa.operator_id);
                                      const wt = workerTypes.find(w => w.id === wa.worker_type_id);
                                      const workerColor = wt?.color || '#8B5CF6';
                                      return (
                                        <span 
                                          key={`assigned-${idx}`} 
                                          className="text-[10px] text-white px-1.5 py-0.5 rounded"
                                          style={{ backgroundColor: workerColor }}
                                        >
                                          {op?.name?.split(' ')[0] || 'Unknown'}
                                        </span>
                                      );
                                    })}
                                    {dayWorkerAllocs.length > 10 && (
                                      <span className="text-[10px] text-amber-400 px-1.5 py-0.5 rounded bg-amber-500/10">+{dayWorkerAllocs.length - 10}</span>
                                    )}
                                    {/* Show empty pills for unfilled requirements */}
                                    {job.worker_requirements && job.worker_requirements.map((req, idx) => {
                                      const wt = workerTypes.find(w => w.id === req.worker_type_id);
                                      const assignedCount = dayWorkerAllocs.filter(wa => wa.worker_type_id === req.worker_type_id).length;
                                      const unfilledCount = (req.quantity || 1) - assignedCount;
                                      return Array.from({ length: Math.max(0, unfilledCount) }).map((_, i) => (
                                        <span 
                                          key={`unfilled-${idx}-${i}`} 
                                          className="text-[10px] px-1.5 py-0.5 rounded border-2 border-dashed"
                                          style={{ 
                                            borderColor: wt?.color ? `${wt.color}60` : '#8B5CF660',
                                            color: wt?.color ? `${wt.color}` : '#8B5CF6'
                                          }}
                                        >
                                          {wt?.name || 'Worker'}
                                        </span>
                                      ));
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                            {/* No allocation at all — show empty pills */}
                            {!(job.asset_requirements?.length > 0 && allocations.some(a => a.job_id === job.id && a.asset_requirement_id)) && !dayAllocation && (
                              <div className="text-xs space-y-1">
                                {job.equipment_type !== 'No Asset' && (
                                  <>
                                    <div className="flex items-center gap-1">
                                      <div className="w-5 h-5 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center">
                                        <Truck className="h-3 w-3 text-muted-foreground/40" />
                                      </div>
                                      <span className="text-muted-foreground/50 italic">No asset</span>
                                    </div>
                                    {isPrimeMoverJob && (
                                      <div className="flex items-center gap-1" data-testid={`prime-mover-cell-${job.id}`}>
                                        <div className="w-5 h-5 rounded-full border-2 border-dashed border-blue-500/40 flex items-center justify-center">
                                          <Truck className="h-3 w-3 text-blue-500/40" />
                                        </div>
                                        <span className="text-muted-foreground/50 italic">No {primeMoverTypeName?.toLowerCase() || 'prime mover'}</span>
                                      </div>
                                    )}
                                    <div className="flex items-center gap-1">
                                      <div className="w-5 h-5 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center">
                                        <Users className="h-3 w-3 text-muted-foreground/40" />
                                      </div>
                                      <span className="text-muted-foreground/50 italic">No operator</span>
                                    </div>
                                  </>
                                )}
                                {/* Show assigned workers AND empty pills for unfilled requirements */}
                                {(dayWorkerAllocs.length > 0 || (job.worker_requirements && job.worker_requirements.length > 0)) && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {dayWorkerAllocs.slice(0, 10).map((wa, idx) => {
                                      const op = getOperatorInfo(wa.operator_id);
                                      const wt = workerTypes.find(w => w.id === wa.worker_type_id);
                                      const workerColor = wt?.color || '#8B5CF6';
                                      return (
                                        <span 
                                          key={`assigned-${idx}`} 
                                          className="text-[10px] text-white px-1.5 py-0.5 rounded"
                                          style={{ backgroundColor: workerColor }}
                                        >
                                          {op?.name?.split(' ')[0] || 'Unknown'}
                                        </span>
                                      );
                                    })}
                                    {dayWorkerAllocs.length > 10 && (
                                      <span className="text-[10px] text-amber-400 px-1.5 py-0.5 rounded bg-amber-500/10">+{dayWorkerAllocs.length - 10}</span>
                                    )}
                                    {job.worker_requirements && job.worker_requirements.map((req, idx) => {
                                      const wt = workerTypes.find(w => w.id === req.worker_type_id);
                                      const assignedCount = dayWorkerAllocs.filter(wa => wa.worker_type_id === req.worker_type_id).length;
                                      const unfilledCount = (req.quantity || 1) - assignedCount;
                                      return Array.from({ length: Math.max(0, unfilledCount) }).map((_, i) => (
                                        <span 
                                          key={`unfilled-${idx}-${i}`} 
                                          className="text-[10px] px-1.5 py-0.5 rounded border-2 border-dashed"
                                          style={{ 
                                            borderColor: wt?.color ? `${wt.color}60` : '#8B5CF660',
                                            color: wt?.color ? `${wt.color}` : '#8B5CF6'
                                          }}
                                        >
                                          {wt?.name || 'Worker'}
                                        </span>
                                      ));
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getStatusBadge(getEffectiveStatus(job, viewDateStr))}
                              {job.status === 'cancelled' && (
                                job.cancellation_email_sent ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-green-500" title={`Cancellation email sent${job.cancellation_reason ? ` · ${job.cancellation_reason}` : ''}`} data-testid={`cancel-email-sent-${job.id}`}>
                                    <MailCheck className="h-3.5 w-3.5" /> Email sent
                                  </span>
                                ) : (
                                  <button
                                    className="inline-flex items-center gap-1 text-xs text-amber-500 hover:underline"
                                    title="Cancellation email not sent — click to send"
                                    onClick={(e) => { e.stopPropagation(); handleSendCancellationEmail(job); }}
                                    data-testid={`cancel-email-send-${job.id}`}
                                  >
                                    <MailX className="h-3.5 w-3.5" /> Email not sent
                                  </button>
                                )
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  data-testid={`job-actions-${job.id}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); handleOpenJobDialog(job); }}
                                  data-testid={`edit-job-${job.id}`}
                                >
                                  <Pencil className="h-4 w-4 mr-2" />
                                  {job.status === 'docketed' || job.status === 'invoiced' ? 'View Job' : 'Edit Job'}
                                </DropdownMenuItem>
                                {/* Hide allocation editing for docketed/invoiced jobs */}
                                {job.status !== 'docketed' && job.status !== 'invoiced' && (
                                  <DropdownMenuItem
                                    className="group"
                                    onClick={(e) => { e.stopPropagation(); handleOpenAllocationDialog(job); }}
                                    data-testid={`allocate-job-${job.id}`}
                                  >
                                    <Truck className="h-4 w-4 mr-2 text-[#ffd108] group-focus:text-black" />
                                    {allocation ? 'Edit Allocation' : 'Allocate Job'}
                                  </DropdownMenuItem>
                                )}
                                {(tenantSettings.worker_job_email_enabled || workerAckEnabled || notifyOperatorEnabled) && (
                                  <DropdownMenuItem
                                    className="group"
                                    onClick={(e) => { e.stopPropagation(); setEmailJob(job); }}
                                    data-testid={`notify-workers-row-${job.id}`}
                                    disabled={job.is_tbc}
                                    title={job.is_tbc ? 'Cannot notify workers on TBC jobs' : undefined}
                                  >
                                    <Bell className="h-4 w-4 mr-2 text-[#ffd108] group-focus:text-black" />
                                    Notify Workers
                                  </DropdownMenuItem>
                                )}
                                {/* View allocation (read-only) for docketed/invoiced jobs */}
                                {(job.status === 'docketed' || job.status === 'invoiced') && allocation && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleOpenAllocationDialog(job); }}
                                    data-testid={`view-allocation-${job.id}`}
                                  >
                                    <Eye className="h-4 w-4 mr-2 text-muted-foreground" />
                                    View Allocation
                                  </DropdownMenuItem>
                                )}
                                {job.status === 'allocated' && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleConfirmJob(job); }}
                                    data-testid={`confirm-job-${job.id}`}
                                  >
                                    <CheckCircle2 className="h-4 w-4 mr-2 text-blue-500" />
                                    Confirm Job
                                  </DropdownMenuItem>
                                )}
                                {(job.status === 'confirmed' || job.status === 'mixed') && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleUnconfirmJob(job); }}
                                    data-testid={`unconfirm-job-${job.id}`}
                                  >
                                    <RotateCcw className="h-4 w-4 mr-2 text-amber-500" />
                                    Unconfirm Job
                                  </DropdownMenuItem>
                                )}
                                {/* Create Docket - only for confirmed jobs that are today or past, not future */}
                                {job.status === 'confirmed' && job.start_date <= format(new Date(), 'yyyy-MM-dd') && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleOpenDocketDialog(job); }}
                                    data-testid={`create-docket-${job.id}`}
                                  >
                                    <FileText className="h-4 w-4 mr-2 text-green-500" />
                                    Create Docket
                                  </DropdownMenuItem>
                                )}
                                {/* View Docket(s) - shown for docketed/invoiced OR confirmed multi-day jobs (which might have partial dockets) */}
                                {(job.status === 'docketed' || job.status === 'invoiced' || (job.status === 'confirmed' && isMultiDayJob(job))) && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleViewDockets(job); }}
                                    data-testid={`view-dockets-${job.id}`}
                                  >
                                    <Eye className="h-4 w-4 mr-2 text-purple-500" />
                                    {isMultiDayJob(job) ? 'View Dockets' : 'View Docket'}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); setConfirmJobId(job.id); }}
                                  data-testid={`send-confirmation-row-${job.id}`}
                                >
                                  <Mail className="h-4 w-4 mr-2 text-green-500" />
                                  Send Customer Confirmation
                                </DropdownMenuItem>
                                {canUseChat && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); openJobChat(job.id); }}
                                    data-testid={`job-chat-${job.id}`}
                                  >
                                    <MessageSquare className="h-4 w-4 mr-2 text-blue-500" />
                                    Job Chat
                                  </DropdownMenuItem>
                                )}
                                {allocation && notifyOperatorEnabled && (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleNotifyOperator(job); }}
                                    data-testid={`notify-operator-${job.id}`}
                                  >
                                    <Bell className="h-4 w-4 mr-2 text-muted-foreground" />
                                    Send Custom Message
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); handleDuplicateJob(job); }}
                                  data-testid={`duplicate-job-${job.id}`}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  Duplicate Job
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); handleOpenExtendJob(job); }}
                                  data-testid={`extend-job-${job.id}`}
                                >
                                  <CalendarPlus className="h-4 w-4 mr-2" />
                                  Extend Job
                                </DropdownMenuItem>
                                {compliancePacksEnabled && (
                                  <DropdownMenuItem
                                    className="group"
                                    onClick={(e) => { e.stopPropagation(); setCompliancePackJob(job); }}
                                    data-testid={`compliance-pack-row-${job.id}`}
                                  >
                                    <ShieldCheck className="h-4 w-4 mr-2 text-[#ffd108] group-focus:text-black" />
                                    Compliance Pack
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                {job.status === 'cancelled' ? (
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); handleUncancelJob(job); }}
                                    data-testid={`uncancel-job-${job.id}`}
                                  >
                                    <RotateCcw className="h-4 w-4 mr-2 text-blue-500" />
                                    Un-cancel Job
                                  </DropdownMenuItem>
                                ) : (
                                  !['docketed', 'invoiced'].includes(job.status) && (
                                    <DropdownMenuItem
                                      onClick={(e) => { e.stopPropagation(); handleOpenCancelJob(job); }}
                                      data-testid={`cancel-job-${job.id}`}
                                    >
                                      <Ban className="h-4 w-4 mr-2 text-zinc-500" />
                                      Cancel Job
                                    </DropdownMenuItem>
                                  )
                                )}
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); isMultiDayJob(job) ? handleRemoveDayFromJob(job, currentDate) : handleDeleteJob(job.id); }}
                                  className="text-destructive focus:text-destructive"
                                  data-testid={`delete-job-${job.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  {isMultiDayJob(job) ? 'Remove This Day' : 'Delete Job'}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                        {/* Setup Group Gantt ribbon — temporarily disabled per user request.
                            Keeping the block in source so we can switch it back on later.
                            Previously rendered on first/last day of the parent job in single-day List view.
                        {(() => {
                          if (!(job.is_setup_parent && job.setup_group_id)) return null;
                          if (!isSingleDayView) return null;
                          const viewDateStr2 = viewDateRange.from ? format(viewDateRange.from, 'yyyy-MM-dd') : null;
                          if (!viewDateStr2) return null;
                          const jobEndDate = job.end_date || job.start_date;
                          const isFirstDay = viewDateStr2 === job.start_date;
                          const isLastDay = viewDateStr2 === jobEndDate;
                          if (!isFirstDay && !isLastDay) return null;
                          return (
                            <TableRow data-testid={`gantt-row-${job.id}`} className="hover:bg-transparent">
                              <TableCell colSpan={9} className="p-0">
                                <SetupGroupGanttRibbon
                                  api={api}
                                  groupId={job.setup_group_id}
                                  onJobClick={(childJob) => handleOpenJobDialog(childJob)}
                                  statusOf={(j) => getEffectiveStatus(j)}
                                  phase={isLastDay && !isFirstDay ? 'demob' : (isFirstDay && !isLastDay ? 'mob' : 'all')}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })()}
                        */}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* WEEK VIEW */}
      {view === 'week' && (
        <div className="space-y-3">
          {/* Week view filter bar */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="week-filter-bar">
            <Popover modal={true}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5" data-testid="week-asset-type-filter">
                  <Filter className="h-3.5 w-3.5" />
                  {assetTypeFilter.length === 0 ? 'All Asset Types' : `${assetTypeFilter.length} type${assetTypeFilter.length > 1 ? 's' : ''}`}
                  <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search types..." />
                  <CommandList className="max-h-[250px] overflow-y-auto">
                    <CommandEmpty>No type found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        onSelect={() => setAssetTypeFilter([])}
                      >
                        <Check className={`mr-2 h-4 w-4 ${assetTypeFilter.length === 0 ? 'opacity-100' : 'opacity-0'}`} />
                        All Asset Types
                      </CommandItem>
                    </CommandGroup>
                    <CommandGroup heading="Asset Types">
                      {visibleAssetTypes.map(type => (
                        <CommandItem
                          key={type}
                          value={type}
                          onSelect={() => {
                            setAssetTypeFilter(prev =>
                              prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
                            );
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${assetTypeFilter.includes(type) ? 'opacity-100' : 'opacity-0'}`} />
                          {type}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {assetTypeFilter.length > 0 && (
              <>
                {assetTypeFilter.map(type => (
                  <Badge key={type} variant="secondary" className="text-xs gap-1">
                    {type}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setAssetTypeFilter(prev => prev.filter(t => t !== type))} />
                  </Badge>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setAssetTypeFilter([])}
                >
                  Clear
                </Button>
              </>
            )}
            <div className="ml-auto flex items-center gap-3">
              <div className="flex items-center gap-1 rounded-md border border-border p-0.5" data-testid="week-density-toggle">
                {[
                  { key: 'comfortable', label: 'Comfortable' },
                  { key: 'cozy', label: 'Cozy' },
                  { key: 'compact', label: 'Compact' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setDensity(opt.key)}
                    data-testid={`week-density-${opt.key}`}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      density === opt.key
                        ? 'bg-[#ffd108] text-black font-semibold'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-muted-foreground">
                {weekAssetRows.length} asset{weekAssetRows.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <Card className="card-industrial overflow-hidden">
            <div className="overflow-x-auto">
              <div
                className="min-w-[900px]"
                style={{
                  '--sch-row': densityHeights.row,
                  '--sch-span': densityHeights.span,
                  '--sch-cell': densityHeights.cell,
                  '--sch-cell-empty': densityHeights.cellEmpty,
                }}
              >
                {/* Header: asset column + 7 day columns */}
                <div className="grid border-b" style={{ gridTemplateColumns: '180px repeat(7, minmax(0, 1fr))' }}>
                  <div className="p-2 border-r bg-muted/30 flex items-center">
                    <span className="label-overline text-xs">Asset</span>
                  </div>
                  {weekDays.map((day) => (
                    <div 
                      key={day.toISOString()} 
                      className={`p-2 text-center border-r last:border-r-0 ${
                        isSameDay(day, new Date()) ? 'bg-[#ffd108]/10' : ''
                      }`}
                    >
                      <div className="label-overline">{format(day, 'EEE')}</div>
                      <div className={`font-heading text-lg ${
                        isSameDay(day, new Date()) ? 'text-[#ffd108] font-bold' : ''
                      }`}>
                        {format(day, 'd')}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Body: one row per asset */}
                {weekAssetRows.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground text-sm">
                    No jobs this week{assetTypeFilter.length > 0 ? ' matching filter' : ''}
                  </div>
                ) : (
                  weekAssetRows.map((row, idx) => {
                    const hasSpans = row.spans.length > 0;
                    const hasDayJobs = weekDays.some(day => (row.dayJobs.get(format(day, 'yyyy-MM-dd')) || []).length > 0);
                    const assetCtx = row.equipmentId ? { equipmentType: row.equipmentType, equipmentId: row.equipmentId } : null;

                    return (
                    <div 
                      key={`${row.label}-${idx}`} 
                      className={`border-b last:border-b-0 ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}
                    >
                      <div className="grid" style={{ gridTemplateColumns: '180px repeat(7, minmax(0, 1fr))' }}>
                        {/* Sticky asset label — spans all sub-rows */}
                        <div
                          className="p-2 border-r bg-muted/20 sticky left-0 z-10 flex flex-col justify-center"
                          style={{ minHeight: 'var(--sch-row)', ...(hasSpans && hasDayJobs ? { gridRow: '1 / 3' } : {}) }}
                          data-testid={`week-asset-row-${idx}`}
                        >
                          <div className="font-semibold text-xs truncate" title={row.label}>
                            {row.label}
                          </div>
                        </div>

                        {/* --- Spanning bars (grid row 1) --- */}
                        {hasSpans && row.spans.map((span) => {
                          const { job, startCol, endCol } = span;
                          const customer = getCustomerInfo(job.customer_id);
                          const allJobDates = getJobDates(job);
                          const firstVisibleDate = format(weekDays[startCol], 'yyyy-MM-dd');
                          const firstDayIdx = allJobDates.indexOf(firstVisibleDate);
                          const totalDays = allJobDates.length;

                          // Build per-day status segments
                          const segments = [];
                          for (let c = startCol; c <= endCol; c++) {
                            const dayStr = format(weekDays[c], 'yyyy-MM-dd');
                            const status = getEffectiveStatus(job, dayStr);
                            const hasDocket = (jobDocketsCache[job.id] || []).some(d => d.job_date === dayStr);
                            segments.push({ dayStr, status, hasDocket, colIdx: c });
                          }

                          const statusColorMap = {
                            not_allocated: 'bg-red-500/25 border-red-500/40',
                            partially_allocated: 'bg-orange-500/25 border-orange-500/40',
                            allocated: 'bg-amber-500/25 border-amber-500/40',
                            confirmed: 'bg-blue-500/25 border-blue-500/40',
                            docketed: 'bg-purple-500/25 border-purple-500/40',
                            invoiced: 'bg-green-500/25 border-green-500/40',
                            mixed: 'bg-amber-500/25 border-amber-500/40',
                            tbc: 'bg-zinc-500/25 border-zinc-500/40',
                          };
                          const statusDotColor = {
                            not_allocated: 'bg-red-500',
                            partially_allocated: 'bg-orange-500',
                            allocated: 'bg-amber-500',
                            confirmed: 'bg-blue-500',
                            docketed: 'bg-purple-500',
                            invoiced: 'bg-green-500',
                            mixed: 'bg-amber-500',
                            tbc: 'bg-zinc-500',
                          };

                          return (
                            <div
                              key={`span-${job.id}`}
                              className="flex cursor-pointer group/span relative"
                              style={{ gridColumn: `${startCol + 2} / ${endCol + 3}`, gridRow: 1 }}
                              onClick={(e) => { e.stopPropagation(); handleOpenJobDialog(job); }}
                              data-testid={`week-span-${job.id}`}
                            >
                              {/* Segmented status bar */}
                              <div className="flex w-full m-1 rounded-md overflow-hidden border border-zinc-700/50 group-hover/span:border-[#ffd108]/50 transition-colors" style={{ minHeight: 'var(--sch-span)' }}>
                                {segments.map((seg, si) => (
                                  <div
                                    key={seg.dayStr}
                                    className={`flex-1 relative ${statusColorMap[seg.status] || 'bg-zinc-500/20'} ${si < segments.length - 1 ? 'border-r border-zinc-600/30' : ''}`}
                                    title={`${format(weekDays[seg.colIdx], 'EEE d MMM')}: ${seg.status.replace('_', ' ')}${seg.hasDocket ? ' (docketed)' : ''}`}
                                  >
                                    {/* Status dot — top-centre */}
                                    <div className="flex justify-center pt-1">
                                      <div className={`w-1.5 h-1.5 rounded-full ${statusDotColor[seg.status] || 'bg-zinc-500'}`} />
                                    </div>
                                    {/* Docket indicator */}
                                    {seg.hasDocket && (
                                      <ClipboardCheck className="h-2.5 w-2.5 text-purple-400 absolute top-0.5 right-0.5" />
                                    )}
                                  </div>
                                ))}
                                {/* Overlay: job info centred on the full bar */}
                                <div className="absolute inset-0 flex items-center justify-center px-2 pointer-events-none">
                                  <div className="flex flex-col items-center gap-0.5 text-[11px] bg-black/50 backdrop-blur-sm rounded px-3 py-1 max-w-[90%]">
                                    {/* Row 1: customer + time + day range */}
                                    <div className="flex items-center gap-2 font-medium truncate w-full justify-center">
                                      <span className="font-semibold truncate">{customer?.company_name || 'Unknown'}</span>
                                      <MandatoryFormsBadge jobId={job.id} />
                                      {job.is_night_shift && <Moon className="h-3 w-3 text-indigo-400 shrink-0" />}
                                      {job.is_tbc && <span className="text-[9px] font-semibold text-zinc-300 bg-zinc-500/40 px-1 rounded shrink-0">TBC</span>}
                                      {job.start_time && (
                                        <span className="text-[#ffd108] whitespace-nowrap">{formatTime(job.start_time, tenantSettings.time_format)}{job.end_time ? ` – ${formatTime(job.end_time, tenantSettings.time_format)}` : ''}</span>
                                      )}
                                      <span className="text-muted-foreground whitespace-nowrap text-[10px]">
                                        Day {(firstDayIdx >= 0 ? firstDayIdx + 1 : 1)}–{(firstDayIdx >= 0 ? firstDayIdx + segments.length : segments.length)} of {totalDays}
                                      </span>
                                    </div>
                                    {/* Row 2: asset + operator + workers */}
                                    {(() => {
                                      // Look up allocation for any day within the span
                                      const spanDayStr = format(weekDays[startCol], 'yyyy-MM-dd');
                                      const spanAlloc = allocations.find(a => a.job_id === job.id && a.start_date <= spanDayStr && (a.end_date || a.start_date) >= spanDayStr);
                                      const spanEq = spanAlloc ? getEquipmentInfo(spanAlloc.equipment_id) : null;
                                      const spanOp = spanAlloc ? getOperatorInfo(spanAlloc.operator_id) : null;
                                      // Worker allocations for any day in the span
                                      const spanWorkers = workerAllocations.filter(wa => wa.job_id === job.id && wa.start_date <= spanDayStr && (wa.end_date || wa.start_date) >= spanDayStr);
                                      const workerNames = spanWorkers.map(wa => {
                                        const wOp = getOperatorInfo(wa.operator_id);
                                        return wOp?.name || null;
                                      }).filter(Boolean);

                                      const parts = [];
                                      if (spanEq) parts.push(`${spanEq.equipment_type || ''} ${spanEq.asset_id ? `(${spanEq.asset_id})` : ''}`.trim());
                                      else if (job.equipment_type) parts.push(job.equipment_type);
                                      if (spanOp) parts.push(spanOp.name);
                                      else if (spanAlloc) parts.push('Unassigned');
                                      if (workerNames.length > 0) parts.push(workerNames.join(', '));

                                      if (parts.length === 0) return null;
                                      return (
                                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground truncate w-full justify-center">
                                          {spanEq && <Truck className="h-2.5 w-2.5 flex-shrink-0" />}
                                          <span className="truncate">{parts.join(' · ')}</span>
                                        </div>
                                      );
                                    })()}
                                    {/* Allocate button for multi-day jobs */}
                                    {(() => {
                                      const spanDayStr2 = format(weekDays[startCol], 'yyyy-MM-dd');
                                      const hasAlloc = allocations.some(a => a.job_id === job.id && a.start_date <= spanDayStr2 && (a.end_date || a.start_date) >= spanDayStr2);
                                      return (
                                        <div
                                          className={`text-[10px] cursor-pointer pointer-events-auto px-2 py-0.5 rounded ${hasAlloc ? 'text-blue-400 hover:text-blue-300 hover:underline' : 'text-amber-500 hover:text-amber-400 hover:underline'}`}
                                          onClick={(e) => { e.stopPropagation(); handleOpenAllocationDialog(job); }}
                                          data-testid={`week-span-allocate-${job.id}`}
                                        >
                                          {hasAlloc ? 'Edit Allocation' : '+ Allocate'}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {/* --- Remaining single-day jobs + empty cells (next grid row when spans exist) --- */}
                        {(!hasSpans || hasDayJobs) && (
                          <>
                            {weekDays.map((day, dayColIdx) => {
                              const dayStr = format(day, 'yyyy-MM-dd');
                              const cellJobs = row.dayJobs.get(dayStr) || [];
                              return (
                                <div
                                  key={dayStr}
                                  style={{ gridColumn: dayColIdx + 2, gridRow: hasSpans && hasDayJobs ? 2 : 1, minHeight: (cellJobs.length === 0 && !hasSpans) ? 'var(--sch-cell-empty)' : 'var(--sch-cell)' }}
                                  className={`border-r last:border-r-0 p-1 min-w-0 overflow-hidden ${
                                    isSameDay(day, new Date()) ? 'bg-[#ffd108]/5' : ''
                                  } ${cellJobs.length === 0 && !hasSpans ? 'cursor-pointer hover:bg-muted/30 transition-colors' : ''}`}
                                  onClick={() => {
                                    if (cellJobs.length === 0) handleOpenJobDialog(null, day, null, assetCtx);
                                  }}
                                  data-testid={`week-cell-${idx}-${dayStr}`}
                                >
                                  <div className="space-y-1">
                                    {cellJobs.map((job) => (
                                      <JobCard key={job.id} job={job} forDate={day} />
                                    ))}
                                  </div>
                                  {cellJobs.length > 0 && (
                                    <button
                                      data-testid={`week-add-job-${idx}-${dayStr}`}
                                      className="w-full mt-1 py-0.5 text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/40 rounded border border-dashed border-transparent hover:border-muted-foreground/30 transition-all flex items-center justify-center gap-1"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenJobDialog(null, day, null, assetCtx);
                                      }}
                                    >
                                      <Plus className="h-3 w-3" />
                                      <span>Add Job</span>
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* BOARD/KANBAN VIEW */}
      {view === 'board' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
          {/* Swim Lanes by Status */}
          {[
            { status: 'tbc', label: 'TBC', colorVar: null, bgVar: null, customColor: '#71717a', customBg: '#71717a20' },
            { status: 'not_allocated', label: 'Not Allocated', colorVar: '--status-not-allocated-border', bgVar: '--status-not-allocated-bg' },
            { status: 'partially_allocated', label: 'Partial', colorVar: '--status-partially-allocated-border', bgVar: '--status-partially-allocated-bg' },
            { status: 'allocated', label: 'Allocated', colorVar: '--status-allocated-border', bgVar: '--status-allocated-bg' },
            { status: 'confirmed', label: 'Confirmed', colorVar: '--status-confirmed-border', bgVar: '--status-confirmed-bg' },
            { status: 'docketed', label: 'Docketed', colorVar: '--status-docketed-border', bgVar: '--status-docketed-bg' },
            { status: 'invoiced', label: 'Invoiced', colorVar: '--status-invoiced-border', bgVar: '--status-invoiced-bg' }
          ].map(({ status, label, colorVar, bgVar, customColor, customBg }) => {
            const selectedDateStr = format(currentDate, 'yyyy-MM-dd');
            
            // Filter jobs that appear on the selected date AND have matching effective status for that date
            const laneJobs = jobs.filter(job => {
              // Dry-hire jobs live on the dedicated Dry Hire tab.
              if (job.hire_type === 'dry') return false;
              const jobStartDate = job.start_date;
              const jobEndDate = job.end_date || job.start_date;
              // Job shows if selected date falls within job's date range and is not excluded
              const isOnDate = jobStartDate <= selectedDateStr && jobEndDate >= selectedDateStr;
              if (!isOnDate) return false;
              if (job.excluded_dates && job.excluded_dates.includes(selectedDateStr)) return false;
              
              // Use effective status for this specific date
              const effectiveStatus = getEffectiveStatus(job, selectedDateStr);
              return effectiveStatus === status;
            });

            return (
              <div key={status} className="rounded-lg p-3 min-h-[400px]" style={{ backgroundColor: customBg || `var(${bgVar})` }}>
                {/* Lane Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: customColor || `var(${colorVar})` }} />
                    <h3 className="font-heading font-semibold text-sm">{label}</h3>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {laneJobs.length}
                  </Badge>
                </div>

                {/* Job Cards */}
                <div className="space-y-3">
                  {laneJobs.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No jobs
                    </div>
                  ) : (
                    laneJobs.map(job => {
                      const allocation = getJobAllocation(job.id);
                      const eq = allocation ? getEquipmentInfo(allocation.equipment_id) : null;
                      const op = allocation ? getOperatorInfo(allocation.operator_id) : null;
                      // Prime Mover (Phase 2.1)
                      const kanbanJobEqType = equipmentTypesWithIds.find(t => t.name === job.equipment_type);
                      const kanbanIsPrimeMoverJob = !!kanbanJobEqType?.requires_prime_mover;
                      const kanbanPrimeMoverTypeName = kanbanIsPrimeMoverJob
                        ? (equipmentTypesWithIds.find(t => t.id === kanbanJobEqType.prime_mover_asset_type_id)?.name || 'Prime Mover')
                        : null;
                      const kanbanPrimeMoverEq = (allocation?.prime_mover_equipment_id)
                        ? getEquipmentInfo(allocation.prime_mover_equipment_id)
                        : null;
                      const customer = getCustomerInfo(job.customer_id);
                      const location = getJobLocation(job);

                      // Per-day custom start/end time override for the selected board date
                      const boardDayOverride = jobDayDetails[job.id]?.[selectedDateStr];
                      const boardStartTime = boardDayOverride?.start_time || job.start_time;
                      const boardEndTime = boardDayOverride?.end_time || job.end_time;
                      const boardHasOverride = !!(boardDayOverride?.start_time || boardDayOverride?.end_time);

                      return (
                        <div 
                          key={job.id} 
                          className="bg-card border border-border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => handleOpenJobDialog(job)}
                        >
                          {/* Time */}
                          {tenantSettings.depot_time_enabled && job.depot_time && job.depot_time !== boardStartTime && (
                            <div className="flex items-center gap-1 text-[#c9a227] text-xs mb-1">
                              <Clock className="h-3 w-3" />
                              Depot {formatTime(job.depot_time, tenantSettings.time_format)}
                            </div>
                          )}
                          {boardStartTime && (
                            <div className="flex items-center gap-1 text-[#ffd108] font-bold text-sm mb-2">
                              <Clock className="h-3 w-3" />
                              {tenantSettings.depot_time_enabled && job.depot_time && job.depot_time !== boardStartTime ? 'Start ' : ''}{formatTime(boardStartTime, tenantSettings.time_format)}{boardEndTime && (<span className="font-normal text-[#c9a227]"> – {formatTime(boardEndTime, tenantSettings.time_format)}</span>)}{boardHasOverride && (<span className="text-amber-600 text-[10px] font-normal" title="Custom time for this day (overrides the job default)" data-testid={`board-day-time-override-${job.id}`}>•</span>)}
                            </div>
                          )}

                          {/* Customer & Site */}
                          <h4 className="font-semibold text-sm truncate flex items-center gap-1">
                            <span className="truncate">{customer?.company_name || 'Unknown Customer'}</span>
                            <MandatoryFormsBadge jobId={job.id} />
                          </h4>
                          {location.address && (
                            <p className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-1">
                              {location.isMultiStop ? <Navigation className="h-3 w-3 text-[#ffd108] shrink-0" /> : <MapPin className="h-3 w-3" />}
                              {location.isOneOff && <span className="text-[#ffd108]">📍</span>}
                              {location.address}
                              {location.isMultiStop && location.stopCount > 0 && (
                                <span className="text-[#ffd108]">· {location.stopCount} {location.stopCount === 1 ? 'stop' : 'stops'}</span>
                              )}
                            </p>
                          )}

                          {/* Description */}
                          {job.description && (
                            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                              {job.description}
                            </p>
                          )}

                          {/* Equipment Type Badge */}
                          {job.equipment_type && (
                            <Badge variant="outline" className="mt-2 text-xs">
                              {job.equipment_type}
                            </Badge>
                          )}

                          {/* Equipment & Operator Icons */}
                          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border">
                            {/* Equipment Circle */}
                            <div 
                              className={`relative w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-colors ${
                                eq 
                                  ? 'bg-[#ffd108] text-black' 
                                  : 'border-2 border-dashed border-muted-foreground hover:border-[#ffd108] hover:bg-[#ffd108]/10'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAllocationDialog(job);
                              }}
                              title={eq ? eq.asset_id : 'Click to allocate equipment'}
                            >
                              <Truck className={`h-4 w-4 ${eq ? '' : 'text-muted-foreground'}`} />
                              {(allocation?.additional_trailer_equipment_ids?.filter(Boolean).length || 0) > 0 && (
                                <span
                                  className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center"
                                  title={`${1 + allocation.additional_trailer_equipment_ids.filter(Boolean).length} trailers in rig`}
                                  data-testid={`board-trailer-count-${job.id}`}
                                >
                                  ×{1 + allocation.additional_trailer_equipment_ids.filter(Boolean).length}
                                </span>
                              )}
                            </div>

                            {/* Prime Mover Circle (Phase 2.1) */}
                            {kanbanIsPrimeMoverJob && (
                              <div
                                className={`w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-colors ${
                                  kanbanPrimeMoverEq
                                    ? 'bg-blue-500 text-white'
                                    : 'border-2 border-dashed border-blue-500/50 hover:border-blue-500 hover:bg-blue-500/10'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenAllocationDialog(job);
                                }}
                                title={kanbanPrimeMoverEq ? `${kanbanPrimeMoverTypeName}: ${kanbanPrimeMoverEq.asset_id}` : `Click to allocate ${kanbanPrimeMoverTypeName}`}
                                data-testid={`board-prime-mover-${job.id}`}
                              >
                                <Truck className={`h-4 w-4 ${kanbanPrimeMoverEq ? '' : 'text-blue-500/60'}`} />
                              </div>
                            )}

                            {/* Operator Circle */}
                            <div 
                              className={`w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-colors ${
                                op 
                                  ? 'bg-[#ffd108] text-black' 
                                  : 'border-2 border-dashed border-muted-foreground hover:border-[#ffd108] hover:bg-[#ffd108]/10'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAllocationDialog(job);
                              }}
                              title={op ? op.name : 'Click to allocate operator'}
                            >
                              <Users className={`h-4 w-4 ${op ? '' : 'text-muted-foreground'}`} />
                            </div>

                            {/* Worker Type Circles */}
                            {job.worker_requirements?.length > 0 && (
                              <div className="flex items-center gap-1">
                                {job.worker_requirements.map((wr) => {
                                  const workerType = workerTypes.find(wt => wt.id === wr.worker_type_id);
                                  const jobWorkerAllocs = getJobWorkerAllocations(job.id).filter(
                                    wa => wa.worker_type_id === wr.worker_type_id
                                  );
                                  const allocatedCount = jobWorkerAllocs.length;
                                  const isFullyAllocated = allocatedCount >= wr.quantity;
                                  const isPartiallyAllocated = allocatedCount > 0 && allocatedCount < wr.quantity;
                                  
                                  return (
                                    <div
                                      key={wr.worker_type_id}
                                      className={`w-7 h-7 rounded-full flex items-center justify-center cursor-pointer transition-colors text-xs font-bold ${
                                        isFullyAllocated
                                          ? 'bg-green-500 text-white'
                                          : isPartiallyAllocated
                                          ? 'bg-amber-500 text-white'
                                          : 'border-2 border-dashed border-muted-foreground hover:border-[#ffd108] hover:bg-[#ffd108]/10'
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenAllocationDialog(job);
                                      }}
                                      title={`${workerType?.name || 'Worker'}: ${allocatedCount}/${wr.quantity} assigned`}
                                    >
                                      {wr.quantity}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Allocated Names */}
                            <div className="flex-1 min-w-0">
                              {eq || op ? (
                                <div className="text-xs">
                                  <div className="truncate">
                                    {eq && <span className="font-medium">{eq.asset_id}</span>}
                                    {eq && op && <span className="text-muted-foreground"> • </span>}
                                    {op && <span>{op.name}</span>}
                                    {workerAckEnabled && allocation && op && (
                                      <span className="ml-1.5 inline-flex items-center align-middle">
                                        <WorkerAckDot allocation={allocation} size={7} />
                                      </span>
                                    )}
                                  </div>
                                  <RigTrailers trailerIds={allocation?.additional_trailer_equipment_ids} getEquipmentInfo={getEquipmentInfo} variant="board" jobId={job.id} />
                                  <ComboMembers equipmentId={allocation?.equipment_id} getEquipmentInfo={getEquipmentInfo} allocations={allocations} allocation={allocation} variant="board" jobId={job.id} />
                                  {getAllocatedAttachmentsDisplay(job.id) && (
                                    <div className="text-muted-foreground truncate mt-0.5">
                                      {getAllocatedAttachmentsDisplay(job.id)}
                                    </div>
                                  )}
                                  {/* Show allocated workers */}
                                  {job.worker_requirements?.length > 0 && (
                                    <div className="text-muted-foreground truncate mt-0.5">
                                      {job.worker_requirements.map(wr => {
                                        const workerType = workerTypes.find(wt => wt.id === wr.worker_type_id);
                                        const jobWorkerAllocs = getJobWorkerAllocations(job.id).filter(
                                          wa => wa.worker_type_id === wr.worker_type_id
                                        );
                                        const allocatedNames = jobWorkerAllocs
                                          .map(wa => getOperatorInfo(wa.operator_id)?.name)
                                          .filter(Boolean)
                                          .join(', ');
                                        
                                        if (allocatedNames) {
                                          return (
                                            <span key={wr.worker_type_id} className="block">
                                              {workerType?.name}: {allocatedNames}
                                            </span>
                                          );
                                        }
                                        return null;
                                      })}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground italic">
                                  Not allocated
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Add Job Button */}
                {status === 'not_allocated' && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full mt-3 border-2 border-dashed border-muted-foreground/30 hover:border-[#ffd108] hover:bg-[#ffd108]/10"
                    onClick={() => handleOpenJobDialog(null, currentDate)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Job
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* DRY HIRE VIEW */}
      {view === 'dryhire' && (
        <DryHireView
          api={api}
          refreshKey={dryHireRefreshKey}
          onOpenJob={(job) => {
            // Find the up-to-date job from local state if present (so editing
            // works even though dry-hire jobs are filtered out of List/Week/Board).
            const fresh = jobs.find(j => j.id === job.id) || job;
            handleOpenJobDialog(fresh);
          }}
          onAllocateAsset={(job) => {
            const fresh = jobs.find(j => j.id === job.id) || job;
            // Dry-hire jobs use the slim, centered allocation modal — NOT the
            // multi-day wet-hire allocation dialog.
            const existing = (allocations || []).find(a => a.job_id === fresh.id) || null;
            setDryHireAllocModal({ open: true, job: fresh, allocation: existing });
          }}
        />
      )}

      {/* TOWER CRANE VIEW */}
      {view === 'towercrane' && (
        <TowerCraneView
          api={api}
          refreshKey={dryHireRefreshKey}
          onOpenJob={(job) => {
            navigate(`/schedule/tower-crane/${job.id}`);
          }}
        />
      )}


      {/* FOLDERS VIEW (Northlands beta — flag: schedule_folders) */}
      {view === 'folders' && (
        <div data-testid="schedule-folders-view">
          <ScheduleFolderView
            api={api}
            date={currentDate}
            quickReserve={isFeatureEnabled('quick_reserve')}
            onConverted={(jobId) => { fetchData(); toast.success('Job created from reservation'); }}
            onJobClick={(jobId) => {
              const job = (jobs || []).find(j => j.id === jobId);
              if (job) handleOpenJobDialog(job);
            }}
          />
        </div>
      )}

      </div>{/* End scrollable content area */}

      {/* DRY HIRE ALLOCATION MODAL */}
      <DryHireAllocationModal
        open={dryHireAllocModal.open}
        onOpenChange={(o) => setDryHireAllocModal(prev => ({ ...prev, open: o }))}
        api={api}
        job={dryHireAllocModal.job}
        existingAllocation={dryHireAllocModal.allocation}
        onSaved={() => { fetchData(); setDryHireRefreshKey(k => k + 1); }}
      />

      {/* JOB SHEET (Flyout Panel) */}
      <Sheet open={jobDialogOpen} onOpenChange={(open) => {
        // While a nested quick-add dialog (contact / site / customer) is open,
        // ignore close requests on the parent job form. Dismissing the inner
        // dialog (Esc / click-outside) otherwise propagates and closes this
        // Sheet too.
        if (!open && (contactDialogOpen || siteDialogOpen || customerDialogOpen)) {
          return;
        }
        setJobDialogOpen(open);
      }}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-scroll" style={{ overflowY: 'scroll' }}>
          <SheetHeader className="pb-4 border-b">
            <div className="flex items-center gap-3">
              <SheetTitle className="font-heading text-xl flex-1">
                {editingJob ? (
                  <>
                    {editingJob.status === 'docketed' || editingJob.status === 'invoiced' ? 'View' : 'Edit'} Job #{editingJob.job_number}
                    {jobEditMode === 'day' && editingJobDate && (
                      <span className="text-muted-foreground ml-2">
                        - Day {getJobDates(editingJob).indexOf(editingJobDate) + 1} of {getJobDates(editingJob).length} ({formatDateDisplay(editingJobDate)})
                      </span>
                    )}
                    {jobEditMode === 'overall' && isMultiDayJob(editingJob) && (
                      <span className="text-muted-foreground ml-2 text-sm">
                        (Overall Job)
                      </span>
                    )}
                  </>
                ) : (
                  'Create New Job'
                )}
              </SheetTitle>
              {editingJob && <div className="mr-8">{getStatusBadge(editingJob.status)}</div>}
            </div>
            {/* Day editing mode indicator */}
            {editingJob && jobEditMode === 'day' && editingJobDate && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-md">
                <Calendar className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-amber-600">
                  Editing Day {getJobDates(editingJob).indexOf(editingJobDate) + 1} only — 
                  <button 
                    type="button"
                    className="underline ml-1 hover:text-amber-700"
                    onClick={() => handleOpenJobDialog(editingJob, null, 'overall')}
                  >
                    Edit overall job instead
                  </button>
                </span>
              </div>
            )}
            {editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced') && (
              <p className="text-sm text-muted-foreground mt-2">
                This job is {editingJob.status} and cannot be modified
              </p>
            )}
          </SheetHeader>
          
          {/* Recurring series indicator */}
          {editingJob?.recurrence_group_id && (
            <div className="mt-3">
              <RecurringIndicator
                job={editingJob}
                onViewPlan={() => setViewPlanOpen(true)}
                onEditFuture={() => setEditFutureOpen(true)}
                onChangePattern={() => setChangePatternOpen(true)}
              />
            </div>
          )}

          {/* Presence indicator - show who else is viewing */}
          {editingJob && jobOthersViewing.length > 0 && (
            <PresenceIndicator othersViewing={jobOthersViewing} className="my-4" />
          )}
          
          {/* Version conflict alert */}
          {versionConflict && (
            <VersionConflictAlert 
              message={versionConflict.message}
              onRefresh={handleRefreshAndReload}
              className="my-4"
            />
          )}

          {/* Setup Group Panel — REMOVED from here; moved into the Setup tab below */}

          {/* Prompt to apply setup profile for jobs that don't have one yet */}
          {editingJob && !editingJob.setup_group_id && (() => {
            const eqType = equipmentTypesWithIds.find(t => t.name === editingJob.equipment_type);
            if (eqType?.requires_setup && (eqType?.setup_profile_ids?.length > 0)) {
              return (
                <div className="my-4 p-3 border border-blue-500/30 bg-blue-500/5 rounded-lg flex items-center justify-between gap-3" data-testid="apply-setup-prompt">
                  <div className="flex items-center gap-2 text-sm">
                    <Layers className="h-4 w-4 text-blue-500 shrink-0" />
                    <span>
                      <strong>{editingJob.equipment_type}</strong> has a setup profile. Generate mobilisation bookings?
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => setMobilisationPrompt({ open: true, jobId: editingJob.id, jobData: editingJob })}
                    data-testid="apply-setup-btn"
                  >
                    <Layers className="h-3.5 w-3.5 mr-1.5" />
                    Apply Setup Profile
                  </Button>
                </div>
              );
            }
            return null;
          })()}
          
          {/* Tabs for Details / Setup / Files / Activity (only when editing an existing job) */}
          {editingJob && workerAckEnabled && editingJob.hire_type !== 'dry' && (
            <div className="mt-4 mb-2 p-3 bg-muted/30 rounded border border-border">
              <JobAckPanel
                jobId={editingJob.id}
                onChange={async () => {
                  fetchData();
                  try {
                    const res = await api.get(`/jobs/${editingJob.id}`);
                    if (res.data) setEditingJob(res.data);
                  } catch (_) { /* fetchData already refreshed the grid */ }
                }}
              />
            </div>
          )}
          {editingJob && (() => {
            const hasSetup = !!editingJob.setup_group_id;
            // Decide tab count: base 2 (Details + Files), +1 for Setup, +1 for Activity (admin)
            const cols = 2 + (hasSetup ? 1 : 0) + (user?.role === 'admin' ? 1 : 0);
            const gridCls = { 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' }[cols] || 'grid-cols-2';
            return (
              <Tabs value={jobDrawerTab} onValueChange={setJobDrawerTab} className="mt-4 mb-2">
                <TabsList className={`grid w-full ${gridCls}`}>
                  <TabsTrigger value="details" data-testid="job-tab-details">Details</TabsTrigger>
                  {hasSetup && (
                    <TabsTrigger value="setup" data-testid="job-tab-setup">
                      <Layers className="h-3.5 w-3.5 mr-1" />
                      Setup Group
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="files" data-testid="job-tab-files">Files</TabsTrigger>
                  {user?.role === 'admin' && (
                    <TabsTrigger value="activity" data-testid="job-tab-activity">Activity</TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            );
          })()}

          {/* Setup Group tab content */}
          {editingJob && jobDrawerTab === 'setup' && editingJob.setup_group_id && (
            <div className="py-4" data-testid="job-tab-setup-content">
              <SetupGroupPanel
                api={api}
                job={editingJob}
                allocations={allocations}
                equipment={equipment}
                attachments={attachments}
                onRefresh={() => {
                  fetchData();
                  if (editingJob) {
                    api.get(`/jobs`).then(res => {
                      const fresh = res.data.find(j => j.id === editingJob.id);
                      if (fresh) setEditingJob(fresh);
                    }).catch(() => {});
                  }
                }}
              />
            </div>
          )}
          
          {/* Activity Log tab content (admin only) */}
          {editingJob && jobDrawerTab === 'activity' && user?.role === 'admin' && (
            <div className="py-4">
              <JobActivityLog jobId={editingJob.id} />
            </div>
          )}
          
          {/* Files tab content */}
          {editingJob && jobDrawerTab === 'files' && (
            <JobFilesTab jobId={editingJob.id} api={api} />
          )}
          
          <form onSubmit={handleSubmitJob} className="space-y-4 py-4" data-testid="job-form" style={{ display: (editingJob && (jobDrawerTab === 'files' || jobDrawerTab === 'activity' || jobDrawerTab === 'setup')) ? 'none' : undefined }}>
            {/* Summary Card for Day-Specific Editing */}
            {editingJob && jobEditMode === 'day' && editingJobDate && (
              <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  Job Summary (Read Only)
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Customer:</span>
                    <p className="font-medium">{customers.find(c => c.id === editingJob.customer_id)?.company_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Equipment:</span>
                    <p className="font-medium">{editingJob.equipment_type || '-'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Site:</span>
                    <p className="font-medium">{(() => {
                      const loc = getJobLocation(editingJob);
                      return loc.name !== 'No Location' ? (loc.isOneOff ? loc.address : loc.name) : '-';
                    })()}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Dates:</span>
                    <p className="font-medium">{formatDateDisplay(editingJob.start_date)} - {formatDateDisplay(editingJob.end_date)}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Job Description:</span>
                    <p className="font-medium">{editingJob.description || '-'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Overall Job Fields - Hidden when editing specific day */}
            {!(editingJob && jobEditMode === 'day' && editingJobDate) && (
              <>
            {/* Project Selector — shown when projects flag is on */}
            {projectsEnabled && (
              <div className="space-y-2" data-testid="project-select-section">
                <Label className="label-overline flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5" /> Project (optional)
                </Label>
                <Popover open={projectPopoverOpen} onOpenChange={setProjectPopoverOpen} modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between font-normal"
                      data-testid="job-project-select"
                    >
                      {jobForm.project_id ? (
                        <span className="flex items-center gap-2 truncate">
                          <Layers className="h-3.5 w-3.5 text-[#ffd108]" />
                          {activeProjects.find(p => p.id === jobForm.project_id)?.name || 'Selected project'}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Link to a project...</span>
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search projects..." className="text-xs" />
                      <CommandList className="max-h-[250px] overflow-y-auto">
                        <CommandEmpty>No active projects found</CommandEmpty>
                        {jobForm.project_id && (
                          <CommandGroup>
                            <CommandItem
                              value="__clear_project__"
                              onSelect={() => handleProjectSelect(null)}
                              className="text-muted-foreground text-xs"
                              data-testid="job-project-clear"
                            >
                              <X className="mr-1.5 h-3 w-3" /> Clear project
                            </CommandItem>
                          </CommandGroup>
                        )}
                        <CommandGroup heading="Active Projects">
                          {activeProjects.map((p) => (
                            <CommandItem
                              key={p.id}
                              value={`${p.name} ${p.customer_name || ''}`}
                              onSelect={() => handleProjectSelect(p.id)}
                              className="text-xs"
                              data-testid={`job-project-option-${p.id}`}
                            >
                              <Check className={`mr-2 h-4 w-4 ${jobForm.project_id === p.id ? 'opacity-100' : 'opacity-0'}`} />
                              <div className="flex flex-col">
                                <span className="font-medium">{p.name}</span>
                                <span className="text-muted-foreground">{p.customer_name}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {jobForm.project_id && (
                  <p className="text-xs text-[#ffd108] flex items-center gap-1">
                    <Layers className="h-3 w-3" />
                    Customer and site pre-filled from project. Changing the customer will unlink the project.
                  </p>
                )}
              </div>
            )}

            {/* Linked Job Selector - Only for new jobs when allow_linked_jobs is enabled */}
            {!editingJob && tenantSettings.field_rules?.allow_linked_jobs?.enabled && (
              <div className="space-y-2" data-testid="parent-job-section">
                <Label className="label-overline flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5" /> Parent Job (optional)
                </Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DatePicker
                      value={parentJobSearchDate}
                      onChange={(date) => setParentJobSearchDate(date)}
                      formatDisplay={formatDateDisplay}
                      className="w-full"
                      testId="parent-job-date"
                    />
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">then select job:</span>
                </div>
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between font-normal"
                      data-testid="parent-job-select"
                    >
                      {jobForm.parent_job_id ? (
                        <span className="flex items-center gap-2 truncate">
                          <Link2 className="h-3.5 w-3.5 text-[#ffd108]" />
                          {(() => {
                            const pj = jobs.find(j => j.id === jobForm.parent_job_id);
                            if (!pj) return 'Unknown Job';
                            const cust = customers.find(c => c.id === pj.customer_id);
                            return `#${pj.parent_job_number ? pj.parent_job_number + '-' + pj.sub_job_index : pj.job_number} - ${cust?.company_name || 'Unknown'} - ${pj.equipment_type || ''}`;
                          })()}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Select parent job to create a linked sub-job</span>
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search by job #, customer, or description..." className="text-xs" />
                      <CommandList className="max-h-[250px] overflow-y-auto">
                        <CommandEmpty>
                          {parentJobSearchDate
                            ? `No jobs found for ${formatDateDisplay(parentJobSearchDate)}`
                            : 'Select a date above to see available parent jobs'}
                        </CommandEmpty>
                        {jobForm.parent_job_id && (
                          <CommandGroup>
                            <CommandItem
                              value="__clear__"
                              onSelect={() => setJobForm(prev => ({ ...prev, parent_job_id: '' }))}
                              className="text-muted-foreground text-xs"
                            >
                              <X className="mr-1.5 h-3 w-3" /> Clear parent (standalone job)
                            </CommandItem>
                          </CommandGroup>
                        )}
                        <CommandGroup heading={parentJobSearchDate ? `Jobs on ${formatDateDisplay(parentJobSearchDate)}` : 'Jobs'}>
                          {jobs
                            .filter(j => {
                              if (j.parent_job_id) return false;
                              if (!parentJobSearchDate) return false;
                              const d = parentJobSearchDate;
                              if (j.start_date <= d && (j.end_date ? j.end_date >= d : j.start_date === d)) return true;
                              return false;
                            })
                            .map(j => {
                              const cust = customers.find(c => c.id === j.customer_id);
                              const displayNum = j.job_number;
                              return (
                                <CommandItem
                                  key={j.id}
                                  value={`${j.job_number} ${cust?.company_name || ''} ${j.description || ''} ${j.equipment_type || ''}`}
                                  onSelect={() => {
                                    setJobForm(prev => ({
                                      ...prev,
                                      parent_job_id: j.id,
                                      customer_id: j.customer_id,
                                      site_id: j.site_id || prev.site_id,
                                      equipment_type: j.equipment_type || prev.equipment_type,
                                      start_date: j.start_date || prev.start_date,
                                      end_date: j.end_date || prev.end_date,
                                      start_time: j.start_time || prev.start_time,
                                      end_time: j.end_time || prev.end_time,
                                      depot_time: j.depot_time || prev.depot_time,
                                      description: j.description || prev.description,
                                      hire_type: j.hire_type || prev.hire_type,
                                      job_type: j.job_type || prev.job_type,
                                      po_number: j.po_number || prev.po_number,
                                    }));
                                  }}
                                  className="text-xs"
                                >
                                  <span className="flex items-center gap-2 truncate">
                                    <span className="font-mono font-bold text-[#ffd108]">#{displayNum}</span>
                                    <span className="truncate">{cust?.company_name || 'Unknown'}</span>
                                    <span className="text-muted-foreground truncate">- {j.equipment_type || 'No type'}</span>
                                    <span className="text-muted-foreground truncate">({formatDateDisplay(j.start_date)})</span>
                                  </span>
                                </CommandItem>
                              );
                            })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {jobForm.parent_job_id && (
                  <p className="text-xs text-[#ffd108] flex items-center gap-1">
                    <Link2 className="h-3 w-3" />
                    This will be created as a sub-job. Fields inherited from parent — you can override any.
                  </p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label className="label-overline">Customer <span className="text-red-500">*</span></Label>
              <Popover open={customerComboboxOpen} onOpenChange={setCustomerComboboxOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={customerComboboxOpen}
                    className="w-full justify-between font-normal"
                    data-testid="job-customer-select"
                    disabled={editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced')}
                  >
                    {jobForm.customer_id ? (
                      <span className="flex items-center gap-2 truncate">
                        {customers.find(c => c.id === jobForm.customer_id)?.company_name || 'Select customer'}
                        {customers.find(c => c.id === jobForm.customer_id)?.requires_po && (
                          <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">PO</Badge>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Search customers...</span>
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search customers..." />
                    <CommandList className="max-h-[450px] overflow-y-auto">
                      <CommandEmpty>No customer found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="__add_new__"
                          onSelect={() => {
                            handleCustomerSelect('__add_new__');
                            setCustomerComboboxOpen(false);
                          }}
                          className="text-[#ffd108] font-medium"
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          ADD NEW CUSTOMER
                        </CommandItem>
                      </CommandGroup>
                      {customers.length > 0 && (
                        <CommandGroup heading="Customers">
                          {customers.map((c) => (
                            <CommandItem
                              key={c.id}
                              value={c.company_name || `customer-${c.id}`}
                              onSelect={() => {
                                handleCustomerSelect(c.id);
                                setCustomerComboboxOpen(false);
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${jobForm.customer_id === c.id ? 'opacity-100' : 'opacity-0'}`}
                              />
                              <span className="flex items-center gap-2">
                                {c.company_name}
                                {c.requires_po && (
                                  <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 ml-1">PO</Badge>
                                )}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Contact Dropdown - Shows contacts for selected customer */}
            {jobForm.customer_id && (
              <div className="space-y-2">
                <Label className="label-overline">Contact</Label>
                <Popover open={contactComboboxOpen} onOpenChange={setContactComboboxOpen} modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={contactComboboxOpen}
                      className="w-full justify-between font-normal"
                      data-testid="job-contact-select"
                      disabled={editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced')}
                    >
                      {jobForm.contact_id ? (
                        <span className="flex items-center gap-2 truncate">
                          <User className="h-3 w-3 text-muted-foreground" />
                          {customerContacts.find(c => c.id === jobForm.contact_id)?.name || 'Select contact'}
                          {customerContacts.find(c => c.id === jobForm.contact_id)?.position && (
                            <span className="text-muted-foreground text-xs">({customerContacts.find(c => c.id === jobForm.contact_id)?.position})</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Select contact (optional)</span>
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search contacts..." />
                      <CommandList className="max-h-[300px] overflow-y-auto">
                        <CommandEmpty>No contact found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="__add_new_contact__"
                            onSelect={() => {
                              handleContactSelect('__add_new__');
                              setContactComboboxOpen(false);
                            }}
                            className="text-[#ffd108] font-medium"
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            ADD NEW CONTACT
                          </CommandItem>
                        </CommandGroup>
                        {customerContacts.length > 0 && (
                          <CommandGroup heading="Contacts">
                            {customerContacts.map((contact) => (
                              <CommandItem
                                key={contact.id}
                                value={contact.name || `contact-${contact.id}`}
                                onSelect={() => {
                                  handleContactSelect(contact.id);
                                  setContactComboboxOpen(false);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${jobForm.contact_id === contact.id ? 'opacity-100' : 'opacity-0'}`}
                                />
                                <span className="flex items-center gap-2">
                                  {contact.name}
                                  {contact.position && <span className="text-muted-foreground text-xs">({contact.position})</span>}
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                        {customerContacts.length === 0 && (
                          <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                            No contacts for this customer
                          </div>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* PO Number - Required if customer.requires_po is true */}
            {selectedCustomerRequiresPO && (
              <div className="space-y-2">
                <Label className="label-overline flex items-center gap-2">
                  PO Number
                  <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Required</Badge>
                </Label>
                <Input
                  value={jobForm.po_number}
                  onChange={(e) => setJobForm(prev => ({ ...prev, po_number: e.target.value }))}
                  placeholder="Enter Purchase Order number"
                  required={selectedCustomerRequiresPO}
                  data-testid="job-po-number"
                />
              </div>
            )}

            {/* Optional PO Number for non-required customers */}
            {!selectedCustomerRequiresPO && jobForm.customer_id && (
              <div className="space-y-2">
                <Label className="label-overline">PO Number (Optional)</Label>
                <Input
                  value={jobForm.po_number}
                  onChange={(e) => setJobForm(prev => ({ ...prev, po_number: e.target.value }))}
                  placeholder="Enter Purchase Order number if applicable"
                  data-testid="job-po-number"
                />
              </div>
            )}

            {/* Location — Haulage or Multi-Stop: Stops Editor | Standard: Site picker */}
            {(jobForm.job_type === 'haulage' || jobForm.requires_multi_stops) ? (
              <div className="space-y-2">
                {/* Oversize load flag (haulage) */}
                <div className="rounded-lg border p-3 space-y-2 bg-muted/30" data-testid="oversize-section">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={!!jobForm.oversize} onCheckedChange={(c) => setJobForm(prev => ({ ...prev, oversize: !!c }))} data-testid="oversize-checkbox" />
                    <span className="text-sm font-medium flex items-center gap-1"><AlertTriangle className="h-4 w-4 text-amber-500" /> Flag Oversize</span>
                  </label>
                  {jobForm.oversize && (
                    <Input value={jobForm.oversize_meters || ''} onChange={(e) => setJobForm(prev => ({ ...prev, oversize_meters: e.target.value }))} placeholder="Oversize (metres)" data-testid="oversize-meters-input" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <Label className="label-overline flex items-center gap-2">
                    <Navigation className="h-4 w-4" /> Route Stops
                    {jobForm.requires_multi_stops && jobForm.job_type !== 'haulage' && (
                      <span className="text-red-500">*</span>
                    )}
                  </Label>
                  {jobForm.job_type !== 'haulage' && jobForm.requires_multi_stops && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setJobForm(prev => ({ ...prev, requires_multi_stops: false }))}
                      data-testid="use-single-location-btn"
                    >
                      <MapPin className="h-3.5 w-3.5 mr-1" /> Use single location
                    </Button>
                  )}
                </div>
                <HaulageStopsEditor
                  stops={jobForm.stops || []}
                  onChange={(stops) => setJobForm(prev => ({ ...prev, stops }))}
                  country={tenantSettings?.country || 'AU'}
                  sites={jobForm.customer_id ? getCustomerSites(jobForm.customer_id) : []}
                  onAddNewSite={(stopIdx) => { setSiteDialogStopIdx(stopIdx); setSiteDialogOpen(true); }}
                  api={api}
                />
                {/* Signature settings — per-job override of tenant defaults */}
                <div className="mt-3 p-3 border border-border rounded-lg space-y-3" data-testid="job-signature-settings">
                  <Label className="label-overline">Docket Signatures (override tenant default)</Label>
                  <div>
                    <Label className="text-xs text-muted-foreground">Customer signs</Label>
                    <div className="grid grid-cols-4 gap-2 mt-1">
                      {[{ v: '', l: 'Use default' }, { v: 'start', l: 'Start' }, { v: 'end', l: 'End' }, { v: 'both', l: 'Both' }].map((opt) => (
                        <button
                          type="button"
                          key={opt.v || 'default'}
                          onClick={() => setJobForm(prev => ({ ...prev, signature_timing: opt.v || null }))}
                          className={`px-2 py-1.5 rounded-md text-xs border transition-colors ${(jobForm.signature_timing || '') === opt.v ? 'bg-[#ffd108] text-black border-[#ffd108] font-medium' : 'bg-transparent text-muted-foreground border-border hover:border-[#ffd108]/50'}`}
                          data-testid={`job-signature-timing-${opt.v || 'default'}`}
                        >
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Signature at every waypoint</Label>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      {[{ v: null, l: 'Use default' }, { v: true, l: 'On' }, { v: false, l: 'Off' }].map((opt) => (
                        <button
                          type="button"
                          key={String(opt.v)}
                          onClick={() => setJobForm(prev => ({ ...prev, waypoint_signature: opt.v }))}
                          className={`px-2 py-1.5 rounded-md text-xs border transition-colors ${((jobForm.waypoint_signature ?? null) === opt.v) ? 'bg-[#ffd108] text-black border-[#ffd108] font-medium' : 'bg-transparent text-muted-foreground border-border hover:border-[#ffd108]/50'}`}
                          data-testid={`job-waypoint-signature-${opt.v === null ? 'default' : opt.v ? 'on' : 'off'}`}
                        >
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="label-overline">Job Location <span className="text-red-500">*</span></Label>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">One-off address</Label>
                  <Switch
                    checked={jobForm.use_one_off_address}
                    onCheckedChange={(checked) => setJobForm({ 
                      ...jobForm, 
                      use_one_off_address: checked,
                      site_id: checked ? '' : jobForm.site_id,
                      one_off_street: checked ? jobForm.one_off_street : '',
                      one_off_suburb: checked ? jobForm.one_off_suburb : '',
                      one_off_state: checked ? jobForm.one_off_state : '',
                      one_off_postcode: checked ? jobForm.one_off_postcode : ''
                    })}
                  />
                </div>
              </div>
              
              {jobForm.use_one_off_address ? (
                <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
                  <div>
                    <Label className="text-xs text-muted-foreground">Search Address</Label>
                    <GooglePlacesAutocomplete
                      placeholder="Start typing an address..."
                      country={tenantSettings.country}
                      onPlaceSelect={(place) => {
                        setJobForm(prev => ({
                          ...prev,
                          one_off_street: place.street_address || '',
                          one_off_suburb: place.suburb || '',
                          one_off_state: place.state || '',
                          one_off_postcode: place.postcode || ''
                        }));
                      }}
                      data-testid="job-one-off-address-autocomplete"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Or enter manually below</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Street Address</Label>
                    <Input
                      value={jobForm.one_off_street}
                      onChange={(e) => setJobForm(prev => ({ ...prev, one_off_street: e.target.value }))}
                      placeholder="e.g., 123 Main Street"
                      data-testid="job-one-off-street"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">{t('suburb')}</Label>
                      <Input
                        value={jobForm.one_off_suburb}
                        onChange={(e) => setJobForm(prev => ({ ...prev, one_off_suburb: e.target.value }))}
                        placeholder={t('suburb')}
                        data-testid="job-one-off-suburb"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">{t('state')}</Label>
                      <Select 
                        value={jobForm.one_off_state} 
                        onValueChange={(v) => setJobForm(prev => ({ ...prev, one_off_state: v }))}
                      >
                        <SelectTrigger data-testid="job-one-off-state">
                          <SelectValue placeholder={countryMeta?.stateLabel || t('state')} />
                        </SelectTrigger>
                        <SelectContent>
                          {statesList.map(s => (
                            <SelectItem key={s.code} value={s.code}>{s.code}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">{countryMeta?.postalLabel || t('postcode')}</Label>
                      <Input
                        value={jobForm.one_off_postcode}
                        onChange={(e) => setJobForm(prev => ({ ...prev, one_off_postcode: e.target.value }))}
                        placeholder="0000"
                        maxLength={4}
                        data-testid="job-one-off-postcode"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <Popover open={siteComboboxOpen} onOpenChange={setSiteComboboxOpen} modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={siteComboboxOpen}
                      className="w-full justify-between font-normal"
                      data-testid="job-site-select"
                      disabled={!jobForm.customer_id}
                    >
                      {jobForm.site_id ? (
                        <span className="truncate">
                          {(() => {
                            const s = getCustomerSites(jobForm.customer_id).find(s => s.id === jobForm.site_id);
                            return s ? `${s.name ? s.name + ' - ' : ''}${formatSiteAddress(s)}`.replace(/ - $/, '') || 'Unnamed Site' : 'Select site';
                          })()}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{jobForm.customer_id ? "Search sites..." : "Select customer first"}</span>
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search sites..." />
                      <CommandList className="max-h-[300px] overflow-y-auto">
                        <CommandEmpty>No site found.</CommandEmpty>
                        {jobForm.customer_id && (
                          <CommandGroup>
                            <CommandItem
                              value="__add_new_site__"
                              onSelect={() => {
                                handleSiteSelect('__add_new__');
                                setSiteComboboxOpen(false);
                              }}
                              className="text-[#ffd108] font-medium"
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              ADD NEW SITE
                            </CommandItem>
                          </CommandGroup>
                        )}
                        {getCustomerSites(jobForm.customer_id).length > 0 && (
                          <CommandGroup heading="Sites">
                            {getCustomerSites(jobForm.customer_id).map((s) => (
                              <CommandItem
                                key={s.id}
                                value={`${s.name || ''} ${formatSiteAddress(s)}`}
                                onSelect={() => {
                                  handleSiteSelect(s.id);
                                  setSiteComboboxOpen(false);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${jobForm.site_id === s.id ? 'opacity-100' : 'opacity-0'}`}
                                />
                                {s.name ? `${s.name} - ${formatSiteAddress(s)}` : formatSiteAddress(s) || 'Unnamed Site'}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            )}

            {/* Site Contact Selector — directly under Job Location; hidden for multi-stop/haulage */}
            {jobForm.site_id && !jobForm.use_one_off_address && !jobForm.requires_multi_stops && jobForm.job_type !== 'haulage' && (
              <div className="space-y-2" data-testid="site-contact-section">
                <div className="flex items-center justify-between">
                  <Label className="label-overline flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Site Contacts
                  </Label>
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setAddSiteContactOpen(true)} data-testid="add-site-contact-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add contact
                  </Button>
                </div>
                <Popover open={siteContactComboboxOpen} onOpenChange={setSiteContactComboboxOpen} modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={siteContactComboboxOpen}
                      className="w-full justify-between font-normal"
                      data-testid="job-site-contact-select"
                    >
                      {jobForm.site_contact_ids?.length > 0 ? (
                        <span className="truncate">{jobForm.site_contact_ids.length} contact{jobForm.site_contact_ids.length > 1 ? 's' : ''} selected</span>
                      ) : (
                        <span className="text-muted-foreground">Select site contacts...</span>
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search site contacts..." />
                      <CommandList className="max-h-[300px] overflow-y-auto">
                        <CommandEmpty>No site contact found.</CommandEmpty>
                        <CommandGroup heading="Site Contacts">
                          {siteContacts.map((contact) => {
                            const selected = jobForm.site_contact_ids?.includes(contact.id);
                            return (
                              <CommandItem
                                key={contact.id}
                                value={contact.name || `site-contact-${contact.id}`}
                                onSelect={() => {
                                  setJobForm(prev => {
                                    const ids = prev.site_contact_ids || [];
                                    return {
                                      ...prev,
                                      site_contact_ids: ids.includes(contact.id)
                                        ? ids.filter(id => id !== contact.id)
                                        : [...ids, contact.id],
                                    };
                                  });
                                }}
                                data-testid={`site-contact-option-${contact.id}`}
                              >
                                <Check className={`mr-2 h-4 w-4 ${selected ? 'opacity-100' : 'opacity-0'}`} />
                                <span className="flex items-center gap-2">
                                  {contact.name}
                                  {contact.job_title && <span className="text-muted-foreground text-xs">({contact.job_title})</span>}
                                  {contact.is_primary && <Badge variant="outline" className="text-xs ml-1">Primary</Badge>}
                                </span>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {jobForm.site_contact_ids?.length > 0 && (
                  <div className="space-y-1.5" data-testid="selected-site-contacts">
                    {jobForm.site_contact_ids.map(id => {
                      const contact = siteContacts.find(c => c.id === id);
                      if (!contact) return null;
                      return (
                        <div key={id} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm" data-testid={`selected-site-contact-${id}`}>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 min-w-0">
                            <span className="font-medium truncate">{contact.name}</span>
                            {contact.is_primary && <Badge variant="outline" className="text-xs">Primary</Badge>}
                            {contact.phone && <span className="flex items-center gap-1 text-muted-foreground"><Phone className="h-3 w-3" />{contact.phone}</span>}
                            {contact.email && <span className="flex items-center gap-1 text-muted-foreground"><Mail className="h-3 w-3" />{contact.email}</span>}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0"
                            onClick={() => setJobForm(prev => ({ ...prev, site_contact_ids: (prev.site_contact_ids || []).filter(x => x !== id) }))}
                            data-testid={`remove-site-contact-${id}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Pinpoint Location & Multi-Stop Toggle — below Location section */}
            {!(editingJob && jobEditMode === 'day' && editingJobDate) && (
              <div className="flex items-center gap-4">
                {/* Pinpoint Map */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPinpointMapOpen(true)}
                  className="flex items-center gap-1.5"
                  data-testid="pinpoint-map-btn"
                >
                  <MapPin className="h-3.5 w-3.5 text-[#ffd108]" />
                  {jobForm.latitude ? 'Update Pin' : 'Pinpoint Location'}
                </Button>
                {jobForm.latitude && (
                  <span className="text-xs text-muted-foreground">
                    Pin set ({jobForm.latitude.toFixed(4)}, {jobForm.longitude.toFixed(4)})
                  </span>
                )}
                <div className="flex-1" />
                {/* Multi-Stop Toggle — only for non-haulage when setting enabled */}
                {jobForm.job_type !== 'haulage' && tenantSettings.field_rules?.allow_multi_stops?.enabled && (
                  <div className="flex items-center gap-2">
                    <Switch
                      id="requires-multi-stops"
                      checked={jobForm.requires_multi_stops}
                      onCheckedChange={(checked) => setJobForm(prev => ({
                        ...prev,
                        requires_multi_stops: checked,
                        stops: checked ? (prev.stops?.length ? prev.stops : []) : prev.stops,
                      }))}
                      data-testid="multi-stops-toggle"
                    />
                    <Label htmlFor="requires-multi-stops" className="cursor-pointer text-sm flex items-center gap-1.5">
                      <Navigation className="h-3.5 w-3.5" /> Multi-Stops
                    </Label>
                  </div>
                )}
              </div>
            )}

            {/* Starting Location — gated on field_rules.starting_location.enabled */}
            {tenantSettings.field_rules?.starting_location?.enabled && (
              <div className="space-y-2" data-testid="starting-location-section">
                <Label className="label-overline flex items-center gap-2">
                  <Navigation className="h-4 w-4" />
                  Starting Location {tenantSettings.field_rules?.starting_location?.required && <span className="text-red-500">*</span>}
                </Label>
                <GooglePlacesAutocomplete
                  placeholder="Where will workers start from?"
                  country={tenantSettings.country}
                  value={jobForm.starting_location}
                  onChange={(e) => setJobForm(prev => ({ ...prev, starting_location: e.target.value }))}
                  onPlaceSelect={(place) => setJobForm(prev => ({ ...prev, starting_location: place.formatted_address || place.street_address || '' }))}
                  data-testid="job-starting-location-input"
                />
              </div>
            )}

            {/* Site Contact selector relocated above, directly under Job Location */}

            <div className="space-y-2" data-testid="hire-type-panel">
              <Label className="label-overline">Hire Type</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="hire-type-wet-btn"
                  data-active={jobForm.hire_type !== 'dry' && jobForm.hire_type !== 'labour'}
                  onClick={() => setJobForm(prev => ({
                    ...prev,
                    hire_type: 'wet',
                    hire_period_unit: '',
                    pickup_from_yard: false,
                    return_to_yard: false,
                  }))}
                  className={`flex-1 min-w-[140px] px-3 py-2 text-sm rounded-md border transition-colors ${
                    (jobForm.hire_type !== 'dry' && jobForm.hire_type !== 'labour')
                      ? 'bg-primary text-primary-foreground border-primary font-semibold'
                      : 'bg-background hover:bg-accent border-border text-foreground'
                  }`}
                >
                  Wet-Hire <span className="text-xs opacity-70 ml-1">(operator + asset)</span>
                </button>
                <button
                  type="button"
                  data-testid="hire-type-dry-btn"
                  data-active={jobForm.hire_type === 'dry'}
                  onClick={() => setJobForm(prev => ({
                    ...prev,
                    hire_type: 'dry',
                    hire_period_unit: prev.hire_period_unit || 'day',
                    // Dry-hire is open-ended by default — clear any End Date carried
                    // over from a previous wet/labour selection. The actual end_date
                    // is set automatically when the hire is Off-Hired.
                    end_date: '',
                  }))}
                  className={`flex-1 min-w-[140px] px-3 py-2 text-sm rounded-md border transition-colors ${
                    jobForm.hire_type === 'dry'
                      ? 'bg-amber-500 text-white border-amber-500 font-semibold'
                      : 'bg-background hover:bg-accent border-border text-foreground'
                  }`}
                >
                  Dry-Hire <span className="text-xs opacity-70 ml-1">(asset-only · period rate)</span>
                </button>
                <button
                  type="button"
                  data-testid="hire-type-labour-btn"
                  data-active={jobForm.hire_type === 'labour'}
                  onClick={() => setJobForm(prev => ({
                    ...prev,
                    hire_type: 'labour',
                    // Labour hire has no asset — clear equipment + dry-hire flags
                    equipment_type: '',
                    attachment_type_ids: [],
                    create_equipment_id: '',
                    hire_period_unit: '',
                    pickup_from_yard: false,
                    return_to_yard: false,
                  }))}
                  className={`flex-1 min-w-[140px] px-3 py-2 text-sm rounded-md border transition-colors ${
                    jobForm.hire_type === 'labour'
                      ? 'bg-indigo-500 text-white border-indigo-500 font-semibold'
                      : 'bg-background hover:bg-accent border-border text-foreground'
                  }`}
                >
                  Labour Hire <span className="text-xs opacity-70 ml-1">(no asset)</span>
                </button>
              </div>
              {jobForm.hire_type === 'labour' && (
                <div className="mt-2 p-2 rounded-md border-l-4 border-indigo-500 bg-indigo-500/5 text-xs text-muted-foreground" data-testid="labour-hire-hint">
                  Labour-hire jobs supply workers only (e.g. rigger, dogman, supervisor) with no asset. The Asset Type field is hidden; add at least one Worker Requirement below.
                </div>
              )}
              {jobForm.hire_type === 'dry' && (
                <div className="mt-3 space-y-3 p-3 rounded-md border-l-4 border-amber-500 bg-amber-500/5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wide">Hire Period</Label>
                      <select
                        data-testid="hire-period-unit-select"
                        className="w-full mt-1 px-2 py-1.5 text-sm rounded-md border bg-background"
                        value={jobForm.hire_period_unit || 'day'}
                        onChange={e => setJobForm(prev => ({ ...prev, hire_period_unit: e.target.value }))}
                      >
                        <option value="day">Daily</option>
                        <option value="week">Weekly</option>
                        <option value="month">Monthly</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-2 justify-end">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid="pickup-from-yard-checkbox"
                          checked={!!jobForm.pickup_from_yard}
                          onChange={e => setJobForm(prev => ({ ...prev, pickup_from_yard: e.target.checked }))}
                          className="h-4 w-4"
                        />
                        <span>Customer picks up from yard</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid="return-to-yard-checkbox"
                          checked={!!jobForm.return_to_yard}
                          onChange={e => setJobForm(prev => ({ ...prev, return_to_yard: e.target.checked }))}
                          className="h-4 w-4"
                        />
                        <span>Customer returns to yard</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide">Hire Notes (optional)</Label>
                    <Input
                      data-testid="hire-notes-input"
                      value={jobForm.hire_notes || ''}
                      onChange={e => setJobForm(prev => ({ ...prev, hire_notes: e.target.value }))}
                      placeholder="Special handover / collection instructions"
                      className="mt-1"
                    />
                  </div>
                  {/* Tower Crane toggle — only for crane-hire tenants */}
                  {isCraneHire && (
                    <div className="border-t border-amber-300/30 pt-3 mt-1 space-y-3">
                      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="tower-crane-toggle">
                        <input
                          type="checkbox"
                          checked={!!jobForm.is_tower_crane}
                          onChange={e => setJobForm(prev => ({ ...prev, is_tower_crane: e.target.checked, tower_crane_billing_period: e.target.checked ? (prev.tower_crane_billing_period || 'weekly') : '' }))}
                          className="h-4 w-4"
                        />
                        <span className="font-medium">Tower Crane Job</span>
                      </label>
                      {jobForm.is_tower_crane && (
                        <div className="space-y-2 ml-6">
                          <p className="text-xs text-muted-foreground">
                            Long-term site placement with allocated workers. Billed periodically for equipment rental + labour hours from daily dockets.
                          </p>
                          <div>
                            <Label className="text-xs uppercase tracking-wide">Billing Period</Label>
                            <select
                              data-testid="tower-crane-billing-period-select"
                              className="w-full mt-1 px-2 py-1.5 text-sm rounded-md border bg-background"
                              value={jobForm.tower_crane_billing_period || 'weekly'}
                              onChange={e => setJobForm(prev => ({ ...prev, tower_crane_billing_period: e.target.value }))}
                            >
                              <option value="weekly">Weekly</option>
                              <option value="fortnightly">Fortnightly</option>
                              <option value="monthly">Monthly</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Dry-hire jobs require no operator and are billed by period. Pre-hire inspections auto-spawn on create; configure period rates in the price list.
                  </p>
                  {editingJob?.id && (
                    <>
                      {/* Inline asset picker — only meaningful once the job exists.
                          Shares the same component as the standalone modal so the
                          two surfaces never drift apart. */}
                      <DryHireAssetPicker
                        api={api}
                        job={editingJob}
                        existingAllocation={(allocations || []).find(a => a.job_id === editingJob.id) || null}
                        layout="inline"
                        onSaved={() => { fetchData(); setDryHireRefreshKey(k => k + 1); }}
                      />
                      <HireLifecyclePanel jobId={editingJob.id} api={api} />
                      <HireInspectionsReviewPanel jobId={editingJob.id} api={api} />
                      <DryHireInvoicingPanel jobId={editingJob.id} api={api} />
                    </>
                  )}
                </div>
              )}
            </div>

            {jobForm.hire_type !== 'labour' && (
            multiAssetEnabled ? (
              <AssetRequirementsEditor
                requirements={jobForm.asset_requirements}
                onChange={(reqs) => {
                  setJobForm(prev => ({
                    ...prev,
                    asset_requirements: reqs,
                    equipment_type: reqs[0]?.equipment_type || prev.equipment_type,
                  }));
                }}
                equipmentTypes={equipmentTypes.map(t => typeof t === 'string' ? t : t.name)}
              />
            ) : (
            <div className="space-y-2">
              <Label className="label-overline">Asset Type <span className="text-red-500">*</span></Label>
              <Popover open={assetTypeComboboxOpen} onOpenChange={setAssetTypeComboboxOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={assetTypeComboboxOpen}
                    className="w-full justify-between font-normal"
                    data-testid="job-equipment-type-select"
                  >
                    {jobForm.equipment_type ? (
                      <span className="truncate">{jobForm.equipment_type}</span>
                    ) : (
                      <span className="text-muted-foreground">Search asset types...</span>
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search asset types..." />
                    <CommandList className="max-h-[300px] overflow-y-auto">
                      <CommandEmpty>No asset type found.</CommandEmpty>
                      <CommandGroup heading="Asset Types">
                        {equipmentTypes.map((type) => {
                          const typeName = typeof type === 'string' ? type : type.name;
                          const typeKey = typeof type === 'string' ? type : type.id;
                          return (
                            <CommandItem
                              key={typeKey}
                              value={typeName}
                              onSelect={() => {
                                // When equipment type changes, filter out incompatible attachment types
                                const newFilteredAttachments = getFilteredAttachmentTypes(typeName);
                                // Functional updater — the .map() closure could capture a stale
                                // jobForm after the user has toggled Hire Type / other fields.
                                setJobForm(prev => {
                                  const validAttachmentIds = (prev.attachment_type_ids || []).filter(id =>
                                    newFilteredAttachments.some(at => at.id === id)
                                  );
                                  return {
                                    ...prev,
                                    equipment_type: typeName,
                                    attachment_type_ids: validAttachmentIds,
                                    create_equipment_id: ''
                                  };
                                });
                                setAssetTypeComboboxOpen(false);
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${jobForm.equipment_type === typeName ? 'opacity-100' : 'opacity-0'}`}
                              />
                              {typeName}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            )
            )}

            {/* Asset Allocation on Create - shown when rule enabled and asset type selected */}
            {!editingJob && jobForm.hire_type !== 'labour' && jobForm.equipment_type && (tenantSettings.field_rules?.asset_allocation_on_create?.enabled === true) && (
              <div className="space-y-2">
                <Label className="label-overline flex items-center gap-2">
                  <Truck className="h-4 w-4" />
                  Asset
                </Label>
                <Popover open={createAssetComboboxOpen} onOpenChange={setCreateAssetComboboxOpen} modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={createAssetComboboxOpen}
                      className="w-full justify-between font-normal"
                      data-testid="job-create-asset-select"
                    >
                      {jobForm.create_equipment_id ? (
                        (() => {
                          const eq = equipment.find(e => e.id === jobForm.create_equipment_id);
                          return <span className="truncate">{eq ? `${eq.equipment_type}${eq.asset_id ? ` (${eq.asset_id})` : ''}` : 'Unknown'}</span>;
                        })()
                      ) : (
                        <span className="text-muted-foreground">Select an asset to allocate...</span>
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search assets..." />
                      <CommandList className="max-h-[300px] overflow-y-auto">
                        <CommandEmpty>No assets found for this type.</CommandEmpty>
                        <CommandGroup heading="Available Assets">
                          <CommandItem
                            value="__none__"
                            onSelect={() => {
                              setJobForm(prev => ({ ...prev, create_equipment_id: '' }));
                              setCreateAssetComboboxOpen(false);
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${!jobForm.create_equipment_id ? 'opacity-100' : 'opacity-0'}`} />
                            <span className="text-muted-foreground">-- None --</span>
                          </CommandItem>
                          {equipment
                            .filter(eq => eq.equipment_type === jobForm.equipment_type && eq.status !== 'decommissioned')
                            .map((eq) => (
                              <CommandItem
                                key={eq.id}
                                value={`${eq.equipment_type} ${eq.asset_id || ''}`}
                                onSelect={() => {
                                  setJobForm(prev => ({ ...prev, create_equipment_id: eq.id }));
                                  setCreateAssetComboboxOpen(false);
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${jobForm.create_equipment_id === eq.id ? 'opacity-100' : 'opacity-0'}`} />
                                {eq.equipment_type}{eq.asset_id ? ` (${eq.asset_id})` : ''}
                                {eq.status === 'maintenance' && <span className="ml-2 text-xs text-amber-500">(maintenance)</span>}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {jobForm.equipment_type && getFilteredAttachmentTypes(jobForm.equipment_type).length > 0 && (
              <div className="space-y-2">
                <Label className="label-overline flex items-center gap-2">
                  <Wrench className="h-4 w-4" />
                  Attachment Types Required
                </Label>
                {!jobForm.equipment_type && (
                  <p className="text-xs text-amber-600 mb-2">
                    Select an equipment type first to see available attachments
                  </p>
                )}
                <div className="border border-border rounded-md p-3 max-h-32 overflow-y-auto space-y-2">
                  {getFilteredAttachmentTypes(jobForm.equipment_type).map((type) => (
                    <div key={type.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`job-att-type-${type.id}`}
                        checked={jobForm.attachment_type_ids.includes(type.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setJobForm({
                              ...jobForm,
                              attachment_type_ids: [...jobForm.attachment_type_ids, type.id]
                            });
                          } else {
                            setJobForm({
                              ...jobForm,
                              attachment_type_ids: jobForm.attachment_type_ids.filter(id => id !== type.id)
                            });
                          }
                        }}
                      />
                      <label htmlFor={`job-att-type-${type.id}`} className="text-sm cursor-pointer">
                        {type.name}
                      </label>
                    </div>
                  ))}
                </div>
                {jobForm.attachment_type_ids.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {jobForm.attachment_type_ids.length} type(s) selected: {jobForm.attachment_type_ids.map(id => getAttachmentTypeName(id)).join(', ')}
                  </p>
                )}
              </div>
            )}
              </>
            )}

            {/* Worker Requirements Section - For overall job editing (hidden for standard dry-hire, shown for tower crane) */}
            {(jobForm.hire_type !== 'dry' || jobForm.is_tower_crane) && workerTypes.length > 0 && (tenantSettings.field_rules?.additional_workers?.enabled !== false) && !(editingJob && jobEditMode === 'day' && editingJobDate) && (
              <div className="space-y-2">
                <Label className="label-overline flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Additional Workers Required
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Select workers like Dogmen, Spotters, Riggers, etc.
                </p>
                <div className="border border-border rounded-md p-3 space-y-3">
                  {workerTypes.map((type) => {
                    const existing = jobForm.worker_requirements.find(wr => wr.worker_type_id === type.id);
                    return (
                      <div key={type.id} className="flex items-center gap-3">
                        <Checkbox
                          id={`job-worker-type-${type.id}`}
                          checked={!!existing}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setJobForm({
                                ...jobForm,
                                worker_requirements: [
                                  ...jobForm.worker_requirements,
                                  { worker_type_id: type.id, quantity: 1 }
                                ]
                              });
                            } else {
                              // Check if there are existing worker allocations for this type
                              if (editingJob) {
                                const existingAllocs = workerAllocations.filter(
                                  wa => wa.job_id === editingJob.id && wa.worker_type_id === type.id
                                );
                                if (existingAllocs.length > 0) {
                                  const workerNames = existingAllocs
                                    .map(wa => operators.find(o => o.id === wa.operator_id)?.name || 'Unknown')
                                    .join(', ');
                                  toast.warning(
                                    `${workerNames} allocated as ${type.name} will be unassigned when you save`,
                                    { duration: 5000 }
                                  );
                                }
                              }
                              setJobForm({
                                ...jobForm,
                                worker_requirements: jobForm.worker_requirements.filter(
                                  wr => wr.worker_type_id !== type.id
                                )
                              });
                            }
                          }}
                        />
                        <label htmlFor={`job-worker-type-${type.id}`} className="text-sm cursor-pointer flex-1">
                          {type.name}
                          {type.description && (
                            <span className="text-xs text-muted-foreground ml-1">({type.description})</span>
                          )}
                        </label>
                        {existing && (
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => {
                                if (existing.quantity > 1) {
                                  setJobForm({
                                    ...jobForm,
                                    worker_requirements: jobForm.worker_requirements.map(wr =>
                                      wr.worker_type_id === type.id
                                        ? { ...wr, quantity: wr.quantity - 1 }
                                        : wr
                                    )
                                  });
                                }
                              }}
                              disabled={existing.quantity <= 1}
                            >
                              -
                            </Button>
                            <span className="text-sm font-medium w-6 text-center">{existing.quantity}</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => {
                                setJobForm({
                                  ...jobForm,
                                  worker_requirements: jobForm.worker_requirements.map(wr =>
                                    wr.worker_type_id === type.id
                                      ? { ...wr, quantity: wr.quantity + 1 }
                                      : wr
                                  )
                                });
                              }}
                            >
                              +
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {jobForm.worker_requirements.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {jobForm.worker_requirements.reduce((sum, wr) => sum + wr.quantity, 0)} worker(s) selected: {
                      jobForm.worker_requirements.map(wr => {
                        const wt = workerTypes.find(t => t.id === wr.worker_type_id);
                        return `${wr.quantity}x ${wt?.name || 'Unknown'}`;
                      }).join(', ')
                    }
                  </p>
                )}
              </div>
            )}

            {/* Day Work Description - Only shown when editing a specific day */}
            {editingJob && jobEditMode === 'day' && editingJobDate && (
              <div className="space-y-2 p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                <Label className="label-overline flex items-center gap-2">
                  <FileText className="h-4 w-4 text-amber-500" />
                  Day Work Description
                  <span className="text-xs text-muted-foreground font-normal">
                    (Specific to {formatDateDisplay(editingJobDate)})
                  </span>
                </Label>
                <Textarea
                  value={dayWorkDescription}
                  onChange={(e) => setDayWorkDescription(e.target.value)}
                  placeholder="Enter day-specific work description (e.g., 'Arrive on site and start excavations')"
                  className="min-h-[80px]"
                  data-testid="day-work-description"
                />
                <p className="text-xs text-muted-foreground">
                  This description is specific to Day {getJobDates(editingJob).indexOf(editingJobDate) + 1} of {getJobDates(editingJob).length}.
                </p>
              </div>
            )}

            {/* Day Start/End Time overrides - Only shown when editing a specific day */}
            {editingJob && jobEditMode === 'day' && editingJobDate && (
              <div className="space-y-2 p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg" data-testid="day-time-override-section">
                <Label className="label-overline flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-500" />
                  Day Start / End Time
                  <span className="text-xs text-muted-foreground font-normal">
                    (Specific to {formatDateDisplay(editingJobDate)})
                  </span>
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Start time</Label>
                    <TimeField
                      value={dayStartTime}
                      onChange={(v) => setDayStartTime(v || '')}
                      allowNone
                      testId="day-start-time"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">End time</Label>
                    <TimeField
                      value={dayEndTime}
                      onChange={(v) => setDayEndTime(v || '')}
                      allowNone
                      testId="day-end-time"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Leave blank to use the job default ({editingJob.start_time || '—'}{editingJob.end_time ? `–${editingJob.end_time}` : ''}). Only this day is affected.
                </p>
              </div>
            )}

            {/* Day-Specific Workers Section - multi-day (non tower crane): read-only, crew live on the block */}
            {jobForm.hire_type !== 'dry' && editingJob && jobEditMode === 'day' && editingJobDate && isBlockCrewReadOnly(editingJob) && (tenantSettings.field_rules?.additional_workers?.enabled !== false) && (() => {
              const dayBlock = allocations.find(a => a.job_id === editingJob.id && !a.is_support_asset && a.start_date <= editingJobDate && (a.end_date || a.start_date) >= editingJobDate);
              return (
                <DayCrewReadOnly
                  crew={getDayCrewForBlock(editingJob.id, editingJobDate, dayBlock?.id || null)}
                  workerTypes={workerTypes}
                  operators={operators}
                  title={`Additional Workers for ${formatDateDisplay(editingJobDate)}`}
                  testIdPrefix="day-drawer-crew"
                  onEditBlock={() => { const j = editingJob; setJobDialogOpen(false); handleOpenMultiDayDrawer(j); }}
                />
              );
            })()}

            {/* Day-Specific Workers Section - For day editing mode (tower crane and labour-only jobs) */}
            {jobForm.hire_type !== 'dry' && editingJob && jobEditMode === 'day' && editingJobDate && !isBlockCrewReadOnly(editingJob) && workerTypes.length > 0 && (tenantSettings.field_rules?.additional_workers?.enabled !== false) && (
              <div className="space-y-3">
                <Label className="label-overline flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Additional Workers for {formatDateDisplay(editingJobDate)}
                </Label>
                
                {/* Show job's worker requirements */}
                {editingJob.worker_requirements && editingJob.worker_requirements.length > 0 ? (
                  <div className="bg-muted/50 border border-border rounded-md p-3 space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">Job Requirements:</p>
                    <div className="flex flex-wrap gap-2">
                      {editingJob.worker_requirements.map((req, idx) => {
                        const wt = workerTypes.find(w => w.id === req.worker_type_id);
                        // Count how many of this type are currently assigned
                        const assignedCount = dayWorkerSelections.filter(
                          s => s.worker_type_id === req.worker_type_id && s.operator_id
                        ).length;
                        const requiredQty = req.quantity || 1;
                        const isFulfilled = assignedCount >= requiredQty;
                        
                        return (
                          <div 
                            key={req.worker_type_id || `wt-${idx}`} 
                            className={`flex items-center gap-2 px-2 py-1 rounded text-sm ${
                              isFulfilled ? 'bg-green-500/10 border border-green-500/30' : 'bg-amber-500/10 border border-amber-500/30'
                            }`}
                          >
                            <span 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: wt?.color || '#8B5CF6' }}
                            />
                            <span>{wt?.name || 'Worker'}</span>
                            <span className={`font-mono text-xs ${isFulfilled ? 'text-green-600' : 'text-amber-600'}`}>
                              {assignedCount}/{requiredQty}
                            </span>
                            {isFulfilled && <Check className="h-3 w-3 text-green-500" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No worker requirements defined for this job.</p>
                )}
                
                <p className="text-xs text-muted-foreground">
                  Assign workers to fulfill the requirements above, or add additional workers as needed.
                </p>
                
                {/* Current worker selections */}
                <div className="space-y-2">
                  {dayWorkerSelections.map((selection, idx) => {
                    const wt = workerTypes.find(w => w.id === selection.worker_type_id);
                    // Get operators that can do this worker type
                    const eligibleOperators = operators.filter(op => 
                      op.worker_type_ids?.includes(selection.worker_type_id)
                    );
                    // Include currently selected operator even if not in eligible list
                    const currentOperator = selection.operator_id ? operators.find(op => op.id === selection.operator_id) : null;
                    const displayOperators = currentOperator && !eligibleOperators.find(op => op.id === currentOperator.id)
                      ? [currentOperator, ...eligibleOperators]
                      : eligibleOperators;
                    
                    return (
                      <div key={`dw-${idx}`} className="flex items-center gap-2 p-2 border border-border rounded-md">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: wt?.color || '#8B5CF6' }}
                        />
                        <Select
                          value={selection.worker_type_id}
                          onValueChange={(value) => {
                            const updated = [...dayWorkerSelections];
                            updated[idx] = { ...updated[idx], worker_type_id: value, operator_id: '' };
                            setDayWorkerSelections(updated);
                          }}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue placeholder="Worker Type" />
                          </SelectTrigger>
                          <SelectContent>
                            {workerTypes.map(wt => (
                              <SelectItem key={wt.id} value={wt.id}>
                                <span className="flex items-center gap-2">
                                  <span 
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: wt.color || '#8B5CF6' }}
                                  />
                                  {wt.name}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={selection.operator_id || ''}
                          onValueChange={(value) => {
                            const updated = [...dayWorkerSelections];
                            updated[idx] = { ...updated[idx], operator_id: value };
                            setDayWorkerSelections(updated);
                          }}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select worker..." />
                          </SelectTrigger>
                          <SelectContent>
                            {displayOperators.length > 0 ? (
                              displayOperators.map(op => (
                                <SelectItem key={op.id} value={op.id}>
                                  {op.name}
                                  {currentOperator?.id === op.id && !eligibleOperators.find(e => e.id === op.id) && (
                                    <span className="text-muted-foreground ml-1">(current)</span>
                                  )}
                                </SelectItem>
                              ))
                            ) : (
                              <div className="px-2 py-1.5 text-sm text-muted-foreground">No operators for this type</div>
                            )}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            setDayWorkerSelections(dayWorkerSelections.filter((_, i) => i !== idx));
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
                
                {/* Add worker button */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Default to first required worker type if available, otherwise first worker type
                    const defaultType = editingJob.worker_requirements?.[0]?.worker_type_id || workerTypes[0]?.id || '';
                    setDayWorkerSelections([
                      ...dayWorkerSelections,
                      { worker_type_id: defaultType, operator_id: '' }
                    ]);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Worker
                </Button>
              </div>
            )}

            {/* Date fields - Hidden when editing a specific day of multi-day job */}
            {!(editingJob && jobEditMode === 'day' && editingJobDate) && (
              <div className={`grid gap-4 ${jobForm.hire_type === 'dry' ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="label-overline">Start Date <span className="text-red-500">*</span></Label>
                    <DatePicker
                      value={jobForm.start_date}
                      onChange={(date) => setJobForm(prev => ({ ...prev, start_date: date }))}
                      formatDisplay={formatDateDisplay}
                      className="w-full"
                      testId="job-start-date"
                    />
                  </div>
                  {jobForm.hire_type === 'dry' ? (
                    <div className="space-y-2">
                      <Label className="label-overline">
                        Expected Off-Hire Date
                        <span className="text-muted-foreground normal-case ml-1.5">(optional)</span>
                      </Label>
                      <DatePicker
                        value={jobForm.expected_off_hire_date || ''}
                        onChange={(date) => setJobForm(prev => ({ ...prev, expected_off_hire_date: date || null }))}
                        formatDisplay={formatDateDisplay}
                        minDate={jobForm.start_date}
                        className="w-full"
                        testId="expected-off-hire-date-input"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Indicative return date — drives "Overdue" follow-ups. The hire stays open until you mark it Off-Hired (which sets the actual end date automatically).
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="label-overline">End Date</Label>
                      <DatePicker
                        value={jobForm.end_date}
                        onChange={(date) => setJobForm(prev => ({ ...prev, end_date: date }))}
                        formatDisplay={formatDateDisplay}
                        minDate={jobForm.start_date}
                        className="w-full"
                        testId="job-end-date"
                      />
                    </div>
                  )}
                  {editingJob && (editingJob.excluded_dates?.length > 0) && (
                    <div className="space-y-2" data-testid="excluded-dates-section">
                      <Label className="label-overline">Excluded Days</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {editingJob.excluded_dates.map(d => (
                          <Badge key={d} variant="secondary" className="text-xs gap-1 pl-2 pr-1 py-0.5">
                            {formatDateDisplay(d)}
                            <button
                              type="button"
                              onClick={() => handleReaddDate(editingJob.id, d)}
                              className="ml-0.5 hover:text-foreground rounded-sm"
                              title="Re-add this day"
                              data-testid={`readd-date-${d}`}
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {tenantSettings.depots_enabled && (
                <div className="space-y-2 mb-4">
                  <Label className="label-overline">Depot</Label>
                  <Select
                    value={jobForm.depot_id || '__none__'}
                    onValueChange={(value) => setJobForm(prev => ({ ...prev, depot_id: value === '__none__' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="job-depot-select"><SelectValue placeholder="Select depot" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No depot</SelectItem>
                      {depots.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}
                {jobForm.hire_type !== 'dry' && (
                <div className="space-y-4">
                  {tenantSettings.depot_time_enabled && (
                  <div className="space-y-2">
                    <Label className="label-overline">Depot Time</Label>
                    <div className="flex gap-2">
                      <Select 
                        value={jobForm.depot_time?.split(':')[0] || '06'} 
                        onValueChange={(hour) => {
                          setJobForm(prev => {
                            const currentMinute = prev.depot_time?.split(':')[1] || '00';
                            return { ...prev, depot_time: `${hour}:${currentMinute}` };
                          });
                        }}
                        data-testid="depot-time-hour"
                      >
                        <SelectTrigger className="w-20 font-mono" data-testid="depot-time-hour-trigger">
                          <SelectValue placeholder="HH" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(hour => (
                            <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="flex items-center text-lg font-bold">:</span>
                      <Select 
                        value={jobForm.depot_time?.split(':')[1] || '00'} 
                        onValueChange={(minute) => {
                          setJobForm(prev => {
                            const currentHour = prev.depot_time?.split(':')[0] || '06';
                            return { ...prev, depot_time: `${currentHour}:${minute}` };
                          });
                        }}
                        data-testid="depot-time-minute"
                      >
                        <SelectTrigger className="w-20 font-mono" data-testid="depot-time-minute-trigger">
                          <SelectValue placeholder="MM" />
                        </SelectTrigger>
                        <SelectContent>
                          {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(minute => (
                            <SelectItem key={minute} value={minute}>{minute}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  )}
                  <div className="space-y-2">
                    <Label className="label-overline">Start Time <span className="text-red-500">*</span></Label>
                  <div className="flex gap-2">
                    <TimeField
                      value={jobForm.start_time || '07:00'}
                      onChange={(v) => setJobForm(prev => ({ ...prev, start_time: v || '07:00' }))}
                      testId="job-start-time"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Est. Duration{tenantSettings.field_rules?.require_est_duration?.enabled ? ' *' : ''}</Label>
                  <Select 
                    value={jobForm.est_duration?.toString() || 'none'} 
                    onValueChange={(value) => {
                      const dur = value === 'none' ? '' : parseFloat(value);
                      setJobForm(prev => {
                        const updates = { ...prev, est_duration: dur };
                        // Auto-calculate end_time from start_time + duration
                        if (dur && prev.start_time) {
                          const [h, m] = prev.start_time.split(':').map(Number);
                          const dH = Math.floor(dur);
                          const dM = (dur % 1) * 60;
                          let eH = h + dH;
                          let eM = m + dM;
                          if (eM >= 60) { eH += 1; eM -= 60; }
                          if (eH >= 24) eH -= 24;
                          updates.end_time = `${eH.toString().padStart(2, '0')}:${Math.round(eM).toString().padStart(2, '0')}`;
                        }
                        return updates;
                      });
                    }}
                  >
                    <SelectTrigger className="font-mono">
                      <SelectValue placeholder="Select hours" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-</SelectItem>
                      {[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12].map(hours => (
                        <SelectItem key={hours} value={hours.toString()}>{hours} {hours === 1 ? 'hour' : 'hours'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* End Time - always editable */}
                <div className="space-y-2">
                  <Label className="label-overline">End Time</Label>
                  <div className="flex items-center gap-1">
                    <TimeField
                      value={jobForm.end_time || ''}
                      allowNone
                      testId="job-end-time"
                      onChange={(v) => {
                        setJobForm(prev => {
                          if (!v) return { ...prev, end_time: '', est_duration: '' };
                          const updates = { ...prev, end_time: v };
                          if (prev.start_time) {
                            const [sH, sM] = prev.start_time.split(':').map(Number);
                            const [eH, eM] = v.split(':').map(Number);
                            let diff = (eH * 60 + eM) - (sH * 60 + sM);
                            if (diff < 0) diff += 24 * 60;
                            const durHours = Math.round(diff / 30) * 0.5;
                            updates.est_duration = (durHours > 0 && durHours <= 12) ? durHours : '';
                          }
                          return updates;
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
              )}
            </div>
            )}

            {/* Description, Notes, Tags - Hidden in day edit mode */}
            {!(editingJob && jobEditMode === 'day' && editingJobDate) && (
              <>
            <div className="space-y-2">
              <Label className="label-overline">
                Description <span className="text-red-500">*</span>
                {editingJob && isMultiDayJob(editingJob) && (
                  <span className="text-xs text-muted-foreground font-normal ml-2">
                    (Applies to entire job)
                  </span>
                )}
              </Label>
              <AutoResizeTextarea
                value={jobForm.description}
                onChange={(e) => setJobForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Job description..."
                minRows={2}
                required
              />
            </div>

            {!editingJob && (
              <div className="space-y-2" data-testid="job-quantity-section">
                <Label className="label-overline">Number of jobs to create</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={jobForm.job_quantity}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value, 10);
                    const val = Number.isNaN(raw) ? '' : Math.min(Math.max(raw, 1), 20);
                    setJobForm(prev => ({ ...prev, job_quantity: val }));
                  }}
                  onBlur={() => setJobForm(prev => ({ ...prev, job_quantity: Math.min(Math.max(parseInt(prev.job_quantity, 10) || 1, 1), 20) }))}
                  className="w-24"
                  data-testid="job-quantity-input"
                />
                <p className="text-xs text-muted-foreground">Creates this many identical jobs (e.g. 3 × 100t cranes). Max 20.</p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="label-overline">Notes</Label>
              <Textarea
                value={jobForm.notes}
                onChange={(e) => setJobForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            {/* Tags/Labels with Autocomplete */}
            <div className="space-y-2">
              <Label className="label-overline">Tags / Labels</Label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagInputKeyDown}
                      onFocus={() => setTagInput(tagInput)} // Trigger re-render to show suggestions
                      placeholder="Type to search or add new tag..."
                      disabled={editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced')}
                      list="tag-suggestions"
                    />
                    {/* Datalist for native browser autocomplete */}
                    <datalist id="tag-suggestions">
                      {availableTags
                        .filter(tag => {
                          const tagText = typeof tag === 'string' ? tag : (tag?.name || tag?.id || '');
                          return !jobForm.tags.includes(tagText);
                        })
                        .map((tag, index) => {
                          const tagText = typeof tag === 'string' ? tag : (tag?.name || tag?.id || '');
                          return <option key={`tag-${tagText}`} value={tagText} />;
                        })
                      }
                    </datalist>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddTag}
                    disabled={!tagInput.trim() || (editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced'))}
                    title="Add new tag"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {/* Show popular tags as quick-add buttons */}
                {availableTags.length > 0 && jobForm.tags.length === 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Popular tags:</p>
                    <div className="flex flex-wrap gap-2">
                      {availableTags.slice(0, 5).map((tag, index) => {
                        const tagText = typeof tag === 'string' ? tag : (tag?.name || tag?.id || String(tag));
                        return (
                          <Button
                            key={`tag-${typeof tag === "string" ? tag : tag?.name || index}`}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setJobForm(prev => ({ ...prev, tags: [...prev.tags, tagText] }));
                            }}
                            className="h-7 text-xs"
                          >
                            + {tagText}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {jobForm.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {jobForm.tags.map((tag, index) => {
                    // Ensure tag is a string (handle object format)
                    const tagText = typeof tag === 'string' ? tag : (tag?.name || tag?.id || String(tag));
                    return (
                      <Badge key={`tb-${typeof tag === "string" ? tag : tag?.name || index}`} variant="secondary" className="pl-2 pr-1 py-1">
                        {tagText}
                        {(!editingJob || (editingJob.status !== 'docketed' && editingJob.status !== 'invoiced')) && (
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="ml-1 hover:bg-muted rounded-full p-0.5"
                          >
                            <XCircle className="h-3 w-3" />
                          </button>
                        )}
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>
              </>
            )}

            {/* Night Shift & TBC Toggles - Hidden in day edit mode */}
            {!(editingJob && jobEditMode === 'day' && editingJobDate) && (
              <div className="flex items-center gap-6 pt-1" data-testid="job-flags-section">
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_night_shift"
                    checked={jobForm.is_night_shift}
                    onCheckedChange={(checked) => setJobForm(prev => ({ ...prev, is_night_shift: checked }))}
                    data-testid="night-shift-toggle"
                  />
                  <Label htmlFor="is_night_shift" className="flex items-center gap-1.5 cursor-pointer text-sm">
                    <Moon className="h-3.5 w-3.5 text-indigo-400" />
                    Night Shift
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_tbc"
                    checked={jobForm.is_tbc}
                    onCheckedChange={(checked) => setJobForm(prev => ({ ...prev, is_tbc: checked }))}
                    disabled={editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced')}
                    data-testid="tbc-toggle"
                  />
                  <Label htmlFor="is_tbc" className="cursor-pointer text-sm text-zinc-400">
                    To Be Confirmed (TBC)
                  </Label>
                </div>
              </div>
            )}

            {/* ── Make Recurring toggle — only for new job creation ── */}
            {!editingJob && (
              <div className="space-y-3 pt-1">
                <div className="flex items-center gap-2">
                  <Switch
                    id="schedule-recurring"
                    checked={isRecurring}
                    onCheckedChange={(v) => { setIsRecurring(v); if (!v) setRecurrenceConfig(null); }}
                    data-testid="schedule-recurring-toggle"
                  />
                  <Label htmlFor="schedule-recurring" className="flex items-center gap-1.5 cursor-pointer text-sm">
                    <Repeat className="h-3.5 w-3.5 text-amber-400" /> Make Recurring
                  </Label>
                </div>
                {isRecurring && (
                  <RecurrenceConfig
                    startDate={jobForm.start_date}
                    onConfigChange={setRecurrenceConfig}
                    api={api}
                    formatDateDisplay={(d) => {
                      try { return format(new Date(d + 'T00:00:00'), tenantSettings?.date_format === 'MM/DD/YYYY' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'); }
                      catch { return d; }
                    }}
                  />
                )}
              </div>
            )}

            {/* File Attachments - Only for new job creation */}
            {!editingJob && (
              <div className="space-y-2" data-testid="create-job-files-section">
                <Label className="label-overline">Attachments</Label>
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    pendingFiles.length > 0 ? 'border-muted-foreground/40' : 'border-muted-foreground/20'
                  }`}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const dropped = Array.from(e.dataTransfer.files);
                    if (dropped.length) {
                      setPendingFiles(prev => [...prev, ...dropped.map(f => ({ file: f, visibleToOperators: pendingFilesVisible }))]);
                    }
                  }}
                  data-testid="create-job-file-drop-zone"
                >
                  <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
                  <p className="text-xs text-muted-foreground">Drag & drop or click to attach files</p>
                  <div className="flex items-center justify-center gap-3 mt-2">
                    <input
                      ref={pendingFileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const selected = Array.from(e.target.files);
                        if (selected.length) {
                          setPendingFiles(prev => [...prev, ...selected.map(f => ({ file: f, visibleToOperators: pendingFilesVisible }))]);
                        }
                        e.target.value = '';
                      }}
                      data-testid="create-job-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => pendingFileInputRef.current?.click()}
                      className="text-xs h-7"
                      data-testid="create-job-choose-files-btn"
                    >
                      <Upload className="h-3 w-3 mr-1" /> Choose Files
                    </Button>
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pendingFilesVisible}
                        onChange={(e) => setPendingFilesVisible(e.target.checked)}
                        className="rounded border-muted-foreground/30"
                      />
                      Visible to operators
                    </label>
                  </div>
                </div>
                {pendingFiles.length > 0 && (
                  <div className="space-y-1">
                    {pendingFiles.map((pf, idx) => (
                      <div key={`pf-${pf.file.name}-${idx}`} className="flex items-center gap-2 px-2 py-1.5 border rounded text-xs bg-muted/30">
                        <FileIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="flex-1 truncate">{pf.file.name}</span>
                        <span className="text-muted-foreground shrink-0">
                          {pf.file.size < 1024 * 1024
                            ? `${(pf.file.size / 1024).toFixed(0)} KB`
                            : `${(pf.file.size / (1024 * 1024)).toFixed(1)} MB`}
                        </span>
                        <button
                          type="button"
                          title={pf.visibleToOperators ? 'Visible to operators — click to hide' : 'Hidden from operators — click to show'}
                          onClick={() => setPendingFiles(prev => prev.map((f, i) => i === idx ? { ...f, visibleToOperators: !f.visibleToOperators } : f))}
                          className={`p-0.5 rounded transition-colors ${pf.visibleToOperators ? 'text-green-500 hover:text-green-400' : 'text-muted-foreground/40 hover:text-muted-foreground'}`}
                          data-testid={`toggle-pending-visibility-${idx}`}
                        >
                          {pf.visibleToOperators ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
                          className="text-muted-foreground hover:text-destructive p-0.5"
                          data-testid={`remove-pending-file-${idx}`}
                        >
                          <XCircle className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <SheetFooter className="pt-4 border-t mt-6">
              <Button type="button" variant="outline" onClick={() => setJobDialogOpen(false)} onMouseDown={() => document.activeElement?.blur()}>
                {editingJob && (editingJob.status === 'docketed' || editingJob.status === 'invoiced') ? 'Close' : 'Cancel'}
              </Button>
              {(!editingJob || (editingJob.status !== 'docketed' && editingJob.status !== 'invoiced')) && (
                <>
                  {/* Update Day button when editing specific day */}
                  {editingJob && jobEditMode === 'day' && editingJobDate && (
                    <Button 
                      type="button" 
                      className="btn-volt" 
                      data-testid="save-day-btn" 
                      onClick={async () => {
                        try {
                          // Save day work description
                          await saveDayWorkDescription(editingJob.id, editingJobDate, dayWorkDescription);
                          
                          // Crew on multi-day jobs are owned by allocation blocks — never edited here.
                          // Tower crane jobs keep the per-day crew editor.
                          if (!isBlockCrewReadOnly(editingJob)) {
                          // Save worker allocations for this day
                          // First, get existing allocations for this day
                          const existingDayAllocs = workerAllocations.filter(wa => 
                            wa.job_id === editingJob.id && 
                            wa.start_date <= editingJobDate && 
                            (wa.end_date || wa.start_date) >= editingJobDate
                          );
                          
                          // Delete removed allocations
                          for (const existing of existingDayAllocs) {
                            const stillExists = dayWorkerSelections.find(s => s.id === existing.id);
                            if (!stillExists) {
                              try {
                                await api.delete(`/worker-allocations/${existing.id}`);
                              } catch (err) {
                                console.error('Failed to delete worker allocation:', err);
                              }
                            }
                          }
                          
                          // Create/update allocations
                          for (const selection of dayWorkerSelections) {
                            if (selection.operator_id && selection.worker_type_id) {
                              if (selection.id) {
                                // Update existing
                                try {
                                  await api.patch(`/worker-allocations/${selection.id}`, {
                                    operator_id: selection.operator_id,
                                    worker_type_id: selection.worker_type_id
                                  });
                                } catch (err) {
                                  const lb = getLeaveBlock(err);
                                  if (lb) { toast.error(lb.message || 'Worker is on leave'); }
                                  else { console.error('Failed to update worker allocation:', err); }
                                }
                              } else {
                                // Create new
                                try {
                                  await api.post('/worker-allocations', {
                                    job_id: editingJob.id,
                                    worker_type_id: selection.worker_type_id,
                                    operator_id: selection.operator_id,
                                    start_date: editingJobDate,
                                    end_date: editingJobDate
                                  });
                                } catch (err) {
                                  const lb = getLeaveBlock(err);
                                  if (lb) { toast.error(lb.message || 'Worker is on leave'); }
                                  else { console.error('Failed to create worker allocation:', err); }
                                }
                              }
                            }
                          }
                          }
                          
                          // Refresh data
                          fetchData();
                          toast.success('Day updated successfully');
                          setJobDialogOpen(false);
                        } catch (error) {
                          console.error('Failed to update day:', error);
                          toast.error('Failed to update day');
                        }
                      }}
                    >
                      Update Day
                    </Button>
                  )}
                  {/* Regular Update/Create button for overall job editing */}
                  {!(editingJob && jobEditMode === 'day' && editingJobDate) && (
                    <Button type="submit" className="btn-volt" data-testid="save-job-btn" onMouseDown={() => document.activeElement?.blur()}>{editingJob ? 'Update' : 'Create'} Job</Button>
                  )}
                </>
              )}
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* EDIT MODE PROMPT FOR MULTI-DAY JOBS */}
      {/* Add new site contact (from Job form) */}
      <Dialog open={addSiteContactOpen} onOpenChange={setAddSiteContactOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-site-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Site Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input value={newSiteContact.name} onChange={(e) => setNewSiteContact({ ...newSiteContact, name: e.target.value })} placeholder="Contact name" data-testid="new-site-contact-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Job Title</Label>
              <Input value={newSiteContact.job_title} onChange={(e) => setNewSiteContact({ ...newSiteContact, job_title: e.target.value })} placeholder="e.g. Site Manager" data-testid="new-site-contact-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input value={newSiteContact.phone} onChange={(e) => setNewSiteContact({ ...newSiteContact, phone: e.target.value })} placeholder="Phone" data-testid="new-site-contact-phone" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={newSiteContact.email} onChange={(e) => setNewSiteContact({ ...newSiteContact, email: e.target.value })} placeholder="Email" data-testid="new-site-contact-email" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddSiteContactOpen(false)} data-testid="cancel-new-site-contact">Cancel</Button>
            <Button onClick={handleCreateSiteContact} disabled={savingSiteContact || !newSiteContact.name.trim()} data-testid="save-new-site-contact">
              {savingSiteContact ? 'Adding...' : 'Add Contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editModePromptOpen} onOpenChange={setEditModePromptOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-volt" />
              Edit Multi-Day Job
            </DialogTitle>
          </DialogHeader>
          {editingJob && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Job #{editingJob.job_number} spans {getJobDates(editingJob).length} days 
                ({formatDateDisplay(editingJob.start_date)} - {formatDateDisplay(editingJob.end_date)}).
              </p>
              <p className="text-sm">What would you like to edit?</p>
              
              <div className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start h-auto py-3"
                  onClick={() => handleEditModeSelection('overall')}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-volt/10 rounded">
                      <CalendarDays className="h-5 w-5 text-volt" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium">Edit Overall Job</div>
                      <div className="text-xs text-muted-foreground">
                        Customer, equipment, dates, description
                      </div>
                    </div>
                  </div>
                </Button>
                
                <div className="text-xs text-muted-foreground text-center py-1">or edit a specific day:</div>
                
                <div className="max-h-48 overflow-y-auto space-y-2" id="day-selection-list">
                  {getJobDates(editingJob).map((dateStr, idx) => {
                    const isSelected = suggestedEditDate === dateStr;
                    return (
                      <Button 
                        key={dateStr}
                        id={isSelected ? 'suggested-day-btn' : undefined}
                        variant={isSelected ? 'default' : 'outline'}
                        className={`w-full justify-start h-auto py-2 ${isSelected ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500' : ''}`}
                        onClick={() => handleEditModeSelection('day', dateStr)}
                        ref={isSelected ? (el) => {
                          // Auto-scroll to the selected day
                          if (el) {
                            setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
                          }
                        } : undefined}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded ${isSelected ? 'bg-white/20' : 'bg-amber-500/10'}`}>
                            <Calendar className={`h-4 w-4 ${isSelected ? 'text-white' : 'text-amber-500'}`} />
                          </div>
                          <div className="text-left">
                            <div className="text-sm flex items-center gap-2">
                              Day {idx + 1} - {formatDateDisplay(dateStr)}
                              {isSelected && <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded">Selected</span>}
                            </div>
                            <div className={`text-xs ${isSelected ? 'text-white/80' : 'text-muted-foreground'}`}>
                              Day Work Description only
                            </div>
                          </div>
                        </div>
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* MULTI-DAY ALLOCATION MANAGEMENT DIALOG */}
      <Dialog open={multiDayDrawerOpen} onOpenChange={(open) => {
        setMultiDayDrawerOpen(open);
        if (!open) {
          setEditingMultiDayAllocation(null);
          setMultiDayJob(null);
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-volt" />
              Multi-Day Job Allocations
            </DialogTitle>
          </DialogHeader>
          
          {multiDayJob && (
            <div className="space-y-4 mt-4">
              {/* Job Info */}
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{getCustomerInfo(multiDayJob.customer_id)?.company_name}</p>
                <p className="text-sm text-muted-foreground">{multiDayJob.description}</p>
                <div className="flex items-center gap-2 mt-2 text-sm">
                  <Calendar className="h-4 w-4 text-volt" />
                  <span>{formatDateDisplay(multiDayJob.start_date)} - {formatDateDisplay(multiDayJob.end_date)}</span>
                </div>
              </div>
              
              {/* Coverage Stats */}
              {multiDayStats && (
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Coverage</span>
                    <span className="text-sm">
                      <span className="text-green-600">{multiDayStats.confirmed_days}</span>
                      {multiDayStats.draft_days > 0 && (
                        <span className="text-amber-600"> + {multiDayStats.draft_days} draft</span>
                      )}
                      <span className="text-muted-foreground"> / {multiDayStats.total_days} days</span>
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                    <div className="h-full flex">
                      <div 
                        className="bg-green-500 h-full" 
                        style={{ width: `${(multiDayStats.confirmed_days / multiDayStats.total_days) * 100}%` }}
                      />
                      <div 
                        className="bg-amber-500 h-full" 
                        style={{ width: `${(multiDayStats.draft_days / multiDayStats.total_days) * 100}%` }}
                      />
                    </div>
                  </div>
                  {multiDayStats.next_unallocated_date && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Next unallocated: {formatDateDisplay(multiDayStats.next_unallocated_date)}
                    </p>
                  )}
                </div>
              )}
              
              {/* Allocations List */}
              {!editingMultiDayAllocation ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">Allocations</h3>
                    <Button size="sm" onClick={handleAddMultiDayAllocation} className="btn-volt" onMouseDown={() => document.activeElement?.blur()}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Allocation
                    </Button>
                  </div>
                  
                  {loadingMultiDay ? (
                    <div className="text-center py-8 text-muted-foreground">Loading...</div>
                  ) : multiDayAllocations.length === 0 ? (
                    <div className="text-center py-8 border rounded-lg border-dashed">
                      <Calendar className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No allocations yet</p>
                      <Button size="sm" variant="outline" className="mt-2" onClick={handleAddMultiDayAllocation}>
                        Add First Allocation
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {multiDayAllocations.map((alloc) => (
                        <React.Fragment key={alloc.id}>
                        {renderUnlinkedCrewNote(alloc)}
                        <div 
                          className={`border rounded-lg p-3 ${
                            alloc.status === 'draft' 
                              ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20' 
                              : 'border-green-500 bg-green-50 dark:bg-green-950/20'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant={alloc.status === 'draft' ? 'warning' : 'success'}>
                                  {alloc.status === 'draft' ? '🟡 DRAFT' : '🟢 CONFIRMED'}
                                </Badge>
                                <span className="text-sm font-medium">
                                  {formatDateDisplay(alloc.start_date)}
                                  {alloc.end_date && alloc.end_date !== alloc.start_date && (
                                    <> - {formatDateDisplay(alloc.end_date)}</>
                                  )}
                                </span>
                              </div>
                              <div className="text-sm space-y-0.5">
                                <div className="flex items-center gap-1">
                                  <User className="h-3 w-3 text-muted-foreground" />
                                  {alloc.operator_name} ({alloc.operator_code})
                                </div>
                                <div className="flex items-center gap-1">
                                  <Truck className="h-3 w-3 text-muted-foreground" />
                                  {alloc.equipment_name} ({alloc.equipment_asset_id})
                                </div>
                                {alloc.worker_allocations?.length > 0 && (
                                  <div className="flex items-center gap-1">
                                    <Users className="h-3 w-3 text-muted-foreground" />
                                    {alloc.worker_allocations.map(w => 
                                      `${w.worker_type_name}${w.operator_name ? ` - ${w.operator_name}` : ''}`
                                    ).join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditMultiDayAllocation(alloc)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                {alloc.status === 'draft' ? (
                                  <DropdownMenuItem onClick={() => handleConfirmAllocation(alloc.id)}>
                                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                                    Confirm
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => handleRevertToDraft(alloc.id)}>
                                    <XCircle className="h-4 w-4 mr-2 text-amber-600" />
                                    Revert to Draft
                                  </DropdownMenuItem>
                                )}
                                {multiDayStats?.next_unallocated_date && (
                                  <DropdownMenuItem onClick={() => handleCopyAllocation(alloc.id)}>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Copy to Next Period
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteMultiDayAllocation(alloc.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  
                  {/* Unallocated period notice */}
                  {multiDayStats && multiDayStats.unallocated_days > 0 && (
                    <div className="border border-dashed border-amber-500 rounded-lg p-3 bg-amber-50 dark:bg-amber-950/20">
                      <div className="flex items-center gap-2 text-amber-600">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          {multiDayStats.unallocated_days} day{multiDayStats.unallocated_days !== 1 ? 's' : ''} unallocated
                        </span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="mt-2 border-amber-500 text-amber-600 hover:bg-amber-100"
                        onClick={handleAddMultiDayAllocation}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Allocation
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                /* Allocation Edit Form */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">
                      {editingMultiDayAllocation === 'new' ? 'Add Allocation' : 'Edit Allocation'}
                    </h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setEditingMultiDayAllocation(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Start Date <span className="text-red-500">*</span></Label>
                      <DatePicker
                        value={multiDayAllocationForm.start_date}
                        onChange={(date) => setMultiDayAllocationForm(prev => ({ 
                          ...prev, 
                          start_date: date 
                        }))}
                        formatDisplay={formatDateDisplay}
                        minDate={multiDayJob?.start_date}
                        maxDate={multiDayJob?.end_date}
                        className="w-full"
                        testId="multi-day-alloc-start-date"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>End Date</Label>
                      <DatePicker
                        value={multiDayAllocationForm.end_date}
                        onChange={(date) => setMultiDayAllocationForm(prev => ({ 
                          ...prev, 
                          end_date: date 
                        }))}
                        formatDisplay={formatDateDisplay}
                        minDate={multiDayAllocationForm.start_date || multiDayJob?.start_date}
                        maxDate={multiDayJob?.end_date}
                        className="w-full"
                        testId="multi-day-alloc-end-date"
                      />
                      <p className="text-xs text-muted-foreground">Leave blank for single day</p>
                    </div>
                  </div>
                  
                  
                  {!multiDayAllocationForm.is_combo_allocation && (
                    <div>
                      <Label>Asset <span className="text-red-500">*</span></Label>
                      {multiDayJob?.equipment_type && (
                        <p className="text-xs text-muted-foreground mb-1">
                          Required type: <span className="font-medium text-foreground">{multiDayJob.equipment_type}</span>
                        </p>
                      )}
                      <Select
                        value={multiDayAllocationForm.equipment_id}
                        onValueChange={(value) => setMultiDayAllocationForm(prev => ({ 
                          ...prev, 
                          equipment_id: value 
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={`Select ${multiDayJob?.equipment_type || 'asset'}`} />
                        </SelectTrigger>
                        <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                          {(() => {
                            const jobEquipmentType = multiDayJob?.equipment_type;
                            
                            // Helper to render equipment item with status pill
                            const renderEquipmentItem = (eq, showType = false) => {
                              const allocStatus = getResourceAllocationStatus(
                                'equipment',
                                eq.id,
                                multiDayAllocationForm.start_date,
                                multiDayAllocationForm.end_date,
                                multiDayJob?.id
                              );
                              return (
                                <SelectItem key={eq.id} value={eq.id} className={showType ? "" : "pl-4"}>
                                  <span className="flex items-center gap-2">
                                    <span className={showType ? "" : "font-medium"}>{eq.asset_id}</span>
                                    {showType && eq.equipment_type && (
                                      <span className="text-muted-foreground text-xs">({eq.equipment_type})</span>
                                    )}
                                    {!showType && eq.equipment_type && (
                                      <span className="text-muted-foreground text-xs">- {eq.equipment_type}</span>
                                    )}
                                    {allocStatus && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                        IN USE
                                      </span>
                                    )}
                                  </span>
                                </SelectItem>
                              );
                            };
                            
                            // If no equipment type specified, show all
                            if (!jobEquipmentType) {
                              return equipment.map((eq) => renderEquipmentItem(eq, true));
                            }
                            
                            // Split equipment into matching and other types
                            const matchingEquipment = equipment.filter(eq => eq.equipment_type === jobEquipmentType);
                            const otherEquipment = equipment.filter(eq => eq.equipment_type !== jobEquipmentType);
                            
                            return (
                              <>
                                {/* Matching Equipment Group */}
                                {matchingEquipment.length > 0 && (
                                  <SelectGroup>
                                    <SelectLabel className="text-xs font-semibold text-[#ffd108] flex items-center gap-1">
                                      <CheckCircle2 className="h-3 w-3" />
                                      {jobEquipmentType} (Recommended)
                                    </SelectLabel>
                                    {matchingEquipment.map((eq) => renderEquipmentItem(eq, false))}
                                  </SelectGroup>
                                )}
                                
                                {/* Other Equipment Group */}
                                {otherEquipment.length > 0 && (
                                  <>
                                    {matchingEquipment.length > 0 && <SelectSeparator />}
                                    <SelectGroup>
                                      <SelectLabel className="text-xs text-muted-foreground">
                                        Other Equipment
                                      </SelectLabel>
                                      {otherEquipment.map((eq) => (
                                        <SelectItem key={eq.id} value={eq.id} className="pl-4">
                                          <span className="flex items-center gap-2">
                                            <span>{eq.asset_id}</span>
                                            <span className="text-muted-foreground text-xs">({eq.equipment_type || 'Unknown type'})</span>
                                            {(() => {
                                              const allocStatus = getResourceAllocationStatus(
                                                'equipment',
                                                eq.id,
                                                multiDayAllocationForm.start_date,
                                                multiDayAllocationForm.end_date,
                                                multiDayJob?.id
                                              );
                                              return allocStatus && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                                  IN USE
                                                </span>
                                              );
                                            })()}
                                          </span>
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </>
                                )}
                                
                                {/* No matching equipment warning */}
                                {matchingEquipment.length === 0 && (
                                  <div className="px-2 py-1.5 text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 rounded">
                                    No {jobEquipmentType} equipment available
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Combo Equipment Selection */}
                  {multiDayAllocationForm.is_combo_allocation && (() => {
                    return (
                    <div className="space-y-3">
                      <Label>Combo Assets — allocate one asset for each component</Label>
                      {multiDayAllocationForm.combo_equipment_ids.map((comp, idx) => {
                        return (
                        <div key={`make-${idx}`} className="flex items-center gap-2">
                          <span className="text-sm min-w-[100px]">
                            {comp.component_type_name}
                            <span className="text-red-500 ml-0.5">*</span>:
                          </span>
                          <Select
                            value={comp.equipment_id}
                            onValueChange={(value) => {
                              const updated = [...multiDayAllocationForm.combo_equipment_ids];
                              updated[idx] = { ...updated[idx], equipment_id: value };
                              setMultiDayAllocationForm(prev => ({ 
                                ...prev, 
                                combo_equipment_ids: updated 
                              }));
                            }}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder={`Select ${comp.component_type_name || 'asset'}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {equipment
                                .filter(eq => eq.equipment_type === comp.component_type_name)
                                .map(eq => (
                                  <SelectItem key={eq.id} value={eq.id}>
                                    <span className="flex items-center gap-2">
                                      <span className="font-medium">{eq.asset_id}</span>
                                      <span className="text-muted-foreground text-xs">({eq.equipment_type})</span>
                                      {eq.supplier_name && <span className="text-muted-foreground text-xs">- {eq.supplier_name}</span>}
                                    </span>
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                        );
                      })}
                    </div>
                    );
                  })()}
                  
                  {/* Primary Worker picker — hidden for dry-hire jobs (asset only) */}
                  {multiDayJob?.hire_type !== 'dry' && (
                  <div>
                    <Label>Primary Worker <span className="text-red-500">*</span></Label>
                    <Select
                      value={multiDayAllocationForm.operator_id}
                      onValueChange={(value) => setMultiDayAllocationForm(prev => ({ 
                        ...prev, 
                        operator_id: value 
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select worker" />
                      </SelectTrigger>
                      <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                        {operators.map(op => {
                          const allocStatus = getResourceAllocationStatus(
                            'operator',
                            op.id,
                            multiDayAllocationForm.start_date,
                            multiDayAllocationForm.end_date,
                            multiDayJob?.id
                          );
                          return (
                            <SelectItem key={op.id} value={op.id}>
                              <span className="flex items-center gap-2">
                                <span>{op.name}</span>
                                {op.supplier_name && <span className="text-muted-foreground text-xs">- {op.supplier_name}</span>}
                                {allocStatus && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                    IN USE
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  )}

                  {/* Worker Allocations */}
                  {multiDayAllocationForm.worker_allocations.length > 0 && (
                    <div className="space-y-2">
                      <Label>Additional Workers</Label>
                      {multiDayAllocationForm.worker_allocations.map((wa, idx) => {
                        const workerType = workerTypes.find(wt => wt.id === wa.worker_type_id);
                        return (
                          <div key={`model-${idx}`} className="flex items-center gap-2">
                            <span className="text-sm min-w-[80px]">{workerType?.name || 'Worker'}:</span>
                            <Select
                              value={wa.operator_id || '__none__'}
                              onValueChange={(value) => {
                                const updated = [...multiDayAllocationForm.worker_allocations];
                                updated[idx] = { ...updated[idx], operator_id: value === '__none__' ? '' : value };
                                setMultiDayAllocationForm(prev => ({ 
                                  ...prev, 
                                  worker_allocations: updated 
                                }));
                              }}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue placeholder="Select worker" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">Not assigned</SelectItem>
                                {operators.map(op => {
                                  const allocStatus = getResourceAllocationStatus(
                                    'operator',
                                    op.id,
                                    multiDayAllocationForm.start_date,
                                    multiDayAllocationForm.end_date,
                                    multiDayJob?.id
                                  );
                                  return (
                                    <SelectItem key={op.id} value={op.id}>
                                      <span className="flex items-center gap-2">
                                        <span>{op.name}</span>
                                        {op.supplier_name && <span className="text-muted-foreground text-xs">- {op.supplier_name}</span>}
                                        {allocStatus && (
                                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                            IN USE
                                          </span>
                                        )}
                                      </span>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Status Selection */}
                  <div className="border rounded-lg p-3 space-y-2">
                    <Label>Save as:</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="allocation_status"
                          value="draft"
                          checked={multiDayAllocationForm.status === 'draft'}
                          onChange={() => setMultiDayAllocationForm(prev => ({ ...prev, status: 'draft' }))}
                          className="text-amber-500"
                        />
                        <span className="text-sm">
                          <span className="font-medium">Draft</span>
                          <span className="text-muted-foreground"> - operator will not see</span>
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="allocation_status"
                          value="confirmed"
                          checked={multiDayAllocationForm.status === 'confirmed'}
                          onChange={() => setMultiDayAllocationForm(prev => ({ ...prev, status: 'confirmed' }))}
                          className="text-green-500"
                        />
                        <span className="text-sm">
                          <span className="font-medium">Confirmed</span>
                          <span className="text-muted-foreground"> - operator will see</span>
                        </span>
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setEditingMultiDayAllocation(null)}
                      onMouseDown={() => document.activeElement?.blur()}
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="flex-1 btn-volt"
                      onClick={handleSaveMultiDayAllocation}
                      disabled={
                        // Dry-hire jobs require equipment but NOT an operator.
                        (selectedJobForAllocation?.hire_type !== 'dry' && !multiDayAllocationForm.operator_id) ||
                        !multiDayAllocationForm.start_date
                      }
                      onMouseDown={() => document.activeElement?.blur()}
                    >
                      {editingMultiDayAllocation === 'new' ? 'Add Allocation' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ALLOCATION DIALOG */}
      <Dialog open={allocationDialogOpen} onOpenChange={setAllocationDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedJobForAllocation?.status === 'docketed' || selectedJobForAllocation?.status === 'invoiced' 
                ? 'View Allocation' 
                : (allocationForm.equipment_id || allocationForm.operator_id) ? 'Edit Allocation' : 'Allocate Resources'}
            </DialogTitle>
          </DialogHeader>
          {selectedJobForAllocation && (
            <div className="space-y-4">
              {/* Read-only notice for docketed/invoiced jobs */}
              {(selectedJobForAllocation.status === 'docketed' || selectedJobForAllocation.status === 'invoiced') && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    <AlertCircle className="h-4 w-4 inline mr-1" />
                    This job has been docketed. Allocations cannot be modified to maintain data accuracy.
                  </p>
                </div>
              )}
              <div className="p-3 bg-muted rounded-sm">
                <p className="text-sm">
                  <span className="font-medium">{getCustomerInfo(selectedJobForAllocation.customer_id)?.company_name}</span>
                  <br />
                  <span className="text-muted-foreground">{selectedJobForAllocation.description}</span>
                </p>
              </div>

              {/* Multi-Asset Slot Selector — shown when job has multiple asset requirements */}
              {selectedJobForAllocation?.asset_requirements?.length > 0 && (
                <div className="border rounded-lg overflow-hidden" data-testid="multi-asset-slots">
                  <div className="px-3 py-2 bg-muted/50 border-b">
                    <p className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
                      <Truck className="h-3.5 w-3.5" />
                      Asset Slots ({(() => {
                        const totalSlots = selectedJobForAllocation.asset_requirements.reduce((s, r) => s + (r.quantity || 1), 0);
                        const filledSlots = allocations.filter(a => a.job_id === selectedJobForAllocation.id && a.asset_requirement_id).length;
                        return `${filledSlots}/${totalSlots} allocated`;
                      })()})
                    </p>
                  </div>
                  <div className="divide-y">
                    {selectedJobForAllocation.asset_requirements.flatMap((req, reqIdx) =>
                      Array.from({ length: req.quantity || 1 }, (_, instanceIdx) => {
                        const slotKey = `${req.id}-${instanceIdx}`;
                        const existingAlloc = allocations.find(
                          a => a.job_id === selectedJobForAllocation.id &&
                               a.asset_requirement_id === req.id &&
                               (a.asset_requirement_index ?? 0) === instanceIdx
                        );
                        const isSelected = allocationForm.asset_requirement_id === req.id &&
                                          (allocationForm.asset_requirement_index ?? 0) === instanceIdx;
                        const eqInfo = existingAlloc ? equipment.find(e => e.id === existingAlloc.equipment_id) : null;
                        const opInfo = existingAlloc ? operators.find(o => o.id === existingAlloc.operator_id) : null;
                        return (
                          <button
                            key={slotKey}
                            type="button"
                            onClick={() => {
                              if (existingAlloc) {
                                // Load existing allocation into form
                                setAllocationForm(prev => ({
                                  ...prev,
                                  equipment_id: existingAlloc.equipment_id || '',
                                  operator_id: existingAlloc.operator_id || '',
                                  asset_requirement_id: req.id,
                                  asset_requirement_index: instanceIdx,
                                }));
                              } else {
                                // Fresh slot
                                setAllocationForm(prev => ({
                                  ...prev,
                                  equipment_id: '',
                                  operator_id: '',
                                  asset_requirement_id: req.id,
                                  asset_requirement_index: instanceIdx,
                                }));
                              }
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
                              isSelected
                                ? 'bg-[#ffd108]/10 border-l-2 border-l-[#ffd108]'
                                : 'hover:bg-muted/30'
                            }`}
                            data-testid={`asset-slot-${req.id}-${instanceIdx}`}
                          >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                              existingAlloc ? 'bg-emerald-500/20 text-emerald-400' : 'bg-muted text-muted-foreground'
                            }`}>
                              {reqIdx * (req.quantity || 1) + instanceIdx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">
                                {req.equipment_type}
                                {req.quantity > 1 && <span className="text-muted-foreground ml-1">#{instanceIdx + 1}</span>}
                              </p>
                              {existingAlloc ? (
                                <p className="text-[10px] text-emerald-400 truncate">
                                  {eqInfo?.asset_id || 'Asset'} · {opInfo?.name || 'No operator'}
                                </p>
                              ) : (
                                <p className="text-[10px] text-muted-foreground italic">Not allocated</p>
                              )}
                            </div>
                            {isSelected && (
                              <div className="w-2 h-2 rounded-full bg-[#ffd108]" />
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {/* Smart Operator Suggestions - Only show if AI is enabled */}
              {isAiAvailable() && (
              <div className="border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowSuggestions(!showSuggestions)}
                  className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-[#ffd108]/10 to-transparent hover:from-[#ffd108]/20 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#ffd108]" />
                    <span className="font-heading font-semibold text-sm">Smart Worker Suggestions</span>
                    {operatorSuggestions.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {operatorSuggestions.filter(s => s.meets_requirements && s.is_available).length} qualified
                      </Badge>
                    )}
                  </div>
                  {showSuggestions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                
                {showSuggestions && (
                  <div className="p-3 border-t bg-muted/30">
                    {suggestionsLoading ? (
                      <div className="text-center py-4">
                        <div className="animate-spin h-6 w-6 border-2 border-[#ffd108] border-t-transparent rounded-full mx-auto"></div>
                        <p className="text-sm text-muted-foreground mt-2">Analyzing operators...</p>
                      </div>
                    ) : operatorSuggestions.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No worker suggestions available
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {operatorSuggestions.slice(0, 6).map((suggestion, idx) => (
                          <div 
                            key={suggestion.operator_id}
                            onClick={() => selectSuggestedOperator(suggestion.operator_id)}
                            className={`p-3 rounded-lg border cursor-pointer transition-all ${
                              allocationForm.operator_id === suggestion.operator_id
                                ? 'border-[#ffd108] bg-[#ffd108]/10'
                                : suggestion.meets_requirements && suggestion.is_available
                                  ? 'border-green-500/30 hover:border-green-500 hover:bg-green-500/5'
                                  : 'border-border opacity-60 hover:opacity-100'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3">
                                {/* Rank Circle */}
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                  idx === 0 ? 'bg-[#ffd108] text-black' :
                                  idx === 1 ? 'bg-gray-400 text-black' :
                                  idx === 2 ? 'bg-amber-700 text-white' :
                                  'bg-muted text-muted-foreground'
                                }`}>
                                  {idx + 1}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{suggestion.operator_name}</span>
                                    {suggestion.is_preferred && (
                                      <Star className="h-3 w-3 text-[#ffd108] fill-[#ffd108]" />
                                    )}
                                  </div>
                                  {suggestion.operator_suburb && (
                                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                                      <MapPin className="h-3 w-3" />
                                      {suggestion.operator_suburb} {suggestion.operator_postcode}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-bold text-[#ffd108]">{suggestion.score}</div>
                                <div className="text-xs text-muted-foreground">score</div>
                              </div>
                            </div>
                            
                            {/* Status Indicators */}
                            <div className="flex flex-wrap gap-1 mt-2">
                              {suggestion.is_available ? (
                                <Badge className="bg-green-500/20 text-green-400 text-xs">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />Available
                                </Badge>
                              ) : (
                                <Badge className="bg-red-500/20 text-red-400 text-xs">
                                  <XCircle className="h-3 w-3 mr-1" />Busy
                                </Badge>
                              )}
                              {suggestion.meets_requirements ? (
                                <Badge className="bg-green-500/20 text-green-400 text-xs">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />Qualified
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                  <AlertTriangle className="h-3 w-3 mr-1" />Missing Req
                                </Badge>
                              )}
                              {suggestion.is_preferred && (
                                <Badge className="bg-[#ffd108]/20 text-[#ffd108] text-xs">
                                  <Star className="h-3 w-3 mr-1" />Preferred #{suggestion.preference_priority || '-'}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Reasons */}
                            {(suggestion.reasons.length > 0 || suggestion.warnings.length > 0) && (
                              <div className="mt-2 text-xs space-y-1">
                                {suggestion.reasons.slice(0, 3).map((reason, i) => (
                                  <div key={`added-${i}`} className="text-green-400 flex items-center gap-1">
                                    <CheckCircle2 className="h-3 w-3" />{reason}
                                  </div>
                                ))}
                                {suggestion.warnings.slice(0, 2).map((warning, i) => (
                                  <div key={`removed-${i}`} className="text-amber-400 flex items-center gap-1">
                                    <AlertTriangle className="h-3 w-3" />{warning}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Asset and Operator - hidden for No Asset (labour-only) jobs */}
              {selectedJobForAllocation?.equipment_type === 'No Asset' ? (
                <div className="p-3 bg-muted/50 rounded-lg border border-dashed">
                  <p className="text-sm text-muted-foreground">
                    <Users className="h-4 w-4 inline mr-1" />
                    This is a <span className="font-medium text-foreground">labour-only</span> job. Workers are allocated via the Planning Board or Worker Allocations.
                  </p>
                </div>
              ) : (
              <>
              <div className="space-y-2">
                <Label className="label-overline">Asset</Label>
                
                {/* Regular Equipment Selection (non-combo) */}
                {!allocationForm.is_combo_allocation && (
                  <>
                    {/* Show required equipment type — use the selected asset slot's type for multi-asset, or the job's root type */}
                    {(() => {
                      const activeSlotType = (() => {
                        if (allocationForm.asset_requirement_id && selectedJobForAllocation?.asset_requirements?.length > 0) {
                          const req = selectedJobForAllocation.asset_requirements.find(r => r.id === allocationForm.asset_requirement_id);
                          return req?.equipment_type;
                        }
                        return selectedJobForAllocation?.equipment_type;
                      })();
                      return activeSlotType ? (
                        <p className="text-xs text-muted-foreground mb-1">
                          Required type: <span className="font-medium text-foreground">{activeSlotType}</span>
                        </p>
                      ) : null;
                    })()}
                    <Select value={allocationForm.equipment_id} onValueChange={(v) => handleEquipmentSelectionForAlloc(v)} onOpenChange={(open) => !open && document.activeElement?.blur()}>
                      <SelectTrigger><SelectValue placeholder={`Select ${(() => {
                        if (allocationForm.asset_requirement_id && selectedJobForAllocation?.asset_requirements?.length > 0) {
                          const req = selectedJobForAllocation.asset_requirements.find(r => r.id === allocationForm.asset_requirement_id);
                          return req?.equipment_type || 'asset';
                        }
                        return selectedJobForAllocation?.equipment_type || 'asset';
                      })()}`} /></SelectTrigger>
                      <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                        {(() => {
                          const equipmentList = overrideCheck ? equipment : availableEquipment;
                          // For multi-asset: use the selected slot's equipment type, not the job root
                          const jobEquipmentType = (() => {
                            if (allocationForm.asset_requirement_id && selectedJobForAllocation?.asset_requirements?.length > 0) {
                              const req = selectedJobForAllocation.asset_requirements.find(r => r.id === allocationForm.asset_requirement_id);
                              return req?.equipment_type;
                            }
                            return selectedJobForAllocation?.equipment_type;
                          })();
                          
                          // If no equipment type specified on job, show flat list
                          if (!jobEquipmentType) {
                            return equipmentList.map((eq) => (
                              <SelectItem key={eq.id} value={eq.id}>
                                {eq.asset_id} {eq.equipment_type && `(${eq.equipment_type})`}
                                {overrideCheck && eq.status !== 'available' && (
                                  <span className="ml-2 text-xs text-amber-500">({eq.status})</span>
                                )}
                              </SelectItem>
                            ));
                          }
                          
                          // Split equipment into matching and other types
                          const matchingEquipment = equipmentList.filter(eq => eq.equipment_type === jobEquipmentType);
                          const otherEquipment = equipmentList.filter(eq => eq.equipment_type !== jobEquipmentType);
                          
                          return (
                            <>
                              {/* Matching Equipment Group */}
                              {matchingEquipment.length > 0 && (
                                <SelectGroup>
                                  <SelectLabel className="text-xs font-semibold text-[#ffd108] flex items-center gap-1">
                                    <CheckCircle2 className="h-3 w-3" />
                                    {jobEquipmentType} (Recommended)
                                  </SelectLabel>
                                  {matchingEquipment.map((eq) => (
                                    <SelectItem key={eq.id} value={eq.id} className="pl-4">
                                      <span className="flex items-center gap-2">
                                        <span className="font-medium">{eq.asset_id}</span>
                                        {eq.equipment_type && <span className="text-muted-foreground text-xs">- {eq.equipment_type}</span>}
                                        {eq.supplier_name && <span className="text-muted-foreground text-xs">- {eq.supplier_name}</span>}
                                        {overrideCheck && eq.status !== 'available' && (
                                          <span className="text-xs text-amber-500">({eq.status})</span>
                                        )}
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              )}
                              
                              {/* Other Assets Group */}
                              {otherEquipment.length > 0 && (
                                <>
                                  {matchingEquipment.length > 0 && <SelectSeparator />}
                                  <SelectGroup>
                                    <SelectLabel className="text-xs text-muted-foreground">
                                      Other Assets
                                    </SelectLabel>
                                    {otherEquipment.map((eq) => (
                                      <SelectItem key={eq.id} value={eq.id} className="pl-4">
                                        <span className="flex items-center gap-2">
                                          <span>{eq.asset_id}</span>
                                          <span className="text-muted-foreground text-xs">({eq.equipment_type || 'Unknown type'})</span>
                                          {eq.supplier_name && <span className="text-muted-foreground text-xs">- {eq.supplier_name}</span>}
                                          {overrideCheck && eq.status !== 'available' && (
                                            <span className="text-xs text-amber-500">({eq.status})</span>
                                          )}
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                </>
                              )}
                              
                              {/* No matching equipment warning */}
                              {matchingEquipment.length === 0 && (
                                <div className="px-2 py-1.5 text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 rounded">
                                  No {jobEquipmentType} equipment available
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </SelectContent>
                    </Select>
                    {!overrideCheck && availableEquipment.length === 0 && (
                      <p className="text-xs text-[#FF4F00] flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />No available equipment
                      </p>
                    )}
                  </>
                )}

                {/* Combo line-up (read-only) — shows the member assets a combo asset reserves */}
                {(() => {
                  const sel = allocationForm.equipment_id ? getEquipmentInfo(allocationForm.equipment_id) : null;
                  const members = (sel?.combo_members || []).filter(m => m?.equipment_id);
                  if (members.length === 0) return null;
                  return (
                    <div className="mt-2 p-3 rounded-md border border-purple-300/50 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 space-y-1.5" data-testid="edit-alloc-combo-lineup">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-purple-700 dark:text-purple-300">
                        <Layers className="h-3.5 w-3.5" /> Combo line-up — {members.length} assets reserved
                      </div>
                      {members.map((m, i) => {
                        const memEq = getEquipmentInfo(m.equipment_id);
                        const label = memEq?.asset_id || m.asset_id || memEq?.name || m.component_type_name || 'Asset';
                        return (
                          <div key={m.equipment_id || i} className="flex items-center justify-between text-xs" data-testid={`edit-alloc-combo-member-${i}`}>
                            <span className="text-foreground">{label}</span>
                            {m.component_type_name && <span className="text-muted-foreground">{m.component_type_name}</span>}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Additional Trailer slots — multi-trailer rigs (B-Double / Road Train) */}
                {!allocationForm.is_combo_allocation && (() => {
                  const jobEqType = equipmentTypesWithIds.find(t => t.name === selectedJobForAllocation?.equipment_type);
                  const trailerCount = jobEqType?.requires_prime_mover ? (jobEqType?.trailer_count || 1) : 1;
                  if (trailerCount <= 1) return null;
                  const extraCount = trailerCount - 1; // trailer 1 is the primary equipment_id
                  const equipmentList = overrideCheck ? equipment : availableEquipment;
                  const trailerOptions = equipmentList.filter(eq => eq.equipment_type === selectedJobForAllocation?.equipment_type);
                  const current = allocationForm.additional_trailer_equipment_ids || [];
                  const setTrailerAt = (idx, val) => {
                    const next = [...current];
                    while (next.length < extraCount) next.push('');
                    next[idx] = val;
                    setAllocationForm({ ...allocationForm, additional_trailer_equipment_ids: next });
                  };
                  return (
                    <div className="mt-3 p-3 rounded-md border border-amber-200 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-950/20 space-y-2" data-testid="trailer-slots-section">
                      <Label className="text-xs flex items-center gap-1">
                        <Truck className="h-3 w-3" />
                        {selectedJobForAllocation?.equipment_type} — {trailerCount} trailers
                      </Label>
                      <p className="text-[11px] text-muted-foreground">
                        Trailer 1 is the asset selected above. Allocate the remaining {extraCount} trailer{extraCount > 1 ? 's' : ''} below.
                      </p>
                      {Array.from({ length: extraCount }).map((_, idx) => {
                        const selectedHere = current[idx] || '';
                        const usedElsewhere = [
                          allocationForm.equipment_id,
                          allocationForm.prime_mover_equipment_id,
                          ...current.filter((_, i) => i !== idx),
                        ].filter(Boolean);
                        const opts = trailerOptions.filter(eq => !usedElsewhere.includes(eq.id) || eq.id === selectedHere);
                        return (
                          <div key={idx} className="space-y-1">
                            <Label className="text-[11px] text-muted-foreground">Trailer {idx + 2} <span className="text-red-500">*</span></Label>
                            <Select value={selectedHere} onValueChange={(v) => setTrailerAt(idx, v)}>
                              <SelectTrigger data-testid={`trailer-slot-select-${idx + 2}`}>
                                <SelectValue placeholder={`Select trailer ${idx + 2}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {opts.length === 0 ? (
                                  <div className="px-2 py-1.5 text-xs text-muted-foreground">No available {selectedJobForAllocation?.equipment_type}</div>
                                ) : opts.map(eq => (
                                  <SelectItem key={eq.id} value={eq.id}>
                                    {eq.asset_id} {eq.equipment_type && `(${eq.equipment_type})`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Prime Mover picker — Mobilisation Phase 2.1 */}
                {!allocationForm.is_combo_allocation && (() => {
                  const jobEqType = equipmentTypesWithIds.find(t => t.name === selectedJobForAllocation?.equipment_type);
                  if (!jobEqType?.requires_prime_mover || !jobEqType?.prime_mover_asset_type_id) return null;
                  const pmType = equipmentTypesWithIds.find(t => t.id === jobEqType.prime_mover_asset_type_id);
                  const pmName = pmType?.name || 'Prime Mover';
                  const equipmentList = overrideCheck ? equipment : availableEquipment;
                  const pmOptions = equipmentList.filter(eq => eq.equipment_type === pmName);
                  return (
                    <div className="mt-3 p-3 rounded-md border border-blue-200 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-950/20 space-y-2">
                      <Label className="text-xs flex items-center gap-1">
                        <Truck className="h-3 w-3" />
                        Prime Mover ({pmName}) *
                      </Label>
                      <Select
                        value={allocationForm.prime_mover_equipment_id || ''}
                        onValueChange={(v) => setAllocationForm({ ...allocationForm, prime_mover_equipment_id: v })}
                      >
                        <SelectTrigger data-testid="prime-mover-equipment-select">
                          <SelectValue placeholder={`Select ${pmName}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {pmOptions.length === 0 ? (
                            <div className="px-2 py-1.5 text-xs text-muted-foreground">No available {pmName}</div>
                          ) : pmOptions.map(eq => (
                            <SelectItem key={eq.id} value={eq.id}>
                              {eq.asset_id} {eq.equipment_type && `(${eq.equipment_type})`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-muted-foreground">
                        {selectedJobForAllocation?.equipment_type} requires a {pmName} to operate. Same operator drives the pair.
                      </p>
                    </div>
                  );
                })()}
                
                {/* Combo Equipment Selection */}
                {allocationForm.is_combo_allocation && (() => {
                  const comboEqType = equipmentTypesWithIds.find(t => t.name === selectedJobForAllocation?.equipment_type);

                  return (
                  <div className="space-y-3 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-medium text-purple-700 dark:text-purple-300">
                      <Truck className="h-4 w-4" />
                      Combo Equipment — Allocate one asset for each component
                    </div>
                    
                    {allocationForm.combo_equipment_ids?.length > 0 ? (
                      allocationForm.combo_equipment_ids.map((comboItem, index) => {
                        // Skip rendering if comboItem is invalid
                        if (!comboItem?.component_type_id) return null;
                        
                        // Get equipment matching this component type
                        const equipmentList = overrideCheck ? equipment : availableEquipment;
                        const matchingEquipment = equipmentList.filter(eq => {
                            const eqType = equipmentTypesWithIds.find(t => t.name === eq.equipment_type);
                            return eqType?.id === comboItem.component_type_id;
                          });
                        const otherEquipment = equipmentList.filter(eq => {
                            const eqType = equipmentTypesWithIds.find(t => t.name === eq.equipment_type);
                            return eqType?.id !== comboItem.component_type_id;
                          });
                        
                        // Exclude already selected equipment in other combo slots
                        const selectedInOtherSlots = allocationForm.combo_equipment_ids
                          .filter((_, i) => i !== index)
                          .map(c => c?.equipment_id)
                          .filter(Boolean);
                        
                        const filterSelected = (list) => list.filter(
                          eq => !selectedInOtherSlots.includes(eq.id) || eq.id === comboItem.equipment_id
                        );
                        const filteredMatching = filterSelected(matchingEquipment);
                        const filteredOther = filterSelected(otherEquipment);

                        const renderItem = (eq) => (
                          <SelectItem key={eq.id} value={eq.id}>
                            <span className="flex items-center gap-2">
                              <span className="font-medium">{eq.asset_id}</span>
                              <span className="text-muted-foreground text-xs">({eq.equipment_type})</span>
                              {eq.supplier_name && <span className="text-muted-foreground text-xs">- {eq.supplier_name}</span>}
                              {overrideCheck && eq.status !== 'available' && (
                                <span className="text-xs text-amber-500">({eq.status})</span>
                              )}
                            </span>
                          </SelectItem>
                        );
                        
                        return (
                          <div key={comboItem.component_type_id || index} className="space-y-1">
                            <Label className="text-xs text-purple-600 dark:text-purple-400">
                              {comboItem.component_type_name || 'Unknown Component'}
                              <span className="ml-0.5">*</span>
                            </Label>
                            <Select 
                              value={comboItem.equipment_id || ''} 
                              onValueChange={(v) => {
                                const newComboEquipIds = [...allocationForm.combo_equipment_ids];
                                newComboEquipIds[index] = { ...comboItem, equipment_id: v };
                                setAllocationForm({ ...allocationForm, combo_equipment_ids: newComboEquipIds });
                              }}
                            >
                              <SelectTrigger className="bg-white dark:bg-slate-800">
                                <SelectValue placeholder={`Select ${comboItem.component_type_name || 'asset'}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {filteredMatching.length > 0 && (
                                  <SelectGroup>
                                    <SelectLabel className="text-xs font-semibold text-[#ffd108] flex items-center gap-1">
                                      <CheckCircle2 className="h-3 w-3" />
                                      {comboItem.component_type_name} (Recommended)
                                    </SelectLabel>
                                    {filteredMatching.map(renderItem)}
                                  </SelectGroup>
                                )}
                                {filteredMatching.length === 0 && (
                                  <div className="px-2 py-1.5 text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 rounded">
                                    No {comboItem.component_type_name} available
                                  </div>
                                )}
                                {filteredOther.length > 0 && (
                                  <>
                                    {filteredMatching.length > 0 && <SelectSeparator />}
                                    <SelectGroup>
                                      <SelectLabel className="text-xs text-muted-foreground">Other Assets</SelectLabel>
                                      {filteredOther.map(renderItem)}
                                    </SelectGroup>
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-xs text-amber-600">
                        No combo components configured for this equipment type
                      </p>
                    )}
                    
                    {allocationForm.combo_equipment_ids?.some(c => c && !c.equipment_id) && (
                        <p className="text-xs text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Please select an asset for all required components
                        </p>
                    )}
                  </div>
                  );
                })()}
              </div>

              {/* Attachments Selection - Now between Equipment and Operator */}
              {(() => {
                const jobAttachmentTypeIds = selectedJobForAllocation?.attachment_type_ids || [];
                const filteredAttachments = attachments.filter(a => 
                  (a.status === 'available' || allocationForm.attachment_ids.includes(a.id)) &&
                  (jobAttachmentTypeIds.length === 0 || jobAttachmentTypeIds.includes(a.attachment_type_id))
                );
                
                if (filteredAttachments.length === 0 && jobAttachmentTypeIds.length === 0) return null;
                
                return (
                  <div className="space-y-2">
                    <Label className="label-overline flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      Attachments
                      {jobAttachmentTypeIds.length > 0 && (
                        <span className="text-xs text-muted-foreground font-normal">
                          (Required: {jobAttachmentTypeIds.map(id => getAttachmentTypeName(id)).join(', ')})
                        </span>
                      )}
                    </Label>
                    {filteredAttachments.length > 0 ? (
                      <div className="border border-border rounded-md p-3 max-h-32 overflow-y-auto space-y-2">
                        {filteredAttachments.map((attachment) => (
                          <div key={attachment.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`attachment-${attachment.id}`}
                              checked={allocationForm.attachment_ids.includes(attachment.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setAllocationForm({
                                    ...allocationForm,
                                    attachment_ids: [...allocationForm.attachment_ids, attachment.id]
                                  });
                                } else {
                                  setAllocationForm({
                                    ...allocationForm,
                                    attachment_ids: allocationForm.attachment_ids.filter(id => id !== attachment.id)
                                  });
                                }
                              }}
                            />
                            <label
                              htmlFor={`attachment-${attachment.id}`}
                              className="text-sm cursor-pointer flex-1"
                            >
                              {attachment.name}
                              {attachment.size && <span className="text-muted-foreground ml-1">({attachment.size})</span>}
                            </label>
                            <Badge variant="outline" className="text-xs">{getAttachmentTypeName(attachment.attachment_type_id)}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No attachments available for required types</p>
                    )}
                  </div>
                );
              })()}

              {/* Operator picker — hidden for dry-hire jobs (asset only, no operator needed) */}
              {selectedJobForAllocation?.hire_type !== 'dry' && (
              <div className="space-y-2">
                <Label className="label-overline">Primary Worker</Label>
                <Select value={allocationForm.operator_id} onValueChange={(v) => setAllocationForm({ ...allocationForm, operator_id: v })} onOpenChange={(open) => !open && document.activeElement?.blur()}>
                  <SelectTrigger><SelectValue placeholder="Select worker" /></SelectTrigger>
                  <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                    {/* Show available operators first, then unavailable */}
                    {operators
                      .filter(op => {
                        if (!op.is_available && !overrideCheck) return false;
                        // Hide only workers on approved full-day leave (pending and part-day are shown with badges)
                        const leaveEntries = unavailableWorkers[op.id] || [];
                        const hasApprovedFullDay = leaveEntries.some(l => l.status === 'approved' && l.is_full_day !== false);
                        if (hasApprovedFullDay && !overrideCheck) return false;
                        return true;
                      })
                      .map((op) => {
                      const conflicts = getOperatorConflicts(
                        op.id, 
                        allocationForm.start_date, 
                        allocationForm.end_date,
                        selectedJobForAllocation?.id
                      );
                      const leaveEntries = unavailableWorkers[op.id] || [];
                      const hasApprovedFullDay = leaveEntries.some(l => l.status === 'approved' && l.is_full_day !== false);
                      const hasPending = leaveEntries.some(l => l.status === 'pending');
                      const partDay = leaveEntries.find(l => l.status === 'approved' && l.is_full_day === false);
                      return (
                        <SelectItem key={op.id} value={op.id}>
                          <div className="flex items-center justify-between w-full gap-2">
                            <span>{op.name}</span>
                            {op.supplier_name && <span className="text-muted-foreground text-xs">- {op.supplier_name}</span>}
                            <div className="flex items-center gap-1">
                              {hasApprovedFullDay && (
                                <span className="text-xs text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">
                                  On Leave
                                </span>
                              )}
                              {!hasApprovedFullDay && hasPending && (
                                <span className="text-xs text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded">
                                  Leave requested
                                </span>
                              )}
                              {!hasApprovedFullDay && !hasPending && partDay && (
                                <span className="text-xs text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded">
                                  Part-day leave {partDay.start_time || ''}–{partDay.end_time || ''}
                                </span>
                              )}
                              {conflicts.length > 0 && (
                                <span className="text-xs text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded">
                                  Allocated ({conflicts[0].customer_name})
                                </span>
                              )}
                              {overrideCheck && !op.is_available && (
                                <span className="text-xs text-amber-500">(unavailable)</span>
                              )}
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                    {/* Show unavailable operators with warning if override is off */}
                    {!overrideCheck && operators.filter(op => !op.is_available).length > 0 && availableOperators.length === 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs text-muted-foreground border-t mt-1 pt-2">
                          Unavailable operators (enable Override Check to select):
                        </div>
                        {operators.filter(op => !op.is_available).map((op) => (
                          <div key={op.id} className="px-2 py-1.5 text-sm text-muted-foreground opacity-60">
                            {op.name} <span className="text-xs">(unavailable)</span>
                          </div>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                {!overrideCheck && availableOperators.length === 0 && operators.length > 0 && (
                  <p className="text-xs text-[#FF4F00] flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    All {operators.length} operators are marked as unavailable. Enable "Override Check" below or update operator leave in Operators screen.
                  </p>
                )}
                {!overrideCheck && operators.length === 0 && (
                  <p className="text-xs text-[#FF4F00] flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />No workers found. Create workers in the Workers section.
                  </p>
                )}
              </div>
              )}

              </>
              )}
              {/* End of Asset/Operator section (hidden for No Asset) */}

              {/* Additional Workers Section */}
              {dayEdit && (tenantSettings.field_rules?.additional_workers?.enabled !== false) && (
                <DayCrewReadOnly
                  crew={getDayCrewForBlock(dayEdit.jobId, dayEdit.date, dayEdit.allocationId)}
                  workerTypes={workerTypes}
                  operators={operators}
                  title="Additional Workers Required"
                  testIdPrefix="day-alloc-crew"
                  onEditBlock={() => { const j = selectedJobForAllocation; setAllocationDialogOpen(false); handleOpenMultiDayDrawer(j); }}
                />
              )}
              {!dayEdit && (tenantSettings.field_rules?.additional_workers?.enabled !== false) && selectedJobForAllocation?.worker_requirements?.length > 0 && (
                <div className="space-y-3 p-3 bg-muted/50 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#ffd108]" />
                    <Label className="label-overline">Additional Workers Required</Label>
                  </div>
                  <div className="space-y-3">
                    {selectedJobForAllocation.worker_requirements.map((wr, idx) => {
                      const workerType = workerTypes.find(wt => wt.id === wr.worker_type_id);
                      // For each required quantity, show a dropdown
                      return Array.from({ length: wr.quantity }).map((_, qIdx) => {
                        const allocationKey = `${wr.worker_type_id}_${qIdx}`;
                        const existingAlloc = allocationForm.worker_allocations.find(
                          (wa, waIdx) => wa.worker_type_id === wr.worker_type_id && 
                          allocationForm.worker_allocations.filter((w, i) => w.worker_type_id === wr.worker_type_id && i < waIdx).length === qIdx
                        );
                        const currentOperatorId = existingAlloc?.operator_id || 'unassigned';
                        
                        return (
                          <div key={allocationKey} className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-28">
                              <Badge variant="outline" className="text-xs">
                                {workerType?.name || 'Unknown'} #{qIdx + 1}
                              </Badge>
                            </div>
                            <Select
                              value={currentOperatorId}
                              onValueChange={(v) => {
                                const actualValue = v === 'unassigned' ? '' : v;
                                const newWorkerAllocations = [...allocationForm.worker_allocations];
                                // Find or create the allocation entry
                                let found = false;
                                let count = 0;
                                for (let i = 0; i < newWorkerAllocations.length; i++) {
                                  if (newWorkerAllocations[i].worker_type_id === wr.worker_type_id) {
                                    if (count === qIdx) {
                                      newWorkerAllocations[i] = { ...newWorkerAllocations[i], operator_id: actualValue };
                                      found = true;
                                      break;
                                    }
                                    count++;
                                  }
                                }
                                if (!found) {
                                  newWorkerAllocations.push({
                                    worker_type_id: wr.worker_type_id,
                                    operator_id: actualValue
                                  });
                                }
                                setAllocationForm({ ...allocationForm, worker_allocations: newWorkerAllocations });
                              }}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue placeholder="Select worker" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unassigned">-- Not assigned --</SelectItem>
                                {operators
                                  .filter(op => op.id !== allocationForm.operator_id) // Exclude main operator
                                  .map((op) => {
                                    const conflicts = getOperatorConflicts(
                                      op.id, 
                                      allocationForm.start_date, 
                                      allocationForm.end_date,
                                      selectedJobForAllocation?.id
                                    );
                                    return (
                                      <SelectItem key={op.id} value={op.id}>
                                        <div className="flex items-center justify-between w-full gap-2">
                                          <span className={!op.is_available ? 'text-muted-foreground' : ''}>
                                            {op.name}
                                          </span>
                                          <div className="flex items-center gap-1">
                                            {!op.is_available && (
                                              <span className="text-xs text-amber-500">(unavailable)</span>
                                            )}
                                            {conflicts.length > 0 && (
                                              <span className="text-xs text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded">
                                                Allocated ({conflicts[0].customer_name})
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </SelectItem>
                                    );
                                  })}
                                {operators.filter(op => op.id !== allocationForm.operator_id).length === 0 && (
                                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                    No other operators available. Create operators in the Operators section.
                                  </div>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      });
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Assign workers to additional worker roles for this job
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Start Date</Label>
                  {/* Single-day job: read-only, Multi-day job: editable */}
                  {(!selectedJobForAllocation?.end_date || selectedJobForAllocation?.end_date === selectedJobForAllocation?.start_date) ? (
                    <div className="flex items-center h-9 px-3 border border-input rounded-md bg-muted/50">
                      <span className="font-mono text-sm">{formatDateDisplay(allocationForm.start_date)}</span>
                    </div>
                  ) : (
                    <DatePicker
                      value={allocationForm.start_date}
                      onChange={(date) => setAllocationForm({ ...allocationForm, start_date: date })}
                      formatDisplay={formatDateDisplay}
                      minDate={selectedJobForAllocation?.start_date}
                      maxDate={selectedJobForAllocation?.end_date}
                      className="w-full"
                      testId="allocation-start-date"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">End Date</Label>
                  {/* Single-day job: read-only, Multi-day job: editable */}
                  {(!selectedJobForAllocation?.end_date || selectedJobForAllocation?.end_date === selectedJobForAllocation?.start_date) ? (
                    <div className="flex items-center h-9 px-3 border border-input rounded-md bg-muted/50">
                      <span className="font-mono text-sm">{allocationForm.end_date ? formatDateDisplay(allocationForm.end_date) : '-'}</span>
                    </div>
                  ) : (
                    <DatePicker
                      value={allocationForm.end_date}
                      onChange={(date) => setAllocationForm({ ...allocationForm, end_date: date })}
                      formatDisplay={formatDateDisplay}
                      minDate={allocationForm.start_date || selectedJobForAllocation?.start_date}
                      maxDate={selectedJobForAllocation?.end_date}
                      className="w-full"
                      testId="allocation-end-date"
                    />
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <Label htmlFor="override-check" className="text-sm font-medium cursor-pointer">
                    Override Check
                  </Label>
                </div>
                <Switch
                  id="override-check"
                  checked={overrideCheck}
                  onCheckedChange={setOverrideCheck}
                  data-testid="override-check-switch"
                />
              </div>
              {overrideCheck && (
                <p className="text-xs text-amber-500">
                  Shows operators on leave. You'll be asked for a reason before saving.
                </p>
              )}

              {/* Support Asset Allocations — non-billable assets like transport trucks */}
              {selectedJobForAllocation?.equipment_type !== 'No Asset' && (
                <SupportAssetPanel
                  jobId={selectedJobForAllocation?.id}
                  api={api}
                  equipment={equipment}
                  readOnly={selectedJobForAllocation?.status === 'docketed' || selectedJobForAllocation?.status === 'invoiced'}
                />
              )}

              <DialogFooter>
                {/* Only show Remove button for non-docketed/invoiced jobs */}
                {getJobAllocation(selectedJobForAllocation.id) && 
                 selectedJobForAllocation.status !== 'docketed' && 
                 selectedJobForAllocation.status !== 'invoiced' && (
                  <Button type="button" variant="destructive" onClick={() => { handleDeleteAllocation(getJobAllocation(selectedJobForAllocation.id).id); setAllocationDialogOpen(false); }} onMouseDown={() => document.activeElement?.blur()}>
                    Remove
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => setAllocationDialogOpen(false)} onMouseDown={() => document.activeElement?.blur()}>
                  {selectedJobForAllocation.status === 'docketed' || selectedJobForAllocation.status === 'invoiced' ? 'Close' : 'Cancel'}
                </Button>
                {/* Only show Save button for non-docketed/invoiced jobs */}
                {selectedJobForAllocation.status !== 'docketed' && selectedJobForAllocation.status !== 'invoiced' && (
                  <Button 
                    type="button" 
                    className="btn-volt" 
                    disabled={
                      // Labour-only jobs don't require an operator/equipment — just worker_allocations.
                      // Dry-hire jobs require equipment but NOT an operator.
                      selectedJobForAllocation?.equipment_type !== 'No Asset' && (
                        (selectedJobForAllocation?.hire_type !== 'dry' && !allocationForm.operator_id) || 
                        (allocationForm.is_combo_allocation 
                          ? (() => {
                              const comboItems = allocationForm.combo_equipment_ids || [];
                              return comboItems.some(c => !c?.equipment_id);
                            })()
                          : !allocationForm.equipment_id)
                      )
                    }
                    onMouseDown={(e) => {
                      // Blur any focused element to prevent date picker issues
                      if (document.activeElement) {
                        document.activeElement.blur();
                      }
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSubmitAllocation();
                    }}
                  >
                    {(() => {
                      const assetReqs = selectedJobForAllocation?.asset_requirements || [];
                      if (assetReqs.length > 0) {
                        const totalSlots = assetReqs.reduce((s, r) => s + (r.quantity || 1), 0);
                        const filledSlots = allocations.filter(a => a.job_id === selectedJobForAllocation?.id && a.asset_requirement_id).length;
                        // +1 for the one we're about to save (if new)
                        const existsForSlot = allocations.some(a =>
                          a.job_id === selectedJobForAllocation?.id &&
                          a.asset_requirement_id === allocationForm.asset_requirement_id &&
                          (a.asset_requirement_index ?? 0) === (allocationForm.asset_requirement_index ?? 0)
                        );
                        const nextFilled = existsForSlot ? filledSlots : filledSlots + 1;
                        if (nextFilled < totalSlots) {
                          return `Save & Next (${nextFilled}/${totalSlots})`;
                        }
                        return `Save — All Slots Done`;
                      }
                      return 'Save Allocation';
                    })()}
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QUICK ADD CUSTOMER DIALOG */}
      <QuickAddCustomerDialog
        open={customerDialogOpen}
        onOpenChange={setCustomerDialogOpen}
        api={api}
        onCreated={handleCustomerCreated}
      />

      {/* QUICK ADD CONTACT DIALOG */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <User className="h-5 w-5 text-[#ffd108]" />
              Add Contact
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateContact} className="space-y-4">
            <div className="p-3 bg-muted rounded-sm">
              <p className="text-sm text-muted-foreground">
                Adding contact for: <span className="font-medium text-foreground">{getCustomerInfo(jobForm.customer_id)?.company_name}</span>
              </p>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Name <span className="text-red-500">*</span></Label>
              <Input
                data-testid="contact-name-input"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                placeholder="John Smith"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Position / Title</Label>
              <Input
                data-testid="contact-position-input"
                value={contactForm.position}
                onChange={(e) => setContactForm({ ...contactForm, position: e.target.value })}
                placeholder="Site Manager"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Phone</Label>
                <Input
                  data-testid="contact-phone-input"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  placeholder="0400 000 000"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Email</Label>
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setContactDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" data-testid="contact-submit-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Contact
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QUICK ADD SITE DIALOG */}
      <Dialog open={siteDialogOpen} onOpenChange={setSiteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#ffd108]" />
              Quick Add Site
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSite} className="space-y-4">
            <div className="p-3 bg-muted rounded-sm">
              <p className="text-sm text-muted-foreground">
                Adding site for: <span className="font-medium text-foreground">{getCustomerInfo(jobForm.customer_id)?.company_name}</span>
              </p>
            </div>
            <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
              <div>
                <Label className="text-xs text-muted-foreground">Search Address</Label>
                <GooglePlacesAutocomplete
                  placeholder="Start typing an address..."
                  country={tenantSettings.country}
                  onPlaceSelect={(place) => {
                    setSiteForm(prev => ({
                      ...prev,
                      name: [place.street_address, place.suburb].filter(Boolean).join(', ') || place.name || prev.name,
                      street_address: place.street_address || '',
                      suburb: place.suburb || '',
                      state: place.state || '',
                      postcode: place.postcode || ''
                    }));
                  }}
                  data-testid="site-address-autocomplete"
                />
                <p className="text-xs text-muted-foreground mt-1">Or enter manually below</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Site Name</Label>
                <Input value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} placeholder="e.g. 17 Highview Cres, Abuntha" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Street Address <span className="text-red-500">*</span></Label>
                <Input
                  value={siteForm.street_address}
                  onChange={(e) => setSiteForm({ ...siteForm, street_address: e.target.value })}
                  placeholder="e.g., 123 Main Street"
                  required
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">{t('suburb')} <span className="text-red-500">*</span></Label>
                  <Input
                    value={siteForm.suburb}
                    onChange={(e) => setSiteForm({ ...siteForm, suburb: e.target.value })}
                    placeholder={t('suburb')}
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">{countryMeta?.stateLabel || t('state')}</Label>
                  <Select 
                    value={siteForm.state} 
                    onValueChange={(v) => setSiteForm({ ...siteForm, state: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={countryMeta?.stateLabel || t('state')} />
                    </SelectTrigger>
                    <SelectContent>
                      {statesList.map(s => (
                        <SelectItem key={s.code} value={s.code}>{s.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">{countryMeta?.postalLabel || t('postcode')}</Label>
                  <Input
                    value={siteForm.postcode}
                    onChange={(e) => setSiteForm({ ...siteForm, postcode: e.target.value })}
                    placeholder="0000"
                    maxLength={4}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Notes</Label>
              <Textarea value={siteForm.notes} onChange={(e) => setSiteForm({ ...siteForm, notes: e.target.value })} placeholder="Access instructions..." rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSiteDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-volt"><Plus className="h-4 w-4 mr-2" />Add Site</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CREATE DOCKET DRAWER - Time-based (for transcribing paper dockets) */}
      <Sheet open={docketDialogOpen} onOpenChange={setDocketDialogOpen}>
        <SheetContent side="right" className="w-[900px] sm:max-w-[900px] overflow-y-auto" data-testid="create-docket-dialog">
          <SheetHeader>
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#ffd108]" />
              Create Docket
            </SheetTitle>
          </SheetHeader>
          
          {selectedJobForDocket && (
            <form onSubmit={handleSubmitDocket} className="space-y-6">
              {/* Job Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="label-overline text-xs">Docket Date</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDateDisplay(docketDateForJob || selectedJobForDocket.start_date)}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Customer</p>
                  <p className="font-medium flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    {getCustomerInfo(selectedJobForDocket.customer_id)?.company_name || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Site</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {getSiteInfo(selectedJobForDocket.site_id)?.name || 
                     (selectedJobForDocket.one_off_street ? `${selectedJobForDocket.one_off_street}, ${selectedJobForDocket.one_off_suburb || ''}` : 'No site')}
                  </p>
                </div>
                {selectedJobForDocket.equipment_type !== 'No Asset' && (
                  <div>
                    <p className="label-overline text-xs">Asset</p>
                    <p className="font-medium flex items-center gap-1">
                      <Truck className="h-4 w-4" />
                      {selectedJobForDocket.equipment_type}
                    </p>
                  </div>
                )}
              </div>

              {selectedJobForDocket.description && (
                <div className="p-3 border rounded-lg">
                  <p className="label-overline text-xs mb-1">Job Description</p>
                  <p className="text-sm">{selectedJobForDocket.description}</p>
                </div>
              )}

              {/* Docket Items - Time-based */}
              <div>
                <h3 className="font-heading font-semibold mb-3">Time & Hours</h3>
                <div className="space-y-4">
                  {docketItems.map((item) => (
                    <div key={item.id} className="p-4 border rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant={item.item_type === 'equipment' ? 'default' : item.item_type === 'worker' ? 'outline' : 'secondary'}>
                          {item.item_type === 'equipment' ? 'Equipment' : item.item_type === 'worker' ? 'Worker' : 'Attachment'}
                        </Badge>
                        <span className="font-medium">{item.display_name || item.type_name}</span>
                      </div>
                      
                      <div className={`grid gap-3 ${sheetDepotMode && item.item_type !== 'attachment' ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'}`}>
                        {/* Depart Depot (depot mode) */}
                        {sheetDepotMode && item.item_type !== 'attachment' && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Depart Depot</Label>
                          <div className="flex gap-1">
                            <Select value={(item.depart_depot_time || '').split(':')[0] || ''} onValueChange={(hour) => { const mins = (item.depart_depot_time || '').split(':')[1] || '00'; handleDocketItemChange(item.id, 'depart_depot_time', `${hour}:${mins}`); }}>
                              <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="HH" /></SelectTrigger>
                              <SelectContent>{Array.from({length:24},(_,i)=>i.toString().padStart(2,'0')).map(h=><SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                            </Select>
                            <span className="flex items-center text-muted-foreground">:</span>
                            <Select value={(item.depart_depot_time || '').split(':')[1] || ''} onValueChange={(mins) => { const hour = (item.depart_depot_time || '').split(':')[0] || '07'; handleDocketItemChange(item.id, 'depart_depot_time', `${hour}:${mins}`); }}>
                              <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="MM" /></SelectTrigger>
                              <SelectContent>{['00','05','10','15','20','25','30','35','40','45','50','55'].map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        </div>
                        )}
                        {/* Start Time / Arrive Site */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">{sheetDepotMode && item.item_type !== 'attachment' ? 'Arrive Site' : 'Start Time'}</Label>
                          <div className="flex gap-1">
                            <Select
                              value={item.start_time?.split(':')[0] || '08'}
                              onValueChange={(hour) => {
                                const mins = item.start_time?.split(':')[1] || '00';
                                handleDocketItemChange(item.id, 'start_time', `${hour}:${mins}`);
                              }}
                            >
                              <SelectTrigger className="h-9 flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0')).map((hour) => (
                                  <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <span className="flex items-center text-muted-foreground">:</span>
                            <Select
                              value={item.start_time?.split(':')[1] || '00'}
                              onValueChange={(mins) => {
                                const hour = item.start_time?.split(':')[0] || '08';
                                handleDocketItemChange(item.id, 'start_time', `${hour}:${mins}`);
                              }}
                            >
                              <SelectTrigger className="h-9 flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map((mins) => (
                                  <SelectItem key={mins} value={mins}>{mins}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Finish Time / Depart Site */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">{sheetDepotMode && item.item_type !== 'attachment' ? 'Depart Site' : 'Finish Time'}</Label>
                          <div className="flex gap-1">
                            <Select
                              value={item.finish_time?.split(':')[0] || '17'}
                              onValueChange={(hour) => {
                                const mins = item.finish_time?.split(':')[1] || '00';
                                handleDocketItemChange(item.id, 'finish_time', `${hour}:${mins}`);
                              }}
                            >
                              <SelectTrigger className="h-9 flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0')).map((hour) => (
                                  <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <span className="flex items-center text-muted-foreground">:</span>
                            <Select
                              value={item.finish_time?.split(':')[1] || '00'}
                              onValueChange={(mins) => {
                                const hour = item.finish_time?.split(':')[0] || '17';
                                handleDocketItemChange(item.id, 'finish_time', `${hour}:${mins}`);
                              }}
                            >
                              <SelectTrigger className="h-9 flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map((mins) => (
                                  <SelectItem key={mins} value={mins}>{mins}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Arrive Depot (depot mode) */}
                        {sheetDepotMode && item.item_type !== 'attachment' && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Arrive Depot</Label>
                          <div className="flex gap-1">
                            <Select value={(item.arrive_depot_time || '').split(':')[0] || ''} onValueChange={(hour) => { const mins = (item.arrive_depot_time || '').split(':')[1] || '00'; handleDocketItemChange(item.id, 'arrive_depot_time', `${hour}:${mins}`); }}>
                              <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="HH" /></SelectTrigger>
                              <SelectContent>{Array.from({length:24},(_,i)=>i.toString().padStart(2,'0')).map(h=><SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                            </Select>
                            <span className="flex items-center text-muted-foreground">:</span>
                            <Select value={(item.arrive_depot_time || '').split(':')[1] || ''} onValueChange={(mins) => { const hour = (item.arrive_depot_time || '').split(':')[0] || '17'; handleDocketItemChange(item.id, 'arrive_depot_time', `${hour}:${mins}`); }}>
                              <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="MM" /></SelectTrigger>
                              <SelectContent>{['00','05','10','15','20','25','30','35','40','45','50','55'].map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        </div>
                        )}
                        {/* Spacer when non-depot */}
                        {!sheetDepotMode && <div></div>}
                      </div>
                      
                      {/* Second row: Lunch, Travel, Total Hours */}
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        {/* Lunch Break */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Lunch (mins)</Label>
                          <Select
                            value={String(item.lunch_minutes || 0)}
                            onValueChange={(v) => handleDocketItemChange(item.id, 'lunch_minutes', parseInt(v))}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[0, 15, 30, 45, 60, 90, 120].map((mins) => (
                                <SelectItem key={mins} value={String(mins)}>{mins} min</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Travel Time — manual select when not depot, read-only when depot */}
                        {sheetDepotMode && item.item_type !== 'attachment' ? (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Travel</Label>
                          <div className="h-9 px-3 flex items-center bg-muted rounded-md border text-xs font-mono">
                            {item.travel_out_minutes != null && item.travel_return_minutes != null
                              ? `+${item.travel_out_minutes} / +${item.travel_return_minutes} min`
                              : '— (set depot times)'}
                          </div>
                        </div>
                        ) : (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Travel (mins)</Label>
                          <Select
                            value={String(item.travel_minutes || 0)}
                            onValueChange={(v) => handleDocketItemChange(item.id, 'travel_minutes', parseInt(v))}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[0, 15, 30, 45, 60, 90, 120, 150, 180].map((mins) => (
                                <SelectItem key={mins} value={String(mins)}>{mins} min</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        )}
                        
                        {/* Total Hours */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Total Hours</Label>
                          <div className="h-9 px-3 flex items-center bg-muted rounded-md border font-mono font-medium text-[#ffd108]">
                            {(parseFloat(item.qty) || 0).toFixed(2)} hrs
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Formula: (Finish - Start) - Lunch + Travel
                </p>
              </div>

              {/* Material Loads Section — hidden for crane_hire and concrete_pumping tenants */}
              {!isCraneHire && !isConcretePumping && (
              <div className="space-y-3" data-testid="material-loads-section">
                  <div className="flex items-center justify-between">
                    <Label className="label-overline">Material Loads</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddMaterialLoad}
                      data-testid="add-material-load-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Load
                    </Button>
                  </div>
                  
                  {docketMaterialLoads.length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
                      No material loads added yet
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {docketMaterialLoads.map((load) => (
                        <div key={load.id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant={load.load_type === 'pickup' ? 'default' : load.load_type === 'disposal' ? 'destructive' : 'secondary'}>
                                {load.load_type === 'pickup' ? 'Pickup' : load.load_type === 'disposal' ? 'Disposal' : 'Buy Back'}
                              </Badge>
                              <span className="font-medium">{load.material_type_name}</span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {load.quantity} {load.unit} from {load.yard_name}
                              {load.tip_docket_number && ` • Tip #${load.tip_docket_number}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditMaterialLoad(load)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMaterialLoad(load.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Concrete Pumped — concrete pumping tenants only */}
              {isConcretePumping && (
                <div className="space-y-2" data-testid="concrete-pumped-section">
                  <Label className="label-overline">
                    Concrete Pumped <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={docketConcretePumped}
                      onChange={(e) => setDocketConcretePumped(e.target.value)}
                      placeholder="0.0"
                      className="max-w-[200px]"
                      data-testid="concrete-pumped-input"
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap" data-testid="concrete-pumped-unit">
                      m³ (cubic metres)
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Total cubic metres of concrete pumped for this job.
                  </p>
                </div>
              )}

              {/* Work Completed */}
              <div className="space-y-2">
                <Label className="label-overline">Work Completed</Label>
                <Textarea
                  value={docketWorkCompleted}
                  onChange={(e) => setDocketWorkCompleted(e.target.value)}
                  placeholder="Describe the work completed on this job..."
                  rows={3}
                  data-testid="docket-work-completed"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="label-overline">Notes</Label>
                <Textarea
                  value={docketNotes}
                  onChange={(e) => setDocketNotes(e.target.value)}
                  placeholder="Additional notes for this docket..."
                  rows={2}
                  data-testid="docket-notes"
                />
              </div>

              {/* Paper Docket Upload */}
              <div className="space-y-3">
                <Label className="label-overline">Paper Docket (Optional)</Label>
                <p className="text-xs text-muted-foreground">
                  Upload a photo or scan of the original paper docket for your records.
                </p>
                
                {!docketPaperUrl ? (
                  <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-[#ffd108]/50 transition-colors">
                    <input
                      type="file"
                      id="paper-docket-upload"
                      accept="image/*,.pdf"
                      onChange={handleDocketPaperUpload}
                      className="hidden"
                      disabled={docketUploading}
                    />
                    <label htmlFor="paper-docket-upload" className="cursor-pointer">
                      {docketUploading ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
                          <span className="text-sm text-muted-foreground">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <div className="p-3 rounded-full bg-muted">
                            <FileText className="h-6 w-6 text-muted-foreground" />
                          </div>
                          <span className="text-sm font-medium">Click to upload paper docket</span>
                          <span className="text-xs text-muted-foreground">PNG, JPG, PDF up to 10MB</span>
                        </div>
                      )}
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-green-500/20">
                        <Check className="h-4 w-4 text-green-500" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{docketPaperName}</p>
                        <a 
                          href={docketPaperUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-[#ffd108] hover:underline flex items-center gap-1"
                        >
                          View document <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleDeleteDocketPaper}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <SheetFooter>
                <Button type="button" variant="outline" onClick={() => setDocketDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-volt" data-testid="save-docket-btn">
                  <FileText className="h-4 w-4 mr-2" />
                  Save Docket
                </Button>
              </SheetFooter>
            </form>
          )}
        </SheetContent>
      </Sheet>

      {/* DUPLICATE JOB DIALOG */}
      <Dialog open={duplicatePoDialogOpen} onOpenChange={setDuplicatePoDialogOpen}>
        <DialogContent className="max-w-md" data-testid="duplicate-po-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Duplicate Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Date Pickers */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline text-xs">Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="duplicate-date-picker">
                      <Calendar className="h-4 w-4 mr-2" />
                      {duplicateJobDate ? formatDateDisplay(format(duplicateJobDate, 'yyyy-MM-dd')) : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={duplicateJobDate}
                      onSelect={(date) => date && setDuplicateJobDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="label-overline text-xs">End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="duplicate-end-date-picker">
                      <Calendar className="h-4 w-4 mr-2" />
                      {duplicateJobEndDate ? formatDateDisplay(format(duplicateJobEndDate, 'yyyy-MM-dd')) : 'Single day'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={duplicateJobEndDate}
                      onSelect={(date) => setDuplicateJobEndDate(date || null)}
                      disabled={(date) => duplicateJobDate && date < duplicateJobDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* PO Number - show if customer requires PO or job has a PO */}
            {duplicateJobData?.po_number && (
              <div className="space-y-3">
                <Label className="label-overline text-xs">PO Number</Label>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="po-reuse"
                    name="po-choice"
                    checked={duplicatePoUseExisting}
                    onChange={() => setDuplicatePoUseExisting(true)}
                    className="accent-[#ffd108]"
                  />
                  <Label htmlFor="po-reuse" className="cursor-pointer">
                    Re-use existing PO: <span className="font-mono font-semibold">{duplicateJobData?.po_number}</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="po-new"
                    name="po-choice"
                    checked={!duplicatePoUseExisting}
                    onChange={() => setDuplicatePoUseExisting(false)}
                    className="accent-[#ffd108]"
                  />
                  <Label htmlFor="po-new" className="cursor-pointer">Enter a new PO number</Label>
                </div>
                {!duplicatePoUseExisting && (
                  <Input
                    value={duplicatePoNumber}
                    onChange={(e) => setDuplicatePoNumber(e.target.value)}
                    placeholder="Enter new PO number"
                    autoFocus
                    data-testid="duplicate-po-input"
                  />
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicatePoDialogOpen(false)}>Cancel</Button>
            <Button
              className="btn-volt"
              onClick={handleConfirmDuplicate}
              disabled={
                !duplicateJobDate ||
                (duplicateJobData?.po_number && !duplicatePoUseExisting && !duplicatePoNumber.trim())
              }
              data-testid="confirm-duplicate-btn"
            >
              Duplicate Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MATERIAL LOAD DIALOG — hidden for crane_hire tenants */}
      {!isCraneHire && (
      <Dialog open={showMaterialLoadDialog} onOpenChange={(open) => {
        if (!open) {
          setShowMaterialLoadDialog(false);
          setEditingMaterialLoad(null);
        }
      }}>
        <DialogContent className="max-w-md" data-testid="material-load-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-[#ffd108]" />
              {editingMaterialLoad?.id.startsWith('load-') ? 'Add Material Load' : 'Edit Material Load'}
            </DialogTitle>
          </DialogHeader>
          
          {editingMaterialLoad && (
            <div className="space-y-4 py-2">
              {/* Load Type */}
              <div className="space-y-2">
                <Label className="label-overline">Load Type <span className="text-red-500">*</span></Label>
                <Select
                  value={editingMaterialLoad.load_type}
                  onValueChange={(v) => setEditingMaterialLoad(prev => ({ ...prev, load_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pickup">Pickup (load onto truck)</SelectItem>
                    <SelectItem value="disposal">Disposal (tip off)</SelectItem>
                    <SelectItem value="buy_back">Buy Back</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Yard */}
              <div className="space-y-2">
                <Label className="label-overline">Yard <span className="text-red-500">*</span></Label>
                <Select
                  value={editingMaterialLoad.yard_id}
                  onValueChange={handleMaterialLoadYardChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select yard" />
                  </SelectTrigger>
                  <SelectContent>
                    {yards.map((yard) => (
                      <SelectItem key={yard.id} value={yard.id}>{yard.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Material Type */}
              <div className="space-y-2">
                <Label className="label-overline">Material Type <span className="text-red-500">*</span></Label>
                <Select
                  value={editingMaterialLoad.material_type_id}
                  onValueChange={handleMaterialLoadTypeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select material" />
                  </SelectTrigger>
                  <SelectContent>
                    {materialTypes.filter(t => t.is_active !== false).map((matType) => (
                      <SelectItem key={matType.id} value={matType.id}>{matType.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Quantity & Unit */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="label-overline">Quantity <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editingMaterialLoad.quantity}
                    onChange={(e) => setEditingMaterialLoad(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                    placeholder="0"
                    data-testid="material-load-qty"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Unit <span className="text-red-500">*</span></Label>
                  <Select
                    value={editingMaterialLoad.unit}
                    onValueChange={(v) => setEditingMaterialLoad(prev => ({ ...prev, unit: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tonne">Tonne</SelectItem>
                      <SelectItem value="cubic_metre">Cubic Metre (m³)</SelectItem>
                      <SelectItem value="load">Load</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Tip Docket Number (for disposal) */}
              {editingMaterialLoad.load_type === 'disposal' && (
                <div className="space-y-2">
                  <Label className="label-overline">Tip Docket Number</Label>
                  <Input
                    value={editingMaterialLoad.tip_docket_number || ''}
                    onChange={(e) => setEditingMaterialLoad(prev => ({ ...prev, tip_docket_number: e.target.value }))}
                    placeholder="Enter tip docket number"
                    data-testid="tip-docket-number"
                  />
                </div>
              )}
              
              {/* Tip Docket Photo (for disposal) */}
              {editingMaterialLoad.load_type === 'disposal' && (
                <div className="space-y-2">
                  <Label className="label-overline">Tip Docket Photo</Label>
                  {!editingMaterialLoad.tip_docket_photo_url ? (
                    <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-[#ffd108]/50 transition-colors">
                      <input
                        type="file"
                        id="tip-docket-photo"
                        accept="image/*"
                        onChange={handleTipDocketPhotoUpload}
                        className="hidden"
                      />
                      <label htmlFor="tip-docket-photo" className="cursor-pointer flex flex-col items-center gap-1">
                        <Camera className="h-5 w-5 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Upload tip docket photo</span>
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-2 border rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500" />
                        <a 
                          href={editingMaterialLoad.tip_docket_photo_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-[#ffd108] hover:underline"
                        >
                          View photo
                        </a>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingMaterialLoad(prev => ({ ...prev, tip_docket_photo_url: '' }))}
                        className="text-destructive hover:text-destructive h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Notes */}
              <div className="space-y-2">
                <Label className="label-overline">Notes</Label>
                <Input
                  value={editingMaterialLoad.notes || ''}
                  onChange={(e) => setEditingMaterialLoad(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => {
              setShowMaterialLoadDialog(false);
              setEditingMaterialLoad(null);
            }}>
              Cancel
            </Button>
            <Button type="button" className="btn-volt" onClick={handleSaveMaterialLoad} data-testid="save-material-load-btn">
              Save Load
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* CASCADE-MOBILISATION-DATES MODAL */}
      <Dialog open={cascadeMobModal.open} onOpenChange={(open) => !open && handleCascadeMobCancel()}>
        <DialogContent className="sm:max-w-lg" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="cascade-mob-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-volt" />
              Shift Linked Mobilisation Jobs?
            </DialogTitle>
            <DialogDescription>
              You're moving this setup-parent job by{' '}
              <strong>{cascadeMobModal.deltaDays > 0 ? '+' : ''}{cascadeMobModal.deltaDays} day{Math.abs(cascadeMobModal.deltaDays) === 1 ? '' : 's'}</strong>.
              There {cascadeMobModal.legCount === 1 ? 'is' : 'are'} <strong>{cascadeMobModal.legCount}</strong> linked mobilisation/demobilisation job{cascadeMobModal.legCount === 1 ? '' : 's'} in this setup group.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 text-sm text-muted-foreground">
            Shifting them keeps their offset relative to the parent intact (e.g., a leg scheduled 1 day before the parent stays 1 day before). Jobs already <strong>docketed</strong> or <strong>invoiced</strong> are skipped.
          </div>
          <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
            <Button variant="outline" onClick={handleCascadeMobCancel} data-testid="cascade-mob-cancel">
              Cancel
            </Button>
            <Button variant="outline" onClick={handleCascadeMobOnlyParent} data-testid="cascade-mob-only-parent">
              Only this job
            </Button>
            <Button className="btn-volt" onClick={handleCascadeMobConfirm} data-testid="cascade-mob-confirm" autoFocus>
              Yes, shift {cascadeMobModal.legCount} linked job{cascadeMobModal.legCount === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* EXTEND ALLOCATIONS MODAL */}
      <Dialog open={extendAllocationsModal.open} onOpenChange={(open) => !open && handleExtendAllocationsCancel()}>
        <DialogContent className="sm:max-w-lg" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-volt" />
              Extend Allocations?
            </DialogTitle>
            <DialogDescription>
              You are extending the job end date. Would you like to extend existing allocations to match?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Job Info */}
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium text-sm">{extendAllocationsModal.jobDescription}</p>
              <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>New end date: <strong className="text-foreground">{formatDateDisplay(extendAllocationsModal.newEndDate)}</strong></span>
              </div>
            </div>
            
            {/* Allocations to extend */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Allocations that can be extended:</Label>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {extendAllocationsModal.allocations.map((alloc) => (
                  <div 
                    key={alloc.id} 
                    className="flex items-center justify-between p-2 bg-muted/50 rounded-md border text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{alloc.operator_name}</span>
                      <span className="text-muted-foreground">({alloc.operator_code})</span>
                    </div>
                    <div className="text-muted-foreground">
                      {formatDateDisplay(alloc.start_date)} - {formatDateDisplay(alloc.end_date || alloc.start_date)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Extension preview */}
            <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <ArrowRight className="h-4 w-4" />
                <span className="text-sm">
                  {extendAllocationsModal.allocations.length} allocation{extendAllocationsModal.allocations.length !== 1 ? 's' : ''} will be extended to <strong>{formatDateDisplay(extendAllocationsModal.newEndDate)}</strong>
                </span>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => handleExtendAllocationsConfirm(false)}
              className="w-full sm:w-auto"
              onMouseDown={() => document.activeElement?.blur()}
            >
              Update Job Only
            </Button>
            <Button
              onClick={() => handleExtendAllocationsConfirm(true)}
              className="btn-volt w-full sm:w-auto"
              onMouseDown={() => document.activeElement?.blur()}
            >
              <CalendarDays className="h-4 w-4 mr-2" />
              Extend Allocations
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EXTEND JOB MODAL */}
      <Dialog open={extendJobModal.open} onOpenChange={(open) => !open && setExtendJobModal(prev => ({ ...prev, open: false }))}>
        <DialogContent className="sm:max-w-lg" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-volt" />
              Extend Job
            </DialogTitle>
            <DialogDescription>
              Extend the job duration by adding more days
            </DialogDescription>
          </DialogHeader>
          
          {extendJobModal.job && (
            <div className="space-y-4 py-4">
              {/* Job Info */}
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{extendJobModal.job.description || 'No description'}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Current dates: {formatDateDisplay(extendJobModal.job.start_date)}
                  {extendJobModal.job.end_date && extendJobModal.job.end_date !== extendJobModal.job.start_date && (
                    <> - {formatDateDisplay(extendJobModal.job.end_date)}</>
                  )}
                </p>
              </div>
              
              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Extension Starts</Label>
                  <div className="flex h-9 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm font-mono items-center">
                    {extendJobModal.dateFrom ? formatDateDisplay(extendJobModal.dateFrom) : '-'}
                  </div>
                  <p className="text-xs text-muted-foreground">Day after current end date</p>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">New End Date <span className="text-red-500">*</span></Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full justify-start text-left font-mono h-9 ${!extendJobModal.dateTo && 'text-muted-foreground'}`}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {extendJobModal.dateTo ? formatDateDisplay(extendJobModal.dateTo) : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={extendJobModal.dateTo ? parse(extendJobModal.dateTo, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            setExtendJobModal(prev => ({ ...prev, dateTo: format(date, 'yyyy-MM-dd') }));
                          }
                        }}
                        disabled={(date) => {
                          const minDate = extendJobModal.dateFrom ? parse(extendJobModal.dateFrom, 'yyyy-MM-dd', new Date()) : new Date();
                          return date < minDate;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              
              {/* Extend Allocations Option */}
              {extendJobModal.allocations.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="extend-allocations-checkbox"
                      checked={extendJobModal.extendAllocations}
                      onChange={(e) => setExtendJobModal(prev => ({ ...prev, extendAllocations: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="extend-allocations-checkbox" className="text-sm font-medium cursor-pointer">
                      Extend existing allocations to new end date
                    </Label>
                  </div>
                  
                  {extendJobModal.extendAllocations && (
                    <div className="ml-6 space-y-2">
                      <p className="text-xs text-muted-foreground">The following allocations will be extended:</p>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {extendJobModal.allocations.map((alloc) => (
                          <div 
                            key={alloc.id}
                            className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm"
                          >
                            <div className="flex items-center gap-2">
                              <User className="h-3 w-3 text-muted-foreground" />
                              <span>{alloc.operator_name}</span>
                            </div>
                            <div className="text-muted-foreground text-xs">
                              {formatDateDisplay(alloc.end_date || alloc.start_date)} → {extendJobModal.dateTo ? formatDateDisplay(extendJobModal.dateTo) : '...'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {extendJobModal.allocations.length === 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-sm">
                    <AlertTriangle className="h-4 w-4" />
                    <span>This job has no allocations. Only the job dates will be extended.</span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setExtendJobModal(prev => ({ ...prev, open: false }))}
              disabled={extendJobModal.loading}
              className="w-full sm:w-auto"
              onMouseDown={() => document.activeElement?.blur()}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExtendJobSubmit}
              disabled={!extendJobModal.dateTo || extendJobModal.loading}
              className="btn-volt w-full sm:w-auto"
              onMouseDown={() => document.activeElement?.blur()}
            >
              {extendJobModal.loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CalendarPlus className="h-4 w-4 mr-2" />
              )}
              Extend Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Single Docket Drawer - Full featured with Tabs like Dockets page */}
      <Sheet open={viewDocketDrawerOpen} onOpenChange={setViewDocketDrawerOpen}>
        <SheetContent side="right" className="w-full max-w-[1200px] sm:w-[1200px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#ffd108]" />
              Docket #{viewingDocket?.docket_number}
            </SheetTitle>
          </SheetHeader>
          
          {viewingDocket && (
            <Tabs defaultValue="docket" className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="docket" className="flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4" />
                  Docket
                </TabsTrigger>
                <TabsTrigger value="invoice" className="flex items-center gap-2">
                  <FileDown className="h-4 w-4" />
                  Invoice
                </TabsTrigger>
              </TabsList>
              
              {/* Docket Tab - Submitted Docket Data */}
              <TabsContent value="docket" className="space-y-6 pt-4">
                {/* Header Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="label-overline text-xs">Date</p>
                    <p className="font-medium flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDateDisplay(viewingDocket.job_date)}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Customer</p>
                    <p className="font-medium flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {viewingDocket.customer_name}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Site</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {viewingDocket.site_name || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Status</p>
                    <Badge className={
                      viewingDocket.status === 'approved' ? 'bg-green-500/20 text-green-600' :
                      viewingDocket.status === 'pending_approval' ? 'bg-amber-500/20 text-amber-600' :
                      viewingDocket.status === 'exported' ? 'bg-blue-500/20 text-blue-600' :
                      'bg-gray-500/20 text-gray-600'
                    }>
                      {viewingDocket.status?.replace('_', ' ').toUpperCase() || 'DRAFT'}
                    </Badge>
                  </div>
                </div>

                {/* Submitted Hours */}
                <div>
                  <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#ffd108]" />
                    Hours Recorded
                  </h3>
                  <div className="space-y-2">
                    {viewingDocket.items?.filter(item => item.item_type !== 'toll' && item.item_type !== 'fuel_levy').map((item, idx) => {
                      const isConcrete = item.item_type === 'concrete';
                      const unitLabel = isConcrete ? (item.uom || 'm³') : 'h';
                      return (
                      <div key={item.id || idx} className="bg-muted/50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {item.item_type === 'equipment' ? (
                              <div className="w-8 h-8 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                                <Truck className="h-4 w-4 text-[#ffd108]" />
                              </div>
                            ) : item.item_type === 'worker' ? (
                              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                                <User className="h-4 w-4 text-blue-500" />
                              </div>
                            ) : isConcrete ? (
                              <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                                <Package className="h-4 w-4 text-orange-500" />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-500/20 flex items-center justify-center">
                                <Wrench className="h-4 w-4 text-gray-500" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{item.display_name || item.type_name}</p>
                              {item.description && (
                                <p className="text-sm text-muted-foreground">{item.description}</p>
                              )}
                              {/* Start Time and Finish Time with labels (depot-aware) */}
                              {(item.start_time || item.finish_time) && (
                                <div className="flex items-center gap-4 mt-1 text-xs flex-wrap">
                                  {hasDepotTimes(item) && item.depart_depot_time && (
                                    <span className="flex items-center gap-1">
                                      <span className="text-muted-foreground">Depart Depot:</span>
                                      <span className="font-mono text-foreground">{formatTime(item.depart_depot_time, tenantSettings.time_format)}</span>
                                    </span>
                                  )}
                                  {item.start_time && (
                                    <span className="flex items-center gap-1">
                                      <span className="text-muted-foreground">{hasDepotTimes(item) ? 'Arrive Site:' : 'Start Time:'}</span>
                                      <span className="font-mono text-foreground">{formatTime(item.start_time, tenantSettings.time_format)}</span>
                                    </span>
                                  )}
                                  {item.finish_time && (
                                    <span className="flex items-center gap-1">
                                      <span className="text-muted-foreground">{hasDepotTimes(item) ? 'Depart Site:' : 'Finish Time:'}</span>
                                      <span className="font-mono text-foreground">{formatTime(item.finish_time, tenantSettings.time_format)}</span>
                                    </span>
                                  )}
                                  {hasDepotTimes(item) && item.arrive_depot_time && (
                                    <span className="flex items-center gap-1">
                                      <span className="text-muted-foreground">Arrive Depot:</span>
                                      <span className="font-mono text-foreground">{formatTime(item.arrive_depot_time, tenantSettings.time_format)}</span>
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-bold text-[#ffd108]">{item.qty || item.quantity}{isConcrete ? ` ${unitLabel}` : 'h'}</p>
                          </div>
                        </div>
                        {/* Lunch & Travel breakdown */}
                        {(item.lunch_minutes > 0 || item.travel_minutes > 0) && (
                          <div className="flex items-center gap-4 mt-2 ml-11 text-xs text-muted-foreground">
                            {item.lunch_minutes > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="text-amber-500">Lunch:</span> -{item.lunch_minutes}min
                              </span>
                            )}
                            {hasDepotTimes(item) && item.travel_out_minutes != null && item.travel_return_minutes != null
                              ? (
                                <span className="flex items-center gap-1">
                                  <span className="text-blue-500">Travel:</span> +{item.travel_out_minutes} out / +{item.travel_return_minutes} return min
                                </span>
                              )
                              : item.travel_minutes > 0 && (
                                <span className="flex items-center gap-1">
                                  <span className="text-blue-500">Travel:</span> +{item.travel_minutes}min
                                </span>
                              )
                            }
                          </div>
                        )}
                        {/* SMU (meter) readings — equipment items, when captured */}
                        {item.item_type === 'equipment' && (item.start_smu != null || item.finish_smu != null) && (
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 ml-11 text-xs text-muted-foreground" data-testid={`schedule-docket-smu-${item.id || idx}`}>
                            <span className="flex items-center gap-1">
                              <span className="text-[#ffd108]">Meter Start:</span>
                              <span className="font-mono text-foreground">{Number(item.start_smu ?? 0).toFixed(1)}</span>
                              <span>{item.smu_unit || 'Hours'}</span>
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="text-[#ffd108]">Finish:</span>
                              <span className="font-mono text-foreground">{Number(item.finish_smu ?? 0).toFixed(1)}</span>
                            </span>
                            {item.start_smu != null && item.finish_smu != null && (
                              <span className="flex items-center gap-1">
                                <span className="text-emerald-500">Δ</span>
                                <span className="font-mono text-foreground">
                                  {(Number(item.finish_smu) - Number(item.start_smu)).toFixed(1)}
                                </span>
                                <span>{item.smu_unit || 'Hours'}</span>
                              </span>
                            )}
                            {(item.start_smu_2 != null || item.finish_smu_2 != null) && (
                              <span className="flex items-center gap-1">
                                <span className="text-blue-400">2nd:</span>
                                <span className="font-mono text-foreground">
                                  {Number(item.start_smu_2 ?? 0).toFixed(1)} → {Number(item.finish_smu_2 ?? 0).toFixed(1)}
                                </span>
                                <span>{item.smu_unit_2 || 'KM'}</span>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>

                {/* Support Assets — non-billable logistics equipment */}
                {viewingDocket.support_assets && viewingDocket.support_assets.length > 0 && (
                  <div data-testid="schedule-docket-support-assets">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <LinkIcon className="h-4 w-4 text-cyan-500" />
                      Support Assets
                      <span className="text-[10px] font-normal text-cyan-500 bg-cyan-500/10 px-1.5 py-0.5 rounded">Non-Billable</span>
                    </h3>
                    <div className="space-y-2">
                      {viewingDocket.support_assets.map((sa, idx) => (
                        <div key={sa.id || idx} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-dashed border-cyan-500/20">
                          <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                            <Truck className="h-4 w-4 text-cyan-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{sa.equipment_name || sa.asset_id || 'Support Asset'}</p>
                            <p className="text-xs text-muted-foreground">
                              {sa.equipment_type}{sa.rego ? ` \u2022 ${sa.rego}` : ''}
                              {' \u2022 '}{sa.start_date}{sa.end_date && sa.end_date !== sa.start_date ? ` \u2192 ${sa.end_date}` : ''}
                            </p>
                            {sa.notes && <p className="text-xs text-muted-foreground mt-0.5">{sa.notes}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tolls */}
                {viewingDocket.items?.some(item => item.item_type === 'toll') && (
                  <div data-testid="schedule-docket-tolls">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-purple-400" />
                      Tolls
                    </h3>
                    <div className="space-y-2">
                      {viewingDocket.items.filter(item => item.item_type === 'toll').map((item, idx) => (
                        <div key={item.id || idx} className="bg-muted/50 p-3 rounded-lg flex items-center justify-between" data-testid={`schedule-docket-toll-${idx}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                              <Receipt className="h-4 w-4 text-purple-400" />
                            </div>
                            <p className="font-medium">{item.type_name || item.display_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-semibold text-purple-400">Qty: {item.qty}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Work Completed */}
                {viewingDocket.work_completed && (
                  <div>
                    <h3 className="font-heading font-semibold mb-2">Work Completed</h3>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm">{viewingDocket.work_completed}</p>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {viewingDocket.notes && (
                  <div>
                    <h3 className="font-heading font-semibold mb-2">Docket Notes</h3>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm">{viewingDocket.notes}</p>
                    </div>
                  </div>
                )}

                {/* Route Progress (haulage jobs) — parity with Dockets "View Details" sheet */}
                <RouteProgressList docket={viewingDocket} tenantSettings={tenantSettings} variant="bordered" containerTestId="schedule-docket-route-progress" rowTestIdPrefix="schedule-docket-stop" api={api} onStopsChanged={(stops) => setViewingDocket(prev => prev ? { ...prev, job_stops: stops } : prev)} />

                {/* Customer Sign-off */}
                {(viewingDocket.customer_signoff_confirmed || viewingDocket.signature_data) && (
                  <div className="border-t pt-4">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Customer Sign-off
                    </h3>
                    <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div>
                        <p className="font-medium">{viewingDocket.customer_signoff_name || viewingDocket.signed_by || 'Customer'}</p>
                        <p className="text-sm text-muted-foreground">
                          {(viewingDocket.customer_signoff_signature || viewingDocket.signature_data) ? 'Confirmed & Signed' : 'Confirmed (signature not captured)'}
                        </p>
                      </div>
                      {(viewingDocket.customer_signoff_signature || viewingDocket.signature_data) && (
                        <div className="ml-auto bg-white rounded p-2">
                          <img 
                            src={viewingDocket.customer_signoff_signature || viewingDocket.signature_data} 
                            alt="Customer Signature" 
                            className="h-12 max-w-[150px] object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {viewingDocket.signature_skipped && (
                  <div className="border-t pt-4">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Signature Not Captured
                    </h3>
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg space-y-1">
                      <p className="font-medium text-amber-700 dark:text-amber-400">
                        {viewingDocket.signature_skip_reason === 'no_one_on_site' ? 'No one on site' : viewingDocket.signature_skip_reason === 'customer_refused' ? 'Customer refused' : 'Other'}
                      </p>
                      {viewingDocket.signature_skip_notes && (
                        <p className="text-sm text-muted-foreground">{viewingDocket.signature_skip_notes}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Docket Photos */}
                {viewingDocket.photos?.length > 0 && (
                  <div className="border-t pt-4" data-testid="docket-photos-section">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <Camera className="h-4 w-4 text-blue-500" />
                      Photos ({viewingDocket.photos.length})
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {viewingDocket.photos.map((photo, idx) => {
                        const blobUrl = photoBlobUrls[idx];
                        return (
                          <button
                            key={idx}
                            onClick={() => blobUrl && setPhotoLightbox({ open: true, index: idx })}
                            className="aspect-square rounded-lg overflow-hidden border border-border hover:border-[#ffd108] transition-colors focus:outline-none focus:ring-2 focus:ring-[#ffd108]"
                            data-testid={`docket-photo-thumb-${idx}`}
                          >
                            {blobUrl ? (
                              <img src={blobUrl} alt={`Docket photo ${idx + 1}`} className="w-full h-full object-cover" />
                            ) : blobUrl === null ? (
                              <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground text-xs">Unavailable</div>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-muted animate-pulse" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Photo Lightbox */}
                {photoLightbox.open && viewingDocket.photos?.length > 0 && photoBlobUrls[photoLightbox.index] && (
                  <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
                    onClick={() => setPhotoLightbox({ open: false, index: 0 })}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setPhotoLightbox({ open: false, index: 0 });
                      if (e.key === 'ArrowLeft') setPhotoLightbox(p => ({ ...p, index: Math.max(0, p.index - 1) }));
                      if (e.key === 'ArrowRight') setPhotoLightbox(p => ({ ...p, index: Math.min(viewingDocket.photos.length - 1, p.index + 1) }));
                    }}
                    tabIndex={0}
                    role="dialog"
                    data-testid="photo-lightbox"
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); setPhotoLightbox({ open: false, index: 0 }); }}
                      className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
                      data-testid="photo-lightbox-close"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    {viewingDocket.photos.length > 1 && photoLightbox.index > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setPhotoLightbox(p => ({ ...p, index: p.index - 1 })); }}
                        className="absolute left-4 text-white/80 hover:text-white z-10"
                        data-testid="photo-lightbox-prev"
                      >
                        <ChevronLeft className="h-8 w-8" />
                      </button>
                    )}
                    {viewingDocket.photos.length > 1 && photoLightbox.index < viewingDocket.photos.length - 1 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setPhotoLightbox(p => ({ ...p, index: p.index + 1 })); }}
                        className="absolute right-4 text-white/80 hover:text-white z-10"
                        data-testid="photo-lightbox-next"
                      >
                        <ChevronRight className="h-8 w-8" />
                      </button>
                    )}
                    <img
                      src={photoBlobUrls[photoLightbox.index]}
                      alt={`Photo ${photoLightbox.index + 1} of ${viewingDocket.photos.length}`}
                      className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="photo-lightbox-image"
                    />
                    <div className="absolute bottom-4 text-white/60 text-sm">
                      {photoLightbox.index + 1} / {viewingDocket.photos.length}
                    </div>
                  </div>
                )}

                {/* Submitted Timestamp */}
                {viewingDocket.created_at && (
                  <div className="text-sm text-muted-foreground text-right pt-2 border-t">
                    Submitted: {formatDateTime(viewingDocket.created_at, tenantSettings.date_format, tenantSettings.time_format)}
                  </div>
                )}
              </TabsContent>
              
              {/* Invoice Tab - Editable Line Items */}
              <TabsContent value="invoice" className="space-y-6 pt-4">
                {viewingDocket?.status === 'draft' ? (
                  <p className="text-sm text-muted-foreground">
                    Edit line items and pricing for invoicing. Changes cannot be made after approval.
                  </p>
                ) : (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      This docket is <strong>{viewingDocket?.status?.replace('_', ' ')}</strong>. Invoice details are read-only.
                    </p>
                  </div>
                )}
                
                {/* Header Info - Read Only */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="label-overline text-xs">Date</p>
                    <p className="font-medium flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDateDisplay(viewingDocket.job_date)}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Customer</p>
                    <p className="font-medium flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {viewingDocket.customer_name}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Site</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {viewingDocket.site_name || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Status</p>
                    <Badge className={
                      viewingDocket.status === 'approved' ? 'bg-green-500/20 text-green-600' :
                      viewingDocket.status === 'pending_approval' ? 'bg-amber-500/20 text-amber-600' :
                      viewingDocket.status === 'exported' ? 'bg-blue-500/20 text-blue-600' :
                      'bg-gray-500/20 text-gray-600'
                    }>
                      {viewingDocket.status?.replace('_', ' ').toUpperCase() || 'DRAFT'}
                    </Badge>
                  </div>
                </div>

                {/* Price List Indicator */}
                {docketUsedPriceList && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-sm">
                      Prices calculated using: <span className="font-semibold text-green-700 dark:text-green-300">{docketUsedPriceList}</span>
                    </span>
                  </div>
                )}

                {/* Editable Items Table */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-semibold">Docket Items</h3>
                    <div className="flex items-center gap-2">
                      {viewingDocket?.status === 'draft' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDocketRecalculatePrices}
                            disabled={docketRecalculating}
                            className="flex items-center gap-2"
                          >
                            {docketRecalculating ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4" />
                            )}
                            Recalculate
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDocketAddLineItem}
                            className="flex items-center gap-2"
                            data-testid="add-line-item-btn"
                          >
                            <Plus className="h-4 w-4" />
                            Add Line
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table className="text-sm">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="label-overline w-[100px] text-xs px-2">Account ID</TableHead>
                          <TableHead className="label-overline text-xs px-2">Description</TableHead>
                          <TableHead className="label-overline w-[80px] text-xs px-2">Qty</TableHead>
                          <TableHead className="label-overline w-[80px] text-xs px-2">UOM</TableHead>
                          <TableHead className="label-overline w-[100px] text-xs px-2 text-right">Price</TableHead>
                          <TableHead className="label-overline w-[100px] text-xs px-2 text-right">Total</TableHead>
                          {viewingDocket?.status === 'draft' && (
                            <TableHead className="label-overline w-[40px] text-xs px-1"></TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {docketEditItems.map((item) => (
                          <TableRow key={item.id} className="h-10">
                            <TableCell className="px-2 py-1">
                              <Input
                                value={item.account_item_id || ''}
                                onChange={(e) => handleDocketEditItemChange(item.id, 'account_item_id', e.target.value)}
                                placeholder="ID"
                                className="font-mono text-xs h-7 px-2"
                                disabled={viewingDocket?.status !== 'draft'}
                              />
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              <div className="flex items-center gap-2">
                                {item.item_type && (
                                  <Badge 
                                    variant={item.item_type === 'equipment' ? 'default' : 'secondary'} 
                                    className={`text-[10px] px-1.5 py-0 shrink-0 ${item.item_type === 'worker' ? 'bg-blue-600 text-white' : ''}`}
                                  >
                                    {item.item_type === 'equipment' ? 'E' : item.item_type === 'worker' ? 'L' : 'A'}
                                  </Badge>
                                )}
                                <Input
                                  value={item.description || item.display_name || item.type_name || ''}
                                  onChange={(e) => handleDocketEditItemChange(item.id, 'description', e.target.value)}
                                  placeholder="Line item description..."
                                  className="text-xs h-7 px-2 flex-1"
                                  disabled={viewingDocket?.status !== 'draft'}
                                />
                              </div>
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              <Input
                                type="number"
                                step="0.5"
                                min="0"
                                value={item.qty ?? item.quantity ?? 0}
                                onChange={(e) => handleDocketEditItemChange(item.id, 'qty', parseFloat(e.target.value) || 0)}
                                className="font-mono text-xs h-7 px-2 w-full text-center"
                                disabled={viewingDocket?.status !== 'draft'}
                              />
                              {item.billed_qty != null && Number(item.billed_qty) !== Number(item.qty ?? item.quantity ?? 0) && (
                                <p className="text-[10px] text-muted-foreground mt-0.5 text-center" title={item.min_hire_note || 'Minimum hire applied'} data-testid={`min-hire-hint-${item.id}`}>
                                  Billed: {item.billed_qty} (min hire)
                                </p>
                              )}
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              <Select
                                value={item.uom || 'HOUR'}
                                onValueChange={(v) => handleDocketEditItemChange(item.id, 'uom', v)}
                                disabled={viewingDocket?.status !== 'draft'}
                              >
                                <SelectTrigger className="h-7 text-xs px-2">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="HOUR">HOUR</SelectItem>
                                  <SelectItem value="DAY">DAY</SelectItem>
                                  <SelectItem value="WEEK">WEEK</SelectItem>
                                  <SelectItem value="EACH">EACH</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="px-2 py-1 text-right">
                              <Input
                                type="number"
                                step="1"
                                min="0"
                                value={item.unit_price || item.rate || 0}
                                onChange={(e) => handleDocketEditItemChange(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                                className="font-mono text-xs h-7 px-2 text-right w-full"
                                disabled={viewingDocket?.status !== 'draft'}
                              />
                            </TableCell>
                            <TableCell className="px-2 py-1 text-right font-mono text-xs font-medium">
                              {tenantSettings.currency_symbol}{((parseFloat(item.billed_qty ?? item.qty ?? item.quantity ?? 0)) * (parseFloat(item.unit_price || item.rate || 0))).toFixed(2)}
                            </TableCell>
                            {viewingDocket?.status === 'draft' && (
                              <TableCell className="px-1 py-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDocketRemoveLineItem(item.id)}
                                  className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                                  data-testid={`remove-line-${item.id}`}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Totals Section */}
                  <div className="flex justify-end mt-4 pt-4 border-t">
                    <div className="w-64 space-y-2">
                      {(() => {
                        const subtotal = docketEditItems.reduce((sum, item) => sum + ((parseFloat(item.billed_qty ?? item.qty ?? item.quantity ?? 0)) * (parseFloat(item.unit_price || item.rate || 0))), 0);
                        const taxRate = tenantSettings.tax_name !== 'None' ? (tenantSettings.tax_rate || 0) : 0;
                        const taxAmount = subtotal * (taxRate / 100);
                        const total = subtotal + taxAmount;
                        return (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Subtotal</span>
                              <span className="font-mono">{tenantSettings.currency_symbol}{subtotal.toFixed(2)}</span>
                            </div>
                            {taxRate > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{tenantSettings.tax_name} ({taxRate}%)</span>
                                <span className="font-mono">{tenantSettings.currency_symbol}{taxAmount.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between pt-2 border-t">
                              <span className="font-semibold">Total</span>
                              <span className="text-xl font-bold font-mono text-[#ffd108]">
                                {tenantSettings.currency_symbol}{total.toFixed(2)}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="label-overline">Notes</Label>
                  <Textarea
                    value={docketEditNotes}
                    onChange={(e) => setDocketEditNotes(e.target.value)}
                    placeholder="Additional notes for this docket..."
                    rows={2}
                    disabled={viewingDocket?.status !== 'draft'}
                  />
                </div>

                {/* Save button for draft dockets only - shown inline in Invoice tab */}
                {viewingDocket?.status === 'draft' && (
                  <div className="flex justify-end pt-4 border-t mt-6">
                    <Button onClick={handleDocketSaveEdit} className="btn-volt" disabled={docketSaving}>
                      {docketSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          {/* Footer for Docket Tab - Always visible */}
          <SheetFooter className="pt-4 border-t mt-6 flex-wrap gap-2">
            <div className="flex gap-2 flex-1 flex-wrap">
              <Button variant="outline" onClick={() => handleGenerateDocketPDF(viewingDocket)} data-testid="schedule-docket-download-pdf-btn">
                <FileDown className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              {viewingDocket?.status === 'approved' && (
                <>
                  {tenantSettings.xero_connected && !viewingDocket.xero_invoice_id && (
                    <Button variant="outline" onClick={() => handleExportDocketToXero(viewingDocket)}>
                      <ExternalLink className="h-4 w-4 mr-2 text-[#13B5EA]" />
                      Export to Xero
                    </Button>
                  )}
                  {tenantSettings.myob_connected && !viewingDocket.myob_invoice_id && (
                    <Button variant="outline" onClick={() => handleExportDocketToMyob(viewingDocket)}>
                      <ExternalLink className="h-4 w-4 mr-2 text-purple-600" />
                      Export to MYOB
                    </Button>
                  )}
                  {tenantSettings.qbo_connected && !viewingDocket.qbo_invoice_id && (
                    <Button variant="outline" onClick={() => handleExportDocketToQbo(viewingDocket)}>
                      <ExternalLink className="h-4 w-4 mr-2 text-green-600" />
                      Export to QuickBooks
                    </Button>
                  )}
                  <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => handleResetDocketFromDrawer(viewingDocket.id)} data-testid="schedule-reset-docket-btn">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset Docket
                  </Button>
                </>
              )}
              {viewingDocket?.status === 'draft' && (
                <Button variant="outline" onClick={() => handleSubmitDocketForApproval(viewingDocket.id)}>
                  <Send className="h-4 w-4 mr-2" />
                  Submit for Approval
                </Button>
              )}
              {viewingDocket?.status === 'pending_approval' && (
                <>
                  <Button variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => handleApproveDocketFromDrawer(viewingDocket.id)}>
                    <Check className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => handleRejectDocketFromDrawer(viewingDocket.id)}>
                    <X className="h-4 w-4 mr-2" />
                    Return to Draft
                  </Button>
                </>
              )}
            </div>
            <Button variant="outline" onClick={() => setViewDocketDrawerOpen(false)}>
              Close
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Dockets List Drawer (for multi-day jobs) */}
      <Sheet open={docketsListDrawerOpen} onOpenChange={setDocketsListDrawerOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-500" />
              Job Dockets
              {selectedJobForDocketsList && (
                <Badge variant="secondary">
                  Job #{selectedJobForDocketsList.job_number}
                </Badge>
              )}
            </SheetTitle>
          </SheetHeader>
          
          <div className="py-4">
            {selectedJobForDocketsList && (
              <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                <p className="font-medium">{customers.find(c => c.id === selectedJobForDocketsList.customer_id)?.company_name || 'Unknown'}</p>
                <p className="text-sm text-muted-foreground">
                  {formatDateDisplay(selectedJobForDocketsList.start_date)} - {formatDateDisplay(selectedJobForDocketsList.end_date)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{jobDocketsList.length} docket(s)</p>
              </div>
            )}
            
            <div className="space-y-2">
              {jobDocketsList.map((docket) => (
                <div 
                  key={docket.id}
                  className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => handleOpenDocketFromList(docket)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{formatDateDisplay(docket.job_date)}</p>
                      <p className="text-sm text-muted-foreground">
                        {docket.start_time || '-'} - {docket.finish_time || '-'}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        docket.status === 'approved' ? 'bg-green-500/20 text-green-600' :
                        docket.status === 'pending_approval' ? 'bg-amber-500/20 text-amber-600' :
                        docket.status === 'exported' ? 'bg-blue-500/20 text-blue-600' :
                        'bg-gray-500/20 text-gray-600'
                      }>
                        {docket.status?.replace('_', ' ')}
                      </Badge>
                      <p className="text-sm font-medium mt-1">
                        {(() => {
                          const subtotal = docket.items?.reduce((sum, item) => sum + (parseFloat(item.billed_qty ?? item.qty ?? item.quantity ?? 0)) * (parseFloat(item.unit_price || item.rate || 0)), 0) || 0;
                          const taxRate = tenantSettings.tax_name !== 'None' ? (tenantSettings.tax_rate || 0) : 0;
                          const total = subtotal * (1 + taxRate / 100);
                          return `${tenantSettings.currency_symbol}${total.toFixed(2)}`;
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              
              {jobDocketsList.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No dockets found for this job</p>
              )}
            </div>
          </div>
          
          <SheetFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setDocketsListDrawerOpen(false)}>Close</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Allocation Conflict Warning Dialog */}
      <PinpointMapModal
        open={pinpointMapOpen}
        onOpenChange={setPinpointMapOpen}
        country={tenantSettings.country}
        onConfirm={({ latitude, longitude, address, street_address, suburb, state, postcode }) => {
          setJobForm(prev => {
            const next = { ...prev, latitude, longitude };
            // Pinpoint always overrides location with the dropped pin's address
            if (street_address || suburb || address) {
              next.use_one_off_address = true;
              next.site_id = '';
              next.one_off_street = street_address || address || '';
              next.one_off_suburb = suburb || '';
              next.one_off_state = state || '';
              next.one_off_postcode = postcode || '';
            }
            return next;
          });
        }}
        initialLat={jobForm.latitude}
        initialLng={jobForm.longitude}
        initialAddress={(() => {
          if (jobForm.site_id) {
            const s = sites.find(s => s.id === jobForm.site_id);
            return s ? formatSiteAddress(s) : '';
          }
          return jobForm.one_off_street ? `${jobForm.one_off_street}, ${jobForm.one_off_suburb || ''} ${jobForm.one_off_state || ''}` : '';
        })()}
      />
      <AlertDialog open={showConflictDialog} onOpenChange={setShowConflictDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Allocation Conflict Detected
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  The following conflicts were found. Do you want to proceed anyway?
                </p>
                <div className="max-h-[300px] overflow-y-auto space-y-2">
                  {conflictWarnings.map((warning, idx) => (
                    <div 
                      key={`row-${idx}`} 
                      className={`p-3 border rounded-lg ${
                        warning.type === 'leave_conflict' && warning.severity === 'info'
                          ? 'border-muted bg-muted/40'
                          : 'border-amber-200 bg-amber-50 dark:bg-amber-950/20'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {warning.type === 'operator_conflict' && (
                          <User className="h-4 w-4 text-amber-600 mt-0.5" />
                        )}
                        {warning.type === 'equipment_conflict' && (
                          <Truck className="h-4 w-4 text-amber-600 mt-0.5" />
                        )}
                        {warning.type === 'worker_conflict' && (
                          <Users className="h-4 w-4 text-amber-600 mt-0.5" />
                        )}
                        {warning.type === 'combo_member_conflict' && (
                          <Layers className="h-4 w-4 text-amber-600 mt-0.5" />
                        )}
                        {warning.type === 'leave_conflict' && (
                          <Calendar className={`h-4 w-4 mt-0.5 ${warning.severity === 'info' ? 'text-muted-foreground' : 'text-amber-600'}`} />
                        )}
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${
                            warning.type === 'leave_conflict' && warning.severity === 'info'
                              ? 'text-muted-foreground'
                              : 'text-amber-800 dark:text-amber-200'
                          }`}>
                            {warning.type === 'operator_conflict' && 'Operator Conflict'}
                            {warning.type === 'equipment_conflict' && 'Equipment Conflict'}
                            {warning.type === 'worker_conflict' && 'Worker Conflict'}
                            {warning.type === 'combo_member_conflict' && 'Combo Asset Conflict'}
                            {warning.type === 'leave_conflict' && (warning.severity === 'info' ? 'Leave Info' : 'Leave Conflict')}
                          </p>
                          <p className={`text-sm ${
                            warning.type === 'leave_conflict' && warning.severity === 'info'
                              ? 'text-muted-foreground'
                              : 'text-amber-700 dark:text-amber-300'
                          }`}>
                            {warning.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setConflictWarnings([]);
              setPendingAllocationData(null);
              setConflictContext(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={proceedWithAllocation}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Proceed Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete allocation block confirmation */}
      <AlertDialog open={!!deleteBlockConfirm} onOpenChange={(open) => { if (!open) setDeleteBlockConfirm(null); }}>
        <AlertDialogContent className="max-w-sm" data-testid="delete-block-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Allocation Block</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this allocation block? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-block-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteMultiDayAllocation}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-block-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* EW2-311 — Designated Operator unavailable warning */}
      <AlertDialog open={designatedOpWarning.open} onOpenChange={(o) => { if (!o) setDesignatedOpWarning({ open: false, operator: null, reason: null, equipmentId: null }); }}>
        <AlertDialogContent className="max-w-md" data-testid="designated-operator-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Designated Operator Unavailable
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="text-sm mt-2">
                <span className="font-semibold">{designatedOpWarning.operator?.name}</span> is the designated operator for this asset but is 
                <span className="font-semibold text-amber-600">
                  {designatedOpWarning.reason === 'already_allocated' && ' already allocated to another job'}
                  {designatedOpWarning.reason === 'on_leave' && ' on leave'}
                  {designatedOpWarning.reason === 'inactive' && ' inactive'}
                </span> on this date.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setDesignatedOpWarning({ open: false, operator: null, reason: null, equipmentId: null })}>
              Leave Unassigned
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => {
                setAllocationForm(prev => ({ ...prev, operator_id: designatedOpWarning.operator?.id }));
                toast.info(`Override: assigned ${designatedOpWarning.operator?.name} despite being unavailable`);
                setDesignatedOpWarning({ open: false, operator: null, reason: null, equipmentId: null });
              }}
              data-testid="designated-op-override-btn"
            >
              Assign Anyway
            </AlertDialogAction>
            <Button
              variant="outline"
              onClick={() => {
                setDesignatedOpWarning({ open: false, operator: null, reason: null, equipmentId: null });
                // Focus the operator select — user picks someone else
                toast.info('Select a different operator from the dropdown below');
              }}
              data-testid="designated-op-pick-other-btn"
            >
              Pick Someone Else
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* EW2-286 — Compliance dialog: block (mandatory) or warning (preferred) */}
      {/* Leave Override Dialog */}
      <LeaveOverrideDialog
        open={leaveOverride.open}
        blocks={leaveOverride.blocks}
        onCancel={() => setLeaveOverride({ open: false, blocks: [], callback: null })}
        onOverride={(reason) => {
          const cb = leaveOverride.callback;
          setLeaveOverride({ open: false, blocks: [], callback: null });
          if (cb) cb(reason);
        }}
      />

      <AlertDialog open={complianceBlock.open} onOpenChange={(o) => { if (!o) { setComplianceBlock({ open: false }); setComplianceReason(''); } }}>
        <AlertDialogContent className="max-w-lg" data-testid="compliance-block-dialog">
          <AlertDialogHeader>
            {complianceBlock.mode === 'warning' ? (
              <AlertDialogTitle className="flex items-center gap-2 text-amber-600" data-testid="compliance-warning-title">
                <AlertTriangle className="h-5 w-5" />
                Preferred compliance missing
              </AlertDialogTitle>
            ) : (
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <Shield className="h-5 w-5" />
                Mandatory compliance missing
              </AlertDialogTitle>
            )}
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-1">
                <p data-testid="compliance-block-message">{complianceBlock.message}</p>
                {(complianceBlock.missing || []).length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="compliance-missing-list">
                    {complianceBlock.missing.map((m) => (
                      <Badge key={m.id} variant={complianceBlock.mode === 'warning' ? 'outline' : 'destructive'} className="text-xs">
                        {m.name}{m.source ? ` · ${m.source}` : ''}
                      </Badge>
                    ))}
                  </div>
                )}
                {complianceBlock.mode === 'warning' ? (
                  <p className="text-xs text-muted-foreground pt-1">
                    Preferred (not mandatory). You can proceed with this allocation.
                  </p>
                ) : complianceBlock.canOverride ? (
                  <div className="space-y-2 pt-1">
                    <Label className="text-sm font-medium text-foreground">Override reason (required)</Label>
                    <Textarea
                      data-testid="compliance-override-reason"
                      value={complianceReason}
                      onChange={(e) => setComplianceReason(e.target.value)}
                      placeholder="e.g. Training booked for next week; supervisor approved"
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">This override will be recorded in the audit log.</p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground pt-1">
                    Only an admin can override this. Please select a different operator or arrange the required training.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="compliance-block-cancel" onClick={() => { setComplianceBlock({ open: false }); setComplianceReason(''); }}>
              Cancel
            </AlertDialogCancel>
            {complianceBlock.mode === 'warning' ? (
              <AlertDialogAction
                data-testid="compliance-warning-proceed"
                onClick={(e) => { e.preventDefault(); confirmComplianceWarning(); }}
                className="bg-amber-500 hover:bg-amber-600"
              >
                Proceed Anyway
              </AlertDialogAction>
            ) : complianceBlock.canOverride && (
              <AlertDialogAction
                data-testid="compliance-override-confirm"
                onClick={(e) => { e.preventDefault(); confirmComplianceOverride(); }}
                className="bg-red-600 hover:bg-red-700"
              >
                Override &amp; Assign
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reschedule per-day override strategy prompt (Option A) */}
      <AlertDialog open={reschedulePrompt.open} onOpenChange={(o) => { if (!o) setReschedulePrompt(p => ({ ...p, open: false })); }}>
        <AlertDialogContent data-testid="reschedule-overrides-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              This job has custom day times
            </AlertDialogTitle>
            <AlertDialogDescription>
              You changed this job's dates. Some days have custom start/end times. What should happen to them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-2 py-2">
            <Button className="btn-volt justify-start" onClick={() => applyRescheduleStrategy('shift')} data-testid="reschedule-shift" autoFocus>
              Move times with the job (recommended)
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => applyRescheduleStrategy('keep')} data-testid="reschedule-keep">
              Keep times on their original dates
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => applyRescheduleStrategy('reset')} data-testid="reschedule-reset">
              Reset all custom times to the job default
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reschedule-cancel">Decide later</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Delete Job Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteJobDialogOpen}
        onOpenChange={setDeleteJobDialogOpen}
        title="Delete Job"
        description={`Delete job "${jobToDelete?.job_number || ''}"? This will also delete all associated allocations. This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleConfirmDeleteJob}
        isLoading={isDeletingJob}
        testId="delete-job-dialog"
      />

      {/* Cancel Job Dialog */}
      <CancelJobDialog
        open={cancelJobDialogOpen}
        onOpenChange={setCancelJobDialogOpen}
        job={jobToCancel}
        customerEmail={getCustomerEmail(jobToCancel)}
        reasons={tenantSettings.cancellation_reasons}
        onConfirm={handleConfirmCancelJob}
        submitting={isCancellingJob}
      />

      {/* Notify Operator Dialog */}
      <NotifyOperatorDialog
        open={notifyDialogOpen}
        onOpenChange={setNotifyDialogOpen}
        job={notifyJobData}
        allocation={notifyAllocationData}
        onSent={fetchData}
      />

      {/* Mobilisation Dialog — prompted after creating a job with setup profile */}
      <MobilisationDialog
        open={mobilisationPrompt.open}
        onOpenChange={(open) => {
          if (!open) setMobilisationPrompt({ open: false, jobId: null, jobData: null });
        }}
        api={api}
        jobId={mobilisationPrompt.jobId}
        jobData={mobilisationPrompt.jobData}
        equipmentTypes={equipmentTypesWithIds}
        onComplete={() => {
          setMobilisationPrompt({ open: false, jobId: null, jobData: null });
          fetchData();
          // Refresh the editing job so the setup group panel shows
          if (editingJob && mobilisationPrompt.jobId === editingJob.id) {
            api.get('/jobs').then(res => {
              const fresh = res.data.find(j => j.id === editingJob.id);
              if (fresh) setEditingJob(fresh);
            }).catch(() => {});
          }
        }}
      />

      {/* Transport Prompt Dialog — Mobilisation Phase 2 */}
      <TransportPromptDialog
        open={transportPrompt.open}
        onOpenChange={(open) => {
          if (!open) setTransportPrompt({ open: false, jobId: null, jobData: null, equipmentType: null, transportType: null });
        }}
        api={api}
        jobId={transportPrompt.jobId}
        jobData={transportPrompt.jobData}
        equipmentType={transportPrompt.equipmentType}
        transportType={transportPrompt.transportType}
        onComplete={() => {
          setTransportPrompt({ open: false, jobId: null, jobData: null, equipmentType: null, transportType: null });
          fetchData();
        }}
      />
      <CompliancePackModal
        open={!!compliancePackJob}
        onOpenChange={(o) => { if (!o) setCompliancePackJob(null); }}
        jobId={compliancePackJob?.id}
        jobNumber={compliancePackJob?.job_number}
      />
      {confirmJobId && (
        <SendJobConfirmationModal
          open={!!confirmJobId}
          onOpenChange={(v) => { if (!v) setConfirmJobId(null); }}
          jobId={confirmJobId}
          api={api}
          onSent={() => { setConfirmJobId(null); fetchData(); }}
        />
      )}
      {emailJob && (
        <NotifyWorkersDialog
          open={!!emailJob}
          onOpenChange={(o) => { if (!o) setEmailJob(null); }}
          api={api}
          jobId={emailJob.id}
          job={emailJob}
          jobLabel={`Job #${emailJob.job_number || ''}`}
          onSent={fetchData}
        />
      )}

      {/* ── Recurring Series Management Dialogs ── */}
      {editingJob?.recurrence_group_id && (
        <>
          <ViewPlanDialog
            open={viewPlanOpen}
            onOpenChange={setViewPlanOpen}
            groupId={editingJob.recurrence_group_id}
            currentJobId={editingJob.id}
            api={api}
            onNavigateToJob={async (j) => {
              // Navigate calendar to target date (must be a Date, not string)
              setCurrentDate(parse(j.start_date, 'yyyy-MM-dd', new Date()));
              // Swap job in-place within the already-open dialog — no close/reopen
              // avoids Radix Dialog overlay stacking from overlapping exit/enter animations
              let targetJob = jobs.find(x => x.id === j.id);
              if (!targetJob) {
                // Fallback: fetch the job directly if not in current list
                try {
                  const res = await api.get(`/jobs/${j.id}`);
                  targetJob = res.data;
                } catch { /* ignore */ }
              }
              if (targetJob) {
                handleOpenJobDialog(targetJob);
              }
            }}
            onCancelFuture={() => {
              setViewPlanOpen(false);
              setCancelFutureOpen(true);
            }}
          />
          <EditFutureDialog
            open={editFutureOpen}
            onOpenChange={setEditFutureOpen}
            groupId={editingJob.recurrence_group_id}
            job={editingJob}
            api={api}
            onSuccess={fetchData}
          />
          <CancelFutureDialog
            open={cancelFutureOpen}
            onOpenChange={setCancelFutureOpen}
            groupId={editingJob.recurrence_group_id}
            fromDate={editingJob.start_date}
            api={api}
            onSuccess={() => { fetchData(); setJobDialogOpen(false); }}
          />
          <ChangePatternDialog
            open={changePatternOpen}
            onOpenChange={setChangePatternOpen}
            groupId={editingJob.recurrence_group_id}
            currentConfig={editingJob.recurrence_config}
            fromDate={editingJob.start_date}
            api={api}
            onSuccess={fetchData}
          />
        </>
      )}
    </div>
  );
};

export default Schedule;
